//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: ESRI Map component
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001   30.07.21 Sean Flook          WI39??? Initial Revision.
//    002   17.03.23 Sean Flook          WI40585 Use property wizard when creating properties.
//    003   21.03.23 Sean Flook          WI40592 Do not allow properties to be created on a closed street or a type 3/4 street.
//    004   28.03.23 Sean Flook          WI40632 Set the source for onWizardDone.
//    005   05.04.23 Sean Flook          WI40596 If opening an historic property display the warning dialog.
//    006   28.06.23 Sean Flook          WI40256 Changed Extent to Provenance where appropriate.
//    007   30.06.23 Sean Flook          WI40770 Set suffix letters to uppercase.
//    008   24.07.23 Sean Flook                  Ensure the sketch tools are displayed if a new record is added.
//    009   10.08.23 Sean Flook                  Removed ASD type 66 layer as not required as it will always be a whole road.
//    010   10.08.23 Sean Flook                  Changes required for @arcgis/core version 4.27.6.
//    011   07.09.23 Sean Flook                  Added code to handle unassigned ESUs and for dividing and assigning ESUs.
//    012   20.09.23 Sean Flook                  Implemented ability to show street or property in Google street view. Also few other bug fixes.
//    013   22.09.23 Sean Flook                  Changes required to handle Scottish classifications.
//    014   06.10.23 Sean Flook                  Changes required to handle GeoPlace ASD records.
//    014   10.10.23 Sean Flook        IMANN-163 Changes required for opening the related tab after the property wizard.
//    015   11.10.23 Sean Flook        IMANN-163 Use the centralised doOpenRecord method.
//    016   11.10.23 Sean Flook        IMANN-163 Correctly handle historic properties.
//    017   16.10.23 Sean Flook                  Comment out the type 64 and 66 layers until they have been implemented in the API.
//    018   27.10.23 Sean Flook                  Updated call to SavePropertyAndUpdate.
//    019   03.11.23 Sean Flook        IMANN-175 Added code to allow properties to be selected.
//    020   10.11.23 Sean Flook        IMANN-175 Added code try and correctly highlight properties after doing a move BLPU.
//    021   20.11.23 Sean Flook                  Added street BLPU to the list of classifications that display an icon.
//    022   24.11.23 Sean Flook                  Moved Box and Stack to @mui/system and changed code to use map rather than foreach.
//    023   06.12.23 Sean Flook        IMANN-149 Uncomment the type 64 and 66 layers as the API has now been written.
//    024   19.12.23 Sean Flook                  Various bug fixes.
//    025   20.12.23 Sean Flook        IMANN-152 Display a dialog when user selects the polyline tool when editing.
//    026   02.01.24 Sean Flook                  Changes required to load shape files.
//    027   02.01.24 Sean Flook                  Handle errors when loading shape files.
//    028   03.01.24 Sean Flook                  Fixed warning.
//    029   05.01.24 Sean Flook                  use CSS shortcuts.
//    030   10.01.24 Sean Flook        IMANN-215 Allow ESUs to be assigned to a street when creating a new street.
//    031   11.01.24 Sean Flook        IMANN-163 Close the add property wizard dialog when clicking on view properties.
//    032   25.01.24 Sean Flook                  Changes required after UX review and some fixes for bugs/warnings.
//    033   26.01.24 Sean Flook        IMANN-260 Corrected field name.
//    034   06.02.24 Sean Flook                  Updated street view icon.
//    035   07.02.24 Sean Flook                  Changes required to support viaEuropa mapping for OneScotland.
//    036   07.02.24 Sean Flook                  Changes required to support WFS from viaEuropa mapping for OneScotland.
//    037   08.02.24 Sean Flook                  Added a fix for using viaEuropa mapping with ArcGIS.
//    038   09.02.24 Sean Flook                  Removed Divide and Assign from the street actions.
//    039   09.02.24 Sean Flook                  Modified handleHistoricPropertyClose to handle returning an action from the historic property warning dialog.
//    040   09.02.24 Sean Flook                  Change to use different line symbols for each type of ASD record.
//    041   13.02.24 Sean Flook                  Ensure the ASD geometries are displayed when creating a new street.
//    042   14.02.24 Sean Flook         ASD10_GP When editing an ASD record hide the other ASD geometries.
//    043   14.02.24 Sean Flook         ASD10_GP Filter the current ASD layer to the one that is currently being viewed.
//    044   14.02.24 Sean Flook                  Added a bit of error trapping.
//    045   16.02.24 Sean Flook                  Corrected the parameters in GetViaEuropaFeatureAtCoord.
//    046   16.02.24 Sean Flook         ESU16_GP Whilst assigning ESU prevent anything else from occurring with the ESUs.
//    047   20.02.24 Sean Flook             MUL1 Changes required to selecting properties from the map.
//    048   22.02.24 Sean Flook          ESU3_GP Set the fillOpacity on the highlight to 0.25.
//    049   08.03.24 Sean Flook        IMANN-348 Use the new hasStreetChanged and hasPropertyChanged methods.
//    050   11.03.24 Sean Flook            GLB12 Adjusted height to remove gap.
//    051   11.03.24 Sean Flook         ESU29_GP Set ASD visibility after creating and adding to map.
//    052   22.03.24 Sean Flook            GLB12 Changed to use dataFormStyle so height can be correctly set.
//    053   08.04.24 Sean Flook            STRT4 Changes to allow for authority extent defaults. Changes required to prevent crash when refreshing page.
//    054   16.04.24 Sean Flook                  When loading a SHP file use the mapContext to retain the information and display the layer in the map.
//    055   18.04.24 Sean Flook        IMANN-351 Changes required to prevent crashes when refreshing the page.
//    056   02.05.24 Joshua McCormick  IMANN-283 Set GetStreetTypeLabel and GetStreetStateLabel return street code set to false with map overlay
//    057   02.05.24 Joshua McCormick  IMANN-283 GetStreetTypeLabel and GetStreetStateLabel return street code false for all instances
//    058   03.05.24 Sean Flook                  Moved functions out of useEffect by using useCallback.
//    059   14.05.24 Sean Flook        IMANN-206 Changes required to display all the provenances.
//    060   14.05.24 Sean Flook        IMANN-206 Include property actions to the background provenance layer information dialog.
//    061   20.05.24 Sean Flook        IMANN-444 Refresh the snap layers after loading a SHP file.
//    062   20.05.24 Sean Flook        IMANN-476 Check view has been created first in fadeVisibilityOn.
//    063   21.05.24 Sean Flook        IMANN-462 Moved loading of base mapping into its own method and wait for details before we try and load it.
//    064   04.06.24 Sean Flook        IMANN-507 After redrawing a street or property if we are editing the graphic ensure the popup is disabled and the edit graphics layer is on top.
//    065   06.06.24 Sean Flook        IMANN-522 Always allow editing of provenances.
//    066   11.06.24 Sean Flook        IMANN-527 Corrected values in streetRenderer.
//    067   12.06.24 Sean Flook        IMANN-565 Handle polygon deletion.
//    068   19.06.24 Sean Flook        IMANN-629 Changes to code so that current user is remembered and a 401 error displays the login dialog.
//    069   20.06.24 Sean Flook        IMANN-636 Use the new user rights.
//    070   24.06.24 Sean Flook        IMANN-170 Changes required for cascading parent PAO changes to children.
//    071   26.06.24 Sean Flook        IMANN-586 Reset the snapping layers after the edit graphics layer has been updated.
//    072   26.06.24 Joshua McCormick  IMANN-548 zoomStreet and property fix, zoomPropertyDataRef length > 0 check
//    073   28.06.24 Sean Flook                  Set the active tool in the sketch widget, needs to be uncommented once ESRI has been updated.
//    074   01.07.24 Sean Flook        IMANN-583 Only remove the graphics from the edit layer when required.
//    075   01.07.24 Sean Flook        IMANN-592 When selecting to open a street or property that is not currently part of the map search data add it.
//    076   02.07.24 Sean Flook        IMANN-507 Only add the edit graphics layer once.
//    077   02.07.24 Sean Flook        IMANN-507 When editing a property ensure the edit layer is top of the list.
//    078   02.07.24 Sean Flook        IMANN-689 Set the unassigned ESUs layer visibility to true.
//    079   08.07.24 Sean Flook        IMANN-728 Pass in the new parameter to onExtentChange.
//    080   10.07.24 Sean Flook        IMANN-742 Only allow properties to be added to a street if the user has permission to do that.
//    081   17.07.24 Sean Flook        IMANN-596 When selecting objects ensure at least 500 milliseconds have elapsed since the last selection, this is a work around for an ESRI bug.
//    082   18.07.24 Sean Flook        IMANN-772 Corrected field name.
//    083   22.07.24 Sean Flook        IMANN-774 Added hack to cater for ESRI double event issue for when selecting ESUs.
//    084   13.08.24 Sean Flook        IMANN-918 Do not display the sketch tool if the user does not have rights to edit the object.
//    085   27.08.24 Sean Flook        IMANN-888 Ensure the background streets are redrawn when creating a new street whilst already looking at another street.
//    086   28.08.24 Sean Flook        IMANN-957 Handle if formattedAddress  is missing from the property data.
//    087   03.09.24 Sean Flook        IMANN-972 Prevent infinite loop from occurring.
//    088   10.09.24 Sean Flook        IMANN-980 Only write to the console if the user has the showMessages right.
//endregion Version 1.0.0.0
//region Version 1.0.1.0
//    089   27.09.24 Sean Flook        IMANN-573 when creating a new child or range of children check the parent is not already at the maximum allowable level.
//    090   01.10.24 Sean Flook        IMANN-713 Changed the text on the buttons for adding a property/ies to a street.
//    091   01.10.24 Sean Flook        IMANN-993 Display the extent merge tool.
//    092   03.10.24 Sean Flook        IMANN-958 Check user has property edit permission before trying to create a child/children.
//    093   04.10.24 Sean Flook       IMANN-1005 Use a different colour for a closed street.
//    094   14.10.24 Sean Flook       IMANN-1016 Changes required to handle LLPG Streets.
//    095   14.10.24 Sean Flook       IMANN-1024 Call onEditMapObject when opening a property.
//    096   28.10.24 Joshua McCormick  IMANN-904 useEffect for mapContext.currentClearObject
//    097   05.11.24 Sean Flook        IMANN-904 When clearing the current edit object clear the geometry as well.
//    098   06.11.24 Sean Flook       IMANN-1047 Undo changes done for IMANN-904.
//endregion Version 1.0.1.0
//region Version 1.0.2.0
//    099   21.11.24 Sean Flook       IMANN-1029 Use the correct UPRN when calling GetParentHierarchy.
//endregion Version 1.0.2.0
//region Version 1.0.3.0
//    100   06.01.25 Sean Flook       IMANN-1121 Tidied up code around UI controls.
//    101   06.01.25 Sean Flook       IMANN-1123 Changed Multiple properties to Multiple addresses in popup.
//    102   06.01.25 Sean Flook       IMANN-1123 Changed issue number above.
//    103   07.01.25 Sean Flook       IMANN-1123 Sort properties on logical status to try and get the cluster symbol correct.
//    104   07.01.25 Sean Flook       IMANN-1123 Fixed typo.
//    105   07.01.25 Sean Flook       IMANN-1119 Added a new field to the property features called DisplayLogicalStatus to be used for the display of the symbols.
//    106   09.01.25 Sean Flook        IMANN-781 Include the pkId in the extent object.
//    107   09.01.25 Sean Flook       IMANN-1125 Correctly check for nulls when determining if background data is the same.
//    108   09.01.25 Sean Flook       IMANN-1125 Remove console logs where not required.
//    109   10.01.25 Sean Flook        IMANN-781 Do not redo the layers when editing a provenance record.
//    110   13.01.25 Sean Flook       IMANN-1135 If background streets layer was visible when selecting properties keep it visible.
//    111   13.01.25 Sean Flook       IMANN-1135 Remember the visibility of the layers between redraws.
//    112   13.01.25 Sean Flook       IMANN-1136 Various changes to improve editing provenances.
//    113   13.01.25 Sean Flook       IMANN-1132 Ensure the reordering of the layers is correctly run.
//    114   16.01.25 Sean Flook       IMANN-1135 When selecting properties adjust the opacity of the street layer.
//    115   16.01.25 Sean Flook       IMANN-1136 Correctly handle merging provenance extents.
//endregion Version 1.0.3.0
//region Version 1.0.4.0
//    116   04.02.25 Sean Flook       IMANN-1677 Save the correct object to historicRec when opening a historic property.
//    117   11.02.25 Sean Flook       IMANN-1680 Added some error handling.
//    118   12.02.25 Sean Flook       IMANN-1684 Changes required to set the map extent to the authorities extent when returning to the gazetteer page with no search results.
//    119   12.02.25 Sean Flook       IMANN-1687 Increase the length of the doubleEventWait time.
//endregion Version 1.0.4.0
//region Version 1.0.5.0
//    120   27.01.25 Sean Flook       IMANN-1077 Added some error handling.
//    121   30.01.25 Sean Flook       IMANN-1673 Changes required for new user settings API.
//    122   14.03.25 Sean Flook        IMANN-963 Prevent the selection control from displaying if the user cannot edit the data.
//    123   17.03.25 Sean Flook       IMANN-1691 Include the USRN in the parent property details when creating a child/children.
//    124   17.03.25 Sean Flook       IMANN-1710 Only zoom to the full extent if we are not editing an object.
//    125   18.03.25 Sean Flook       IMANN-1695 Changes to how selecting properties are handled to make things more robust.
//    126   18.03.25 Sean Flook       IMANN-1695 Ensure the list of selected UPRNs are numbers.
//    127   25.03.25 Sean Flook       IMANN-1708 Only set the active sketch tool to polyline if creating a new ASD part road.
//    128   26.03.25 Sean Flook       IMANN-1708 Set the active tool to the select tool when creating or editing an ASD part road.
//    129   23.04.25 Sean Flook       IMANN-1768 Changes required for selecting properties in property and street forms.
//    130   28.04.25 Sean Flook       IMANN-1704 Ensure the edit layer is cleared when it needs to be.
//    131   02.06.25 Sean Flook       IMANN-1803 Only zoom to property if we have the data.
//endregion Version 1.0.5.0
//region Version 1.0.6.0
//    132   20.06.25 Sean Flook       IMANN-1813 Handle errors from export.
//    133   30.06.25 Sean Flook       IMANN-1833 When generating the street map data if there are no ESUs generate a dummy one using the start and end coordinates of the street.
//    134   04.07.25 Sean Flook                  Set the new variant parameter for the ADSSelectionControl.
//    135   22.07.25 Sean Flook       IMANN-1855 Use the new ADSCircularProgress component.
//    136   22.07.25 Sean Flook       IMANN-1855 Set the mapProgress parameter on the ADSCircularProgress component for loading the map.
//    137   05.08.25 Sean Flook       IMANN-1929 Modified checkForUnsavedChanges to use the correct variable.
//    138   06.08.25 Sean Flook       IMANN-1964 Correctly handle opening a property from its polygon.
//endregion Version 1.0.6.0
//region Version 1.0.6.1
//    139   02.09.25 Sean Flook       IMANN-2011 If a parent has been moved ask user if they also want to move its children as well.
//    140   04.09.25 Sean Flook       IMANN-2023 Comment out code to ask user if they want to edit historic properties.
//    141   04.09.25 Sean Flook       IMANN-2015 Sort the propertyDataRef so the lowest logical status is last so it will be displayed correctly.
//endregion Version 1.0.6.1
//region Version 1.0.6.2
//    142   09.09.25 Sean Flook       IMANN-2032 Increased the maxZoom to 23.
//endregion Version 1.0.6.2
//region Version 1.0.6.4
//    143   17.09.25 Sean Flook       IMANN-1998 A polygon with multiple rings is now supported.
//    144   29.09.25 Sean Flook       IMANN-2067 Include prowStatus in data used for ASD 66 layer.
//    145   30.09.25 Sean Flook       IMANN-2076 Modified handleMergeFeatures to handle disjointed polygons.
//    146   02.10.25 Sean Flook       IMANN-2076 Changes required to handle MULTIPOLYGONs.
//endregion Version 1.0.6.4
//region Version 1.0.7.0
//    147   13.06.25 Sean Flook       IMANN-1812 Upgraded to the latest ESRI controls and changed name of file.
//    148   22.08.25 Sean Flook       IMANN-1812 Do not refresh features when they are highlighted.
//    149   26.08.25 Sean Flook       IMANN-1812 After removing the highlight set the variable to null.
//    150   27.08.25 Sean Flook       IMANN-1812 Only highlight features when they are selected.
//    151   01.09.25 Sean Flook       IMANN-1812 Changes required to update React-Router-Dom to v6.
//    152   04.09.25 Sean Flook       IMANN-1812 Changes required to replace React-Router-Dom with React_Router v7.8.2.
//    153   10.09.25 Sean Flook       IMANN-1812 Changes required to implement basemap gallery.
//    154   15.09.25 Sean Flook       IMANN-1812 Changes required to add slider to change layer opacity and to remember the value set between sessions.
//    155   16.09.25 Sean Flook       IMANN-1962 Make the background provenance layer visible by default and added code to be able to save the visibility of background layers between sessions.
//    156   16.09.25 Sean Flook       IMANN-1963 Use getPropertyExtent to get the extent of the property including the provenances.
//    157   18.09.25 Sean Flook       IMANN-1962 Replace instances of mapContext.layerVisibility with getLayerVisibility.
//    158   23.09.25 Sean Flook       IMANN-2041 Changes required to show/hide historical provenance data.
//    159   24.09.25 Sean Flook       IMANN-1031 Changed updatingMap to be numeric.
//    160   26.09.25 Sean Flook       IMANN-1705 Added code to zoom into the geometry when it is being edited.
//    161   29.09.25 Sean Flook       IMANN-2041 Added missing code for showing/hiding historical provenance data when editing a property.
//    162   07.10.25 Sean Flook       IMANN-2041 Fixed some issue with showing/hiding historical provenance data when editing a property.
//    163   24.10.25 Sean Flook       IMANN-1129 Changes required to style shape files when loaded.
//    164   24.10.25 Sean Flook       IMANN-2097 Changes required to load georeferenced image files.
//    165   27.10.25 Sean Flook       IMANN-2092 Use the new spatialReferenceWkid property from mapContext to set the spatialReference used by the layers in the map.
//    166   30.10.25 Sean Flook       IMANN-2092 Use the new getCoordRefSystemName method to get the correct label for the coordinate conversion widget.
//    167   30.10.25 Sean Flook       IMANN-1812 Changes to use the new mapLayer fields and handle customer layers.
//    168   31.10.25 Sean Flook       IMANN-1812 The expand component will automatically use the icon of the component placed within the Expand if it has one.
//    169   03.11.25 Sean Flook       IMANN-2073 Fixed zooming to a street or properties as well as adding ability to zoom to a group of streets and properties.
//    170   06.11.25 Sean Flook       IMANN-2092 Various changes to display the Jersey basemap.
//    171   07.11.25 Sean Flook       IMANN-2097 Changes required to load georeferenced image file and world file.
//    172   11.11.25 Sean Flook       IMANN-2092 Further changes required to display the Jersey basemap.
//    173   12.11.25 Sean Flook       IMANN-2092 Correctly display the coordinates at the cursor.
//    174   05.12.25 Sean Flook       IMANN-2190 Added useSearchExtent to mapContext to flag whether to use the search results when setting the map extent or not.
//    175   17.12.25 Sean Flook       IMANN-2196 Added the information control and the selection control to the map. Also, various required bug fixes.
//    176   17.12.25 Sean Flook       IMANN-2197 Correctly call resetSandbox.
//    177   17.12.25 Sean Flook       IMANN-1705 Use new refreshSketchLayer from mapContext to handle refreshing the sketch layer.
//    178   21.01.26 Sean Flook       IMANN-2204 Various fixes for polygons and replaced GetWktCoordinates, GetPolylineAsWKT and GetPolygonAsWKT with wktToGeometry and geometryToWKT.
//    179   22.01.26 Sean Flook       IMANN-2213 Added a button to clear distance and area measurements.
//    180   23.01.26 Sean Flook       IMANN-2206 Moved call to mapContext.onUseSearchExtentChange.
//    181   26.01.26 Sean Flook       IMANN-2216 Correctly work out the extent for a zoom to street.
//    182   27.01.26 Joshua McCormick IMANN-2221 Add null check for extentData.current to fix new provenance errors
//    183   28.01.26 Sean Flook       IMANN-2255 Add the shape and georeferenced layers below the street and property layers and also disable the popup when editing.
//    184   28.01.26 Sean Flook       IMANN-2215 Ensure snapping features are enabled.
//    185   29.01.26 Sean Flook       IMANN-2258 Display the sketch delete button when required.
//    186   30.01.26 Sean Flook       IMANN-2109 Removed hard coded Jersey base map data as no longer needed since the API has been fixed.
//    187   04.02.26 Joshua McCormick IMANN-2261 Fix street records by USRN to prevent duplicate popup entries.
//    188   11.02.26 Sean Flook       IMANN-2282 Split street and property layer maintenance into separate useEffects.
//    189   11.02.26 Sean Flook       IMANN-2261 Ensure the street and property data is removed from the background data when displaying the data.
//    190   11.02.26 Sean Flook       IMANN-2261 Use the current search data if in the gazetteer map to prevent drawing the background data.
//    191   17.02.26 Sean Flook       IMANN-2262 Corrected geometry type for the type 52 layer.
//    192   20.02.26 Sean Flook       IMANN-2329 Updated call to resetPropertyErrors.
//    193   24.02.26 Sean Flook       IMANN-2340 Changes required to correctly handle deleting an extent.
//    194   25.02.26 Sean Flook       IMANN-2238 Modified call to UpdateAfterSave.
//    195   25.02.26 Sean Flook       IMANN-2341 Changes required to correctly handle deleting an ESU geometry.
//    196   06.03.26 Sean Flook       IMANN-2109 Renamed the map context property currentSearchData to currentMapSearchData to better reflect its purpose.
//    197   09.03.26 Sean Flook       IMANN-2353 Added code to be able to set the view extent to the street and property layers.
//    198   18.03.26 Sean Flook       IMANN-2360 Added some error handling.
//    199   18.03.26 Sean Flook       IMANN-2212 If we have a SHP file or a Georeferenced image file, load again when recreating the map.
//    200   24.03.26 Sean Flook       IMANN-2369 Use getExtentBuffer to get the buffer for an extent and only use the startExtent if required.
//    201   25.03.26 Sean Flook       IMANN-2370 Use getStreetExtent for ASD records and only adjust the extent if required.
//    202   26.03.26 Sean Flook       IMANN-2373 Correctly call getExtentBuffer.
//    203   02.04.26 Sean Flook       IMANN-2393 Added some error handling.
//    204   07.04.26 Sean Flook       IMANN-2395 Ensure we have the required data before calling getAuthorityExtent.
//    205   09.04.26 Sean Flook       IMANN-2398 Only call onRefreshSketchLayerChange if the mapType is not gazetteer.
//    206   17.04.26 Sean Flook       IMANN-2401 Added some error handling.
//    207   20.04.26 Sean Flook       IMANN-2397 Added some development debug statements.
//endregion Version 1.0.7.0
//region Version 1.0.7.1
//    208   23.04.26 Sean Flook       IMANN-2408 Never disable the popup for background layers.
//    209   24.04.26 Sean Flook       IMANN-2397 Ensure we have the backgroundExtent set before trying to set the map extent when loading the street and property layers.
//    210   24.04.26 Sean Flook       IMANN-2410 Added an additional check before using mapContext.currentLayers.zoomStreet.usrn.
//    211   27.04.26 Sean Flook       IMANN-2411 Update the sketchLayer graphics when moving a point. Correctly determine the extent when loading the street and property layers. Correctly compare UPRNs.
//    212   27.04.26 Sean Flook       IMANN-2413 Set the zoom value in the backgroundExtent and turn the mapContext.useSearchExtent flag to a reference so that the correct code is run when required.
//    213   28.04.26 Sean Flook       IMANN-2415 Removed useSearchExtent as not required.
//    214   28.04.26 Sean Flook       IMANN-2381 Change find to findByLayerId.
//    215   28.04.26 Sean Flook       IMANN-2418 Do not limit the refresh of background streets and properties to mapType.
//endregion Version 1.0.7.1
//region Version 1.0.7.2
//    216   01.05.26 Sean Flook       IMANN-2422 Changes required for being able to edit an ESU correctly.
//    217   05.05.26 Sean Flook       IMANN-2433 Do not bother to try and refresh the ESU geometry if we do not have a street layer yet.
//    218   06.05.26 Sean Flook       IMANN-2436 Added a bit more description to the debug messages.
//    219   08.05.26 Sean Flook       IMANN-2438 Added more detailed debug messages and do not hide the street layer in handlePolylineUpdate, finally revert to using mapContext.updatingEsuGeometry when determining if the street layer should be redrawn.
//    220   11.05.26 Sean Flook       IMANN-2442 Use onClearWizardData to clear the wizard data after it has been used.
//    221   18.05.26 Sean Flook       IMANN-2453 Clear geoJSONFeatures after merging the extents.
//    222   18.05.26 Sean Flook       IMANN-2454 Reduce the buffer for deciding to set the view extent when working with provenances.
//endregion Version 1.0.7.2
//
//--------------------------------------------------------------------------------------------------
//endregion header

//region imports

import React, { useRef, useState, useContext, useCallback, useEffect } from "react";
import { useNavigate } from "react-router";
import PropTypes from "prop-types";

import useSandboxStore from "../context/SandboxStore";

import MapContext from "../context/mapContext";
import UserContext from "../context/userContext";
import SettingsContext from "../context/settingsContext";
import StreetContext from "../context/streetContext";
import PropertyContext from "../context/propertyContext";
import LookupContext from "../context/lookupContext";
import SearchContext from "../context/searchContext";
import InformationContext from "../context/informationContext";
import SelectionContext from "../context/selectionContext";

import {
  wktToGeometry,
  getBaseMapLayers,
  ArraysEqual,
  defaultMapLayerIds,
  mergeArrays,
  geometryToWKT,
  doOpenRecord,
  getAuthorityExtent,
  openInStreetView,
} from "../utils/HelperUtils";
import {
  GetStreetStateLabel,
  GetStreetTypeLabel,
  GetAsdPrimaryCodeText,
  GetAuthorityLabel,
  GetWholeRoadLabel,
  GetInterestLabel,
  GetDistrictLabel,
  GetReinstatementLabel,
  GetProwStatusLabel,
  setASDLayerVisibility,
  SaveStreet,
  HasStreetChanged,
  GetCurrentStreetData,
  GetStreetMapData,
  DisplayStreetInStreetView,
  getChangedAssociatedStreetRecords,
  getStreetExtent,
} from "../utils/StreetUtils";
import {
  GetClassificationLabel,
  GetLPILogicalStatusLabel,
  GetProvenanceLabel,
  UpdateRangeAfterSave,
  UpdateAfterSave,
  GetPropertyMapData,
  GetParentHierarchy,
  SavePropertyAndUpdate,
  HasPropertyChanged,
  hasParentPaoChanged,
  hasParentMoved,
  GetCurrentPropertyData,
  getChangedAssociatedPropertyRecords,
  getPropertyExtent,
} from "../utils/PropertyUtils";
import {
  backgroundStreetLayerName,
  unassignedEsusLayerName,
  streetOpenAction,
  streetStreetViewAction,
  streetRenderer,
  streetAddProperty,
  streetAddRangeProperties,
  backgroundPropertyLayerName,
  backgroundProvenanceLayerName,
  sketchLayerName,
  streetLayerTitle,
  llpgStreetLayerTitle,
  PropertyFeatureReduction,
  propertyOpenAction,
  propertyAddChild,
  propertyAddRangeChildren,
  propertyStreetViewAction,
  backgroundPropertyRenderer,
  selectPropertyLayerName,
  streetLayerName,
  llpgStreetLayerName,
  asd51LayerName,
  asd52LayerName,
  asd53LayerName,
  asd61LayerName,
  asd62LayerName,
  asd63LayerName,
  asd64LayerName,
  asd66LayerName,
  propertyLayerName,
  extentLayerName,
  zoomGraphicsLayerName,
  sketchLayerTitle,
  asd51OpenAction,
  asd52OpenAction,
  asd53OpenAction,
  asd61OpenAction,
  asd62OpenAction,
  asd63OpenAction,
  asd64OpenAction,
  asd66OpenAction,
  asd51Renderer,
  asd52Renderer,
  asd53Renderer,
  asd61Renderer,
  asd62Renderer,
  asd63Renderer,
  asd64Renderer,
  asd66Renderer,
  propertyRenderer,
  extentRenderer,
  unselectedLineSymbol,
  selectedLineSymbol,
  doubleEventWait,
  invalidLineSymbol,
  validLineSymbol,
  zoomLayerTitle,
  backgroundStreetLayerTitle,
  unassignedEsusLayerTitle,
  backgroundPropertyLayerTitle,
  selectPropertyLayerTitle,
  backgroundProvenanceLayerTitle,
  asd51LayerTitle,
  asd52LayerTitle,
  asd53LayerTitle,
  asd61LayerTitle,
  asd62LayerTitle,
  asd63LayerTitle,
  asd64LayerTitle,
  asd66LayerTitle,
  provenanceLayerTitle,
  propertyLayerTitle,
  getLayerOpacity,
  setLayerOpacity,
  getLayerVisibility,
  setLayerVisibility,
  setShowHistorical,
  getShowHistorical,
  DEFAULT_SIMPLE_POINT_JSON,
  DEFAULT_SIMPLE_LINE_JSON,
  DEFAULT_SIMPLE_POLYGON_JSON,
  fixSymbolJson,
  getUniqueValuesFromRendererJson,
  getCoordRefSystemName,
  getExtentBuffer,
} from "../utils/MapUtils";
import {
  GetPropertyMapSymbol,
  GetStreetMapSymbol,
  GetBackgroundPropertyMapSymbol,
  GetExtentMapSymbol,
} from "../utils/ADSMapSymbols";
import { useSaveConfirmation } from "../pages/SaveConfirmationPage";

import shp from "shpjs";

import "@arcgis/map-components/dist/components/arcgis-distance-measurement-2d";
import "@arcgis/map-components/dist/components/arcgis-area-measurement-2d";
import "@arcgis/map-components/dist/components/arcgis-coordinate-conversion";
import "@arcgis/map-components/dist/components/arcgis-expand";
import "@arcgis/map-components/dist/components/arcgis-basemap-layer-list";
import "@arcgis/map-components/dist/components/arcgis-basemap-gallery";
import "@arcgis/map-components/dist/components/arcgis-layer-list";
import "@arcgis/map-components/dist/components/arcgis-map";
import "@arcgis/map-components/dist/components/arcgis-placement";
import "@arcgis/map-components/dist/components/arcgis-scale-bar";
import "@arcgis/map-components/dist/components/arcgis-sketch";
import "@arcgis/map-components/dist/components/arcgis-zoom";

import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import WMSLayer from "@arcgis/core/layers/WMSLayer";
import WMTSLayer from "@arcgis/core/layers/WMTSLayer";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer.js";
import Basemap from "@arcgis/core/Basemap";
import LocalBasemapsSource from "@arcgis/core/widgets/BasemapGallery/support/LocalBasemapsSource.js";
import Map from "@arcgis/core/Map";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import MediaLayer from "@arcgis/core/layers/MediaLayer";
import ImageElement from "@arcgis/core/layers/support/ImageElement";
import Polyline from "@arcgis/core/geometry/Polyline";
import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";
import Graphic from "@arcgis/core/Graphic";

import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer.js";
import UniqueValueRenderer from "@arcgis/core/renderers/UniqueValueRenderer.js";

import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol.js";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol.js";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol.js";

import Extent from "@arcgis/core/geometry/Extent";
import ExtentAndRotationGeoreference from "@arcgis/core/layers/support/ExtentAndRotationGeoreference";
import CornersGeoreference from "@arcgis/core/layers/support/CornersGeoreference.js";
import TileInfo from "@arcgis/core/layers/support/TileInfo";
import Conversion from "@arcgis/core/widgets/CoordinateConversion/support/Conversion";

import * as proximityOperator from "@arcgis/core/geometry/operators/proximityOperator.js";
import * as lengthOperator from "@arcgis/core/geometry/operators/lengthOperator.js";
import Transformation from "@arcgis/core/geometry/operators/support/Transformation.js";
import * as affineTransformOperator from "@arcgis/core/geometry/operators/affineTransformOperator.js";
import * as bufferOperator from "@arcgis/core/geometry/operators/bufferOperator.js";
import * as cutOperator from "@arcgis/core/geometry/operators/cutOperator.js";
import * as crossesOperator from "@arcgis/core/geometry/operators/crossesOperator.js";
import * as disjointOperator from "@arcgis/core/geometry/operators/disjointOperator.js";
import * as touchesOperator from "@arcgis/core/geometry/operators/touchesOperator.js";
import * as unionOperator from "@arcgis/core/geometry/operators/unionOperator.js";
import esriRequest from "@arcgis/core/request";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

import { XMLParser } from "fast-xml-parser";

import { Box, Snackbar, Alert, Stack } from "@mui/material";
import ADSCircularProgress from "../components/ADSCircularProgress";

import { CalciteButton } from "@esri/calcite-components-react";

import ADSSelectionControl from "./ADSSelectionControl";
import ADSInformationControl from "./ADSInformationControl";

import AddPropertyWizardDialog from "../dialogs/AddPropertyWizardDialog";
// TODO: uncommented below code when want to reintroduce asking to edit historic.
// import HistoricPropertyDialog from "../dialogs/HistoricPropertyDialog";
import UploadShpFileDialog from "../dialogs/UploadShpFileDialog";

import { GetAlertStyle, GetAlertIcon, GetAlertSeverity, dataFormStyle } from "../utils/ADSStyles";
import Format from "@arcgis/core/widgets/CoordinateConversion/support/Format";

//endregion imports

ADSMap.propTypes = {
  mapType: PropTypes.oneOf(["gazetteer", "street", "property", "wizard"]).isRequired,
  startExtent: PropTypes.object,
};

function ADSMap({ mapType, startExtent }) {
  const mapContext = useContext(MapContext);
  const userContext = useRef(useContext(UserContext));
  const settingsContext = useContext(SettingsContext);
  const streetContext = useContext(StreetContext);
  const propertyContext = useContext(PropertyContext);
  const lookupContext = useRef(useContext(LookupContext));
  const searchContext = useContext(SearchContext);
  const informationContext = useContext(InformationContext);
  const selectionContext = useContext(SelectionContext);

  const sandboxStore = useSandboxStore((state) => state);
  const sourceStreet = useSandboxStore((state) => state.sourceStreet);
  const currentStreet = useSandboxStore((state) => state.currentStreet);
  const currentDescriptorRecord = useSandboxStore((state) => state.currentDescriptorRecord);
  const currentEsuRecord = useSandboxStore((state) => state.currentEsuRecord);
  const currentHighwayDedicationRecord = useSandboxStore((state) => state.currentHighwayDedicationRecord);
  const currentOneWayExemptionRecord = useSandboxStore((state) => state.currentOneWayExemptionRecord);
  const currentMaintenanceResponsibilityRecord = useSandboxStore(
    (state) => state.currentMaintenanceResponsibilityRecord
  );
  const currentReinstatementCategoryRecord = useSandboxStore((state) => state.currentReinstatementCategoryRecord);
  const currentOSSpecialDesignationRecord = useSandboxStore((state) => state.currentOSSpecialDesignationRecord);
  const currentInterestRecord = useSandboxStore((state) => state.currentInterestRecord);
  const currentConstructionRecord = useSandboxStore((state) => state.currentConstructionRecord);
  const currentSpecialDesignationRecord = useSandboxStore((state) => state.currentSpecialDesignationRecord);
  const currentHWWRecord = useSandboxStore((state) => state.currentHWWRecord);
  const currentPRoWRecord = useSandboxStore((state) => state.currentPRoWRecord);
  const currentStreetNoteRecord = useSandboxStore((state) => state.currentStreetNoteRecord);
  const sourceProperty = useSandboxStore((state) => state.sourceProperty);
  const currentProperty = useSandboxStore((state) => state.currentProperty);
  const currentLpiRecord = useSandboxStore((state) => state.currentLpiRecord);
  const currentProvenanceRecord = useSandboxStore((state) => state.currentProvenanceRecord);
  const currentAppCrossRefRecord = useSandboxStore((state) => state.currentAppCrossRefRecord);
  const currentClassificationRecord = useSandboxStore((state) => state.currentClassificationRecord);
  const currentOrganisationRecord = useSandboxStore((state) => state.currentOrganisationRecord);
  const currentSuccessorCrossRefRecord = useSandboxStore((state) => state.currentSuccessorCrossRefRecord);
  const currentPropertyNoteRecord = useSandboxStore((state) => state.currentPropertyNoteRecord);
  const setStore = useSandboxStore((state) => state.setStore);
  const resetSandbox = useSandboxStore((state) => state.resetSandbox);
  const setRefreshRelated = useSandboxStore((state) => state.setRefreshRelated);

  const sourceStreetRef = useRef(undefined);
  const currentStreetRef = useRef(undefined);
  const currentDescriptorRecordRef = useRef(undefined);
  const currentEsuRecordRef = useRef(undefined);
  const currentHighwayDedicationRecordRef = useRef(undefined);
  const currentOneWayExemptionRecordRef = useRef(undefined);
  const currentMaintenanceResponsibilityRecordRef = useRef(undefined);
  const currentReinstatementCategoryRecordRef = useRef(undefined);
  const currentOSSpecialDesignationRecordRef = useRef(undefined);
  const currentInterestRecordRef = useRef(undefined);
  const currentConstructionRecordRef = useRef(undefined);
  const currentSpecialDesignationRecordRef = useRef(undefined);
  const currentHWWRecordRef = useRef(undefined);
  const currentPRoWRecordRef = useRef(undefined);
  const currentStreetNoteRecordRef = useRef(undefined);
  const sourcePropertyRef = useRef(undefined);
  const currentPropertyRef = useRef(undefined);
  const currentLpiRecordRef = useRef(undefined);
  const currentProvenanceRecordRef = useRef(undefined);
  const currentAppCrossRefRecordRef = useRef(undefined);
  const currentClassificationRecordRef = useRef(undefined);
  const currentOrganisationRecordRef = useRef(undefined);
  const currentSuccessorCrossRefRecordRef = useRef(undefined);
  const currentPropertyNoteRecordRef = useRef(undefined);

  const recordAttributes = useRef(null);

  const [arcgisMap, setArcgisMap] = useState(null);
  const arcgisBasemapGallery = useRef(null);
  const arcgisLayerList = useRef(null);
  const arcgisDistanceMeasurementExpand = useRef(null);
  const arcgisDistanceMeasurement = useRef(null);
  const arcgisAreaMeasurementExpand = useRef(null);
  const arcgisAreaMeasurement = useRef(null);
  const arcgisCoordinateConversion = useRef(null);
  const arcgisSketch = useRef(null);
  const baseMapLayers = useRef(null);
  const [baseMapGallerySource, setBaseMapGallerySource] = useState(new LocalBasemapsSource({ basemaps: [] }));
  const layersAdded = useRef(false);
  const shpGeoLayerIndex = useRef(0);

  const [displayESUMergeTool, setDisplayESUMergeTool] = useState(false);
  const [displayExtentMergeTool, setDisplayExtentMergeTool] = useState(false);
  const [selectedExtents, setSelectedExtents] = useState([]);
  const [sketchCreateTools, setSketchCreateTools] = useState([]);
  const [hideSelectionCountLabel, setHideSelectionCountLabel] = useState(false);
  const [showHistoricalExtentData, setShowHistoricalExtentData] = useState(true);
  const [showHistoricalBackgroundProvenanceData, setShowHistoricalBackgroundProvenanceData] = useState(true);
  const [isDistanceMeasurementActive, setIsDistanceMeasurementActive] = useState(true);
  const [isAreaMeasurementActive, setIsAreaMeasurementActive] = useState(true);
  const oldShowHistoricalExtentData = useRef(true);
  const oldShowHistoricalBackgroundProvenanceData = useRef(true);

  const [streetData, setStreetData] = useState(null);
  const [llpgStreetData, setLlpgStreetData] = useState(null);
  const [zoomStreetData, setZoomStreetData] = useState(null);
  const [propertyData, setPropertyData] = useState(null);
  const [zoomPropertyData, setZoomPropertyData] = useState(null);
  const [updatingMap, setUpdatingMap] = useState(true);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);

  const propertyWizardType = useRef(null);
  const propertyWizardParent = useRef(null);
  const [openPropertyWizard, setOpenPropertyWizard] = useState(false);
  const wizardUprn = useRef(null);
  const displayingWizard = useRef(false);

  // TODO: uncommented below code when want to reintroduce asking to edit historic.
  // const [openHistoricProperty, setOpenHistoricProperty] = useState(false);
  // const historicRec = useRef(null);
  const updatingPolygons = useRef(false);

  const backgroundStreetData = useRef(null);
  const unassignedEsusData = useRef(null);
  const backgroundPropertyData = useRef(null);
  const backgroundProvenanceData = useRef(null);
  const refStreetData = useRef(null);
  const refLlpgStreetData = useRef(null);
  const refAsd51Data = useRef(null);
  const refAsd52Data = useRef(null);
  const refAsd53Data = useRef(null);
  const refAsd61Data = useRef(null);
  const refAsd62Data = useRef(null);
  const refAsd63Data = useRef(null);
  const refAsd64Data = useRef(null);
  const refAsd66Data = useRef(null);
  const refPropertyData = useRef(null);
  const extentData = useRef(null);

  const currentMapExtent = useRef(null);

  const spatialReferenceWkid = useRef(mapContext.spatialReference || 27700);
  const isScottish = useRef(settingsContext.isScottish);
  const isWelsh = useRef(settingsContext.isWelsh);
  const authorityCode = useRef(settingsContext.authorityCode);
  const isStreetMap = useRef(false);
  const isPropertyMap = useRef(false);
  const isStreetPropertyMap = useRef(false);

  const navigate = useNavigate();

  const saveConfirmDialog = useSaveConfirmation();

  const backgroundStreetLayer = useRef(null);
  const backgroundPropertyLayer = useRef(null);
  const selectPropertyLayer = useRef(null);
  const backgroundProvenanceLayer = useRef(null);
  const streetLayer = useRef(null);
  const llpgStreetLayer = useRef(null);
  const unassignedEsusLayer = useRef(null);
  const asd51Layer = useRef(null);
  const asd52Layer = useRef(null);
  const asd53Layer = useRef(null);
  const asd61Layer = useRef(null);
  const asd62Layer = useRef(null);
  const asd63Layer = useRef(null);
  const asd64Layer = useRef(null);
  const asd66Layer = useRef(null);
  const propertyLayer = useRef(null);
  const extentLayer = useRef(null);
  const loadedShpFileLayers = useRef([]);
  const loadedGeoFileLayers = useRef([]);
  const sketchLayer = useRef(
    new GraphicsLayer({
      id: sketchLayerName,
      copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
      title: sketchLayerTitle,
      listMode: "hide",
    })
  );

  const oldBackgroundStreets = useRef([]);
  const oldEditStreet = useRef(null);
  const oldZoomStreet = useRef(null);
  const oldZoomBackgroundStreet = useRef(null);
  const oldUnassignedEsus = useRef([]);
  const oldBackgroundProperties = useRef([]);
  const oldEditProperty = useRef(null);
  const oldZoomProperty = useRef(null);
  const oldZoomBackgroundProperty = useRef(null);
  const oldZoomGroup = useRef(null);
  const oldBackgroundProvenances = useRef([]);
  const oldStreets = useRef([]);
  const oldProperties = useRef([]);
  const oldExtents = useRef([]);
  const oldStreetData = useRef([]);
  const oldPropertyData = useRef([]);

  const baseMappingLayerIds = useRef([]);
  const baseLayersSnapEsu = useRef([]);
  const baseLayersSnapBlpu = useRef([]);
  const baseLayersSnapExtent = useRef([]);
  const baseLayersFeature = useRef([]);
  const geoJSONFeatures = useRef([]);

  const highlightStreet = useRef(null);
  const highlightESU = useRef(null);
  const highlightBackgroundESU = useRef(null);
  const highlightUnassignedESU = useRef(null);
  const highlightASD51 = useRef(null);
  const highlightASD52 = useRef(null);
  const highlightASD53 = useRef(null);
  const highlightASD61 = useRef(null);
  const highlightASD62 = useRef(null);
  const highlightASD63 = useRef(null);
  const highlightASD64 = useRef(null);
  const highlightASD66 = useRef(null);
  const highlightProperty = useRef(null);
  const highlightSelectProperty = useRef(null);
  const highlightExtent = useRef(null);
  const existingEsuHighlight = useRef(null);
  const editingObject = useRef(null);
  const selectedLines = useRef([]);
  const selectedEsus = useRef([]);
  const selectedProperties = useRef([]);

  const selectingProperties = useRef(false);
  const loadedShpFileTitles = useRef([]);
  const loadedShpFileIds = useRef([]);
  const loadedGeoFileTitles = useRef([]);
  const loadedGeoFileIds = useRef([]);
  const currentPointCaptureModeRef = useRef(null);
  const lastSelectedExtent = useRef(null);
  const lastSelectedEsu = useRef(null);
  const lastSelectedProperty = useRef(null);

  const streetExtent = useRef(null);
  const propertyExtent = useRef(null);

  const saveResult = useRef(null);
  const saveType = useRef(null);
  const failedValidation = useRef(null);
  const associatedRecords = useRef([]);
  const saveOpenRef = useRef(false);
  const [saveOpen, setSaveOpen] = useState(false);

  const featuresDownloaded = useRef(0);

  const viewReadyEvent = useRef(false);
  const viewChangeEvent = useRef(false);
  const viewClickEvent = useRef(false);
  const basemapGalleryChangeEvent = useRef(false);
  const layerListTriggerEvent = useRef(false);
  const sketchCreateEvent = useRef(false);
  const sketchUpdateEvent = useRef(false);
  const sketchRedoEvent = useRef(false);
  const sketchUndoEvent = useRef(false);
  const sketchDeleteEvent = useRef(false);
  const historicalDataDisplaySet = useRef(false);

  const backgroundExtent = useRef(
    startExtent
      ? {
          xmin: startExtent.xmin,
          ymin: startExtent.ymin,
          xmax: startExtent.xmax,
          ymax: startExtent.ymax,
          spatialReference: startExtent.spatialReference,
          zoom: startExtent.zoom,
        }
      : null
  );
  const lastBackgroundExtent = useRef(new Extent());

  const mapTypeRef = useRef(mapType);
  const devBuild = useRef(false);
  if (process.env.NODE_ENV === "development") {
    devBuild.current = true;
  }

  //! Use the below refs to track whether to display debug statements to avoid having to check the devBuild variable
  //! multiple times and to be able to turn on and off the debug statements without having to change the devBuild variable.
  const debugUseEffects = useRef(devBuild.current && false);
  const debugSetExtents = useRef(devBuild.current && false);
  const debugStreetLayer = useRef(devBuild.current && false);
  const debugPropertyLayer = useRef(devBuild.current && false);

  const zoomGraphicsLayer = useRef(
    new GraphicsLayer({
      id: zoomGraphicsLayerName,
      title: zoomLayerTitle,
      listMode: "hide",
    })
  );

  /**
   * Method used to handle the view ready event.
   */
  const handleViewReady = async () => {
    if (backgroundExtent.current || startExtent) {
      let newExtent = new Extent();
      newExtent.xmin = backgroundExtent.current ? backgroundExtent.current.xmin : startExtent.xmin;
      newExtent.ymin = backgroundExtent.current ? backgroundExtent.current.ymin : startExtent.ymin;
      newExtent.xmax = backgroundExtent.current ? backgroundExtent.current.xmax : startExtent.xmax;
      newExtent.ymax = backgroundExtent.current ? backgroundExtent.current.ymax : startExtent.ymax;
      newExtent.spatialReference = backgroundExtent.current
        ? backgroundExtent.current.spatialReference
        : startExtent.spatialReference;
      arcgisMap.view.extent = newExtent;
      if (debugSetExtents.current)
        console.log("[DEV] Setting view extent [1]:", {
          xLength: newExtent.xmax - newExtent.xmin,
          yLength: newExtent.ymax - newExtent.ymin,
          mapType: mapTypeRef.current,
        });

      if (!mapContext.currentExtent) {
        mapContext.onExtentChange({
          xmin: backgroundExtent.current ? backgroundExtent.current.xmin : startExtent.xmin,
          ymin: backgroundExtent.current ? backgroundExtent.current.ymin : startExtent.ymin,
          xmax: backgroundExtent.current ? backgroundExtent.current.xmax : startExtent.xmax,
          ymax: backgroundExtent.current ? backgroundExtent.current.ymax : startExtent.ymax,
          zoom: backgroundExtent.current ? backgroundExtent.current.zoom : startExtent.zoom,
          spatialReference: backgroundExtent.current
            ? backgroundExtent.current.spatialReference
            : startExtent.spatialReference,
          hasProperties: userContext.current.currentUser.hasProperty,
        });
      }
    }

    if (!arcgisBasemapGallery.current && arcgisMap.querySelector(`.arcgis-basemap-gallery-${mapType}`))
      arcgisBasemapGallery.current = arcgisMap.querySelector(`.arcgis-basemap-gallery-${mapType}`);
    if (!arcgisLayerList.current && arcgisMap.querySelector(`.arcgis-layer-list-${mapType}`))
      arcgisLayerList.current = arcgisMap.querySelector(`.arcgis-layer-list-${mapType}`);
    if (
      !arcgisDistanceMeasurementExpand.current &&
      document.getElementById(`arcgis-distance-measurement-expand-${mapType}`)
    )
      arcgisDistanceMeasurementExpand.current = document.getElementById(
        `arcgis-distance-measurement-expand-${mapType}`
      );
    if (!arcgisDistanceMeasurement.current && document.getElementById(`arcgis-distance-measurement-2d-${mapType}`))
      arcgisDistanceMeasurement.current = document.getElementById(`arcgis-distance-measurement-2d-${mapType}`);
    if (!arcgisAreaMeasurementExpand.current && document.getElementById(`arcgis-area-measurement-expand-${mapType}`))
      arcgisAreaMeasurementExpand.current = document.getElementById(`arcgis-area-measurement-expand-${mapType}`);
    if (!arcgisAreaMeasurement.current && document.getElementById(`arcgis-area-measurement-2d-${mapType}`))
      arcgisAreaMeasurement.current = document.getElementById(`arcgis-area-measurement-2d-${mapType}`);
    if (!arcgisCoordinateConversion.current && arcgisMap.querySelector(`.arcgis-coordinate-conversion-${mapType}`))
      arcgisCoordinateConversion.current = arcgisMap.querySelector(`.arcgis-coordinate-conversion-${mapType}`);
    if (!arcgisSketch.current && arcgisMap.querySelector(`.arcgis-sketch-${mapType}`))
      arcgisSketch.current = arcgisMap.querySelector(`.arcgis-sketch-${mapType}`);

    if (arcgisBasemapGallery.current) {
      if (!basemapGalleryChangeEvent.current) {
        arcgisBasemapGallery.current.addEventListener("arcgisPropertyChange", handleBasemapGalleryChange);
        basemapGalleryChangeEvent.current = true;
      }
    }

    // Set the layer list event listeners when the layer list is ready
    if (arcgisLayerList.current) {
      if (!layerListTriggerEvent.current) {
        arcgisLayerList.current.addEventListener("arcgisTriggerAction", handleTriggerAction);
        layerListTriggerEvent.current = true;
      }
    }

    if (arcgisDistanceMeasurement.current) {
      arcgisDistanceMeasurement.current.addEventListener("arcgisPropertyChange", handleDistanceMeasurementChange);
    }

    if (arcgisAreaMeasurement.current) {
      arcgisAreaMeasurement.current.addEventListener("arcgisPropertyChange", handleAreaMeasurementChange);
    }

    // Set the coordinate conversion component formats and conversions
    if (arcgisCoordinateConversion.current) {
      const coordRefSystemName = await getCoordRefSystemName(spatialReferenceWkid.current);
      if (arcgisCoordinateConversion.current.formats && arcgisCoordinateConversion.current.formats.length > 0) {
        let filteredFormats = arcgisCoordinateConversion.current.formats.items.filter(
          (f) => f.name === "basemap" || f.name === "xy"
        );

        filteredFormats[filteredFormats.findIndex((f) => f.name === "basemap")].label = coordRefSystemName;

        filteredFormats[filteredFormats.findIndex((f) => f.name === "xy")].label = "Long/Lat";
        arcgisCoordinateConversion.current.formats.items = filteredFormats;
      }

      if (arcgisCoordinateConversion.current.conversions && arcgisCoordinateConversion.current.conversions.length > 0) {
        arcgisCoordinateConversion.current.conversions.removeAll();
        const f = arcgisCoordinateConversion.current.formats.items.find((f) => f.name === "basemap");
        if (f.spatialReference.wkid === spatialReferenceWkid.current) {
          arcgisCoordinateConversion.current.conversions.add(new Conversion({ format: f }));
        } else {
          const baseMapFormat = new Format({
            name: coordRefSystemName,
            spatialReference: { wkid: spatialReferenceWkid.current },
          });
          arcgisCoordinateConversion.current.conversions.add(new Conversion({ format: baseMapFormat }));
        }
      }
    }

    // Set the sketch event listeners and initialise the sketch layer
    if (arcgisSketch.current) {
      if (!sketchCreateEvent.current) {
        arcgisSketch.current.addEventListener("arcgisCreate", handleSketchCreate);
        sketchCreateEvent.current = true;
      }

      if (!sketchUpdateEvent.current) {
        arcgisSketch.current.addEventListener("arcgisUpdate", handleSketchUpdate);
        sketchUpdateEvent.current = true;
      }

      if (!sketchRedoEvent.current) {
        arcgisSketch.current.addEventListener("arcgisRedo", handleSketchRedo);
        sketchRedoEvent.current = true;
      }

      if (!sketchUndoEvent.current) {
        arcgisSketch.current.addEventListener("arcgisUndo", handleSketchUndo);
        sketchUndoEvent.current = true;
      }

      if (!sketchDeleteEvent.current) {
        arcgisSketch.current.addEventListener("arcgisDelete", handleSketchDelete);
        sketchDeleteEvent.current = true;
      }

      arcgisSketch.current.layer = sketchLayer.current;
      arcgisSketch.current.layer.popupEnabled = false;
      arcgisSketch.current.layer.listMode = "hide";
      setSketchCreateTools([]);
      if (mapType !== "property") {
        arcgisSketch.current.remove();
      } else {
        setHideSelectionCountLabel(true);
      }
    }

    setUpdatingMap(false);
  };

  /**
   * Method used to handle the view change event.
   */
  const handleViewChange = (event) => {
    const { view, zoom, extent } = event.target;

    if (
      view &&
      view.stationary &&
      zoom > -1 &&
      extent &&
      extent.xmin >= 0 &&
      extent.ymin >= 0 &&
      (!currentMapExtent.current ||
        currentMapExtent.current.xmin !== extent.xmin ||
        currentMapExtent.current.ymin !== extent.ymin ||
        currentMapExtent.current.xmax !== extent.xmax ||
        currentMapExtent.current.ymax !== extent.ymax ||
        currentMapExtent.current.zoom !== zoom)
    ) {
      currentMapExtent.current = {
        xmin: extent.xmin,
        ymin: extent.ymin,
        xmax: extent.xmax,
        ymax: extent.ymax,
        zoom: zoom,
      };

      mapContext.onExtentChange({
        xmin: extent.xmin,
        ymin: extent.ymin,
        xmax: extent.xmax,
        ymax: extent.ymax,
        zoom: zoom,
        spatialReference: { wkid: spatialReferenceWkid.current },
        hasProperties: userContext.current.currentUser.hasProperty,
      });
    }
  };

  /**
   * Method used to handle the view click event.
   *
   * @param {object} event The view click event
   */
  const handleViewClick = (event) => {
    if (
      arcgisCoordinateConversion.current &&
      arcgisCoordinateConversion.current.mode === "capture" &&
      currentPointCaptureModeRef.current
    ) {
      switch (currentPointCaptureModeRef.current) {
        case "divideEsu":
          if (refStreetData.current?.length > 0) {
            refStreetData.current.forEach((street) => {
              if (
                editingObject.current &&
                editingObject.current.objectId &&
                street.esuId === editingObject.current.objectId
              ) {
                const divideEsuGeometry = new Polyline({
                  type: "polyline",
                  paths: street.geometry,
                  spatialReference: { wkid: spatialReferenceWkid.current },
                });

                const dividePoint = proximityOperator.getNearestCoordinate(divideEsuGeometry, event.detail.mapPoint);

                let cutLine = null;

                if (dividePoint.distance !== 0) {
                  cutLine = new Polyline({
                    type: "polyline",
                    paths: [
                      [event.detail.mapPoint.x, event.detail.mapPoint.y],
                      [dividePoint.coordinate.x, dividePoint.coordinate.y],
                    ],
                    spatialReference: { wkid: spatialReferenceWkid.current },
                  });
                } else {
                  let tempCutLine = new Polyline({
                    type: "polyline",
                    paths: [
                      [dividePoint.coordinate.x, dividePoint.coordinate.y],
                      street.geometry[dividePoint.vertexIndex],
                    ],
                    spatialReference: { wkid: spatialReferenceWkid.current },
                  });

                  const transformation = new Transformation();
                  if (lengthOperator.execute(tempCutLine) !== 0) {
                    transformation.rotate(90, dividePoint.coordinate);
                    cutLine = affineTransformOperator.execute(tempCutLine, transformation);
                  } else {
                    if (dividePoint.vertexIndex > 0)
                      tempCutLine = new Polyline({
                        type: "polyline",
                        paths: [
                          [dividePoint.coordinate.x, dividePoint.coordinate.y],
                          street.geometry[dividePoint.vertexIndex - 1],
                        ],
                        spatialReference: { wkid: spatialReferenceWkid.current },
                      });
                    else if (dividePoint.vertexIndex + 1 < street.geometry.length) {
                      tempCutLine = new Polyline({
                        type: "polyline",
                        paths: [
                          [dividePoint.coordinate.x, dividePoint.coordinate.y],
                          street.geometry[dividePoint.vertexIndex + 1],
                        ],
                        spatialReference: { wkid: spatialReferenceWkid.current },
                      });

                      if (lengthOperator.execute(tempCutLine) !== 0) {
                        transformation.rotate(90, dividePoint.coordinate);
                        cutLine = affineTransformOperator.execute(tempCutLine, transformation);
                      }
                    }
                  }
                }

                if (cutLine) {
                  const newEsus = cutOperator.execute(divideEsuGeometry, cutLine);

                  if (newEsus && newEsus.length === 2) mapContext.onEsuDivided(newEsus[0], newEsus[1]);
                  else if (userContext.current.currentUser.showMessages)
                    console.log("[DEBUG] Unable to divide the ESU", newEsus);
                } else if (userContext.current.currentUser.showMessages)
                  console.log("[DEBUG] Run out of options on how to divide the ESU");
              }
            });
          }

          editingObject.current = null;
          break;

        default:
          mapContext.onSetCoordinate({
            x: event.detail.mapPoint.x.toFixed(4),
            y: event.detail.mapPoint.y.toFixed(4),
          });
          break;
      }

      arcgisCoordinateConversion.current.mode = "live";
      event.stopPropagation();
      return;
    }

    const coord = `${event.detail.mapPoint.x},${event.detail.mapPoint.y}`;

    let foundFeatureLayer = false;
    let featureLayer;

    if (editingObject.current && editingObject.current.objectType) {
      switch (editingObject.current.objectType) {
        case 13:
          featureLayer = baseLayersFeature.current.find((x) => x.layer.esuSnap);
          foundFeatureLayer = !!featureLayer;
          break;

        case 22:
          featureLayer = baseLayersFeature.current.find((x) => x.layer.extentSnap);
          foundFeatureLayer = !!featureLayer;
          break;

        default:
          foundFeatureLayer = false;
          break;
      }
    }

    if (foundFeatureLayer) {
      event.stopPropagation();

      let geoJson = null;

      if (geoJSONFeatures.current && geoJSONFeatures.current.length > 0) {
        geoJson = geoJSONFeatures.current.find((x) => x.id === featureLayer.layer.layerId);
      }

      const foundExistingGeoJson = !!geoJson;

      if (!foundExistingGeoJson) {
        geoJson = {
          id: featureLayer.layer.layerId,
          featureCollection: { type: "FeatureCollection", features: [] },
        };
      }

      let selectedGraphics = null;
      const currentLayer =
        arcgisMap && arcgisMap.basemapView.referenceLayerViews.find((x) => x.layer.id === featureLayer.layer.layerId);
      const opts = { include: [sketchLayer.current] };

      if (currentLayer) {
        arcgisMap.view.hitTest(event.detail, opts).then(function (response) {
          const clickedEditGraphic = !!response.results.find((x) => x.graphic.layer.title === sketchLayerTitle);
          let newGraphics;
          switch (featureLayer.layer.geometryType) {
            case "line":
              const clickedGraphic = currentLayer.graphics.find(
                (x) => x.attributes && x.attributes.ID === response.results[0].graphic.attributes.ID
              );

              let newSelectedGraphics;

              if (
                selectedLines.current.find(
                  (x) => x.attributes && x.attributes.ID === response.results[0].graphic.attributes.ID
                )
              ) {
                newSelectedGraphics = selectedLines.current.filter(
                  (x) => x.attributes && x.attributes.ID !== response.results[0].graphic.attributes.ID
                );
                clickedGraphic.symbol = unselectedLineSymbol;
              } else {
                clickedGraphic.symbol = selectedLineSymbol;
                newSelectedGraphics = selectedLines.current;
                newSelectedGraphics.push(clickedGraphic);
              }

              selectedLines.current = newSelectedGraphics;

              newGraphics = currentLayer.graphics.map(
                (x) => [clickedGraphic].find((line) => line.attributes.ID === x.attributes.ID) || x
              );

              setDisplayESUMergeTool(newSelectedGraphics.length > 0);

              sketchLayer.current.graphics.removeAll();
              sketchLayer.current.addMany(newGraphics);
              break;

            case "polygon":
              if (!clickedEditGraphic) {
                if (
                  response.results.length &&
                  (!lastSelectedExtent.current ||
                    response.results[0].graphic.attributes.TOID !== lastSelectedExtent.current.toid ||
                    performance.now() - lastSelectedExtent.current.time > doubleEventWait)
                ) {
                  lastSelectedExtent.current = {
                    toid: response.results[0].graphic.attributes.TOID,
                    time: performance.now(),
                  };
                  newGraphics = currentLayer.layer.graphics.filter(
                    (x) => x.attributes && x.attributes.TOID !== response.results[0].graphic.attributes.TOID
                  );

                  setDisplayExtentMergeTool(newGraphics.length > 0);

                  const newSelected = [...new Set(newGraphics?.map((x) => x.attributes.TOID))];
                  selectionContext.onCurrentExtentChange(newSelected);
                  selectionContext.onSelectionVariantChange(
                    selectedEsus.current && selectedEsus.current.length > 0
                      ? "mapEsu"
                      : newSelected && newSelected.length > 0
                        ? "mapExtent"
                        : selectedProperties.current && selectedProperties.current.length > 0
                          ? "mapProperty"
                          : ""
                  );
                  selectionContext.onSelectionCountChange(
                    newGraphics.length > 0
                      ? newSelected && newSelected.length > 0
                        ? newSelected.length
                        : 0
                      : selectedEsus.current && selectedEsus.current.length > 0
                        ? selectedEsus.current.length
                        : selectedProperties.current && selectedProperties.current.length > 0
                          ? selectedProperties.current.length
                          : 0
                  );
                  selectionContext.onCurrentExtentChange(newSelected);
                  setSelectedExtents(newSelected);
                  if (
                    newSelected.length > 0 &&
                    userContext.current.currentUser &&
                    userContext.current.currentUser.editProperty
                  ) {
                    setDisplayExtentMergeTool(true);
                    selectionContext.onSelectionCountChange(
                      selectedExtents && selectedExtents.length > 0 ? selectedExtents.length : 0
                    );
                  } else {
                    setDisplayExtentMergeTool(false);
                    selectionContext.onSelectionCountChange(
                      selectedEsus.current && selectedEsus.current.length > 0
                        ? selectedEsus.current.length
                        : selectedProperties.current && selectedProperties.current.length > 0
                          ? selectedProperties.current.length
                          : 0
                    );
                  }

                  sketchLayer.current.graphics.removeAll();
                  sketchLayer.current.addMany(newGraphics);
                } else {
                  GetFeatureAtCoord(coord, featureLayer.layer).then(function (json) {
                    if (json) {
                      geoJson.featureCollection.features.push.apply(
                        geoJson.featureCollection.features,
                        json.data.features
                      );

                      if (!foundExistingGeoJson) geoJSONFeatures.current.push(geoJson);

                      if (geoJson.featureCollection.features.length > 0) {
                        selectedGraphics = geoJson.featureCollection.features.map(function (f) {
                          switch (featureLayer.layer.geometryType) {
                            case "line":
                              mapContext.onUpdatingEsuGeometry(true);

                              const polyline = new Polyline({
                                type: "polyline",
                                paths: f.geometry.coordinates[0],
                                spatialReference: { wkid: spatialReferenceWkid.current },
                              });

                              const lineSymbol = {
                                type: "simple-line",
                                color: [51, 136, 255, 0.5],
                                width: 1,
                              };

                              return new Graphic({
                                geometry: polyline,
                                attributes: f.properties,
                                symbol: lineSymbol,
                              });

                            case "polygon":
                              mapContext.onUpdatingProvenance(true);
                              updatingPolygons.current = true;

                              const polygon = new Polygon({
                                type: "polygon",
                                rings:
                                  f.geometry.type === "MultiPolygon"
                                    ? f.geometry.coordinates[0]
                                    : f.geometry.coordinates,
                                spatialReference: { wkid: spatialReferenceWkid.current },
                              });

                              const fillSymbol = {
                                type: "simple-fill",
                                color: [51, 136, 255, 0.5],
                                outline: {
                                  color: [51, 136, 255],
                                  width: 0,
                                },
                              };

                              return new Graphic({
                                geometry: polygon,
                                attributes: f.properties,
                                symbol: fillSymbol,
                              });

                            default:
                              return null;
                          }
                        });

                        sketchLayer.current.graphics.removeAll();
                        sketchLayer.current.addMany(selectedGraphics);

                        const newSelected = [...new Set(selectedGraphics.map((x) => x.attributes.TOID))];
                        selectionContext.onCurrentExtentChange(newSelected);
                        selectionContext.onSelectionVariantChange(
                          selectedEsus.current && selectedEsus.current.length > 0
                            ? "mapEsu"
                            : newSelected && newSelected.length > 0
                              ? "mapExtent"
                              : selectedProperties.current && selectedProperties.current.length > 0
                                ? "mapProperty"
                                : ""
                        );
                        selectionContext.onSelectionCountChange(
                          selectedGraphics.length > 0
                            ? newSelected && newSelected.length > 0
                              ? newSelected.length
                              : 0
                            : selectedEsus.current && selectedEsus.current.length > 0
                              ? selectedEsus.current.length
                              : selectedProperties.current && selectedProperties.current.length > 0
                                ? selectedProperties.current.length
                                : 0
                        );
                        selectionContext.onCurrentExtentChange(newSelected);
                        setSelectedExtents(newSelected);
                        if (
                          newSelected.length > 0 &&
                          userContext.current.currentUser &&
                          userContext.current.currentUser.editProperty
                        ) {
                          setDisplayExtentMergeTool(true);
                          selectionContext.onSelectionCountChange(
                            selectedExtents && selectedExtents.length > 0 ? selectedExtents.length : 0
                          );
                        } else {
                          setDisplayExtentMergeTool(false);
                          selectionContext.onSelectionCountChange(
                            selectedEsus.current && selectedEsus.current.length > 0
                              ? selectedEsus.current.length
                              : selectedProperties.current && selectedProperties.current.length > 0
                                ? selectedProperties.current.length
                                : 0
                          );
                        }
                      }
                    }
                  });
                }
              }
              break;

            default:
              break;
          }
        });
      }
    } else {
      let opts = { include: streetLayer.current };

      if (streetLayer.current) {
        arcgisMap.view.hitTest(event.detail, opts).then(function (response) {
          if (
            currentPointCaptureModeRef.current === "assignEsu" &&
            response.results.length &&
            (!lastSelectedEsu.current ||
              Number(response.results[0].graphic.attributes.EsuId) !== lastSelectedEsu.current.esuId ||
              performance.now() - lastSelectedEsu.current.time > doubleEventWait)
          ) {
            lastSelectedEsu.current = {
              esuId: Number(response.results[0].graphic.attributes.EsuId),
              time: performance.now(),
            };
            const currentIndex = selectedEsus.current.indexOf(Number(response.results[0].graphic.attributes.EsuId));
            const newList = [...selectedEsus.current];

            if (currentIndex === -1) newList.push(Number(response.results[0].graphic.attributes.EsuId));
            else newList.splice(currentIndex, 1);

            selectedEsus.current = newList;
            selectionContext.onSelectionVariantChange(
              selectedEsus.current && selectedEsus.current.length > 0
                ? "mapEsu"
                : selectedExtents && selectedExtents.length > 0
                  ? "mapExtent"
                  : selectedProperties.current && selectedProperties.current.length > 0
                    ? "mapProperty"
                    : ""
            );
            selectionContext.onSelectionCountChange(
              displayExtentMergeTool
                ? selectedExtents && selectedExtents.length > 0
                  ? selectedExtents.length
                  : 0
                : selectedEsus.current && selectedEsus.current.length > 0
                  ? selectedEsus.current.length
                  : selectedProperties.current && selectedProperties.current.length > 0
                    ? selectedProperties.current.length
                    : 0
            );
            selectionContext.onCurrentEsuChange(newList);

            if (newList.length > 0) {
              mapContext.onHighlightListItem("esu", newList);
            } else {
              mapContext.onHighlightClear();
            }
          } else if (
            response.results.length &&
            streetContext.currentStreet &&
            response.results[0].graphic.attributes.USRN.toString() === streetContext.currentStreet.usrn.toString()
          ) {
            if (
              !lastSelectedEsu.current ||
              Number(response.results[0].graphic.attributes.EsuId) !== lastSelectedEsu.current.esuId ||
              performance.now() - lastSelectedEsu.current.time > doubleEventWait
            ) {
              lastSelectedEsu.current = {
                esuId: Number(response.results[0].graphic.attributes.EsuId),
                time: performance.now(),
              };
              streetContext.onEsuSelected(Number(response.results[0].graphic.attributes.EsuId));
            }
          } else {
            streetContext.onEsuSelected(null);
          }
        });
      }

      if (currentPointCaptureModeRef.current === "assignEsu") {
        if (backgroundStreetLayer.current) {
          opts = { include: backgroundStreetLayer.current };
          arcgisMap.view.hitTest(event.detail, opts).then(function (response) {
            if (
              response.results.length &&
              (!lastSelectedEsu.current ||
                Number(response.results[0].graphic.attributes.EsuId) !== lastSelectedEsu.current.esuId ||
                performance.now() - lastSelectedEsu.current.time > doubleEventWait)
            ) {
              lastSelectedEsu.current = {
                esuId: Number(response.results[0].graphic.attributes.EsuId),
                time: performance.now(),
              };
              const currentIndex = selectedEsus.current.indexOf(Number(response.results[0].graphic.attributes.EsuId));
              const newList = [...selectedEsus.current];

              if (currentIndex === -1) newList.push(Number(response.results[0].graphic.attributes.EsuId));
              else newList.splice(currentIndex, 1);

              selectedEsus.current = newList;
              selectionContext.onSelectionVariantChange(
                selectedEsus.current && selectedEsus.current.length > 0
                  ? "mapEsu"
                  : selectedExtents && selectedExtents.length > 0
                    ? "mapExtent"
                    : selectedProperties.current && selectedProperties.current.length > 0
                      ? "mapProperty"
                      : ""
              );
              selectionContext.onSelectionCountChange(
                displayExtentMergeTool
                  ? selectedExtents && selectedExtents.length > 0
                    ? selectedExtents.length
                    : 0
                  : selectedEsus.current && selectedEsus.current.length > 0
                    ? selectedEsus.current.length
                    : selectedProperties.current && selectedProperties.current.length > 0
                      ? selectedProperties.current.length
                      : 0
              );
              selectionContext.onCurrentEsuChange(newList);

              if (newList.length > 0) {
                mapContext.onHighlightListItem("esu", newList);
              } else {
                mapContext.onHighlightClear();
              }
            }
          });
        }

        if (unassignedEsusLayer.current) {
          opts = { include: unassignedEsusLayer.current };
          arcgisMap.view.hitTest(event.detail, opts).then(function (response) {
            if (
              response.results.length &&
              (!lastSelectedEsu.current ||
                Number(response.results[0].graphic.attributes.EsuId) !== lastSelectedEsu.current.esuId ||
                performance.now() - lastSelectedEsu.current.time > doubleEventWait)
            ) {
              lastSelectedEsu.current = {
                esuId: Number(response.results[0].graphic.attributes.EsuId),
                time: performance.now(),
              };
              const currentIndex = selectedEsus.current.indexOf(Number(response.results[0].graphic.attributes.EsuId));
              const newList = [...selectedEsus.current];

              if (currentIndex === -1) newList.push(Number(response.results[0].graphic.attributes.EsuId));
              else newList.splice(currentIndex, 1);

              selectedEsus.current = newList;
              selectionContext.onSelectionVariantChange(
                selectedEsus.current && selectedEsus.current.length > 0
                  ? "mapEsu"
                  : selectedExtents && selectedExtents.length > 0
                    ? "mapExtent"
                    : selectedProperties.current && selectedProperties.current.length > 0
                      ? "mapProperty"
                      : ""
              );
              selectionContext.onSelectionCountChange(
                displayExtentMergeTool
                  ? selectedExtents && selectedExtents.length > 0
                    ? selectedExtents.length
                    : 0
                  : selectedEsus.current && selectedEsus.current.length > 0
                    ? selectedEsus.current.length
                    : selectedProperties.current && selectedProperties.current.length > 0
                      ? selectedProperties.current.length
                      : 0
              );
              selectionContext.onCurrentEsuChange(newList);

              if (newList.length > 0) {
                mapContext.onHighlightListItem("esu", newList);
              } else {
                mapContext.onHighlightClear();
              }
            }
          });
        }
      }
    }
  };

  /**
   * Method used to get the feature at the given coordinates.
   *
   * @param {point} coord The coordinates to get the feature at.
   * @param {object} layer The layer to get the feature from.
   * @return {object} The feature at the given coordinates.
   */
  async function GetFeatureAtCoord(coord, layer) {
    switch (layer.serviceProvider) {
      case "OS":
        const osFeature = await GetOSFeatureAtCoord(coord, layer);
        return osFeature;

      case "viaEuropa":
        const viaEuropaFeature = await GetViaEuropaFeatureAtCoord(coord, layer);
        return viaEuropaFeature;

      case "thinkWare":
        const thinkWareFeature = await GetThinkWareFeatureAtCoord(coord, layer);
        return thinkWareFeature;

      default:
        alert(`Unknown service provider: ${layer.serviceProvider}`);
        break;
    }
  }

  /**
   * Method used to get the OS feature at the given coordinates.
   *
   * @param {point} coord The coordinates to get the feature at.
   * @param {object} layer The layer to get the feature from.
   * @return {object} The OS feature at the given coordinates.
   */
  function GetOSFeatureAtCoord(coord, layer) {
    const wfsParams = {
      key: layer.layerKey,
      service: "WFS",
      request: "GetFeature",
      version: "2.0.0",
      typeNames: layer.activeLayerId,
      propertyName: layer.propertyName,
      outputFormat: "GEOJSON",
      srsName: `urn:ogc:def:crs:EPSG::${spatialReferenceWkid.current}`,
      filter: `<ogc:Filter><ogc:Contains><ogc:PropertyName>SHAPE</ogc:PropertyName><gml:Point srsName="urn:ogc:def:crs:EPSG::${spatialReferenceWkid.current}"><gml:coordinates>${coord}</gml:coordinates></gml:Point></ogc:Contains></ogc:Filter>`,
      count: 1,
    };

    const options = {
      responseType: "json",
    };

    const encodedParameters = Object.keys(wfsParams)
      .map((paramName) => paramName + "=" + encodeURI(wfsParams[paramName]))
      .join("&");

    const url = layer.url + "?" + encodedParameters;

    return esriRequest(url, options)
      .then(function (response) {
        return response;
      })
      .catch((error) => {
        if (userContext.current.currentUser.showMessages) console.error("[ERROR] getting function", { error: error });
      });
  }

  /**
   * Method used to get the ViaEuropa feature at the given coordinates.
   *
   * @param {point} coord The coordinates to get the feature at.
   * @param {object} layer The layer to get the feature from.
   * @return {object} The ViaEuropa feature at the given coordinates.
   */
  function GetViaEuropaFeatureAtCoord(coord, layer) {
    const wfsParams = {
      service: "WFS",
      request: "GetFeature",
      typeNames: layer.activeLayerId,
      propertyName: layer.propertyName,
      outputFormat: "JSON",
      filter: `<Filter xmlns:gml="http://www.opengis.net/gml"><Intersects><PropertyName>geom</PropertyName><gml:Point srsName="${spatialReferenceWkid.current}"><gml:coordinates>${coord}</gml:coordinates></gml:Point></Intersects></Filter>`,
    };

    const options = {
      responseType: "json",
    };

    const encodedParameters = Object.keys(wfsParams)
      .map((paramName) => paramName + "=" + encodeURI(wfsParams[paramName]))
      .join("&");

    const url = `${layer.url}/${layer.layerKey}/wfs?${encodedParameters}`;

    return esriRequest(url, options)
      .then(function (response) {
        return response;
      })
      .catch((error) => {
        if (userContext.current.currentUser.showMessages) console.error("[ERROR] getting function", { error: error });
      });
  }

  /**
   * Method used to get the ThinkWare feature at the given coordinates.
   *
   * @param {point} coord The coordinates to get the feature at.
   * @param {object} layer The layer to get the feature from.
   * @return {object} The ThinkWare feature at the given coordinates.
   */
  function GetThinkWareFeatureAtCoord(coord, layer) {
    const wfsParams = {
      service: "WFS",
      request: "GetFeature",
      version: "2.0",
      srsName: `urn:x-ogc:def:crs:EPSG::${spatialReferenceWkid.current}`,
      typeName: layer.activeLayerId,
      outputFormat: "json",
      propertyName: layer.propertyName,
      filter: `<Filter xmlns="http://www.opengis.net/ogc"><Intersects><PropertyName>polygon</PropertyName><Point srsName="EPSG:${spatialReferenceWkid.current}"><coordinates>${coord}</coordinates></Point></Intersects></Filter>`,
      count: 1,
    };

    const options = {
      responseType: "json",
    };

    const encodedParameters = Object.keys(wfsParams)
      .map((paramName) => paramName + "=" + encodeURI(wfsParams[paramName]))
      .join("&");

    const url = layer.url + "?" + encodedParameters;

    return esriRequest(url, options)
      .then(function (response) {
        return response;
      })
      .catch((error) => {
        if (userContext.current.currentUser.showMessages) console.error("[ERROR] getting function", { error: error });
      });
  }

  /**
   * Method used to handle the basemap gallery change event.
   *
   * @param {object} event The event object.
   */
  const handleBasemapGalleryChange = (event) => {
    if (
      event.detail.name === "activeBasemap" &&
      arcgisBasemapGallery.current &&
      arcgisBasemapGallery.current.activeBasemap.id !== "basemap"
    ) {
      mapContext.onActiveBaseMapChange(arcgisBasemapGallery.current.activeBasemap.id);
    }
  };

  /**
   * Method used to handle the trigger action event from the layer list.
   *
   * @param {object} event The event object.
   */
  const handleTriggerAction = (event) => {
    event.preventDefault();
    const { action, item } = event.detail;
    switch (action.id) {
      case "edit-layer-info":
        // if (sketchLayer.current.loaded)
        break;

      case "show-hide-historical-extent-data":
        setShowHistoricalExtentData((prev) => {
          setShowHistorical(provenanceLayerTitle, !prev);
          return !prev;
        });
        break;

      case "show-hide-historical-background-provenance-data":
        setShowHistoricalBackgroundProvenanceData((prev) => {
          setShowHistorical(backgroundProvenanceLayerTitle, !prev);
          return !prev;
        });
        break;

      case "close-shp-file":
        mapContext.onUnloadShpFile(item.layer.id);
        break;

      case "close-geo-file":
        mapContext.onUnloadGeoFile(item.layer.id);
        break;

      case "download-features":
        const clickedFeatureLayer = baseLayersFeature.current.find((x) => x.layer.layerId === item.layer.id);
        if (clickedFeatureLayer) {
          GetMoreFeatures(clickedFeatureLayer);
        }
        break;

      default:
        break;
    }
  };

  /**
   * Method used to handle the distance measurement change event.
   *
   * @param {object} event The event object.
   */
  const handleDistanceMeasurementChange = (event) => {
    if (event.detail.name === "state") {
      setIsDistanceMeasurementActive(
        event.target[event.detail.name] !== "ready" && event.target[event.detail.name] !== "disabled"
      );
    }
  };

  /**
   * Method used to handle the area measurement change event.
   *
   * @param {object} event The event object.
   */
  const handleAreaMeasurementChange = (event) => {
    if (event.detail.name === "state") {
      setIsAreaMeasurementActive(
        event.target[event.detail.name] !== "ready" && event.target[event.detail.name] !== "disabled"
      );
    }
  };

  /**
   * Method used to handle the sketch create event.
   *
   * @param {SketchCreateEvent} event The sketch create event.
   */
  const handleSketchCreate = (event) => {
    if (event.detail.state === "start") {
      sketchLayer.current.graphics.removeAll();
    }

    switch (event.detail.tool) {
      case "point":
        if (event.detail.state === "complete" || event.detail.type === "undo" || event.detail.type === "redo") {
          mapContext.onSetCoordinate({
            x: event.detail.graphic.geometry.x.toFixed(4),
            y: event.detail.graphic.geometry.y.toFixed(4),
          });
        }
        break;

      case "polyline":
        if (
          event.detail.state === "complete" ||
          event.detail.type === "undo" ||
          event.detail.type === "redo" ||
          (event.detail.graphic.geometry &&
            event.detail.graphic.geometry.paths &&
            event.detail.toolEventInfo &&
            event.detail.toolEventInfo.type === "vertex-add")
        ) {
          handlePolylineUpdate(event.detail.graphic, event.detail.state === "complete");
        }
        break;

      case "polygon":
        if (
          event.detail.state === "complete" ||
          event.detail.type === "undo" ||
          event.detail.type === "redo" ||
          (event.detail.graphic.geometry &&
            event.detail.graphic.geometry.rings &&
            event.detail.toolEventInfo &&
            event.detail.toolEventInfo.type === "vertex-add")
        ) {
          handlePolygonUpdate(event.detail.graphic, event.detail.state === "complete");
        }
        break;

      default:
        if (userContext.current.currentUser.showMessages)
          console.error("[ERROR] Trying to create a new " + event.detail.graphic?.geometry?.type);
        break;
    }
  };

  const setSelectionAnchor = () => {
    if (
      mapContext.currentMapSearchData &&
      (mapContext.currentMapSearchData.editStreet || mapContext.currentMapSearchData.editProperty)
    ) {
      let currentDataTab = null;
      const tabIdsList = mapContext.currentMapSearchData.editProperty
        ? [
            "ads-property-details-tab",
            "ads-property-lpi-tab",
            "ads-property-classification-list-tab",
            "ads-property-classification-tab",
            "ads-property-organisation-list-tab",
            "ads-property-organisation-tab",
            "ads-property-successor-list-tab",
            "ads-property-successor-tab",
            "ads-property-provenance-list-tab",
            "ads-property-provenance-tab",
            "ads-property-cross-ref-list-tab",
            "ads-property-cross-ref-tab",
            "ads-related-tab",
            "ads-note-list-tab",
            "ads-note-tab",
            "ads-history-tab",
          ]
        : mapContext.currentMapSearchData.editStreet
          ? [
              "ads-street-details-tab",
              "ads-street-descriptor-tab",
              "ads-street-esu-list-tab",
              "ads-street-esu-tab",
              "ads-street-highway-dedication-tab",
              "ads-street-one-way-exemption-tab",
              "ads-street-asd-list-tab",
              "ads-street-maintenance-responsibility-tab",
              "ads-street-reinstatement-category-tab",
              "ads-street-os-special-designation-tab",
              "ads-street-interest-tab",
              "ads-street-construction-tab",
              "ads-street-gp-special-designation-tab",
              "ads-street-hww-tab",
              "ads-street-prow-tab",
              "ads-related-tab",
              "ads-note-list-tab",
              "ads-note-tab",
              "ads-history-tab",
            ]
          : null;
      if (tabIdsList) {
        tabIdsList.forEach((tabId) => {
          currentDataTab = document.getElementById(tabId);
          if (currentDataTab) {
            return;
          }
        });
      }
    }
  };

  /**
   * Method used to handle the sketch update event.
   *
   * @param {SketchUpdateEvent} event The sketch update event.
   */
  const handleSketchUpdate = (event) => {
    event.stopPropagation();
    if (!event.detail.aborted) {
      if (
        selectingProperties.current &&
        event.detail.graphics &&
        event.detail.graphics.length > 0 &&
        event.detail.state === "start" &&
        (!lastSelectedProperty.current ||
          Number(lastSelectedProperty.current.uprn) !== Number(event.detail.graphics[0].attributes.uprn) ||
          performance.now() - lastSelectedProperty.current.time > doubleEventWait)
      ) {
        lastSelectedProperty.current = { uprn: event.detail.graphics[0].attributes.uprn, time: performance.now() };
        // Get the list of UPRNs of the selected properties
        const selectedUprns = [];

        event.detail.graphics.forEach((point) => {
          if (point.geometry.type === "point" && !selectedUprns.includes(point.attributes.uprn)) {
            selectedUprns.push(point.attributes.uprn);
          }
        });

        if (selectedProperties.current.length + selectedUprns.length > 300) {
          saveResult.current = false;
          saveType.current = "maxSelectProperties";
          saveOpenRef.current = true;
          setSaveOpen(true);
          mapContext.onSelectPropertiesChange(false);
        } else {
          saveOpenRef.current = false;
          setSaveOpen(false);
          const removedProperties = selectedProperties.current.filter((x) => selectedUprns.includes(x));
          const newSelectedProperties = mergeArrays(
            selectedProperties.current.filter((x) => !removedProperties.includes(x)),
            selectedUprns.filter((x) => !removedProperties.includes(x)),
            (a, b) => a === b
          );
          selectedProperties.current = newSelectedProperties;
          selectionContext.onSelectionVariantChange(
            selectedEsus.current && selectedEsus.current.length > 0
              ? "mapEsu"
              : selectedExtents && selectedExtents.length > 0
                ? "mapExtent"
                : selectedProperties.current && selectedProperties.current.length > 0
                  ? "mapProperty"
                  : ""
          );
          selectionContext.onSelectionCountChange(
            displayExtentMergeTool
              ? selectedExtents && selectedExtents.length > 0
                ? selectedExtents.length
                : 0
              : selectedEsus.current && selectedEsus.current.length > 0
                ? selectedEsus.current.length
                : selectedProperties.current && selectedProperties.current.length > 0
                  ? selectedProperties.current.length
                  : 0
          );
          selectionContext.onPropertyUprnsChange(newSelectedProperties);

          if (newSelectedProperties.length > 0) {
            if (
              !selectionContext.displaySelectionControl &&
              userContext.current.currentUser &&
              userContext.current.currentUser.editProperty
            ) {
              setSelectionAnchor();
            }
            mapContext.onHighlightListItem("selectProperties", newSelectedProperties);
          } else {
            mapContext.onHighlightClear();
          }
        }

        arcgisSketch.current.cancel();
      } else {
        // get the graphic as it is being updated
        const graphic = event.detail.graphics[0];

        if (graphic.geometry) {
          switch (graphic.geometry.type) {
            case "point":
              if (event.detail.toolEventInfo && event.detail.toolEventInfo.type === "move-stop") {
                mapContext.onSetCoordinate({
                  x: graphic.geometry.x.toFixed(4),
                  y: graphic.geometry.y.toFixed(4),
                });
              }
              break;

            case "polyline":
              if (
                event.detail.state === "complete" ||
                (event.detail.toolEventInfo &&
                  (event.detail.toolEventInfo.type === "move-stop" ||
                    event.detail.toolEventInfo.type === "reshape-stop" ||
                    event.detail.toolEventInfo.type === "vertex-add" ||
                    event.detail.toolEventInfo.type === "vertex-remove" ||
                    event.detail.toolEventInfo.type === "scale-stop"))
              ) {
                handlePolylineUpdate(graphic, true);
              }
              break;

            case "polygon":
              if (
                event.detail.state === "complete" ||
                (event.detail.toolEventInfo &&
                  (event.detail.toolEventInfo.type === "move-stop" ||
                    event.detail.toolEventInfo.type === "reshape-stop" ||
                    event.detail.toolEventInfo.type === "vertex-remove" ||
                    event.detail.toolEventInfo.type === "scale-stop"))
              ) {
                handlePolygonUpdate(graphic, true);
              }
              break;

            default:
              break;
          }
        }
      }
    }
  };

  /**
   * Method used to handle the sketch redo event.
   *
   * @param {SketchRedoEvent} event The sketch redo event.
   * */
  const handleSketchRedo = (event) => {
    if (!event.detail.aborted) {
      // get the graphic as it is being updated
      const graphic = event.detail.graphics[0];

      if (graphic.geometry) {
        switch (graphic.geometry.type) {
          case "point":
            mapContext.onSetCoordinate({
              x: graphic.geometry.x.toFixed(4),
              y: graphic.geometry.y.toFixed(4),
            });
            break;

          case "polyline":
            handlePolylineUpdate(graphic, true);
            break;

          case "polygon":
            handlePolygonUpdate(graphic, true);
            break;

          default:
            break;
        }
      }
    }
  };

  /**
   * Method used to handle the sketch undo event.
   *
   * @param {SketchUndoEvent} event The sketch undo event.
   * */
  const handleSketchUndo = (event) => {
    if (!event.detail.aborted) {
      // get the graphic as it is being updated
      const graphic = event.detail.graphics[0];

      if (graphic.geometry) {
        switch (graphic.geometry.type) {
          case "point":
            mapContext.onSetCoordinate({
              x: graphic.geometry.x.toFixed(4),
              y: graphic.geometry.y.toFixed(4),
            });
            break;

          case "polyline":
            handlePolylineUpdate(graphic, true);
            break;

          case "polygon":
            handlePolygonUpdate(graphic, true);
            break;

          default:
            break;
        }
      }
    }
  };

  /**
   * Method used to handle the sketch delete event.
   *
   * @param {SketchDeleteEvent} event The sketch delete event.
   */
  const handleSketchDelete = (event) => {
    // get the graphic as it is being updated
    const graphic = event.detail.graphics[0];
    if (graphic.geometry) {
      switch (graphic.geometry.type) {
        case "polygon":
          handlePolygonUpdate("", true);
          break;

        case "polyline":
          handlePolylineUpdate("", true);
          break;

        default:
          break;
      }
    }
  };

  // Set the map event listeners when the map is ready
  if (arcgisMap) {
    if (!viewReadyEvent.current) {
      arcgisMap.addEventListener("arcgisViewReadyChange", handleViewReady);
      viewReadyEvent.current = true;
    }

    if (!viewChangeEvent.current) {
      arcgisMap.addEventListener("arcgisViewChange", handleViewChange);
      viewChangeEvent.current = true;
    }

    if (!viewClickEvent.current) {
      arcgisMap.addEventListener("arcgisViewClick", handleViewClick);
      viewClickEvent.current = true;
    }

    if (!historicalDataDisplaySet.current) {
      let showHistorical = getShowHistorical(provenanceLayerTitle);
      setShowHistoricalExtentData(showHistorical);
      showHistorical = getShowHistorical(backgroundProvenanceLayerTitle);
      setShowHistoricalBackgroundProvenanceData(showHistorical);
      historicalDataDisplaySet.current = true;
    }
  }

  /**
   * Method used to define the actions available for the feature layers.
   *
   * @param {object} event The event object.
   */
  async function defineActions(event) {
    const { item } = event;

    await item.layer.when();

    if (process.env.NODE_ENV === "development" && item.title === sketchLayerTitle) {
      item.actionsSections = [
        [
          {
            title: "Display info",
            icon: "question-f",
            id: "edit-layer-info",
          },
        ],
      ];
    }

    if ([backgroundProvenanceLayerTitle].includes(item.title)) {
      item.actionsSections = [
        [
          {
            title: showHistoricalBackgroundProvenanceData ? "Hide historical data" : "Show historical data",
            icon: showHistoricalBackgroundProvenanceData ? "circle-f" : "circle",
            id: "show-hide-historical-background-provenance-data",
          },
        ],
      ];
    }

    if ([provenanceLayerTitle].includes(item.title)) {
      item.actionsSections = [
        [
          {
            title: showHistoricalExtentData ? "Hide historical data" : "Show historical data",
            icon: showHistoricalExtentData ? "circle-f" : "circle",
            id: "show-hide-historical-extent-data",
          },
        ],
      ];
    }

    if (
      [
        streetLayerTitle,
        llpgStreetLayerTitle,
        unassignedEsusLayerTitle,
        propertyLayerTitle,
        provenanceLayerTitle,
        backgroundStreetLayerTitle,
        backgroundPropertyLayerTitle,
        backgroundProvenanceLayerTitle,
      ].includes(item.title) ||
      loadedGeoFileTitles.current.includes(item.title)
    ) {
      const label = document.createElement("calcite-label");
      label.innerText = "Opacity";
      label.scale = "s";

      const slider = document.createElement("calcite-slider");
      slider.labelHandles = true;
      slider.labelTicks = true;
      slider.min = 0;
      slider.minLabel = "0";
      slider.max = 1;
      slider.maxLabel = "1";
      slider.scale = "s";
      slider.step = 0.01;
      slider.value = item.layer.opacity;
      slider.ticks = 0.5;

      slider.addEventListener("calciteSliderChange", () => {
        item.layer.opacity = slider.value;
        setLayerOpacity(item.title, isStreetPropertyMap.current, slider.value);
      });

      label.appendChild(slider);

      item.panel = {
        content: label,
        icon: "sliders-horizontal",
        title: "Change layer opacity",
      };
    }

    if (loadedShpFileTitles.current.includes(item.title)) {
      item.actionsSections = [
        [
          {
            title: "Remove layer",
            icon: "x-circle-f",
            id: "close-shp-file",
          },
        ],
      ];
    }

    if (loadedGeoFileTitles.current.includes(item.title)) {
      item.actionsSections = [
        [
          {
            title: "Remove layer",
            icon: "x-circle-f",
            id: "close-geo-file",
          },
        ],
      ];
    }

    if (baseLayersFeature.current.find((x) => x.layer.title === item.title && x.layer.usePaging && x.layer.esuSnap)) {
      item.actionsSections = [
        [
          {
            title: "Download features",
            icon: "download",
            id: "download-features",
          },
        ],
      ];
    }
  }

  /**
   * * Method used to get the features from the layer.
   *
   * @param {object} layer The layer to get the features from.
   * @param {number} count The number of features to get.
   * @param {number} startIndex The index to start getting features from.
   * @return {object} The features from the layer.
   */
  async function GetFeatures(layer, count, startIndex) {
    switch (layer.serviceProvider) {
      case "OS":
        return await GetOSFeatures(layer, count, startIndex);

      case "thinkWare":
        return await GetThinkWareFeatures(layer, count, startIndex);

      default:
        alert(`Unknown service provider: ${layer.serviceProvider}`);
        break;
    }
  }

  /**
   * Method used to get the OS features from the layer.
   *
   * @param {object} layer The layer to get the features from.
   * @param {number} count The number of features to get.
   * @param {number} startIndex The index to start getting features from.
   * @return {object}   The OS features from the layer.
   */
  function GetOSFeatures(layer, count, startIndex) {
    const wfsParams = {
      key: layer.layerKey,
      service: "WFS",
      request: "GetFeature",
      version: "2.0.0",
      typeNames: layer.activeLayerId,
      propertyName: layer.propertyName,
      outputFormat: "GEOJSON",
      srsName: `urn:ogc:def:crs:EPSG::${spatialReferenceWkid.current}`,
      filter: `<ogc:Filter><ogc:BBOX><ogc:PropertyName>SHAPE</ogc:PropertyName><gml:Box srsName="urn:ogc:def:crs:EPSG::${
        spatialReferenceWkid.current
      }"><gml:coordinates>${backgroundExtent.current ? backgroundExtent.current.xmin : 0.0},${
        backgroundExtent.current ? backgroundExtent.current.ymin : 0.0
      } ${backgroundExtent.current ? backgroundExtent.current.xmax : 660000.0},${
        backgroundExtent.current ? backgroundExtent.current.ymax : 1300000.0
      }</gml:coordinates></gml:Box></ogc:BBOX></ogc:Filter>`,
      count: count,
      startIndex: startIndex,
    };

    const options = {
      responseType: "json",
    };

    const encodedParameters = Object.keys(wfsParams)
      .map((paramName) => paramName + "=" + encodeURI(wfsParams[paramName]))
      .join("&");

    const url = layer.url + "?" + encodedParameters;

    return esriRequest(url, options)
      .then(function (response) {
        return response;
      })
      .catch((error) => {
        if (userContext.current.currentUser.showMessages)
          console.error("[ERROR] getting OS functions", {
            layer: layer,
            wfsParams: wfsParams,
            url: url,
            error: error,
          });
      });
  }

  /**
   * Method used to get the ThinkWare features from the layer.
   *
   * @param {object} layer The layer to get the features from.
   * @param {number} count The number of features to get.
   * @param {number} startIndex The index to start getting features from.
   * @return {object} The ThinkWare features from the layer.
   */
  function GetThinkWareFeatures(layer, count, startIndex) {
    const wfsParams = {
      token: layer.token,
      service: "WFS",
      request: "GetFeature",
      version: "1.1.0",
      typeName: layer.activeLayerId,
      propertyName: layer.propertyName,
      outputFormat: "json",
      srsName: `urn:ogc:def:crs:EPSG::${spatialReferenceWkid.current}`,
      filter: `<Filter xmlns="http://www.opengis.net/ogc"><BBOX><PropertyName>shape</PropertyName><Envelope xmlns="http://www.opengis.net/gml" srsName="EPSG:${
        spatialReferenceWkid.current
      }"><lowerCorner>${backgroundExtent.current ? backgroundExtent.current.xmin : 0.0} ${
        backgroundExtent.current ? backgroundExtent.current.ymin : 0.0
      }</lowerCorner><upperCorner>${backgroundExtent.current ? backgroundExtent.current.xmax : 660000.0} ${
        backgroundExtent.current ? backgroundExtent.current.ymax : 1300000.0
      }</upperCorner></Envelope></BBOX></Filter>`,
    };

    const options = {
      query: {
        f: "json",
      },
      responseType: "json",
    };

    const encodedParameters = Object.keys(wfsParams)
      .map((paramName) => paramName + "=" + encodeURI(wfsParams[paramName]))
      .join("&");

    const url = layer.url + "?" + encodedParameters;

    return esriRequest(url, options)
      .then(function (response) {
        return response;
      })
      .catch((error) => {
        if (userContext.current.currentUser.showMessages)
          console.error("[ERROR] getting thinkWare functions", {
            layer: layer,
            wfsParams: wfsParams,
            url: url,
            error: error,
          });
      });
  }

  /**
   * Method used to get the fill colour of the feature based on its code.
   *
   * @param {number} code The code of the feature.
   * @return {array}  The fill colour of the feature.
   */
  function GetFeatureFillColour(code) {
    switch (code) {
      case 10021: // Building
        return [252, 217, 172];

      // case 10025: // Buildings Or Structure
      //   return [255, 0, 0];

      // case 10031: // Built Environment
      //   return [255, 0, 0];

      // case 10044: // General Feature
      //   return [255, 0, 0];

      case 10056: // General Surface
        return [222, 222, 222];

      case 10053: // General Surface/Multi Surface
        return [250, 250, 202];

      // case 10054: // General Surface/Step
      //   return [255, 0, 0];

      // case 10062: // Glasshouse
      //   return [255, 0, 0];

      // case 10065: // Height Control
      //   return [255, 0, 0];

      // case 10076: // Historic Interest
      //   return [255, 0, 0];

      case 10089: // Inland Water
        return [179, 211, 252];

      // case 10093: // Landform
      //   return [255, 0, 0];

      // case 10099: // Landform/Cliff
      //   return [255, 0, 0];

      case 10096: // Landform/Slope
        return [172, 252, 184];

      case 10111: // Natural Environment
        return [172, 252, 184];

      // case 10116: // Network Or Polygon Closing Geometry
      //   return [255, 0, 0];

      case 10123: // Path
        return [195, 195, 195];

      case 10119: // Path/Step
        return [195, 195, 195];

      // case 10126: // Political Or Administrative
      //   return [255, 0, 0];

      // case 10167: // Rail
      //   return [255, 0, 0];

      case 10172: // Road Or Track
        return [255, 255, 255];

      case 10183: // Roadside
        return [222, 222, 222];

      case 10185: // Structure
        return [195, 195, 195];

      // case 10190: // Structure/Archway
      //   return [255, 0, 0];

      // case 10193: // Structure/Pylon
      //   return [255, 0, 0];

      // case 10187: // Structure/Upper Level Of Communication
      //   return [255, 0, 0];

      // case 10199: // Terrain And Height
      //   return [255, 0, 0];

      // case 10210: // Tidal Water
      //   return [255, 0, 0];

      // case 10203: // Tidal Water/Foreshore
      //   return [255, 0, 0];

      // case 10217: // Unclassified
      //   return [255, 0, 0];

      default:
        alert(`Unknown FeatureCode: ${code}`);
        return [255, 0, 0, 1];
    }
  }

  /**
   * Method used to get the outline colour of the feature based on its code.
   *
   * @param {number} code The code of the feature.
   * @return {object} The outline styling of the feature.
   */
  function GetFeatureOutline(code) {
    switch (code) {
      case 10021: // Building
        return {
          color: [191, 191, 191],
          width: 1,
        };

      // case 10025: // Buildings Or Structure
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      // case 10031: // Built Environment
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      // case 10044: // General Feature
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      case 10056: // General Surface
        return {
          color: [201, 201, 201],
          width: 1,
        };

      case 10053: // General Surface/Multi Surface
        return {
          color: [195, 195, 195],
          width: 1,
        };

      // case 10054: // General Surface/Step
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      // case 10062: // Glasshouse
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      // case 10065: // Height Control
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      // case 10076: // Historic Interest
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      case 10089: // Inland Water
        return {
          color: [47, 130, 224],
          width: 1,
        };

      // case 10093: // Landform
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      // case 10099: // Landform/Cliff
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      case 10096: // Landform/Slope
        return {
          // color: [132, 240, 148],
          color: [150, 150, 150],
          width: 1,
        };

      case 10111: // Natural Environment
        return {
          // color: [132, 240, 148],
          color: [150, 150, 150],
          width: 1,
        };

      // case 10116: // Network Or Polygon Closing Geometry
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      case 10123: // Path
        return {
          color: [150, 150, 150],
          width: 1,
        };

      case 10119: // Path/Step
        return {
          color: [150, 150, 150],
          width: 1,
        };

      // case 10126: // Political Or Administrative
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      // case 10167: // Rail
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      case 10172: // Road Or Track
        return {
          color: [255, 255, 255],
          width: 1,
          style: "dash",
        };

      case 10183: // Roadside
        return {
          color: [150, 150, 150],
          width: 1,
        };

      case 10185: // Structure
        return {
          color: [150, 150, 150],
          width: 1,
        };

      // case 10190: // Structure/Archway
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      // case 10193: // Structure/Pylon
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      // case 10187: // Structure/Upper Level Of Communication
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      // case 10199: // Terrain And Height
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      // case 10210: // Tidal Water
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      // case 10203: // Tidal Water/Foreshore
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      // case 10217: // Unclassified
      // return {
      //   color: [255, 0, 0],
      //   width: 1,
      // };

      default:
        return {
          color: [255, 0, 0],
          width: 1,
        };
    }
  }

  /**
   * Method used to get more features from the feature layer.
   *
   * @param {object} featureLayer The feature layer to get more features from.
   */
  function GetMoreFeatures(featureLayer) {
    let geoJson = null;
    if (geoJSONFeatures.current && geoJSONFeatures.current.length > 0) {
      geoJson = geoJSONFeatures.current.find((x) => x.id === featureLayer.layer.layerId);
    }
    const foundExistingGeoJson = !!geoJson;
    if (!foundExistingGeoJson) {
      const baseLayer = baseMapLayers.current.find((x) => x.layerId === featureLayer.layer.layerId);
      geoJson = {
        id: featureLayer.layer.layerId,
        count: baseLayer.maxBatchSize,
        startIndex: 0,
        featureCollection: { type: "FeatureCollection", features: [] },
      };
    }
    let graphics = null;
    let gotAllFeatures = false;
    GetFeatures(featureLayer.layer, geoJson.count, geoJson.startIndex).then(function (json) {
      geoJson.featureCollection.features.push.apply(geoJson.featureCollection.features, json.data.features);
      gotAllFeatures = json.data.features.length < geoJson.count;
      featuresDownloaded.current = geoJson.startIndex + json.data.features.length;
      geoJson.startIndex += geoJson.count;
      if (gotAllFeatures) {
        saveResult.current = true;
        saveType.current = "downloadedAllFeatures";
        saveOpenRef.current = true;
        setSaveOpen(true);
      }
      if (!foundExistingGeoJson) geoJSONFeatures.current.push(geoJson);
      if (geoJson.featureCollection.features.length > 0) {
        graphics = geoJson.featureCollection.features.map(function (f) {
          switch (featureLayer.layer.geometryType) {
            case "line":
              const polyline = new Polyline({
                type: "polyline",
                paths: f.geometry.coordinates[0],
                spatialReference: { wkid: spatialReferenceWkid.current },
              });
              return new Graphic({
                geometry: polyline,
                attributes: f.properties,
                symbol: unselectedLineSymbol,
              });
            case "polygon":
              const polygon = new Polygon({
                type: "polygon",
                rings: f.geometry.coordinates[0],
                spatialReference: { wkid: spatialReferenceWkid.current },
              });
              const fillSymbol = {
                type: "simple-fill",
                color: GetFeatureFillColour(f.properties.FeatureCode),
                outline: GetFeatureOutline(f.properties.FeatureCode),
              };
              return new Graphic({
                geometry: polygon,
                attributes: f.properties,
                symbol: fillSymbol,
              });
            default:
              return null;
          }
        });
        const currentFeatureLayer =
          arcgisMap && arcgisMap.basemapView.referenceLayerViews.findLayerById(featureLayer.layer.layerId);
        if (currentFeatureLayer) {
          sketchLayer.current.graphics.removeAll();
          sketchLayer.current.addMany(graphics);
        }
      }
    });
  }

  /**
   * Method used to create a new polyline graphic.
   *
   * @param {Array} graphicPaths The array of coordinates that make up the polyline.
   * @returns {object} A new polyline graphic object.
   */
  function createPolylineGraphic(graphicPaths) {
    const editPolyline = new Polyline({
      type: "polyline",
      paths: graphicPaths,
      spatialReference: { wkid: spatialReferenceWkid.current },
    });

    const editPolylineGraphic = new Graphic({
      attributes: { typeName: "LineString" },
      geometry: editPolyline,
      symbol: GetStreetMapSymbol(),
    });

    return editPolylineGraphic;
  }

  /**
   * Method used to create a new point graphic.
   *
   * @param {number} x The easting for the property.
   * @param {number} y The northing for the property.
   * @param {number} logicalStatus The logical status for the property.
   * @param {string} classification The classification for the property.
   * @param {boolean} highlighted True if the property should be highlighted; otherwise false.
   * @returns {object} The new point graphic object.
   */
  function createPointGraphic(x, y, logicalStatus, classification, highlighted = false) {
    const editPoint = new Point({
      type: "point",
      x: x,
      y: y,
      spatialReference: { wkid: spatialReferenceWkid.current },
    });

    const editPointGraphic = new Graphic({
      attributes: { typeName: "Point" },
      geometry: editPoint,
      symbol: GetPropertyMapSymbol(logicalStatus, classification, highlighted),
    });

    return editPointGraphic;
  }

  /**
   * Method used to create a new point graphic that can be selected.
   *
   * @param {number} x The easting for the property.
   * @param {number} y The northing for the property.
   * @param {number} logicalStatus The logical status for the property.
   * @returns {object} The new point graphic object.
   */
  function createSelectablePointGraphic(x, y, uprn, logicalStatus) {
    const editPoint = new Point({
      type: "point",
      x: x,
      y: y,
      spatialReference: { wkid: spatialReferenceWkid.current },
    });

    const editPointGraphic = new Graphic({
      attributes: { uprn: uprn, typeName: "Point" },
      geometry: editPoint,
      symbol: GetBackgroundPropertyMapSymbol(logicalStatus),
    });

    return editPointGraphic;
  }

  /**
   * Method used to create a new polygon graphic.
   *
   * @param {Array} graphicRings The array of coordinates that make up the polygon.
   * @param {boolean} historical Indicates if the polygon is historical.
   * @param {string} code The code for the polygon
   * @returns {object} The new polygon graphic object.
   */
  function createPolygonGraphic(graphicRings, historical, code) {
    const editPolygons = [];

    for (let i = 0; i < graphicRings.length; i++) {
      const editPolygon = new Polygon({
        type: "polygon",
        rings: graphicRings[i],
        spatialReference: { wkid: spatialReferenceWkid.current },
      });

      if (editPolygon) {
        editPolygons.push(editPolygon);
      }
    }

    if (editPolygons.length !== 0) {
      const polygonGeometry = editPolygons.length === 1 ? editPolygons[0] : unionOperator.executeMany(editPolygons);
      const mapSymbol = GetExtentMapSymbol(historical, code);
      const editPolygonGraphic = new Graphic({
        attributes: { typeName: "Polygon" },
        geometry: polygonGeometry,
        symbol: mapSymbol,
      });

      return editPolygonGraphic;
    }

    return null;
  }

  /**
   * Method used to handle the polyline update event.
   *
   * @param {object} graphic The graphic object being updated.
   * @param {boolean} canUpdate True if the graphic can be updated; otherwise false.
   */
  function handlePolylineUpdate(graphic, canUpdate) {
    if (!(graphic === "" && canUpdate)) {
      mapContext.onUpdatingEsuGeometry(true);
    }

    if (graphic.geometry?.paths) {
      // check if the polyline intersects itself
      const intersectingSegment = getIntersectingSegment(graphic.geometry);

      graphic.symbol =
        intersectingSegment || graphic.symbol.style === invalidLineSymbol.color ? invalidLineSymbol : validLineSymbol;

      if (!saveOpenRef.current && intersectingSegment) {
        saveResult.current = false;
        saveType.current = "intersectLine";
        saveOpenRef.current = true;
        setSaveOpen(true);
      } else {
        saveOpenRef.current = false;
        setSaveOpen(false);
      }

      // Only update the geometry if it is valid
      if (canUpdate && !intersectingSegment && graphic.symbol.style !== invalidLineSymbol.color) {
        if (highlightESU.current && mapContext.currentHighlight.esu?.length > 0) {
          mapContext.onHighlightClear();
        }
        mapContext.onSetLineGeometry(geometryToWKT(graphic.geometry));
      }
    } else if (canUpdate) {
      mapContext.onSetLineGeometry("");
      setSketchCreateTools(["polyline"]);
      if (!mapContext.createToolActivated && arcgisSketch.current) {
        arcgisSketch.current.create("polyline");
        mapContext.onCreateToolActivated(true);
      }
    }
  }

  /**
   * Method used to handle the polygon update event.
   *
   * @param {object} graphic The graphic object being updated.
   * @param {boolean} canUpdate True if the graphic can be updated; otherwise false.
   */
  function handlePolygonUpdate(graphic, canUpdate) {
    if (!(graphic === "" && canUpdate)) {
      mapContext.onUpdatingProvenance(true);
      updatingPolygons.current = true;
    }
    oldExtents.current = mapContext.currentLayers.extents.filter(
      (x) => x.geometry && Number(x.uprn) === Number(mapContext.currentMapSearchData.editProperty)
    );
    oldShowHistoricalExtentData.current = showHistoricalExtentData;
    if (graphic === "" && canUpdate) {
      mapContext.onSetPolygonGeometry("");
      setSketchCreateTools(["polygon"]);
      if (!mapContext.createToolActivated && arcgisSketch.current) {
        arcgisSketch.current.create("polygon");
        mapContext.onCreateToolActivated(true);
      }
    } else if (graphic.geometry.rings) {
      saveOpenRef.current = false;
      setSaveOpen(false);

      if (canUpdate) {
        mapContext.onSetPolygonGeometry(geometryToWKT(graphic.geometry));
      }
    }
  }

  /**
   * Method used to check if the polyline is self-intersecting.
   *
   * @param {geometry} polyline The polyline to check.
   * @return {boolean} True if the polyline is self-intersecting; otherwise false.
   */
  function isSelfIntersecting(polyline) {
    // if we only have a single line with only 2 nodes it cannot cross itself
    if (polyline.paths.length === 1 && polyline.paths[0].length < 3) {
      return false;
    }

    const line = polyline.clone();
    let lineIntersects = false;

    for (var z = 0; z < polyline.paths.length; z++) {
      if (polyline.paths[z] && polyline.paths[z].length && polyline.paths[z].length > 0) {
        for (var x = 0; x < polyline.paths[z].length; x++) {
          //get the next segment from the polyline that is being drawn
          const lastSegment = getNextSegment(polyline, z, x);
          line.removePoint(z, line.paths[z].length - 1);

          // returns true if the line intersects itself, false otherwise
          lineIntersects = crossesOperator.execute(lastSegment, line);

          if (lineIntersects) break;
        }
      }

      if (lineIntersects) break;
    }

    return lineIntersects;
  }

  /**
   * Method used to get the next segment of the polyline that is being edited.
   *
   * @param {geometry} polyline The polyline to get the next segment from.
   * @param {number} pathIdx The index of the path in the polyline.
   * @param {number} pointIdx The index of the point in the path.
   * @return {object} The next segment of the polyline.
   */
  function getNextSegment(polyline, pathIdx, pointIdx) {
    const line = polyline.clone();
    for (var z = 1; z < pointIdx; z++) {
      line.removePoint(pathIdx, line.paths[pathIdx].length - 1);
    }
    const lastXYPoint = line.removePoint(pathIdx, line.paths[pathIdx].length - 1);
    const existingLineFinalPoint = line.getPoint(pathIdx, line.paths[pathIdx].length - 1);

    return new Polyline({
      type: "polyline",
      spatialReference: arcgisMap.spatialReference,
      hasZ: false,
      paths: [
        [
          [existingLineFinalPoint.x, existingLineFinalPoint.y],
          [lastXYPoint.x, lastXYPoint.y],
        ],
      ],
    });
  }

  /**
   * Checks if the line intersects itself. If yes, change the last segment's symbol giving a visual feedback to the user.
   *
   * @param {geometry} polyline The polyline to check for intersection.
   * @return {object} The intersecting segment graphic or null if there is no intersection.
   */
  function getIntersectingSegment(polyline) {
    if (isSelfIntersecting(polyline)) {
      return new Graphic({
        geometry: getLastSegment(polyline, 0, polyline.paths[0].length),
        symbol: invalidLineSymbol,
      });
    }
    return null;
  }

  /**
   * Method used to get the last segment of the polyline that is being drawn.
   *
   * @param {geometry} polyline The polyline to get the last segment from.
   * @return {Polyline} The last segment of the polyline.
   */
  function getLastSegment(polyline) {
    const line = polyline.clone();
    const lastXYPoint = line.removePoint(0, line.paths[0].length - 1);
    const existingLineFinalPoint = line.getPoint(0, line.paths[0].length - 1);

    return new Polyline({
      type: "polyline",
      spatialReference: arcgisMap.spatialReference,
      hasZ: false,
      paths: [
        [
          [existingLineFinalPoint.x, existingLineFinalPoint.y],
          [lastXYPoint.x, lastXYPoint.y],
        ],
      ],
    });
  }

  /**
   * Event to handle when the save dialog is closed.
   *
   * @param {object} event The event object
   * @param {string} reason The reason the save dialog is closing.
   * @returns
   */
  const handleSaveClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSaveOpen(false);
  };
  /**
   * Event to handle opening the copy alert.
   *
   * @param {string} dataType The type of data that is being copied to the clipboard.
   */
  const handleSetCopyOpen = (dataType) => {
    selectionContext.onSetCopyOpen(dataType);
  };

  /**
   * Method to handle errors from the selection control.
   *
   * @param {String} typeOfError The error type.
   */
  const handleSelectionControlError = (typeOfError) => {
    if (typeOfError) {
      saveResult.current = false;
      saveType.current = typeOfError;
      saveOpenRef.current = true;
    }
  };

  /**
   * Method to get the required alert text.
   *
   * @returns {string|null} the required alert text.
   */
  const getAlertText = () => {
    if (saveOpen && saveType.current) {
      switch (saveType.current) {
        case "intersectLine":
          return "The line intersects itself!";

        case "intersectPolygon":
          return "The polygon intersects itself!";

        case "addPropertyInvalidState":
          return "You are not allowed to create a property on a closed street.";

        case "addPropertyInvalidType":
          return "You are not allowed to create a property on this type of street.";

        case "addRangeInvalidState":
          return "You are not allowed to create properties on a closed street.";

        case "addRangeInvalidType":
          return "You are not allowed to create properties on this type of street.";

        case "streetAlreadyDividedMerged":
          return "There has already been a divide or merge on this street, please save changes before retrying.";

        case "invalidDivideEsu":
          return "Open the associated street before trying to divide this ESU.";

        case "assignEsuSameStreet":
          return "The ESU is already assigned to this street.";

        case "invalidAssignEsu":
          return "You need to open the street you want to assign this ESU to before you can proceed.";

        case "downloadedAllFeatures":
          return `All ${featuresDownloaded.current} available features have been download.`;

        case "maxSelectProperties":
          return "You have exceeded the maximum number of properties (300), reduce the size and use the add to existing option.";

        case "uploadedFile":
          return `${featuresDownloaded.current} has been successfully uploaded.`;

        case "uploadedFileError":
          return `${featuresDownloaded.current} failed to be uploaded.`;

        case "uploadedRendererError":
          return `Unknown renderer in layer file: ${featuresDownloaded.current}.`;

        case "maxParentLevel":
          return "Parent is already at the maximum BLPU hierarchy level.";

        case "noPermission":
          return "You do not have permission to do this.";

        case "noRecordsSelectedToExport":
          return "You have not selected any records to export.";

        case "NoDataToExport":
          return "Unable to get the data required to do an export.";

        default:
          if (saveResult.current) return `The ${saveType.current} has been successfully saved.`;
          else if (failedValidation.current) return `Failed to validate the ${saveType.current} record.`;
          else return `Failed to save the ${saveType.current}.`;
      }
    }
  };

  /**
   * Method to handle saving changes to a street.
   *
   * @param {object} currentStreet The current street.
   */
  const handleSaveStreet = useCallback(
    (currentStreet) => {
      SaveStreet(
        currentStreet,
        streetContext,
        userContext,
        lookupContext.current,
        searchContext,
        mapContext,
        sandboxStore,
        isScottish.current,
        isWelsh.current
      ).then((result) => {
        if (result.street) {
          saveResult.current = true;
          saveType.current = "street";
          setStore(result.store);
          setSaveOpen(true);
        } else {
          saveResult.current = false;
          saveType.current = "street";
          setSaveOpen(true);
        }
      });
    },
    [mapContext, sandboxStore, searchContext, setStore, streetContext]
  );

  /**
   * Method to handle saving changes to a property.
   *
   * @param {object} currentProperty The current property.
   * @param {boolean} cascadeParentPaoChanges If true the child property PAO details need to be changed; otherwise they are not changed.
   * @param {boolean} cascadeMove If true the property location changes need to be cascaded down to the children; otherwise they are not changed.
   */
  const handleSaveProperty = useCallback(
    (currentProperty, cascadeParentPaoChanges, cascadeMove) => {
      SavePropertyAndUpdate(
        currentProperty,
        propertyContext.currentProperty.newProperty,
        propertyContext,
        userContext.current,
        searchContext,
        mapContext,
        sandboxStore,
        isScottish.current,
        isWelsh.current,
        cascadeParentPaoChanges,
        cascadeMove
      ).then((result) => {
        if (result.property) {
          saveResult.current = true;
          saveType.current = "property";
          setStore(result.store);
          setSaveOpen(true);
        } else {
          saveResult.current = false;
          saveType.current = "property";
          setSaveOpen(true);
        }
      });
    },
    [mapContext, propertyContext, sandboxStore, searchContext, setStore]
  );

  /**
   * Method to check for any unsaved changes to the existing record.
   *
   * @param {object} functionAfterCheck The function to be called after we have checked for any unsaved changes.
   */
  const checkForUnsavedChanges = useCallback(
    (functionAfterCheck) => {
      if (sourceStreetRef.current) {
        const streetChanged = HasStreetChanged(
          streetContext.currentStreet.newStreet,
          sourceStreetRef.current,
          currentDescriptorRecordRef.current,
          currentEsuRecordRef.current,
          currentHighwayDedicationRecordRef.current,
          currentOneWayExemptionRecordRef.current,
          currentMaintenanceResponsibilityRecordRef.current,
          currentReinstatementCategoryRecordRef.current,
          currentOSSpecialDesignationRecordRef.current,
          currentInterestRecordRef.current,
          currentConstructionRecordRef.current,
          currentSpecialDesignationRecordRef.current,
          currentHWWRecordRef.current,
          currentPRoWRecordRef.current,
          currentStreetNoteRecordRef.current,
          currentStreetRef.current,
          userContext.current && userContext.current.currentUser && userContext.current.currentUser.hasASD
        );

        if (streetChanged) {
          associatedRecords.current = getChangedAssociatedStreetRecords(
            sourceStreetRef.current,
            currentDescriptorRecordRef.current,
            currentEsuRecordRef.current,
            currentHighwayDedicationRecordRef.current,
            currentOneWayExemptionRecordRef.current,
            currentMaintenanceResponsibilityRecordRef.current,
            currentReinstatementCategoryRecordRef.current,
            currentOSSpecialDesignationRecordRef.current,
            currentInterestRecordRef.current,
            currentConstructionRecordRef.current,
            currentSpecialDesignationRecordRef.current,
            currentHWWRecordRef.current,
            currentPRoWRecordRef.current,
            currentStreetNoteRecordRef.current,
            currentStreetRef.current,
            streetContext.esuDataChanged
          );

          const streetDataRef = currentStreetRef.current ? currentStreetRef.current : sourceStreetRef.current;

          if (associatedRecords.current.length > 0) {
            saveConfirmDialog(associatedRecords.current)
              .then((result) => {
                if (result === "save") {
                  if (streetContext.validateData()) {
                    failedValidation.current = false;
                    const currentStreetData = GetCurrentStreetData(
                      streetDataRef,
                      currentDescriptorRecordRef.current,
                      currentEsuRecordRef.current,
                      currentStreetNoteRecordRef.current,
                      currentMaintenanceResponsibilityRecordRef.current,
                      currentReinstatementCategoryRecordRef.current,
                      currentOSSpecialDesignationRecordRef.current,
                      currentInterestRecordRef.current,
                      currentConstructionRecordRef.current,
                      currentSpecialDesignationRecordRef.current,
                      currentHWWRecordRef.current,
                      currentPRoWRecordRef.current,
                      lookupContext.current,
                      isWelsh.current,
                      isScottish.current,
                      userContext.current && userContext.current.currentUser && userContext.current.currentUser.hasASD
                    );
                    handleSaveStreet(currentStreetData);
                  } else {
                    failedValidation.current = true;
                    saveResult.current = false;
                    setSaveOpen(true);
                  }
                }
                mapContext.onSetCoordinate(null);
                streetContext.onStreetModified(false);
                resetSandbox("street");
                functionAfterCheck();
              })
              .catch(() => {});
          } else {
            saveConfirmDialog(true)
              .then((result) => {
                if (result === "save") {
                  handleSaveStreet(currentStreetRef.current);
                }
                mapContext.onSetCoordinate(null);
                streetContext.onStreetModified(false);
                resetSandbox("street");
                functionAfterCheck();
              })
              .catch(() => {});
          }
        } else {
          mapContext.onSetCoordinate(null);
          streetContext.onStreetModified(false);
          resetSandbox("street");
          functionAfterCheck();
        }
      } else if (sourcePropertyRef.current) {
        const propertyChanged = HasPropertyChanged(
          propertyContext.currentProperty.newProperty,
          sourcePropertyRef.current,
          currentLpiRecordRef.current,
          currentProvenanceRecordRef.current,
          currentAppCrossRefRecordRef.current,
          currentClassificationRecordRef.current,
          currentOrganisationRecordRef.current,
          currentSuccessorCrossRefRecordRef.current,
          currentPropertyNoteRecordRef.current,
          currentPropertyRef.current
        );

        if (propertyChanged) {
          associatedRecords.current = getChangedAssociatedPropertyRecords(
            sourcePropertyRef.current,
            currentLpiRecordRef.current,
            currentProvenanceRecordRef.current,
            currentAppCrossRefRecordRef.current,
            currentClassificationRecordRef.current,
            currentOrganisationRecordRef.current,
            currentSuccessorCrossRefRecordRef.current,
            currentPropertyNoteRecordRef.current
          );

          const parentPaoChanged = hasParentPaoChanged(
            propertyContext.childCount,
            sourcePropertyRef.current,
            currentPropertyRef.current,
            associatedRecords.current.length > 0 && associatedRecords.current.includes("LPI")
              ? currentLpiRecordRef.current
              : null
          );

          const parentMoved = hasParentMoved(
            propertyContext.childCount,
            sourcePropertyRef.current,
            currentPropertyRef.current
          );

          const propertyDataRef = currentPropertyRef.current ? currentPropertyRef.current : sourcePropertyRef.current;

          if (associatedRecords.current.length > 0) {
            saveConfirmDialog(associatedRecords.current, parentPaoChanged, parentMoved)
              .then((result) => {
                if (result === "save" || result === "saveCascade") {
                  if (propertyContext.validateData()) {
                    failedValidation.current = false;
                    const currentPropertyData = GetCurrentPropertyData(
                      propertyDataRef,
                      currentLpiRecordRef.current,
                      currentProvenanceRecordRef.current,
                      currentAppCrossRefRecordRef.current,
                      currentClassificationRecordRef.current,
                      currentOrganisationRecordRef.current,
                      currentSuccessorCrossRefRecordRef.current,
                      currentPropertyNoteRecordRef.current,
                      lookupContext.current,
                      isWelsh.current,
                      isScottish.current
                    );
                    handleSaveProperty(currentPropertyData, result === "saveCascade");
                    mapContext.onSetCoordinate(null);
                    propertyContext.onPropertyModified(false);
                    resetSandbox("property");
                    functionAfterCheck();
                  } else {
                    failedValidation.current = true;
                    saveResult.current = false;
                    setSaveOpen(true);
                  }
                } else {
                  mapContext.onSetCoordinate(null);
                  propertyContext.onPropertyModified(false);
                  resetSandbox("property");
                  functionAfterCheck();
                }
              })
              .catch(() => {});
          } else {
            saveConfirmDialog(true, parentPaoChanged, parentMoved)
              .then((result) => {
                if (result === "save" || result === "saveCascade") {
                  handleSaveProperty(
                    currentPropertyRef.current,
                    result === "saveCascade" || result === "saveCascadeMove",
                    result === "saveCascadeMove" || result === "saveMove"
                  );
                }
                mapContext.onSetCoordinate(null);
                propertyContext.onPropertyModified(false);
                resetSandbox("property");
                functionAfterCheck();
              })
              .catch(() => {});
          }
        } else {
          mapContext.onSetCoordinate(null);
          propertyContext.onPropertyModified(false);
          resetSandbox("property");
          functionAfterCheck();
        }
      } else {
        mapContext.onSetCoordinate(null);
        streetContext.onStreetModified(false);
        propertyContext.onPropertyModified(false);
        resetSandbox("all");
        functionAfterCheck();
      }
    },
    [handleSaveProperty, handleSaveStreet, mapContext, propertyContext, resetSandbox, saveConfirmDialog, streetContext]
  );

  /**
   * Method to handle opening a street from the feature dialog.
   */
  const handleOpenStreet = useCallback(async () => {
    if (recordAttributes.current) {
      propertyContext.onPropertyChange(0, 0, null, null, null, null, null, false, null);

      streetContext.onStreetChange(recordAttributes.current.USRN, recordAttributes.current.Description, false);

      if (userContext.current.currentUser.hasStreet) {
        const foundStreet = mapContext.currentMapSearchData.streets.find(
          (x) => x.usrn.toString() === recordAttributes.current.USRN.toString()
        );

        if (!foundStreet) {
          const streetData = await GetStreetMapData(
            Number(recordAttributes.current.USRN),
            userContext.current,
            isScottish.current
          );

          if (streetData) {
            const engDescriptor = streetData.streetDescriptors.find((x) => x.language === "ENG");

            const townRec = lookupContext.current.currentLookups.towns.find((x) => x.townRef === engDescriptor.townRef);
            const localityRec = lookupContext.current.currentLookups.localities.find(
              (x) => x.localityRef === engDescriptor.locRef
            );

            const newSearchStreet = [
              {
                usrn: streetData.usrn,
                description: engDescriptor.streetDescriptor,
                language: "ENG",
                locality: localityRec ? localityRec.locality : engDescriptor.locality,
                town: townRec ? townRec.town : engDescriptor.town,
                state: !isScottish ? streetData.state : undefined,
                type: streetData.recordType,
                esus:
                  streetData && streetData.esus && streetData.esus.length > 0
                    ? streetData.esus
                        .filter((esu) => esu.changeType !== "D")
                        .map((esu) => ({
                          esuId: esu.esuId,
                          state: isScottish ? esu.state : undefined,
                          geometry:
                            esu.wktGeometry && esu.wktGeometry !== "" ? wktToGeometry(esu.wktGeometry) : undefined,
                        }))
                    : streetData
                      ? [
                          {
                            esuId: -1,
                            state: undefined,
                            geometry: wktToGeometry(
                              `LINESTRING (${streetData.streetStartX} ${streetData.streetStartY}, ${streetData.streetEndX} ${streetData.streetEndY})`
                            ),
                          },
                        ]
                      : [],
                asdType51:
                  isScottish.current && streetData.maintenanceResponsibilities
                    ? streetData.maintenanceResponsibilities
                        .filter((asdRec) => asdRec.changeType !== "D")
                        .map((asdRec) => ({
                          type: 51,
                          pkId: asdRec.pkId,
                          usrn: asdRec.usrn,
                          streetStatus: asdRec.streetStatus,
                          custodianCode: asdRec.custodianCode,
                          maintainingAuthorityCode: asdRec.maintainingAuthorityCode,
                          wholeRoad: asdRec.wholeRoad,
                          geometry:
                            asdRec.wktGeometry && asdRec.wktGeometry !== ""
                              ? wktToGeometry(asdRec.wktGeometry)
                              : undefined,
                        }))
                    : [],
                asdType52:
                  isScottish.current && streetData.reinstatementCategories
                    ? streetData.reinstatementCategories
                        .filter((asdRec) => asdRec.changeType !== "D")
                        .map((asdRec) => ({
                          type: 52,
                          pkId: asdRec.pkId,
                          usrn: asdRec.usrn,
                          reinstatementCategoryCode: asdRec.reinstatementCategoryCode,
                          custodianCode: asdRec.custodianCode,
                          reinstatementAuthorityCode: asdRec.reinstatementAuthorityCode,
                          wholeRoad: asdRec.wholeRoad,
                          geometry:
                            asdRec.wktGeometry && asdRec.wktGeometry !== ""
                              ? wktToGeometry(asdRec.wktGeometry)
                              : undefined,
                        }))
                    : [],
                asdType53:
                  isScottish.current && streetData.specialDesignations
                    ? streetData.specialDesignations
                        .filter((asdRec) => asdRec.changeType !== "D")
                        .map((asdRec) => ({
                          type: 53,
                          pkId: asdRec.pkId,
                          usrn: asdRec.usrn,
                          specialDesignationCode: asdRec.specialDesignationCode,
                          custodianCode: asdRec.custodianCode,
                          authorityCode: asdRec.authorityCode,
                          wholeRoad: asdRec.wholeRoad,
                          geometry:
                            asdRec.wktGeometry && asdRec.wktGeometry !== ""
                              ? wktToGeometry(asdRec.wktGeometry)
                              : undefined,
                        }))
                    : [],
                asdType61:
                  !isScottish.current && userContext.current.currentUser.hasASD && streetData.interests
                    ? streetData.interests
                        .filter((asdRec) => asdRec.changeType !== "D")
                        .map((asdRec) => ({
                          type: 61,
                          pkId: asdRec.pkId,
                          usrn: asdRec.usrn,
                          streetStatus: asdRec.streetStatus,
                          interestType: asdRec.interestType,
                          districtRefAuthority: asdRec.districtRefAuthority,
                          swaOrgRefAuthority: asdRec.swaOrgRefAuthority,
                          wholeRoad: asdRec.wholeRoad,
                          geometry:
                            asdRec.wktGeometry && asdRec.wktGeometry !== ""
                              ? wktToGeometry(asdRec.wktGeometry)
                              : undefined,
                        }))
                    : [],
                asdType62:
                  !isScottish.current && userContext.current.currentUser.hasASD && streetData.constructions
                    ? streetData.constructions
                        .filter((asdRec) => asdRec.changeType !== "D")
                        .map((asdRec) => ({
                          type: 62,
                          pkId: asdRec.pkId,
                          usrn: asdRec.usrn,
                          constructionType: asdRec.constructionType,
                          reinstatementTypeCode: asdRec.reinstatementTypeCode,
                          swaOrgRefConsultant: asdRec.swaOrgRefConsultant,
                          districtRefConsultant: asdRec.districtRefConsultant,
                          wholeRoad: asdRec.wholeRoad,
                          geometry:
                            asdRec.wktGeometry && asdRec.wktGeometry !== ""
                              ? wktToGeometry(asdRec.wktGeometry)
                              : undefined,
                        }))
                    : [],
                asdType63:
                  !isScottish.current && userContext.current.currentUser.hasASD && streetData.specialDesignations
                    ? streetData.specialDesignations
                        .filter((asdRec) => asdRec.changeType !== "D")
                        .map((asdRec) => ({
                          type: 63,
                          pkId: asdRec.pkId,
                          usrn: asdRec.usrn,
                          streetSpecialDesigCode: asdRec.streetSpecialDesigCode,
                          swaOrgRefConsultant: asdRec.swaOrgRefConsultant,
                          districtRefConsultant: asdRec.districtRefConsultant,
                          wholeRoad: asdRec.wholeRoad,
                          geometry:
                            asdRec.wktGeometry && asdRec.wktGeometry !== ""
                              ? wktToGeometry(asdRec.wktGeometry)
                              : undefined,
                        }))
                    : [],
                asdType64:
                  !isScottish.current && userContext.current.currentUser.hasASD && streetData.heightWidthWeights
                    ? streetData.heightWidthWeights
                        .filter((asdRec) => asdRec.changeType !== "D")
                        .map((asdRec) => ({
                          type: 64,
                          pkId: asdRec.pkId,
                          usrn: asdRec.usrn,
                          hwwRestrictionCode: asdRec.hwwRestrictionCode,
                          swaOrgRefConsultant: asdRec.swaOrgRefConsultant,
                          districtRefConsultant: asdRec.districtRefConsultant,
                          wholeRoad: asdRec.wholeRoad,
                          geometry:
                            asdRec.wktGeometry && asdRec.wktGeometry !== ""
                              ? wktToGeometry(asdRec.wktGeometry)
                              : undefined,
                        }))
                    : [],
                asdType66:
                  !isScottish.current && userContext.current.currentUser.hasASD && streetData.publicRightOfWays
                    ? streetData.publicRightOfWays
                        .filter((asdRec) => asdRec.changeType !== "D")
                        .map((asdRec) => ({
                          type: 66,
                          pkId: asdRec.pkId,
                          prowUsrn: asdRec.prowUsrn,
                          prowRights: asdRec.prowRights,
                          prowStatus: asdRec.prowStatus,
                          prowOrgRefConsultant: asdRec.prowOrgRefConsultant,
                          prowDistrictRefConsultant: asdRec.prowDistrictRefConsultant,
                          defMapGeometryType: asdRec.defMapGeometryType,
                          geometry:
                            asdRec.wktGeometry && asdRec.wktGeometry !== ""
                              ? wktToGeometry(asdRec.wktGeometry)
                              : undefined,
                        }))
                    : [],
              },
            ];

            const currentSearchStreets = mergeArrays(
              mapContext.currentMapSearchData.streets,
              newSearchStreet,
              (a, b) => a.usrn === b.usrn
            );

            mapContext.onSearchDataChange(
              currentSearchStreets,
              mapContext.currentMapSearchData.llpgStreets,
              mapContext.currentMapSearchData.properties,
              recordAttributes.current.USRN,
              null
            );
          }
        } else {
          mapContext.onSearchDataChange(
            mapContext.currentMapSearchData.streets,
            mapContext.currentMapSearchData.llpgStreets,
            mapContext.currentMapSearchData.properties,
            recordAttributes.current.USRN,
            null
          );
        }
      } else {
        const foundLlpgStreet = mapContext.currentMapSearchData.llpgStreets.find(
          (x) => x.usrn.toString() === recordAttributes.current.USRN.toString()
        );

        if (!foundLlpgStreet) {
          const llpgStreetData = await GetStreetMapData(
            Number(recordAttributes.current.USRN),
            userContext.current,
            isScottish.current
          );

          if (llpgStreetData) {
            const engDescriptor = llpgStreetData.streetDescriptors.find((x) => x.language === "ENG");

            const townRec = lookupContext.current.currentLookups.towns.find((x) => x.townRef === engDescriptor.townRef);
            const localityRec = lookupContext.current.currentLookups.localities.find(
              (x) => x.localityRef === engDescriptor.locRef
            );

            const newSearchLlpgStreet = [
              {
                usrn: llpgStreetData.usrn,
                description: engDescriptor.streetDescriptor,
                language: "ENG",
                locality: localityRec ? localityRec.locality : engDescriptor.locality,
                town: townRec ? townRec.town : engDescriptor.town,
                state: !isScottish ? llpgStreetData.state : undefined,
                type: llpgStreetData.recordType,
                esus: llpgStreetData
                  ? [
                      {
                        esuId: -1,
                        state: undefined,
                        geometry: wktToGeometry(
                          `LINESTRING (${llpgStreetData.streetStartX} ${llpgStreetData.streetStartY}, ${llpgStreetData.streetEndX} ${llpgStreetData.streetEndY})`
                        ),
                      },
                    ]
                  : [],
                asdType51: [],
                asdType52: [],
                asdType53: [],
                asdType61: [],
                asdType62: [],
                asdType63: [],
                asdType64: [],
                asdType66: [],
              },
            ];

            const currentSearchLlpgStreets = mergeArrays(
              mapContext.currentMapSearchData.llpgStreets,
              newSearchLlpgStreet,
              (a, b) => a.usrn === b.usrn
            );

            mapContext.onSearchDataChange(
              mapContext.currentMapSearchData.streets,
              currentSearchLlpgStreets,
              mapContext.currentMapSearchData.properties,
              recordAttributes.current.USRN,
              null
            );
          }
        } else {
          mapContext.onSearchDataChange(
            mapContext.currentMapSearchData.streets,
            mapContext.currentMapSearchData.llpgStreets,
            mapContext.currentMapSearchData.properties,
            recordAttributes.current.USRN,
            null
          );
        }
      }

      mapContext.onMapChange([], null, null);

      mapContext.onSetCoordinate(null);
      streetContext.onStreetModified(false);
      resetSandbox("street");
    }
  }, [mapContext, resetSandbox, propertyContext, streetContext]);

  /**
   * Method to handle displaying a maintenance responsibility record from the feature dialog (OneScotland only).
   */
  const handleOpenASD51 = useCallback(() => {
    if (recordAttributes.current) {
      streetContext.onGoToField(51, recordAttributes.current.ObjectId - 1, null, "StreetStatus");
    }
  }, [streetContext]);

  /**
   * Method to handle displaying a reinstatement category record from the feature dialog (OneScotland only).
   */
  const handleOpenASD52 = useCallback(() => {
    if (recordAttributes.current) {
      streetContext.onGoToField(52, recordAttributes.current.ObjectId - 1, null, "ReinstatementCategory");
    }
  }, [streetContext]);

  /**
   * Method to handle displaying a special designation record from the feature dialog (OneScotland only).
   */
  const handleOpenASD53 = useCallback(() => {
    if (recordAttributes.current) {
      streetContext.onGoToField(53, recordAttributes.current.ObjectId - 1, null, "StreetSpecialDesigCode");
    }
  }, [streetContext]);

  /**
   * Method to handle displaying an interest record from the feature dialog (GeoPlace only).
   */
  const handleOpenASD61 = useCallback(() => {
    if (recordAttributes.current) {
      streetContext.onGoToField(61, recordAttributes.current.ObjectId - 1, null, "StreetStatus");
    }
  }, [streetContext]);

  /**
   * Method to handle displaying a construction record from the feature dialog (GeoPlace only).
   */
  const handleOpenASD62 = useCallback(() => {
    if (recordAttributes.current) {
      streetContext.onGoToField(62, recordAttributes.current.ObjectId - 1, null, "ConstructionType");
    }
  }, [streetContext]);

  /**
   * Method to handle displaying a special designation record from the feature dialog (GeoPlace only).
   */
  const handleOpenASD63 = useCallback(() => {
    if (recordAttributes.current) {
      streetContext.onGoToField(63, recordAttributes.current.ObjectId - 1, null, "StreetSpecialDesigCode");
    }
  }, [streetContext]);

  /**
   * Method to handle displaying a height, width & weight restriction record from the feature dialog (GeoPlace only).
   */
  const handleOpenASD64 = useCallback(() => {
    if (recordAttributes.current) {
      streetContext.onGoToField(64, recordAttributes.current.ObjectId - 1, null, "HwwRestrictionCode");
    }
  }, [streetContext]);

  /**
   * Method to handle displaying a public right of way record from the feature dialog (GeoPlace only).
   */
  const handleOpenASD66 = useCallback(() => {
    if (recordAttributes.current) {
      streetContext.onGoToField(66, recordAttributes.current.ObjectId - 1, null, "ProwRights");
    }
  }, [streetContext]);

  /**
   * Method to handle adding a single property to a street from the feature dialog.
   */
  const handleAddProperty = useCallback(async () => {
    if (recordAttributes.current && !displayingWizard.current) {
      if ([1, 2, 9].includes(recordAttributes.current.Type) && recordAttributes.current.State !== 4) {
        displayingWizard.current = true;
        saveOpenRef.current = false;
        setSaveOpen(false);

        const rec = {
          address: recordAttributes.current.Description,
          usrn: Number(recordAttributes.current.USRN),
        };
        propertyContext.resetPropertyErrors(false, false);
        propertyContext.onClearWizardData();
        mapContext.onWizardSetCoordinate(null);
        propertyWizardType.current = "property";
        propertyWizardParent.current = rec;
        setOpenPropertyWizard(true);
      } else {
        saveResult.current = false;
        saveType.current = recordAttributes.current.State === 4 ? "addPropertyInvalidState" : "addPropertyInvalidType";
        saveOpenRef.current = true;
        setSaveOpen(true);
      }
    }
  }, [mapContext, propertyContext]);

  /**
   * Method to handle adding a range of properties to a street from the feature dialog.
   */
  const handleAddRange = useCallback(async () => {
    if (recordAttributes.current && !displayingWizard.current) {
      if ([1, 2, 9].includes(recordAttributes.current.Type) && recordAttributes.current.State !== 4) {
        displayingWizard.current = true;
        saveOpenRef.current = false;
        setSaveOpen(false);

        const rec = {
          address: recordAttributes.current.Description,
          usrn: Number(recordAttributes.current.USRN),
        };
        propertyContext.resetPropertyErrors(true, true);
        propertyContext.onClearWizardData();
        mapContext.onWizardSetCoordinate(null);
        propertyWizardType.current = "range";
        propertyWizardParent.current = rec;
        setOpenPropertyWizard(true);
      } else {
        saveResult.current = false;
        saveType.current = recordAttributes.current.State === 4 ? "addRangeInvalidState" : "addRangeInvalidType";
        saveOpenRef.current = true;
        setSaveOpen(true);
      }
    }
  }, [mapContext, propertyContext]);

  /**
   * Method to handle dividing an ESU from the feature dialog.
   */
  const handleDivideEsu = useCallback(() => {
    if (streetContext.esuDividedMerged) {
      saveResult.current = false;
      saveType.current = "streetAlreadyDividedMerged";
      saveOpenRef.current = true;
      setSaveOpen(true);
    } else if (
      recordAttributes.current &&
      streetContext.currentStreet &&
      Number(recordAttributes.current.USRN) === streetContext.currentStreet.usrn
    ) {
      mapContext.onDivideEsu(Number(recordAttributes.current.EsuId));
    } else {
      saveResult.current = false;
      saveType.current = "invalidDivideEsu";
      saveOpenRef.current = true;
      setSaveOpen(true);
    }
  }, [mapContext, streetContext.currentStreet, streetContext.esuDividedMerged]);

  /**
   * Method to handle assigning an ESU from the feature dialog.
   */
  const handleAssignEsu = useCallback(() => {
    if (
      recordAttributes.current &&
      streetContext.currentStreet &&
      (streetContext.currentStreet.usrn > 0 || streetContext.currentStreet.newStreet)
    ) {
      if (Number(recordAttributes.current.USRN) === streetContext.currentStreet.usrn) {
        saveResult.current = false;
        saveType.current = "assignEsuSameStreet";
        saveOpenRef.current = true;
        setSaveOpen(true);
      } else streetContext.onAssignEsu(Number(recordAttributes.current.EsuId));
    } else {
      saveResult.current = false;
      saveType.current = "invalidAssignEsu";
      saveOpenRef.current = true;
      setSaveOpen(true);
    }
  }, [streetContext]);

  /**
   * Display the street in Google Street View.
   */
  const handleStreetStreetView = useCallback(() => {
    if (recordAttributes.current && recordAttributes.current.USRN) {
      DisplayStreetInStreetView(
        Number(recordAttributes.current.USRN),
        userContext.current,
        isScottish.current,
        spatialReferenceWkid.current
      );
    }
  }, []);

  /**
   * Method to handle opening a property from the feature dialog.
   */
  const handleOpenProperty = useCallback(async () => {
    if (recordAttributes.current) {
      if ("Code" in recordAttributes.current) {
        // This originated from a polygon so we need to get the property details first.
        const propertyData = await GetPropertyMapData(recordAttributes.current.UPRN, userContext.current);
        if (propertyData) {
          const engLpi = propertyData.lpis.find((x) => x.language === "ENG");
          if (engLpi) {
            const clickedProperty = {
              type: 24,
              uprn: recordAttributes.current.UPRN,
              address: engLpi.address,
              formattedAddress: engLpi.address,
              postcode: engLpi.postcode,
              easting: propertyData.easting,
              northing: propertyData.northing,
              logical_status: propertyData.logicalStatus,
              classification_code: propertyData.blpuClass,
            };
            // TODO: uncommented below code when want to reintroduce asking to edit historic.
            // if (propertyData.logicalStatus === 8) {
            //   historicRec.current = { property: clickedProperty, related: null };
            //   setOpenHistoricProperty(true);
            // } else {
            doOpenRecord(
              clickedProperty,
              null,
              searchContext.currentSearchData.results,
              mapContext,
              streetContext,
              propertyContext,
              userContext.current,
              isScottish.current
            );
            // TODO: uncommented below code when want to reintroduce asking to edit historic.
            // }
          }
        }
      } else {
        const clickedProperty = {
          type: 24,
          uprn: recordAttributes.current.UPRN,
          address: recordAttributes.current.Address,
          formattedAddress: recordAttributes.current.FormattedAddress,
          postcode: recordAttributes.current.Postcode,
          easting: recordAttributes.current.Easting,
          northing: recordAttributes.current.Northing,
          logical_status: recordAttributes.current.LogicalStatus,
          classification_code: recordAttributes.current.Classification,
        };

        // TODO: uncommented below code when want to reintroduce asking to edit historic.
        // if (recordAttributes.current.LogicalStatus === 8) {
        //   historicRec.current = { property: clickedProperty, related: null };
        //   setOpenHistoricProperty(true);
        // } else {
        doOpenRecord(
          clickedProperty,
          null,
          searchContext.currentSearchData.results,
          mapContext,
          streetContext,
          propertyContext,
          userContext.current,
          isScottish.current
        );
        // TODO: uncommented below code when want to reintroduce asking to edit historic.
        // }
      }
    }
  }, [searchContext.currentSearchData.results, mapContext, streetContext, propertyContext]);

  /**
   * Method to handle adding a single child to a property from the feature dialog.
   */
  const handleAddChild = useCallback(async () => {
    if (recordAttributes.current && !displayingWizard.current) {
      if (userContext.current.currentUser.editProperty) {
        GetParentHierarchy(Number(recordAttributes.current.UPRN), userContext.current).then((parentProperties) => {
          if (!parentProperties || parentProperties.parent.currentParentChildLevel < 4) {
            const engLpiData = parentProperties.parent.lpis
              .filter((x) => x.language === "ENG")
              .sort((a, b) => a.logicalStatus - b.logicalStatus);

            displayingWizard.current = true;
            const rec = {
              address: recordAttributes.current.Address,
              easting: Number(recordAttributes.current.Easting),
              northing: Number(recordAttributes.current.Northing),
              postcode: recordAttributes.current.Postcode,
              uprn: Number(recordAttributes.current.UPRN),
              usrn: engLpiData ? engLpiData[0].usrn : 0,
            };
            propertyContext.resetPropertyErrors(false, false);
            propertyContext.onClearWizardData();
            mapContext.onWizardSetCoordinate(null);
            propertyWizardType.current = "child";
            propertyWizardParent.current = rec;
            setOpenPropertyWizard(true);
          } else {
            saveResult.current = false;
            saveType.current = "maxParentLevel";
            saveOpenRef.current = true;
            setSaveOpen(true);
          }
        });
      } else {
        saveResult.current = false;
        saveType.current = "noPermission";
        saveOpenRef.current = true;
        setSaveOpen(true);
      }
    }
  }, [mapContext, propertyContext]);

  /**
   * Method to handle adding a range of children to a property from the feature dialog.
   */
  const handleAddChildren = useCallback(async () => {
    if (recordAttributes.current && !displayingWizard.current) {
      if (userContext.current.currentUser.editProperty) {
        GetParentHierarchy(Number(recordAttributes.current.UPRN), userContext.current).then((parentProperties) => {
          if (!parentProperties || parentProperties.parent.currentParentChildLevel < 4) {
            const engLpiData = parentProperties.parent.lpis
              .filter((x) => x.language === "ENG")
              .sort((a, b) => a.logicalStatus - b.logicalStatus);

            displayingWizard.current = true;
            const rec = {
              address: recordAttributes.current.Address,
              easting: Number(recordAttributes.current.Easting),
              northing: Number(recordAttributes.current.Northing),
              postcode: recordAttributes.current.Postcode,
              uprn: Number(recordAttributes.current.UPRN),
              usrn: engLpiData ? engLpiData[0].usrn : 0,
            };
            propertyContext.resetPropertyErrors(true, true);
            propertyContext.onClearWizardData();
            mapContext.onWizardSetCoordinate(null);
            propertyWizardType.current = "rangeChildren";
            propertyWizardParent.current = rec;
            setOpenPropertyWizard(true);
          } else {
            saveResult.current = false;
            saveType.current = "maxParentLevel";
            saveOpenRef.current = true;
            setSaveOpen(true);
          }
        });
      } else {
        saveResult.current = false;
        saveType.current = "noPermission";
        saveOpenRef.current = true;
        setSaveOpen(true);
      }
    }
  }, [mapContext, propertyContext]);

  /**
   * Display the property in Google Street View.
   */
  const handlePropertyStreetView = useCallback(() => {
    if (recordAttributes.current) {
      if (recordAttributes.current.Easting && recordAttributes.current.Northing) {
        openInStreetView(
          [Number(recordAttributes.current.Easting), Number(recordAttributes.current.Northing)],
          spatialReferenceWkid.current
        );
      } else if (recordAttributes.current.UPRN) {
        GetPropertyMapData(recordAttributes.current.UPRN, userContext.current).then((propertyData) => {
          if (propertyData) {
            openInStreetView([propertyData.xcoordinate, propertyData.ycoordinate], spatialReferenceWkid.current);
          }
        });
      }
    }
  }, []);

  /**
   * Event to handle when the property wizard dialog is finished.
   *
   * @param {object} wizardData The wizard data object.
   */
  const handlePropertyWizardDone = async (wizardData) => {
    if (wizardData && wizardData.savedProperty) {
      const isRange = Array.isArray(wizardData.savedProperty);

      if (isRange) {
        UpdateRangeAfterSave(
          wizardData.savedProperty,
          userContext.current,
          mapContext,
          propertyContext,
          isWelsh.current,
          searchContext
        );
        resetSandbox("property");
        setRefreshRelated(true);
      } else {
        setStore(
          UpdateAfterSave(
            wizardData.savedProperty,
            userContext.current,
            mapContext,
            propertyContext,
            isWelsh.current,
            searchContext,
            sandboxStore,
            wizardData.type
          )
        );
      }

      if (!isRange) navigate(-1);

      const parentData =
        isRange && wizardData.savedProperty
          ? await GetPropertyMapData(wizardData.savedProperty[0].parentUprn, userContext.current)
          : null;
      const engLpis = parentData && parentData.lpis ? parentData.lpis.filter((x) => x.language === "ENG") : null;
      const parent =
        parentData && engLpis
          ? {
              uprn: parentData.uprn,
              usrn: engLpis[0].usrn,
              easting: parentData.xcoordinate,
              northing: parentData.ycoordinate,
              address: engLpis[0].address,
              postcode: engLpis[0].postcode,
            }
          : null;

      propertyContext.onWizardDone(wizardData, isRange, parent, "map");
    } else propertyContext.onWizardDone(wizardData, false, null, "map");

    resetSandbox("all");
    streetContext.resetStreet();
    streetContext.resetStreetErrors();
    propertyContext.resetProperty();
    propertyContext.resetPropertyErrors(propertyContext.creatingPropertyRange, false);
    mapContext.onEditMapObject(null, null);
  };

  /**
   * Event to handle when the property wizard dialog is closed
   */
  const handlePropertyWizardClose = () => {
    setOpenPropertyWizard(false);
    displayingWizard.current = false;
  };

  /**
   * Event to handle when the historic property dialog is closed.
   *
   * @param {string} action The action taken from the dialog
   */
  // TODO: uncommented below code when want to reintroduce asking to edit historic.
  // const handleHistoricPropertyClose = async (action) => {
  //   setOpenHistoricProperty(false);
  //   if (action === "continue") {
  //     if (historicRec.current) {
  //       doOpenRecord(
  //         historicRec.current.property,
  //         historicRec.current.related,
  //         searchContext.currentSearchData.results,
  //         mapContext,
  //         streetContext,
  //         propertyContext,
  //         userContext.current,
  //         isScottish.current
  //       );
  //     }
  //   }
  // };

  /**
   * Method to handle when the upload shp file button is clicked.
   */
  function handleUploadFile() {
    setOpenUploadFileDialog(true);
  }

  /**
   * Method to upload and process a shape file.
   */
  const doUploadShpFile = (shpFile) => {
    const reader = new FileReader();

    reader.onload = function () {
      if (reader.readyState !== 2 || reader.error) {
        if (userContext.current.currentUser.showMessages)
          console.error(`[ERROR] failed to upload ${shpFile.title}`, {
            readyState: reader.readyState,
            error: reader.error,
          });
        saveResult.current = false;
        featuresDownloaded.current = shpFile.title;
        saveType.current = "uploadedFileError";
        saveOpenRef.current = true;
        setSaveOpen(true);
        return;
      } else {
        shp(reader.result).then(function (geojson) {
          let shpRenderer = null;
          if (shpFile.type === "shp" || !shpFile.layerFile) {
            switch (shpFile.geometryType) {
              case "point":
                shpRenderer = new SimpleRenderer({
                  symbol: SimpleMarkerSymbol.fromJSON(DEFAULT_SIMPLE_POINT_JSON),
                });
                break;
              case "line":
                shpRenderer = new SimpleRenderer({
                  symbol: SimpleLineSymbol.fromJSON(DEFAULT_SIMPLE_LINE_JSON),
                });
                break;
              case "polygon":
                shpRenderer = new SimpleRenderer({
                  symbol: SimpleFillSymbol.fromJSON(DEFAULT_SIMPLE_POLYGON_JSON),
                });
                break;
              default:
                break;
            }

            mapContext.onLoadShpFile(JSON.stringify(geojson), shpFile, shpRenderer);
            saveResult.current = true;
            featuresDownloaded.current = shpFile.title;
            saveType.current = "uploadedFile";
            saveOpenRef.current = true;
            setSaveOpen(true);
          } else {
            const layerReader = new FileReader();

            layerReader.onload = function () {
              if (layerReader.readyState !== 2 || layerReader.error) {
                if (userContext.current.currentUser.showMessages)
                  console.error(`[ERROR] failed to upload ${shpFile.layerFile}`, {
                    readyState: layerReader.readyState,
                    error: layerReader.error,
                  });
                saveResult.current = false;
                featuresDownloaded.current = shpFile.layerFile;
                saveType.current = "uploadedFileError";
                saveOpenRef.current = true;
                setSaveOpen(true);
                return;
              } else {
                let popupTitle = "";
                if (layerReader.result) {
                  if (shpFile.layerFile.name.endsWith(".lyrx")) {
                    const layerJson = JSON.parse(layerReader.result);
                    if (
                      layerJson &&
                      layerJson.layerDefinitions &&
                      layerJson.layerDefinitions.length > 0 &&
                      layerJson.layerDefinitions[0].renderer
                    ) {
                      switch (layerJson.layerDefinitions[0].renderer.type) {
                        case "simple":
                        case "CIMSimpleRenderer":
                          shpRenderer = SimpleRenderer.fromJSON(fixSymbolJson(layerJson.layerDefinitions[0].renderer));
                          break;

                        case "uniqueValue":
                        case "CIMUniqueValueRenderer":
                          shpRenderer = UniqueValueRenderer.fromJSON(
                            getUniqueValuesFromRendererJson(fixSymbolJson(layerJson.layerDefinitions[0].renderer))
                          );
                          break;

                        default:
                          saveResult.current = false;
                          featuresDownloaded.current = `${shpFile.layerFile.name} - [${layerJson.layerDefinitions[0].renderer.type}]`;
                          saveType.current = "uploadedRendererError";
                          saveOpenRef.current = true;
                          setSaveOpen(true);
                          break;
                      }
                      popupTitle = layerJson.layerDefinitions[0].featureTable.displayField || "";
                    }
                  } else if (shpFile.layerFile.name.endsWith(".qml")) {
                    const options = {
                      ignoreAttributes: false,
                      attributeNamePrefix: "",
                    };
                    const xmlParser = new XMLParser(options);
                    const layerJson = xmlParser.parse(layerReader.result);
                    if (layerJson && layerJson.qgis && layerJson.qgis["renderer-v2"]) {
                      const qgisRenderer = layerJson.qgis["renderer-v2"];
                      if (devBuild.current) console.log("[DEV] qgisRenderer", JSON.stringify(qgisRenderer));
                    }
                  }

                  if (shpRenderer) {
                    mapContext.onLoadShpFile(JSON.stringify(geojson), shpFile, shpRenderer, popupTitle);
                    saveResult.current = true;
                    featuresDownloaded.current = shpFile.title;
                    saveType.current = "uploadedFile";
                    saveOpenRef.current = true;
                    setSaveOpen(true);
                  } else {
                    saveResult.current = false;
                    featuresDownloaded.current = shpFile.layerFile.name;
                    saveType.current = "uploadedRendererError";
                    saveOpenRef.current = true;
                    setSaveOpen(true);
                  }
                }
              }
            };

            layerReader.readAsText(shpFile.layerFile);
          }
        });
      }
    };

    reader.readAsArrayBuffer(shpFile.file);
  };

  /**
   * Method to upload and process a georeferenced file.
   */
  const doUploadGeoFile = (geoFile) => {
    const reader = new FileReader();

    reader.onload = function () {
      if (reader.readyState !== 2 || reader.error) {
        if (userContext.current.currentUser.showMessages)
          console.error(`[ERROR] failed to upload ${geoFile.title}`, {
            readyState: reader.readyState,
            error: reader.error,
          });
        saveResult.current = false;
        featuresDownloaded.current = geoFile.title;
        saveType.current = "uploadedFileError";
        saveOpenRef.current = true;
        setSaveOpen(true);
        return;
      } else {
        if (geoFile.type === "geo" || !geoFile.worldFile) {
          mapContext.onLoadGeoFile(reader.result, geoFile);
          saveResult.current = true;
          featuresDownloaded.current = geoFile.title;
          saveType.current = "uploadedFile";
          saveOpenRef.current = true;
          setSaveOpen(true);
        } else {
          const worldReader = new FileReader();

          worldReader.onload = function () {
            if (worldReader.readyState !== 2 || worldReader.error) {
              if (userContext.current.currentUser.showMessages)
                console.error(`[ERROR] failed to upload ${geoFile.worldFile}`, {
                  readyState: worldReader.readyState,
                  error: worldReader.error,
                });
              saveResult.current = false;
              featuresDownloaded.current = geoFile.worldFile;
              saveType.current = "uploadedFileError";
              saveOpenRef.current = true;
              setSaveOpen(true);
              return;
            } else {
              if (worldReader.result) {
                const worldData = worldReader.result.split(/\r?\n/);
                if (worldData.length > 5) {
                  mapContext.onLoadGeoFile(reader.result, geoFile, worldData.slice(0, 6).map(Number));
                  saveResult.current = true;
                  featuresDownloaded.current = geoFile.title;
                  saveType.current = "uploadedFile";
                  saveOpenRef.current = true;
                  setSaveOpen(true);
                } else {
                  saveResult.current = false;
                  featuresDownloaded.current = geoFile.worldFile.name;
                  saveType.current = "uploadedWorldFileError";
                  saveOpenRef.current = true;
                  setSaveOpen(true);
                }
              }
            }
          };

          worldReader.readAsText(geoFile.worldFile);
        }
      }
    };

    reader.readAsDataURL(geoFile.file);
  };

  /**
   * Event to handle when the upload file dialog is closed.
   *
   * @param {object} file The file object returned from the dialog.
   */
  const handleUploadFileDialogClose = (file) => {
    setOpenUploadFileDialog(false);

    if (file) {
      switch (file.type) {
        case "shp":
        case "shplayer":
          doUploadShpFile(file);
          break;

        case "geo":
        case "geoworld":
          doUploadGeoFile(file);
          break;

        default:
          break;
      }
    }
  };

  /**
   * Method to handle deleting classification.
   */
  const handleDeleteClassification = () => {
    selectionContext.onDeleteClassification(true);
  };

  /**
   * Method to handle deleting organisation.
   */
  const handleDeleteOrganisation = () => {
    selectionContext.onDeleteOrganisation(true);
  };

  /**
   * Method to handle deleting successor cross reference.
   */
  const handleDeleteSuccessorCrossRef = () => {
    selectionContext.onDeleteSuccessorCrossRef(true);
  };

  /**
   * Method to handle deleting provenance.
   */
  const handleDeleteProvenance = () => {
    selectionContext.onDeleteProvenance(true);
  };

  /**
   * Method to handle merging features.
   */
  function handleMergeFeatures() {
    const mergeType = displayESUMergeTool ? "ESU" : displayExtentMergeTool ? "Extent" : "Unknown";
    const featureGeometries = [];

    if (sketchLayer.current) {
      switch (mergeType) {
        case "ESU":
          mapContext.onUpdatingEsuGeometry(true);
          for (let i = 0; i < selectedLines.current.length; i++) {
            featureGeometries.push(selectedLines.current[i].geometry);
          }

          const newEsu = unionOperator.executeMany(featureGeometries);
          mapContext.onSetLineGeometry(geometryToWKT(newEsu));

          selectedLines.current = [];
          setDisplayESUMergeTool(false);
          break;

        case "Extent":
          mapContext.onUpdatingProvenance(true);
          updatingPolygons.current = true;
          editingObject.current = null;
          let newExtent = null;
          let combinedGeometries = [];
          for (let i = 0; i < sketchLayer.current.graphics.items.length; i++) {
            if (i === 0) {
              newExtent = sketchLayer.current.graphics.items[i].geometry;
              combinedGeometries.push(i);
            } else if (
              !disjointOperator.execute(newExtent, sketchLayer.current.graphics.items[i].geometry) ||
              touchesOperator.execute(newExtent, sketchLayer.current.graphics.items[i].geometry)
            ) {
              newExtent = unionOperator.execute(newExtent, sketchLayer.current.graphics.items[i].geometry);
              combinedGeometries.push(i);
            }
          }

          const multiGeometries = [];
          multiGeometries.push(newExtent);
          newExtent = null;

          while (combinedGeometries.length < sketchLayer.current.graphics.items.length) {
            for (let i = 0; i < sketchLayer.current.graphics.items.length; i++) {
              if (!combinedGeometries.includes(i)) {
                if (newExtent === null) {
                  newExtent = sketchLayer.current.graphics.items[i].geometry;
                  combinedGeometries.push(i);
                } else if (
                  !disjointOperator.execute(newExtent, sketchLayer.current.graphics.items[i].geometry) ||
                  touchesOperator.execute(newExtent, sketchLayer.current.graphics.items[i].geometry)
                ) {
                  newExtent = unionOperator.execute(newExtent, sketchLayer.current.graphics.items[i].geometry);
                  combinedGeometries.push(i);
                }
              }
            }
            multiGeometries.push(newExtent);
            newExtent = null;
          }

          if (multiGeometries.length > 0) {
            mapContext.onSetPolygonGeometry(geometryToWKT(multiGeometries));
          }

          setDisplayExtentMergeTool(false);
          geoJSONFeatures.current = [];
          selectionContext.onSelectionCountChange(
            selectedEsus.current && selectedEsus.current.length > 0
              ? selectedEsus.current.length
              : selectedProperties.current && selectedProperties.current.length > 0
                ? selectedProperties.current.length
                : 0
          );
          handleCloseSelection();
          break;

        default:
          alert(`Unknown merge type: ${mergeType}`);
          break;
      }
    }
  }

  /**
   * Method to handle clearing distance measurement.
   */
  const handleClearDistanceMeasurement = () => {
    arcgisDistanceMeasurement.current.clear();
    if (arcgisDistanceMeasurementExpand.current.expanded === true) {
      arcgisDistanceMeasurementExpand.current.collapse();
      setIsDistanceMeasurementActive(false);
    }
  };

  /**
   * Method to handle clearing area measurement.
   */
  const handleClearAreaMeasurement = () => {
    arcgisAreaMeasurement.current.clear();
    if (arcgisAreaMeasurementExpand.current.expanded === true) {
      arcgisAreaMeasurementExpand.current.collapse();
      setIsAreaMeasurementActive(false);
    }
  };

  /**
   * Method to handle deleting cross reference.
   */
  const handleDeleteCrossReference = () => {
    selectionContext.onDeleteCrossReference(true);
  };

  /**
   * Event to handle display the alert after moving seed points.
   */
  const handlePropertyMoved = () => {
    saveResult.current = true;
    saveType.current = "propertyMoved";
    saveOpenRef.current = true;
    setSaveOpen(true);
  };

  /**
   * Event to handle the closing of the selection box.
   */
  const handleCloseSelection = () => {
    selectedEsus.current = [];
    setSelectedExtents([]);
    selectionContext.onCloseSelection();
    mapContext.onHighlightClear();
    mapContext.onPointCapture(null);
    mapContext.onSelectPropertiesChange(false);
    informationContext.onClearInformation();
  };

  /**
   * Event to handle when the information cancel button is clicked.
   */
  const handleInformationCancel = () => {
    informationContext.onClearInformation();
  };

  /**
   * Method to format a street to title case.
   *
   * @param {string} str The street to format to title case
   * @returns {string} The formatted street.
   */
  function streetToTitleCase(str) {
    if (!str || str.length === 0) return null;

    if (str.includes(",") && !str.includes(", "))
      return str.replace(",", ", ").replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
      });
    else
      return str.replace(/\w\S*/g, function (txt) {
        return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
      });
  }

  /**
   * Method to format an address to title case.
   *
   * @param {string} str The address to format to title case.
   * @param {string} postcode The postcode of the address.
   * @returns {string} The formatted address.
   */
  function addressToTitleCase(str, postcode) {
    function isNumeric(char) {
      return /\d/.test(char);
    }

    return !str || str.length === 0
      ? str
      : str.replace(postcode, "").replace(/\w\S*/g, function (txt) {
          if (isNumeric(txt.charAt(0))) return txt.toUpperCase();
          else return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
        }) + postcode;
  }

  /**
   * Method to apply edits to a layer.
   *
   * @param {object} editLayer The layer to apply the edits to.
   * @param {object} edits The edits to apply to the layer.
   * @param {boolean} [updateListMode=true] Whether to update the list mode of the layer.
   */
  function applyEditsToLayer(editLayer, edits, updateListMode = true) {
    editLayer
      .applyEdits(edits)
      .then((results) => {
        editLayer.queryFeatures().then((result) => {
          if (updateListMode) editLayer.listMode = result.features?.length > 0 ? "show" : "hide";
        });
      })
      .catch((error) => {
        console.error(`Error applying edits to layer ${editLayer.id}`, error);
      });
  }

  const fadeVisibilityOn = useCallback(
    (layer) => {
      let animating = true;
      let opacity = 0;
      // fade layer's opacity from 0 to whichever value the user has configured
      const finalOpacity = layer.opacity;
      layer.opacity = opacity;

      if (arcgisMap) {
        arcgisMap.view
          .whenLayerView(layer)
          .then((layerView) => {
            function incrementOpacityByFrame() {
              if (opacity >= finalOpacity && animating) {
                animating = false;
                return;
              }

              layer.opacity = opacity;
              opacity += 0.02;

              requestAnimationFrame(incrementOpacityByFrame);
            }

            // Wait for tiles to finish loading before beginning the fade
            reactiveUtils
              .whenOnce(() => !layerView.updating)
              .then(() => {
                requestAnimationFrame(incrementOpacityByFrame);
              });
          })
          .catch((error) => {
            if (error && userContext.current.currentUser.showMessages) {
              console.error("Error fading layer visibility", error);
            }
          });
      }
    },
    [arcgisMap]
  );

  // Set the map when the component is mounted
  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [1]: Set the map");
    setArcgisMap(document.getElementById(`arcgis-map-${mapType}`));
    isStreetMap.current = mapType === "street";
    isPropertyMap.current = mapType === "property";
    isStreetPropertyMap.current = isStreetMap.current || isPropertyMap.current;

    return () => {
      setArcgisMap(null);
    };
  }, [mapType]);

  // Load the base map layers when the map is ready
  useEffect(() => {
    if (!arcgisMap) return;

    let newBaseLayer;
    const baseLayers = [];
    const referenceLayers = [];
    const customerLayers = [];
    const galleryLayers = [];
    const baseLayerIds = [];
    const snapEsu = [];
    const snapBlpu = [];
    const snapExtent = [];
    const featureLayer = [];

    const loadBaseMapLayers = async () => {
      baseMapLayers.current
        .sort((a, b) => a.layerPosition - b.layerPosition)
        .forEach(async (baseLayer) => {
          if (baseLayer.visible) {
            newBaseLayer = null;

            switch (baseLayer.layerType) {
              case 1: // WFS
                // Create an empty GraphicsLayer as a placeholder for when the user actually gets the features
                newBaseLayer = new GraphicsLayer({
                  id: baseLayer.layerId,
                  copyright:
                    baseLayer.copyright && baseLayer.copyright.includes("<<year>>")
                      ? baseLayer.copyright.replace("<<year>>", new Date().getFullYear().toString())
                      : baseLayer.copyright,
                  title: baseLayer.title,
                  spatialReference: { wkid: spatialReferenceWkid.current },
                  listMode: baseLayer.displayInList ? "show" : "hide",
                  maxScale: baseLayer.maxScale,
                  minScale: baseLayer.minScale,
                  opacity: baseLayer.opacity,
                  visible: baseLayer.visible,
                });
                featureLayer.push({ layer: baseLayer, startIndex: 0 });
                break;

              case 2: // WMS
                switch (baseLayer.serviceProvider) {
                  case "thinkWare":
                    newBaseLayer = new WMSLayer({
                      url: baseLayer.url,
                      id: baseLayer.layerId,
                      customParameters: {
                        token: baseLayer.token,
                      },
                      sublayers: [
                        {
                          name: baseLayer.activeLayerId,
                          title: baseLayer.activeLayerTitle,
                        },
                      ],
                      copyright:
                        baseLayer.copyright && baseLayer.copyright.includes("<<year>>")
                          ? baseLayer.copyright.replace("<<year>>", new Date().getFullYear().toString())
                          : baseLayer.copyright,
                      title: baseLayer.title,
                      spatialReference: { wkid: spatialReferenceWkid.current },
                      listMode: baseLayer.displayInList ? "show" : "hide",
                      maxScale: baseLayer.maxScale,
                      minScale: baseLayer.minScale,
                      opacity: baseLayer.opacity,
                      visible: baseLayer.visible,
                    });
                    break;

                  case "viaEuropa":
                    alert("Code to handle viaEuropa WMS layers has not been written yet.");
                    break;

                  case "customer":
                    newBaseLayer = new WMSLayer({
                      url: baseLayer.url,
                      id: baseLayer.layerId,
                      sublayers: [
                        {
                          name: baseLayer.activeLayerId,
                          title: baseLayer.activeLayerTitle,
                          visible: baseLayer.visible,
                        },
                      ],
                      copyright:
                        baseLayer.copyright && baseLayer.copyright.includes("<<year>>")
                          ? baseLayer.copyright.replace("<<year>>", new Date().getFullYear().toString())
                          : baseLayer.copyright,
                      title: baseLayer.title,
                      spatialReference: { wkid: spatialReferenceWkid.current },
                      listMode: baseLayer.displayInList ? "show" : "hide",
                      maxScale: baseLayer.maxScale,
                      minScale: baseLayer.minScale,
                      opacity: baseLayer.opacity,
                      visible: baseLayer.visible,
                    });
                    break;

                  case "Jersey":
                    alert("Code to handle Jersey WMS layers has not been written yet.");
                    break;

                  default: // OS
                    alert("Code to handle OS WMS layers has not been written yet.");
                    break;
                }
                break;

              case 3: // WMTS
                switch (baseLayer.serviceProvider) {
                  case "thinkWare":
                    alert("Code to handle thinkWare WMTS layers has not been written yet.");
                    break;

                  case "customer":
                    alert("Code to handle customer WMTS layers has not been written yet.");
                    break;

                  case "Jersey":
                    alert("Code to handle Jersey WMTS layers has not been written yet.");
                    break;

                  case "viaEuropa":
                    newBaseLayer = new WMTSLayer({
                      url: `${baseLayer.url}/${baseLayer.layerKey}/${baseLayer.activeLayerId}/wmts?client=arcgis`,
                      id: baseLayer.layerId,
                      copyright:
                        baseLayer.copyright && baseLayer.copyright.includes("<<year>>")
                          ? baseLayer.copyright.replace("<<year>>", new Date().getFullYear().toString())
                          : baseLayer.copyright,
                      title: baseLayer.title,
                      spatialReference: { wkid: spatialReferenceWkid.current },
                      listMode: baseLayer.displayInList ? "show" : "hide",
                      maxScale: baseLayer.maxScale,
                      minScale: baseLayer.minScale,
                      opacity: baseLayer.opacity,
                      visible: baseLayer.visible,
                    });
                    break;

                  default: // OS
                    newBaseLayer = new WMTSLayer({
                      url: baseLayer.url,
                      serviceMode: baseLayer.serviceMode,
                      activeLayer: {
                        id: baseLayer.activeLayerId,
                      },
                      customParameters: {
                        key: baseLayer.layerKey,
                      },
                      id: baseLayer.layerId,
                      copyright:
                        baseLayer.copyright && baseLayer.copyright.includes("<<year>>")
                          ? baseLayer.copyright.replace("<<year>>", new Date().getFullYear().toString())
                          : baseLayer.copyright,
                      title: baseLayer.title,
                      spatialReference: { wkid: spatialReferenceWkid.current },
                      listMode: baseLayer.displayInList ? "show" : "hide",
                      maxScale: baseLayer.maxScale,
                      minScale: baseLayer.minScale,
                      opacity: baseLayer.opacity,
                      visible: baseLayer.visible,
                    });
                    break;
                }
                break;

              case 4: // Vector Tile
                switch (baseLayer.serviceProvider) {
                  case "thinkWare":
                    alert("Code to handle thinkWare Vector Tile layers has not been written yet.");
                    break;

                  case "customer":
                    alert("Code to handle customer Vector Tile layers has not been written yet.");
                    break;

                  case "Jersey":
                    newBaseLayer = new VectorTileLayer({
                      url: baseLayer.url,
                      id: baseLayer.layerId,
                      copyright:
                        baseLayer.copyright && baseLayer.copyright.includes("<<year>>")
                          ? baseLayer.copyright.replace("<<year>>", new Date().getFullYear().toString())
                          : baseLayer.copyright,
                      title: baseLayer.title,
                      spatialReference: { wkid: spatialReferenceWkid.current },
                      listMode: baseLayer.displayInList ? "show" : "hide",
                      maxScale: baseLayer.maxScale,
                      minScale: baseLayer.minScale,
                      opacity: baseLayer.opacity,
                      visible: baseLayer.visible,
                    });
                    break;

                  case "viaEuropa":
                    alert("Code to handle viaEuropa Vector Tile layers has not been written yet.");
                    break;

                  default: // OS
                    alert("Code to handle OS Vector Tile layers has not been written yet.");
                    break;
                }
                break;

              default:
                break;
            }

            if (newBaseLayer) {
              baseLayerIds.push(baseLayer.layerId);
              if (baseLayer.esuSnap) snapEsu.push(baseLayer.layerId);
              if (baseLayer.blpuSnap) snapBlpu.push(baseLayer.layerId);
              if (baseLayer.extentSnap) snapExtent.push(baseLayer.layerId);
              if (baseLayer.visible) {
                switch (baseLayer.mapLayerType) {
                  case 1: // Base layer
                    baseLayers.push(newBaseLayer);
                    galleryLayers.push(
                      new Basemap({
                        baseLayers: [newBaseLayer],
                        title: baseLayer.title,
                        id: baseLayer.layerId,
                        thumbnailUrl: baseLayer.thumbnailUrl,
                        spatialReference: { wkid: spatialReferenceWkid.current },
                      })
                    );
                    break;

                  case 2: // Reference layer
                    referenceLayers.push(newBaseLayer);
                    break;

                  case 3: // Customer layer
                    customerLayers.push(newBaseLayer);
                    break;

                  default:
                    break;
                }
              }
            }
          }
        });

      baseMappingLayerIds.current = baseLayerIds;
      baseLayersSnapEsu.current = snapEsu;
      baseLayersSnapBlpu.current = snapBlpu;
      baseLayersSnapExtent.current = snapExtent;
      baseLayersFeature.current = featureLayer;

      const baseMap = new Basemap({
        referenceLayers: referenceLayers,
        baseLayers:
          mapContext.activeBaseMap && baseLayers.length > 0 && baseLayers.find((x) => x.id === mapContext.activeBaseMap)
            ? baseLayers.filter((x) => x.id === mapContext.activeBaseMap)
            : baseLayers.length > 0
              ? [baseLayers[0]]
              : [],
        title: "Basemaps",
        id: "basemap",
        spatialReference: { wkid: spatialReferenceWkid.current },
      });

      setBaseMapGallerySource(new LocalBasemapsSource({ basemaps: galleryLayers }));
      if (!mapContext.activeBaseMap && baseLayers.length > 0) {
        mapContext.onActiveBaseMapChange(baseLayers[0].id);
      }

      const mainMap = new Map({
        basemap: baseMap,
      });

      arcgisMap.map = mainMap;

      let layerIndex = 0;
      if (customerLayers.length > 0) {
        customerLayers.forEach((custLayer) => {
          arcgisMap.addLayer(custLayer, layerIndex);
          layerIndex++;
        });
      }

      shpGeoLayerIndex.current = layerIndex;

      backgroundStreetLayer.current = new FeatureLayer({
        id: backgroundStreetLayerName,
        copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
        source: [],
        fields: [
          {
            name: "ObjectID",
            alias: "ObjectID",
            type: "oid",
          },
          {
            name: "USRN",
            alias: "USRN",
            type: "string",
          },
          {
            name: "EsuId",
            alias: "EsuId",
            type: "string",
          },
          {
            name: "Description",
            alias: "Description",
            type: "string",
          },
          {
            name: "State",
            alias: "State",
            type: "integer",
          },
          {
            name: "Type",
            alias: "Type",
            type: "integer",
          },
          {
            name: "StateLabel",
            alias: "StateLabel",
            type: "string",
          },
          {
            name: "TypeLabel",
            alias: "TypeLabel",
            type: "string",
          },
          {
            name: "SymbolCode",
            alias: "SymbolCode",
            type: "string",
          },
          {
            name: "Geometry",
            alias: "Geometry",
            type: "string",
          },
          // {
          //   name: "LayerTitle",
          //   alias: "LayerTitle",
          //   type: "string",
          // },
        ],
        outFields: ["*"],
        objectIdField: "ObjectID",
        geometryType: "polyline",
        listMode: "hide",
        popupTemplate: {
          title: "{Description}",
          lastEditInfoEnabled: false,
          content: [
            {
              type: "fields",
              fieldInfos: [
                {
                  fieldName: "EsuId",
                  label: "ESU Id",
                },
                {
                  fieldName: "USRN",
                },
                {
                  fieldName: "StateLabel",
                  label: "Status",
                },
                {
                  fieldName: "TypeLabel",
                  label: "Type",
                },
                // {
                //   fieldName: "LayerTitle",
                //   label: "Source layer",
                // },
              ],
            },
          ],
          actions: [streetOpenAction, streetStreetViewAction],
        },
        renderer: streetRenderer,
        opacity: getLayerOpacity(backgroundStreetLayerTitle, isStreetPropertyMap.current),
        spatialReference: { wkid: spatialReferenceWkid.current },
        title: backgroundStreetLayerTitle,
        visible: getLayerVisibility(backgroundStreetLayerTitle),
      });

      if (userContext.current && userContext.current.currentUser.editProperty && backgroundStreetLayer.current) {
        backgroundStreetLayer.current.popupTemplate.actions = [
          streetOpenAction,
          streetAddProperty,
          streetAddRangeProperties,
          streetStreetViewAction,
        ];
      }

      arcgisMap.addLayer(backgroundStreetLayer.current, layerIndex);

      backgroundStreetLayer.current.visible = getLayerVisibility(backgroundStreetLayerTitle);

      reactiveUtils.watch(
        () => backgroundStreetLayer.current.visible,
        (visible) => {
          if (!selectingProperties.current) setLayerVisibility(backgroundStreetLayerTitle, visible);
          if (visible) {
            fadeVisibilityOn(backgroundStreetLayer.current);
          }
        }
      );

      unassignedEsusLayer.current = new FeatureLayer({
        id: unassignedEsusLayerName,
        copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
        source: [],
        fields: [
          {
            name: "ObjectID",
            alias: "ObjectID",
            type: "oid",
          },
          {
            name: "USRN",
            alias: "USRN",
            type: "string",
          },
          {
            name: "EsuId",
            alias: "EsuId",
            type: "string",
          },
          {
            name: "Description",
            alias: "Description",
            type: "string",
          },
          {
            name: "State",
            alias: "State",
            type: "integer",
          },
          {
            name: "Type",
            alias: "Type",
            type: "integer",
          },
          {
            name: "StateLabel",
            alias: "StateLabel",
            type: "string",
          },
          {
            name: "TypeLabel",
            alias: "TypeLabel",
            type: "string",
          },
          {
            name: "SymbolCode",
            alias: "SymbolCode",
            type: "string",
          },
          {
            name: "Geometry",
            alias: "Geometry",
            type: "string",
          },
          // {
          //   name: "LayerTitle",
          //   alias: "LayerTitle",
          //   type: "string",
          // },
        ],
        outFields: ["*"],
        objectIdField: "ObjectID",
        geometryType: "polyline",
        listMode: "hide",
        popupTemplate: {
          title: "Unassigned ESU",
          lastEditInfoEnabled: false,
          content: [
            {
              type: "fields",
              fieldInfos: [
                {
                  fieldName: "EsuId",
                  label: "ESU Id",
                },
                // {
                //   fieldName: "LayerTitle",
                //   label: "Source layer",
                // },
              ],
            },
          ],
          actions: [],
        },
        renderer: streetRenderer,
        opacity: getLayerOpacity(unassignedEsusLayerTitle, isStreetPropertyMap.current),
        spatialReference: { wkid: spatialReferenceWkid.current },
        title: unassignedEsusLayerTitle,
        visible: getLayerVisibility(unassignedEsusLayerTitle),
      });

      layerIndex++;
      arcgisMap.addLayer(unassignedEsusLayer.current, layerIndex);

      unassignedEsusLayer.current.visible = getLayerVisibility(unassignedEsusLayerTitle);

      reactiveUtils.watch(
        () => unassignedEsusLayer.current.visible,
        (visible) => {
          if (!selectingProperties.current) setLayerVisibility(unassignedEsusLayerTitle, visible);
          if (visible) {
            fadeVisibilityOn(unassignedEsusLayer.current);
          }
        }
      );

      if (userContext.current?.currentUser?.hasProperty) {
        backgroundPropertyLayer.current = new FeatureLayer({
          id: backgroundPropertyLayerName,
          copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
          source: [],
          featureReduction: PropertyFeatureReduction(8, 15, 15, 0),
          fields: [
            {
              name: "ObjectID",
              alias: "ObjectID",
              type: "oid",
            },
            {
              name: "UPRN",
              alias: "UPRN",
              type: "string",
            },
            {
              name: "Address",
              alias: "Address",
              type: "string",
            },
            {
              name: "Postcode",
              alias: "Postcode",
              type: "string",
            },
            {
              name: "Easting",
              alias: "Easting",
              type: "double",
            },
            {
              name: "Northing",
              alias: "Northing",
              type: "double",
            },
            {
              name: "DisplayLogicalStatus",
              alias: "DisplayLogicalStatus",
              type: "integer",
            },
            {
              name: "LogicalStatus",
              alias: "LogicalStatus",
              type: "integer",
            },
            {
              name: "LogicalStatusLabel",
              alias: "LogicalStatusLabel",
              type: "string",
            },
            {
              name: "Classification",
              alias: "Classification",
              type: "string",
            },
            {
              name: "ClassificationLabel",
              alias: "ClassificationLabel",
              type: "string",
            },
            // {
            //   name: "LayerTitle",
            //   alias: "LayerTitle",
            //   type: "string",
            // },
          ],
          outFields: ["*"],
          objectIdField: "ObjectID",
          geometryType: "point",
          listMode: "hide",
          popupTemplate: {
            title: "{Address}",
            lastEditInfoEnabled: false,
            content: [
              {
                type: "fields",
                fieldInfos: [
                  {
                    fieldName: "UPRN",
                  },
                  {
                    fieldName: "LogicalStatusLabel",
                    label: "Logical status",
                  },
                  {
                    fieldName: "ClassificationLabel",
                    label: "Classification",
                  },
                  // {
                  //   fieldName: "LayerTitle",
                  //   label: "Source layer",
                  // },
                ],
              },
            ],
            actions: [propertyOpenAction, propertyAddChild, propertyAddRangeChildren, propertyStreetViewAction],
          },
          renderer: backgroundPropertyRenderer,
          opacity: getLayerOpacity(backgroundPropertyLayerTitle, isStreetPropertyMap.current),
          spatialReference: { wkid: spatialReferenceWkid.current },
          title: backgroundPropertyLayerTitle,
          visible: getLayerVisibility(backgroundPropertyLayerTitle),
        });

        layerIndex++;
        arcgisMap.addLayer(backgroundPropertyLayer.current, layerIndex);

        backgroundPropertyLayer.current.visible = getLayerVisibility(backgroundPropertyLayerTitle);

        reactiveUtils.watch(
          () => backgroundPropertyLayer.current.visible,
          (visible) => {
            if (!selectingProperties.current) setLayerVisibility(backgroundPropertyLayerTitle, visible);
            if (visible) {
              fadeVisibilityOn(backgroundPropertyLayer.current);
            }
          }
        );

        selectPropertyLayer.current = new FeatureLayer({
          id: selectPropertyLayerName,
          copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
          source: [],
          fields: [
            {
              name: "ObjectID",
              alias: "ObjectID",
              type: "oid",
            },
            {
              name: "UPRN",
              alias: "UPRN",
              type: "string",
            },
            {
              name: "Address",
              alias: "Address",
              type: "string",
            },
            {
              name: "Postcode",
              alias: "Postcode",
              type: "string",
            },
            {
              name: "Easting",
              alias: "Easting",
              type: "double",
            },
            {
              name: "Northing",
              alias: "Northing",
              type: "double",
            },
            {
              name: "DisplayLogicalStatus",
              alias: "DisplayLogicalStatus",
              type: "integer",
            },
            {
              name: "LogicalStatus",
              alias: "LogicalStatus",
              type: "integer",
            },
            {
              name: "LogicalStatusLabel",
              alias: "LogicalStatusLabel",
              type: "string",
            },
            {
              name: "Classification",
              alias: "Classification",
              type: "string",
            },
            {
              name: "ClassificationLabel",
              alias: "ClassificationLabel",
              type: "string",
            },
            // {
            //   name: "LayerTitle",
            //   alias: "LayerTitle",
            //   type: "string",
            // },
          ],
          outFields: ["*"],
          objectIdField: "ObjectID",
          geometryType: "point",
          listMode: "hide",
          visible: selectingProperties.current,
          popupEnabled: false,
          renderer: backgroundPropertyRenderer,
          opacity: 0.5,
          spatialReference: { wkid: spatialReferenceWkid.current },
          title: selectPropertyLayerTitle,
        });

        layerIndex++;
        arcgisMap.addLayer(selectPropertyLayer.current, layerIndex);

        backgroundProvenanceLayer.current = new FeatureLayer({
          id: backgroundProvenanceLayerName,
          copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
          source: [],
          fields: [
            {
              name: "ObjectID",
              alias: "ObjectID",
              type: "oid",
            },
            {
              name: "UPRN",
              alias: "UPRN",
              type: "string",
            },
            {
              name: "Code",
              alias: "Code",
              type: "string",
            },
            {
              name: "SymbolCode",
              alias: "SymbolCode",
              type: "string",
            },
            {
              name: "CodeLabel",
              alias: "Provenance",
              type: "string",
            },
            // {
            //   name: "LayerTitle",
            //   alias: "LayerTitle",
            //   type: "string",
            // },
          ],
          outFields: ["*"],
          objectIdField: "ObjectID",
          geometryType: "polygon",
          listMode: "hide",
          popupTemplate: {
            title: "Provenance",
            lastEditInfoEnabled: false,
            content: [
              {
                type: "fields",
                fieldInfos: [
                  {
                    fieldName: "UPRN",
                  },
                  {
                    fieldName: "CodeLabel",
                    label: "Provenance",
                  },
                  // {
                  //     fieldName: "LayerTitle",
                  //   label: "Source layer",
                  // },
                ],
              },
            ],
            actions: [propertyOpenAction, propertyAddChild, propertyAddRangeChildren, propertyStreetViewAction],
          },
          renderer: extentRenderer,
          opacity: getLayerOpacity(backgroundProvenanceLayerTitle, isStreetPropertyMap.current),
          spatialReference: { wkid: spatialReferenceWkid.current },
          title: backgroundProvenanceLayerTitle,
          visible: getLayerVisibility(backgroundProvenanceLayerTitle),
        });

        layerIndex++;
        arcgisMap.addLayer(backgroundProvenanceLayer.current, layerIndex);

        backgroundProvenanceLayer.current.visible = getLayerVisibility(backgroundProvenanceLayerTitle);

        reactiveUtils.watch(
          () => backgroundProvenanceLayer.current.visible,
          (visible) => {
            if (!selectingProperties.current) setLayerVisibility(backgroundProvenanceLayerTitle, visible);
            if (visible) {
              fadeVisibilityOn(backgroundProvenanceLayer.current);
            }
          }
        );
      }

      if (userContext.current?.currentUser?.hasStreet) {
        streetLayer.current = new FeatureLayer({
          id: streetLayerName,
          copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
          source: [],
          fields: [
            {
              name: "ObjectID",
              alias: "ObjectID",
              type: "oid",
            },
            {
              name: "USRN",
              alias: "USRN",
              type: "string",
            },
            {
              name: "EsuId",
              alias: "EsuId",
              type: "string",
            },
            {
              name: "Description",
              alias: "Description",
              type: "string",
            },
            {
              name: "State",
              alias: "State",
              type: "integer",
            },
            {
              name: "Type",
              alias: "Type",
              type: "integer",
            },
            {
              name: "StateLabel",
              alias: "StateLabel",
              type: "string",
            },
            {
              name: "TypeLabel",
              alias: "TypeLabel",
              type: "string",
            },
            {
              name: "SymbolCode",
              alias: "SymbolCode",
              type: "string",
            },
            {
              name: "Geometry",
              alias: "Geometry",
              type: "string",
            },
            // {
            //   name: "LayerTitle",
            //   alias: "LayerTitle",
            //   type: "string",
            // },
          ],
          outFields: ["*"],
          objectIdField: "ObjectID",
          geometryType: "polyline",
          listMode: "hide",
          popupTemplate: {
            title: "{Description}",
            lastEditInfoEnabled: false,
            content: [
              {
                type: "fields",
                fieldInfos: [
                  {
                    fieldName: "EsuId",
                    label: "ESU Id",
                  },
                  {
                    fieldName: "USRN",
                  },
                  {
                    fieldName: "StateLabel",
                    label: "Status",
                  },
                  {
                    fieldName: "TypeLabel",
                    label: "Type",
                  },
                  // {
                  //   fieldName: "LayerTitle",
                  //   label: "Source layer",
                  // },
                ],
              },
            ],
            actions: [streetOpenAction, streetStreetViewAction],
          },
          renderer: streetRenderer,
          opacity: getLayerOpacity(streetLayerTitle, isStreetMap.current),
          spatialReference: { wkid: spatialReferenceWkid.current },
          title: streetLayerTitle,
        });

        if (userContext.current?.currentUser.editProperty && streetLayer.current) {
          streetLayer.current.popupTemplate.actions = [
            streetOpenAction,
            streetAddProperty,
            streetAddRangeProperties,
            streetStreetViewAction,
          ];
        }

        layerIndex++;
        arcgisMap.addLayer(streetLayer.current, layerIndex);
      } else {
        llpgStreetLayer.current = new FeatureLayer({
          id: llpgStreetLayerName,
          copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
          source: [],
          fields: [
            {
              name: "ObjectID",
              alias: "ObjectID",
              type: "oid",
            },
            {
              name: "USRN",
              alias: "USRN",
              type: "string",
            },
            {
              name: "EsuId",
              alias: "EsuId",
              type: "string",
            },
            {
              name: "Description",
              alias: "Description",
              type: "string",
            },
            {
              name: "State",
              alias: "State",
              type: "integer",
            },
            {
              name: "Type",
              alias: "Type",
              type: "integer",
            },
            {
              name: "StateLabel",
              alias: "StateLabel",
              type: "string",
            },
            {
              name: "TypeLabel",
              alias: "TypeLabel",
              type: "string",
            },
            {
              name: "SymbolCode",
              alias: "SymbolCode",
              type: "string",
            },
            {
              name: "Geometry",
              alias: "Geometry",
              type: "string",
            },
            // {
            //   name: "LayerTitle",
            //   alias: "LayerTitle",
            //   type: "string",
            // },
          ],
          outFields: ["*"],
          objectIdField: "ObjectID",
          geometryType: "polyline",
          listMode: "hide",
          popupTemplate: {
            title: "{Description}",
            lastEditInfoEnabled: false,
            content: [
              {
                type: "fields",
                fieldInfos: [
                  {
                    fieldName: "USRN",
                  },
                  {
                    fieldName: "StateLabel",
                    label: "Status",
                  },
                  {
                    fieldName: "TypeLabel",
                    label: "Type",
                  },
                  // {
                  //   fieldName: "LayerTitle",
                  //   label: "Source layer",
                  // },
                ],
              },
            ],
            actions: [streetOpenAction, streetStreetViewAction],
          },
          renderer: streetRenderer,
          opacity: getLayerOpacity(llpgStreetLayerTitle, isStreetMap.current),
          spatialReference: { wkid: spatialReferenceWkid.current },
          title: llpgStreetLayerTitle,
        });

        if (userContext.current?.currentUser.editProperty && llpgStreetLayer.current) {
          llpgStreetLayer.current.popupTemplate.actions = [
            streetOpenAction,
            streetAddProperty,
            streetAddRangeProperties,
            streetStreetViewAction,
          ];
        }

        layerIndex++;
        arcgisMap.addLayer(llpgStreetLayer.current, layerIndex);
      }

      if (userContext.current?.currentUser?.hasASD || isScottish.current) {
        if (isScottish.current) {
          asd51Layer.current = new FeatureLayer({
            id: asd51LayerName,
            copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
            source: [],
            fields: [
              {
                name: "ObjectID",
                alias: "ObjectID",
                type: "oid",
              },
              {
                name: "USRN",
                alias: "USRN",
                type: "string",
              },
              {
                name: "PkId",
                alias: "PkId",
                type: "string",
              },
              {
                name: "Description",
                alias: "Description",
                type: "string",
              },
              {
                name: "Custodian",
                alias: "Custodian",
                type: "string",
              },
              {
                name: "Authority",
                alias: "Authority",
                type: "string",
              },
              {
                name: "WholeRoad",
                alias: "WholeRoad",
                type: "string",
              },
              {
                name: "Geometry",
                alias: "Geometry",
                type: "string",
              },
              // {
              //   name: "LayerTitle",
              //   alias: "LayerTitle",
              //   type: "string",
              // },
            ],
            outFields: ["*"],
            objectIdField: "ObjectID",
            geometryType: "polyline",
            listMode: "hide",
            popupTemplate: {
              title: "{Description}",
              lastEditInfoEnabled: false,
              content: [
                {
                  type: "fields",
                  fieldInfos: [
                    {
                      fieldName: "USRN",
                    },
                    {
                      fieldName: "Custodian",
                    },
                    {
                      fieldName: "Authority",
                    },
                    {
                      fieldName: "WholeRoad",
                      label: "Whole road / Part of road",
                    },
                    // {
                    //   fieldName: "LayerTitle",
                    //   label: "Source layer",
                    // },
                  ],
                },
              ],
              actions: [asd51OpenAction],
            },
            renderer: asd51Renderer,
            spatialReference: { wkid: spatialReferenceWkid.current },
            title: asd51LayerTitle,
          });

          layerIndex++;
          arcgisMap.addLayer(asd51Layer.current, layerIndex);

          asd52Layer.current = new FeatureLayer({
            id: asd52LayerName,
            copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
            source: [],
            fields: [
              {
                name: "ObjectID",
                alias: "ObjectID",
                type: "oid",
              },
              {
                name: "USRN",
                alias: "USRN",
                type: "string",
              },
              {
                name: "PkId",
                alias: "PkId",
                type: "string",
              },
              {
                name: "Description",
                alias: "Description",
                type: "string",
              },
              {
                name: "Custodian",
                alias: "Custodian",
                type: "string",
              },
              {
                name: "Authority",
                alias: "Authority",
                type: "string",
              },
              {
                name: "WholeRoad",
                alias: "WholeRoad",
                type: "string",
              },
              {
                name: "Geometry",
                alias: "Geometry",
                type: "string",
              },
              // {
              //   name: "LayerTitle",
              //   alias: "LayerTitle",
              //   type: "string",
              // },
            ],
            outFields: ["*"],
            objectIdField: "ObjectID",
            geometryType: "polyline",
            listMode: "hide",
            popupTemplate: {
              title: "{Description}",
              lastEditInfoEnabled: false,
              content: [
                {
                  type: "fields",
                  fieldInfos: [
                    {
                      fieldName: "USRN",
                    },
                    {
                      fieldName: "Custodian",
                    },
                    {
                      fieldName: "Authority",
                    },
                    {
                      fieldName: "WholeRoad",
                      label: "Whole road / Part of road",
                    },
                    // {
                    //   fieldName: "LayerTitle",
                    //   label: "Source layer",
                    // },
                  ],
                },
              ],
              actions: [asd52OpenAction],
            },
            renderer: asd52Renderer,
            spatialReference: { wkid: spatialReferenceWkid.current },
            title: asd52LayerTitle,
          });

          layerIndex++;
          arcgisMap.addLayer(asd52Layer.current, layerIndex);

          asd53Layer.current = new FeatureLayer({
            id: asd53LayerName,
            copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
            source: [],
            fields: [
              {
                name: "ObjectID",
                alias: "ObjectID",
                type: "oid",
              },
              {
                name: "USRN",
                alias: "USRN",
                type: "string",
              },
              {
                name: "PkId",
                alias: "PkId",
                type: "string",
              },
              {
                name: "Description",
                alias: "Description",
                type: "string",
              },
              {
                name: "Custodian",
                alias: "Custodian",
                type: "string",
              },
              {
                name: "Authority",
                alias: "Authority",
                type: "string",
              },
              {
                name: "WholeRoad",
                alias: "WholeRoad",
                type: "string",
              },
              {
                name: "Geometry",
                alias: "Geometry",
                type: "string",
              },
              // {
              //   name: "LayerTitle",
              //   alias: "LayerTitle",
              //   type: "string",
              // },
            ],
            outFields: ["*"],
            objectIdField: "ObjectID",
            geometryType: "polyline",
            listMode: "hide",
            popupTemplate: {
              title: "{Description}",
              lastEditInfoEnabled: false,
              content: [
                {
                  type: "fields",
                  fieldInfos: [
                    {
                      fieldName: "USRN",
                    },
                    {
                      fieldName: "Custodian",
                    },
                    {
                      fieldName: "Authority",
                    },
                    {
                      fieldName: "WholeRoad",
                      label: "Whole road / Part of road",
                    },
                    // {
                    //   fieldName: "LayerTitle",
                    //   label: "Source layer",
                    // },
                  ],
                },
              ],
              actions: [asd53OpenAction],
            },
            renderer: asd53Renderer,
            spatialReference: { wkid: spatialReferenceWkid.current },
            title: asd53LayerTitle,
          });

          layerIndex++;
          arcgisMap.addLayer(asd53Layer.current, layerIndex);
        } else {
          asd61Layer.current = new FeatureLayer({
            id: asd61LayerName,
            copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
            source: [],
            fields: [
              {
                name: "ObjectID",
                alias: "ObjectID",
                type: "oid",
              },
              {
                name: "USRN",
                alias: "USRN",
                type: "string",
              },
              {
                name: "PkId",
                alias: "PkId",
                type: "string",
              },
              {
                name: "Description",
                alias: "Description",
                type: "string",
              },
              {
                name: "Interest",
                alias: "Interest",
                type: "string",
              },
              {
                name: "Organisation",
                alias: "Organisation",
                type: "string",
              },
              {
                name: "District",
                alias: "District",
                type: "string",
              },
              {
                name: "WholeRoad",
                alias: "WholeRoad",
                type: "string",
              },
              {
                name: "Geometry",
                alias: "Geometry",
                type: "string",
              },
              // {
              //   name: "LayerTitle",
              //   alias: "LayerTitle",
              //   type: "string",
              // },
            ],
            outFields: ["*"],
            objectIdField: "ObjectID",
            geometryType: "polyline",
            listMode: "hide",
            popupTemplate: {
              title: "{Description}",
              lastEditInfoEnabled: false,
              content: [
                {
                  type: "fields",
                  fieldInfos: [
                    {
                      fieldName: "USRN",
                    },
                    {
                      fieldName: "Interest",
                    },
                    {
                      fieldName: "Organisation",
                    },
                    {
                      fieldName: "District",
                    },
                    {
                      fieldName: "WholeRoad",
                      label: "Whole road / Part of road",
                    },
                    // {
                    //   fieldName: "LayerTitle",
                    //   label: "Source layer",
                    // },
                  ],
                },
              ],
              actions: [asd61OpenAction],
            },
            renderer: asd61Renderer,
            spatialReference: { wkid: spatialReferenceWkid.current },
            title: asd61LayerTitle,
          });

          layerIndex++;
          arcgisMap.addLayer(asd61Layer.current, layerIndex);

          asd62Layer.current = new FeatureLayer({
            id: asd62LayerName,
            copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
            source: [],
            fields: [
              {
                name: "ObjectID",
                alias: "ObjectID",
                type: "oid",
              },
              {
                name: "USRN",
                alias: "USRN",
                type: "string",
              },
              {
                name: "PkId",
                alias: "PkId",
                type: "string",
              },
              {
                name: "Description",
                alias: "Description",
                type: "string",
              },
              {
                name: "Reinstatement",
                alias: "Reinstatement",
                type: "string",
              },
              {
                name: "Organisation",
                alias: "Organisation",
                type: "string",
              },
              {
                name: "District",
                alias: "District",
                type: "string",
              },
              {
                name: "WholeRoad",
                alias: "WholeRoad",
                type: "string",
              },
              {
                name: "Geometry",
                alias: "Geometry",
                type: "string",
              },
              // {
              //   name: "LayerTitle",
              //   alias: "LayerTitle",
              //   type: "string",
              // },
            ],
            outFields: ["*"],
            objectIdField: "ObjectID",
            geometryType: "polyline",
            listMode: "hide",
            popupTemplate: {
              title: "{Description}",
              lastEditInfoEnabled: false,
              content: [
                {
                  type: "fields",
                  fieldInfos: [
                    {
                      fieldName: "USRN",
                    },
                    {
                      fieldName: "Reinstatement",
                    },
                    {
                      fieldName: "Organisation",
                    },
                    {
                      fieldName: "District",
                    },
                    {
                      fieldName: "WholeRoad",
                      label: "Whole road / Part of road",
                    },
                    // {
                    //   fieldName: "LayerTitle",
                    //   label: "Source layer",
                    // },
                  ],
                },
              ],
              actions: [asd62OpenAction],
            },
            renderer: asd62Renderer,
            spatialReference: { wkid: spatialReferenceWkid.current },
            title: asd62LayerTitle,
          });

          layerIndex++;
          arcgisMap.addLayer(asd62Layer.current, layerIndex);

          asd63Layer.current = new FeatureLayer({
            id: asd63LayerName,
            copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
            source: [],
            fields: [
              {
                name: "ObjectID",
                alias: "ObjectID",
                type: "oid",
              },
              {
                name: "USRN",
                alias: "USRN",
                type: "string",
              },
              {
                name: "PkId",
                alias: "PkId",
                type: "string",
              },
              {
                name: "Description",
                alias: "Description",
                type: "string",
              },
              {
                name: "Organisation",
                alias: "Organisation",
                type: "string",
              },
              {
                name: "District",
                alias: "District",
                type: "string",
              },
              {
                name: "WholeRoad",
                alias: "WholeRoad",
                type: "string",
              },
              {
                name: "Geometry",
                alias: "Geometry",
                type: "string",
              },
              // {
              //   name: "LayerTitle",
              //   alias: "LayerTitle",
              //   type: "string",
              // },
            ],
            outFields: ["*"],
            objectIdField: "ObjectID",
            geometryType: "polyline",
            listMode: "hide",
            popupTemplate: {
              title: "{Description}",
              lastEditInfoEnabled: false,
              content: [
                {
                  type: "fields",
                  fieldInfos: [
                    {
                      fieldName: "USRN",
                    },
                    {
                      fieldName: "Organisation",
                    },
                    {
                      fieldName: "District",
                    },
                    {
                      fieldName: "WholeRoad",
                      label: "Whole road / Part of road",
                    },
                    // {
                    //   fieldName: "LayerTitle",
                    //   label: "Source layer",
                    // },
                  ],
                },
              ],
              actions: [asd63OpenAction],
            },
            renderer: asd63Renderer,
            spatialReference: { wkid: spatialReferenceWkid.current },
            title: asd63LayerTitle,
          });

          layerIndex++;
          arcgisMap.addLayer(asd63Layer.current, layerIndex);

          asd64Layer.current = new FeatureLayer({
            id: asd64LayerName,
            copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
            source: [],
            fields: [
              {
                name: "ObjectID",
                alias: "ObjectID",
                type: "oid",
              },
              {
                name: "USRN",
                alias: "USRN",
                type: "string",
              },
              {
                name: "PkId",
                alias: "PkId",
                type: "string",
              },
              {
                name: "Description",
                alias: "Description",
                type: "string",
              },
              {
                name: "Organisation",
                alias: "Organisation",
                type: "string",
              },
              {
                name: "District",
                alias: "District",
                type: "string",
              },
              {
                name: "WholeRoad",
                alias: "WholeRoad",
                type: "string",
              },
              {
                name: "Geometry",
                alias: "Geometry",
                type: "string",
              },
              // {
              //   name: "LayerTitle",
              //   alias: "LayerTitle",
              //   type: "string",
              // },
            ],
            outFields: ["*"],
            objectIdField: "ObjectID",
            geometryType: "polyline",
            listMode: "hide",
            popupTemplate: {
              title: "{Description}",
              lastEditInfoEnabled: false,
              content: [
                {
                  type: "fields",
                  fieldInfos: [
                    {
                      fieldName: "USRN",
                    },
                    {
                      fieldName: "Organisation",
                    },
                    {
                      fieldName: "District",
                    },
                    {
                      fieldName: "WholeRoad",
                      label: "Whole road / Part of road",
                    },
                    // {
                    //   fieldName: "LayerTitle",
                    //   label: "Source layer",
                    // },
                  ],
                },
              ],
              actions: [asd64OpenAction],
            },
            renderer: asd64Renderer,
            spatialReference: { wkid: spatialReferenceWkid.current },
            title: asd64LayerTitle,
          });

          layerIndex++;
          arcgisMap.addLayer(asd64Layer.current, layerIndex);

          asd66Layer.current = new FeatureLayer({
            id: asd66LayerName,
            copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
            source: [],
            fields: [
              {
                name: "ObjectID",
                alias: "ObjectID",
                type: "oid",
              },
              {
                name: "USRN",
                alias: "USRN",
                type: "string",
              },
              {
                name: "PkId",
                alias: "PkId",
                type: "string",
              },
              {
                name: "Description",
                alias: "Description",
                type: "string",
              },
              {
                name: "ProwStatus",
                alias: "ProwStatus",
                type: "string",
              },
              {
                name: "Organisation",
                alias: "Organisation",
                type: "string",
              },
              {
                name: "District",
                alias: "District",
                type: "string",
              },
              {
                name: "DefMapGeometryType",
                alias: "DefMapGeometryType",
                type: "string",
              },
              {
                name: "Geometry",
                alias: "Geometry",
                type: "string",
              },
              // {
              //   name: "LayerTitle",
              //   alias: "LayerTitle",
              //   type: "string",
              // },
            ],
            outFields: ["*"],
            objectIdField: "ObjectID",
            geometryType: "polyline",
            listMode: "hide",
            popupTemplate: {
              title: "{Description}",
              lastEditInfoEnabled: false,
              content: [
                {
                  type: "fields",
                  fieldInfos: [
                    {
                      fieldName: "USRN",
                    },
                    {
                      fieldName: "ProwStatus",
                      label: "Status",
                    },
                    {
                      fieldName: "Organisation",
                    },
                    {
                      fieldName: "District",
                    },
                    {
                      fieldName: "DefMapGeometryType",
                      label: "Whole road / Part of road",
                    },
                    // {
                    //   fieldName: "LayerTitle",
                    //   label: "Source layer",
                    // },
                  ],
                },
              ],
              actions: [asd66OpenAction],
            },
            renderer: asd66Renderer,
            spatialReference: { wkid: spatialReferenceWkid.current },
            title: asd66LayerTitle,
          });

          layerIndex++;
          arcgisMap.addLayer(asd66Layer.current, layerIndex);
        }
      }

      if (userContext.current?.currentUser?.hasProperty) {
        extentLayer.current = new FeatureLayer({
          id: extentLayerName,
          copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
          source: [],
          fields: [
            {
              name: "ObjectID",
              alias: "ObjectID",
              type: "oid",
            },
            {
              name: "PkId",
              alias: "PkId",
              type: "string",
            },
            {
              name: "UPRN",
              alias: "UPRN",
              type: "string",
            },
            {
              name: "Code",
              alias: "Code",
              type: "string",
            },
            {
              name: "SymbolCode",
              alias: "SymbolCode",
              type: "string",
            },
            {
              name: "CodeLabel",
              alias: "Provenance",
              type: "string",
            },
            // {
            //   name: "LayerTitle",
            //   alias: "LayerTitle",
            //   type: "string",
            // },
          ],
          outFields: ["*"],
          objectIdField: "ObjectID",
          geometryType: "polygon",
          listMode: "hide",
          popupTemplate: {
            title: "Provenance",
            lastEditInfoEnabled: false,
            content: [
              {
                type: "fields",
                fieldInfos: [
                  {
                    fieldName: "UPRN",
                  },
                  {
                    fieldName: "CodeLabel",
                    label: "Provenance",
                  },
                  // {
                  //   fieldName: "LayerTitle",
                  //   label: "Source layer",
                  // },
                ],
              },
            ],
          },
          renderer: extentRenderer,
          opacity: getLayerOpacity(provenanceLayerTitle, isPropertyMap.current),
          spatialReference: { wkid: spatialReferenceWkid.current },
          title: provenanceLayerTitle,
        });

        layerIndex++;
        arcgisMap.addLayer(extentLayer.current, layerIndex);

        propertyLayer.current = new FeatureLayer({
          id: propertyLayerName,
          copyright: `© Copyright Idox Software Ltd. ${new Date().getFullYear()}`,
          source: [],
          featureReduction: PropertyFeatureReduction(8, 20, 20, 6),
          fields: [
            {
              name: "ObjectID",
              alias: "ObjectID",
              type: "oid",
            },
            {
              name: "UPRN",
              alias: "UPRN",
              type: "string",
            },
            {
              name: "Address",
              alias: "Address",
              type: "string",
            },
            {
              name: "FormattedAddress",
              alias: "FormattedAddress",
              type: "string",
            },
            {
              name: "Postcode",
              alias: "Postcode",
              type: "string",
            },
            {
              name: "Easting",
              alias: "Easting",
              type: "double",
            },
            {
              name: "Northing",
              alias: "Northing",
              type: "double",
            },
            {
              name: "DisplayLogicalStatus",
              alias: "DisplayLogicalStatus",
              type: "integer",
            },
            {
              name: "LogicalStatus",
              alias: "LogicalStatus",
              type: "integer",
            },
            {
              name: "LogicalStatusLabel",
              alias: "LogicalStatusLabel",
              type: "string",
            },
            {
              name: "Classification",
              alias: "Classification",
              type: "string",
            },
            {
              name: "ClassificationLabel",
              alias: "ClassificationLabel",
              type: "string",
            },
            {
              name: "SymbolCode",
              alias: "SymbolCode",
              type: "string",
            },
            // {
            //   name: "LayerTitle",
            //   alias: "LayerTitle",
            //   type: "string",
            // },
          ],
          outFields: ["*"],
          objectIdField: "ObjectID",
          geometryType: "point",
          listMode: "hide",
          popupTemplate: {
            title: "{FormattedAddress}",
            lastEditInfoEnabled: false,
            content: [
              {
                type: "fields",
                fieldInfos: [
                  {
                    fieldName: "UPRN",
                  },
                  {
                    fieldName: "LogicalStatusLabel",
                    label: "Logical status",
                  },
                  {
                    fieldName: "ClassificationLabel",
                    label: "Classification",
                  },
                  // {
                  //   fieldName: "LayerTitle",
                  //   label: "Source layer",
                  // },
                ],
              },
            ],
            actions: [propertyOpenAction, propertyAddChild, propertyAddRangeChildren, propertyStreetViewAction],
          },
          renderer: propertyRenderer,
          opacity: getLayerOpacity(propertyLayerTitle, isPropertyMap.current),
          spatialReference: { wkid: spatialReferenceWkid.current },
          title: propertyLayerTitle,
        });

        layerIndex++;
        arcgisMap.addLayer(propertyLayer.current, layerIndex);
      }

      layerIndex++;
      arcgisMap.addLayer(zoomGraphicsLayer.current, layerIndex);

      if (sketchLayer.current) {
        layerIndex++;
        arcgisMap.addLayer(sketchLayer.current, layerIndex);
      }

      mapContext.onAddingShpFile(mapContext.loadedShpFiles && mapContext.loadedShpFiles.length > 0);
      mapContext.onAddingGeoFile(mapContext.loadedGeoFiles && mapContext.loadedGeoFiles.length > 0);
    };

    if (!baseMapLayers.current) {
      if (debugUseEffects.current) console.log("[DEV] useEffect [2]: Load base map layers");
      getBaseMapLayers(userContext.current).then((result) => {
        baseMapLayers.current = result;
        if (!layersAdded.current) {
          loadBaseMapLayers();
          layersAdded.current = true;
        }
      });
    }

    return () => {};
  }, [arcgisMap, fadeVisibilityOn, mapContext]);

  // Extent change
  useEffect(() => {
    if (
      mapContext.currentExtent &&
      backgroundExtent.current &&
      (backgroundExtent.current.xmin !== mapContext.currentExtent.xmin ||
        backgroundExtent.current.ymin !== mapContext.currentExtent.ymin ||
        backgroundExtent.current.xmax !== mapContext.currentExtent.xmax ||
        backgroundExtent.current.ymax !== mapContext.currentExtent.ymax)
    ) {
      if (debugUseEffects.current) console.log("[DEV] useEffect [3]: Extent change detected");
      backgroundExtent.current = mapContext.currentExtent;
    }
  }, [mapContext.currentExtent]);

  // Set the map extent to the start extent if it is provided
  useEffect(() => {
    if (
      startExtent &&
      arcgisMap &&
      arcgisMap.view &&
      arcgisMap.view.extent &&
      (startExtent.xmin !== arcgisMap.view.extent.xmin ||
        startExtent.ymin !== arcgisMap.view.extent.ymin ||
        startExtent.xmax !== arcgisMap.view.extent.xmax ||
        startExtent.ymax !== arcgisMap.view.extent.ymax)
    ) {
      if (debugUseEffects.current) console.log("[DEV] useEffect [4]: Set the map extent to the start extent");
      backgroundExtent.current = startExtent;

      let newExtent = new Extent();
      newExtent.xmin = startExtent.xmin;
      newExtent.ymin = startExtent.ymin;
      newExtent.xmax = startExtent.xmax;
      newExtent.ymax = startExtent.ymax;
      newExtent.spatialReference = startExtent.spatialReference;
      arcgisMap.view.extent = newExtent;
      if (debugSetExtents.current)
        console.log("[DEV] Setting view extent [2]:", {
          xLength: newExtent.xmax - newExtent.xmin,
          yLength: newExtent.ymax - newExtent.ymin,
          mapType: mapTypeRef.current,
        });
    }
  }, [startExtent, arcgisMap]);

  // Load the background street layer when the map is ready
  useEffect(() => {
    if (!arcgisMap || !backgroundStreetLayer.current) return;

    const haveStreets = mapContext.currentBackgroundData.streets && mapContext.currentBackgroundData.streets.length > 0;

    let oldAndNewSame = haveStreets
      ? ArraysEqual(
          oldBackgroundStreets.current.sort((a, b) => a.usrn - b.usrn),
          mapContext.currentBackgroundData.streets.sort((a, b) => a.usrn - b.usrn)
        )
      : false;

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentMapSearchData.editStreet === null && oldEditStreet.current === null) ||
        (!!mapContext.currentMapSearchData.editStreet &&
          !!oldEditStreet.current &&
          mapContext.currentMapSearchData.editStreet === oldEditStreet.current);
    }

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentLayers.zoomStreet === null && oldZoomBackgroundStreet.current === null) ||
        (!!mapContext.currentLayers.zoomStreet &&
          !!oldZoomBackgroundStreet.current &&
          mapContext.currentLayers.zoomStreet === oldZoomBackgroundStreet.current);
    }

    if (oldAndNewSame) {
      oldAndNewSame =
        (streetData === null && oldStreetData.current === null) ||
        (!!streetData && !!oldStreetData.current && streetData === oldStreetData.current);
    }

    if (!haveStreets || oldAndNewSame) return;
    if (debugUseEffects.current) console.log("[DEV] useEffect [5]: Load background street layer");

    oldBackgroundStreets.current = mapContext.currentBackgroundData.streets;
    oldZoomBackgroundStreet.current = mapContext.currentLayers.zoomStreet;
    oldStreetData.current = streetData;

    if (haveStreets) {
      if (streetData && zoomStreetData) {
        backgroundStreetData.current = mapContext.currentBackgroundData.streets.filter(
          (x) =>
            streetData.findIndex((i) => i.usrn.toString() === x.usrn.toString()) === -1 &&
            zoomStreetData.findIndex((i) => i.usrn.toString() === x.usrn.toString()) === -1
        );
      } else if (streetData) {
        backgroundStreetData.current = mapContext.currentBackgroundData.streets.filter(
          (x) => streetData.findIndex((i) => i.usrn.toString() === x.usrn.toString()) === -1
        );
      } else if (mapContext.currentMapSearchData.editStreet && mapType === "street") {
        backgroundStreetData.current = mapContext.currentBackgroundData.streets.filter(
          (x) => x.usrn.toString() !== mapContext.currentMapSearchData.editStreet.toString()
        );
      } else if (mapContext.currentMapSearchData.streets && mapType === "gazetteer" && zoomStreetData) {
        backgroundStreetData.current = mapContext.currentBackgroundData.streets.filter(
          (x) =>
            mapContext.currentMapSearchData.streets.findIndex((i) => i.usrn.toString() === x.usrn.toString()) === -1 &&
            zoomStreetData.findIndex((i) => i.usrn.toString() === x.usrn.toString()) === -1
        );
      } else if (mapContext.currentMapSearchData.streets && mapType === "gazetteer") {
        backgroundStreetData.current = mapContext.currentBackgroundData.streets.filter(
          (x) =>
            mapContext.currentMapSearchData.streets.findIndex((i) => i.usrn.toString() === x.usrn.toString()) === -1
        );
      } else if (llpgStreetData && zoomStreetData) {
        backgroundStreetData.current = mapContext.currentBackgroundData.streets.filter(
          (x) =>
            llpgStreetData.findIndex((i) => i.usrn.toString() === x.usrn.toString()) === -1 &&
            zoomStreetData.findIndex((i) => i.usrn.toString() === x.usrn.toString()) === -1
        );
      } else if (llpgStreetData) {
        backgroundStreetData.current = mapContext.currentBackgroundData.streets.filter(
          (x) => llpgStreetData.findIndex((i) => i.usrn.toString() === x.usrn.toString()) === -1
        );
      } else if (mapContext.currentMapSearchData.llpgStreets && mapType === "gazetteer" && zoomStreetData) {
        backgroundStreetData.current = mapContext.currentBackgroundData.streets.filter(
          (x) =>
            mapContext.currentMapSearchData.llpgStreets.findIndex((i) => i.usrn.toString() === x.usrn.toString()) ===
              -1 && zoomStreetData.findIndex((i) => i.usrn.toString() === x.usrn.toString()) === -1
        );
      } else if (mapContext.currentMapSearchData.llpgStreets && mapType === "gazetteer") {
        backgroundStreetData.current = mapContext.currentBackgroundData.streets.filter(
          (x) =>
            mapContext.currentMapSearchData.llpgStreets.findIndex((i) => i.usrn.toString() === x.usrn.toString()) === -1
        );
      } else if (zoomStreetData) {
        backgroundStreetData.current = mapContext.currentBackgroundData.streets.filter(
          (x) => zoomStreetData.findIndex((i) => i.usrn.toString() === x.usrn.toString()) === -1
        );
      } else {
        backgroundStreetData.current = mapContext.currentBackgroundData.streets;
      }
    } else {
      backgroundStreetData.current = null;
    }

    const backgroundStreetFeatures = backgroundStreetData.current
      ? backgroundStreetData.current.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths: rec.wktGeometry && rec.wktGeometry !== "" ? wktToGeometry(rec.wktGeometry) : undefined,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            USRN: rec.usrn.toString(),
            EsuId: rec.esuId ? rec.esuId.toString() : "",
            Description: streetToTitleCase(rec.address),
            State: rec.state,
            Type: rec.type ? rec.type : 1,
            StateLabel: GetStreetStateLabel(rec.state, false),
            TypeLabel: GetStreetTypeLabel(rec.type ? rec.type : 1, isScottish.current, false),
            SymbolCode: `${rec.type ? rec.type.toString() : "1"}, ${rec.state.toString()}`,
            // layerTitle: backgroundStreetLayerTitle,
          },
        }))
      : [];

    if (backgroundStreetLayer.current) {
      backgroundStreetLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteStreets = { deleteFeatures: result.features };
          applyEditsToLayer(backgroundStreetLayer.current, deleteStreets);
        }

        if (backgroundStreetFeatures?.length > 0) {
          const addStreets = { addFeatures: backgroundStreetFeatures };
          applyEditsToLayer(backgroundStreetLayer.current, addStreets);

          backgroundStreetLayer.current.visible = getLayerVisibility(backgroundStreetLayerTitle);
        }
      });
    }
  }, [arcgisMap, mapContext, streetData, llpgStreetData, zoomStreetData, mapType]);

  // load the unassigned ESU layer when the map is ready
  useEffect(() => {
    if (!arcgisMap || !unassignedEsusLayer.current) return;

    const haveEsus =
      mapContext.currentBackgroundData &&
      mapContext.currentBackgroundData.unassignedEsus &&
      mapContext.currentBackgroundData.unassignedEsus.length > 0;

    const oldAndNewSame = haveEsus
      ? ArraysEqual(oldUnassignedEsus.current, mapContext.currentBackgroundData.unassignedEsus)
      : false;

    if (!haveEsus || oldAndNewSame) return;
    if (debugUseEffects.current) console.log("[DEV] useEffect [6]: Load unassigned ESU layer");

    oldUnassignedEsus.current = mapContext.currentBackgroundData.unassignedEsus;

    unassignedEsusData.current = mapContext.currentBackgroundData.unassignedEsus;

    const unassignedEsusFeatures =
      unassignedEsusData.current &&
      unassignedEsusData.current.map((rec, index) => ({
        geometry: {
          type: "polyline",
          paths: rec.wktGeometry && rec.wktGeometry !== "" ? wktToGeometry(rec.wktGeometry) : undefined,
          spatialReference: { wkid: spatialReferenceWkid.current },
        },
        attributes: {
          ObjectID: index,
          USRN: "",
          EsuId: rec.esuId ? rec.esuId.toString() : "",
          Description: "",
          State: rec.state ? rec.state : 0,
          Type: 0,
          StateLabel: GetStreetStateLabel(rec.state ? rec.state : 0, false),
          TypeLabel: GetStreetTypeLabel(0, isScottish.current, false),
          SymbolCode: `0, ${rec.state ? rec.state.toString() : "0"}`,
          // layerTitle: unassignedEsusLayerTitle,
        },
      }));

    if (unassignedEsusLayer.current) {
      unassignedEsusLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteEsus = { deleteFeatures: result.features };
          applyEditsToLayer(unassignedEsusLayer.current, deleteEsus);
        }

        if (unassignedEsusFeatures?.length > 0) {
          const addUnassignedEsus = { addFeatures: unassignedEsusFeatures };
          applyEditsToLayer(unassignedEsusLayer.current, addUnassignedEsus);

          unassignedEsusLayer.visible = getLayerVisibility(unassignedEsusLayerTitle);
        }
      });
    }
  }, [arcgisMap, mapContext, mapContext.currentBackgroundData]);

  // load the background property layer when the map is ready
  useEffect(() => {
    if (!arcgisMap || !backgroundPropertyLayer.current) return;

    const haveProperties =
      mapContext.currentBackgroundData &&
      mapContext.currentBackgroundData.properties &&
      mapContext.currentBackgroundData.properties.length > 0;

    let oldAndNewSame = haveProperties
      ? ArraysEqual(
          oldBackgroundProperties.current.sort((a, b) => a.uprn - b.uprn),
          mapContext.currentBackgroundData.properties.sort((a, b) => a.uprn - b.uprn)
        )
      : false;

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentMapSearchData.editProperty === null && oldEditProperty.current === null) ||
        (!!mapContext.currentMapSearchData.editProperty &&
          !!oldEditProperty.current &&
          mapContext.currentMapSearchData.editProperty === oldEditProperty.current);
    }

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentLayers.zoomProperty === null && oldZoomBackgroundProperty.current === null) ||
        (!!mapContext.currentLayers.zoomProperty &&
          !!oldZoomBackgroundProperty.current &&
          mapContext.currentLayers.zoomProperty === oldZoomBackgroundProperty.current);
    }

    if (oldAndNewSame) {
      oldAndNewSame =
        (propertyData === null && oldPropertyData.current === null) ||
        (!!propertyData && !!oldPropertyData.current && propertyData === oldPropertyData.current);
    }

    if (!haveProperties || oldAndNewSame) return;
    if (debugUseEffects.current) console.log("[DEV] useEffect [7]: Load background property layer");

    oldBackgroundProperties.current = mapContext.currentBackgroundData.properties;
    oldZoomBackgroundProperty.current = mapContext.currentLayers.zoomProperty;
    oldPropertyData.current = propertyData;

    if (haveProperties) {
      if (propertyData && zoomPropertyData) {
        backgroundPropertyData.current = mapContext.currentBackgroundData.properties.filter(
          (x) =>
            propertyData.findIndex((i) => Number(i.uprn) === Number(x.uprn)) === -1 &&
            zoomPropertyData.findIndex((i) => Number(i.uprn) === Number(x.uprn)) === -1
        );
      } else if (propertyData) {
        backgroundPropertyData.current = mapContext.currentBackgroundData.properties.filter(
          (x) => propertyData.findIndex((i) => Number(i.uprn) === Number(x.uprn)) === -1
        );
      } else if (mapContext.currentMapSearchData.editProperty && mapType === "property") {
        backgroundPropertyData.current = mapContext.currentBackgroundData.properties.filter(
          (x) => Number(x.uprn) !== Number(mapContext.currentMapSearchData.editProperty)
        );
      } else if (mapContext.currentMapSearchData.properties && mapType === "gazetteer" && zoomPropertyData) {
        backgroundPropertyData.current = mapContext.currentBackgroundData.properties.filter(
          (x) =>
            mapContext.currentMapSearchData.properties.findIndex((i) => Number(i.uprn) === Number(x.uprn)) === -1 &&
            zoomPropertyData.findIndex((i) => Number(i.uprn) === Number(x.uprn)) === -1
        );
      } else if (mapContext.currentMapSearchData.properties && mapType === "gazetteer") {
        backgroundPropertyData.current = mapContext.currentBackgroundData.properties.filter(
          (x) => mapContext.currentMapSearchData.properties.findIndex((i) => Number(i.uprn) === Number(x.uprn)) === -1
        );
      } else if (zoomPropertyData) {
        backgroundPropertyData.current = mapContext.currentBackgroundData.properties.filter(
          (x) => zoomPropertyData.findIndex((i) => Number(i.uprn) === Number(x.uprn)) === -1
        );
      } else {
        backgroundPropertyData.current = mapContext.currentBackgroundData.properties;
      }
    } else {
      backgroundPropertyData.current = null;
    }

    const backgroundPropertyFeatures = backgroundPropertyData.current
      ? backgroundPropertyData.current.map((rec, index) => ({
          geometry: {
            type: "point",
            x: rec.easting,
            y: rec.northing,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            UPRN: rec.uprn ? rec.uprn.toString() : "",
            Address: addressToTitleCase(rec.address, rec.postcode),
            Postcode: rec.postcode,
            Easting: rec.easting,
            Northing: rec.northing,
            DisplayLogicalStatus: backgroundPropertyData.current
              .filter((x) => Number(x.uprn) === Number(rec.uprn))
              .reduce((prev, curr) => (prev.logicalStatus < curr.logicalStatus ? prev : curr)).logicalStatus,
            LogicalStatus: rec.logicalStatus,
            LogicalStatusLabel: GetLPILogicalStatusLabel(rec.logicalStatus, isScottish.current),
            Classification: rec.blpuClass,
            ClassificationLabel: GetClassificationLabel(rec.blpuClass, isScottish.current),
            // layerTitle: backgroundPropertyLayerTitle,
          },
        }))
      : [];

    const selectPropertyFeatures = mapContext.currentBackgroundData.properties
      ? mapContext.currentBackgroundData.properties.map((rec, index) => ({
          geometry: {
            type: "point",
            x: rec.easting,
            y: rec.northing,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            UPRN: rec.uprn ? rec.uprn.toString() : "",
            Address: addressToTitleCase(rec.address, rec.postcode),
            Postcode: rec.postcode,
            Easting: rec.easting,
            Northing: rec.northing,
            DisplayLogicalStatus: mapContext.currentBackgroundData.properties
              .filter((x) => Number(x.uprn) === Number(rec.uprn))
              .reduce((prev, curr) => (prev.logicalStatus < curr.logicalStatus ? prev : curr)).logicalStatus,
            LogicalStatus: rec.logicalStatus,
            LogicalStatusLabel: GetLPILogicalStatusLabel(rec.logicalStatus, isScottish.current),
            Classification: rec.blpuClass,
            ClassificationLabel: GetClassificationLabel(rec.blpuClass, isScottish.current),
            // layerTitle: backgroundPropertyLayerTitle,
          },
        }))
      : [];

    if (backgroundPropertyLayer.current) {
      backgroundPropertyLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteProperties = { deleteFeatures: result.features };
          applyEditsToLayer(backgroundPropertyLayer.current, deleteProperties);
        }

        if (backgroundPropertyData.current?.length > 0) {
          const addProperties = { addFeatures: backgroundPropertyFeatures };
          applyEditsToLayer(backgroundPropertyLayer.current, addProperties);

          backgroundPropertyLayer.visible = getLayerVisibility(backgroundPropertyLayerTitle);
        }
      });
    }

    if (selectPropertyLayer.current) {
      selectPropertyLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteSelectProperties = { deleteFeatures: result.features };
          applyEditsToLayer(selectPropertyLayer.current, deleteSelectProperties, false);
        }

        if (selectPropertyFeatures?.length > 0) {
          const addSelectProperties = { addFeatures: selectPropertyFeatures };
          applyEditsToLayer(selectPropertyLayer.current, addSelectProperties, false);
        }
      });
    }
  }, [arcgisMap, mapContext, mapType, propertyData, zoomPropertyData]);

  // Background provenance layer
  useEffect(() => {
    if (!arcgisMap || !backgroundProvenanceLayer.current) return;

    const haveProvenances =
      mapContext.currentBackgroundData &&
      mapContext.currentBackgroundData.provenances &&
      mapContext.currentBackgroundData.provenances.length > 0;

    const oldAndNewSame = haveProvenances
      ? ArraysEqual(
          oldBackgroundProvenances.current.sort((a, b) => a.uprn - b.uprn),
          mapContext.currentBackgroundData.provenances.sort((a, b) => a.uprn - b.uprn)
        )
      : false;

    if (!haveProvenances) oldBackgroundProvenances.current = [];

    if (
      !haveProvenances ||
      (oldAndNewSame &&
        (oldShowHistoricalBackgroundProvenanceData.current === showHistoricalBackgroundProvenanceData ||
          !mapContext.currentBackgroundData.provenances.find((x) => x.historical)))
    )
      return;
    if (debugUseEffects.current) console.log("[DEV] useEffect [8]: Load background provenance layer");

    oldBackgroundProvenances.current = mapContext.currentBackgroundData.provenances;
    oldShowHistoricalBackgroundProvenanceData.current = showHistoricalBackgroundProvenanceData;

    if (showHistoricalBackgroundProvenanceData) {
      backgroundProvenanceData.current = mapContext.currentMapSearchData.editProperty
        ? mapContext.currentBackgroundData.provenances.filter(
            (rec) => Number(rec.uprn) !== Number(mapContext.currentMapSearchData.editProperty)
          )
        : mapContext.currentBackgroundData.provenances;
    } else {
      backgroundProvenanceData.current = mapContext.currentMapSearchData.editProperty
        ? mapContext.currentBackgroundData.provenances.filter(
            (rec) => Number(rec.uprn) !== Number(mapContext.currentMapSearchData.editProperty) && !rec.historical
          )
        : mapContext.currentBackgroundData.provenances.filter((rec) => !rec.historical);
    }

    const backgroundProvenancesFeatures = [];
    let index = 0;

    for (let i = 0; i < backgroundProvenanceData.current.length; i++) {
      const rec = backgroundProvenanceData.current[i];
      if (rec.wktGeometry && rec.wktGeometry !== "" && rec.wktGeometry.startsWith("MULTIPOLYGON")) {
        const multiPoly = wktToGeometry(rec.wktGeometry);
        for (let j = 0; j < multiPoly.length; j++) {
          const element = multiPoly[j];
          backgroundProvenancesFeatures.push({
            geometry: {
              type: "polygon",
              rings: element,
              spatialReference: { wkid: spatialReferenceWkid.current },
            },
            attributes: {
              ObjectID: index,
              UPRN: rec.uprn,
              Code: rec.provCode,
              SymbolCode: `${rec.historical ? "1" : "0"}, ${rec.provCode}`,
              CodeLabel: GetProvenanceLabel(rec.provCode, isScottish.current),
              // layerTitle: backgroundProvenanceLayerTitle,
            },
          });
          index++;
        }
      } else {
        backgroundProvenancesFeatures.push({
          geometry: {
            type: "polygon",
            rings: rec.wktGeometry && rec.wktGeometry !== "" ? wktToGeometry(rec.wktGeometry) : undefined,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: i,
            UPRN: rec.uprn,
            Code: rec.provCode,
            SymbolCode: `${rec.historical ? "1" : "0"}, ${rec.provCode}`,
            CodeLabel: GetProvenanceLabel(rec.provCode, isScottish.current),
          },
        });
      }
    }

    if (backgroundProvenanceLayer.current) {
      backgroundProvenanceLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteProvenances = { deleteFeatures: result.features };
          applyEditsToLayer(backgroundProvenanceLayer.current, deleteProvenances);
        }

        if (backgroundProvenancesFeatures?.length > 0) {
          const addProvenances = { addFeatures: backgroundProvenancesFeatures };
          applyEditsToLayer(backgroundProvenanceLayer.current, addProvenances);

          backgroundProvenanceLayer.current.visible = getLayerVisibility(backgroundProvenanceLayerTitle);
        }
      });
    }
  }, [arcgisMap, mapContext, showHistoricalBackgroundProvenanceData]);

  // Load the street layers when the map is ready
  useEffect(() => {
    if (!arcgisMap || (!streetLayer.current && !llpgStreetLayer.current)) return;

    const haveStreets = mapContext.currentMapSearchData.streets && mapContext.currentMapSearchData.streets.length > 0;
    if (debugStreetLayer.current) console.log("[DEV] Load street layer [A]", haveStreets);

    let oldAndNewSame = haveStreets
      ? ArraysEqual(
          oldStreets.current.sort((a, b) => a.usrn - b.usrn),
          mapContext.currentMapSearchData.streets.sort((a, b) => a.usrn - b.usrn)
        )
      : false;
    if (debugStreetLayer.current) console.log("[DEV] Load street layer [B]", oldAndNewSame);

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentMapSearchData.editStreet === null && oldEditStreet.current === null) ||
        (!!mapContext.currentMapSearchData.editStreet &&
          !!oldEditStreet.current &&
          mapContext.currentMapSearchData.editStreet === oldEditStreet.current);
      if (debugStreetLayer.current) console.log("[DEV] Load street layer [C]", oldAndNewSame);
    }

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentLayers.zoomStreet === null && oldZoomStreet.current === null) ||
        (!!mapContext.currentLayers.zoomStreet &&
          !!oldZoomStreet.current &&
          mapContext.currentLayers.zoomStreet === oldZoomStreet.current);
      if (debugStreetLayer.current) console.log("[DEV] Load street layer [D]", oldAndNewSame);
    }

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentLayers.zoomGroup === null && oldZoomGroup.current === null) ||
        (!!mapContext.currentLayers.zoomGroup &&
          !!oldZoomGroup.current &&
          ArraysEqual(mapContext.currentLayers.zoomGroup.properties, oldZoomGroup.current.properties) &&
          ArraysEqual(mapContext.currentLayers.zoomGroup.streets, oldZoomGroup.current.streets));
      if (debugStreetLayer.current) console.log("[DEV] Load street layer [E]", oldAndNewSame);
    }

    if (debugStreetLayer.current)
      console.log("[DEV] Load street layer [F]", !haveStreets, oldAndNewSame, mapContext.updatingEsuGeometry);
    if (!haveStreets || oldAndNewSame || mapContext.updatingEsuGeometry) return;
    if (debugUseEffects.current || debugStreetLayer.current) console.log("[DEV] useEffect [9]: Load street layers");

    oldStreets.current = mapContext.currentMapSearchData.streets;
    oldEditStreet.current = mapContext.currentMapSearchData.editStreet;
    oldZoomStreet.current = mapContext.currentLayers.zoomStreet;
    oldZoomGroup.current = mapContext.currentLayers.zoomGroup;
    streetExtent.current = null;

    let streetDataRef = null;
    let llpgStreetDataRef = null;
    let asdType51DataRef = null;
    let asdType52DataRef = null;
    let asdType53DataRef = null;
    let asdType61DataRef = null;
    let asdType62DataRef = null;
    let asdType63DataRef = null;
    let asdType64DataRef = null;
    let asdType66DataRef = null;
    let zoomStreetDataRef = null;

    const editingGraphic = !!editingObject.current && !!editingObject.current.objectType;

    if (mapContext.currentLayers && mapContext.currentMapSearchData.streets) {
      if (mapContext.currentLayers.zoomStreet && mapContext.currentLayers.zoomStreet.usrn) {
        if (
          mapContext.currentMapSearchData.streets &&
          mapContext.currentMapSearchData.streets.length > 0 &&
          userContext.current.currentUser.hasStreet
        ) {
          const currentStreets = mapContext.currentMapSearchData.streets.filter(
            (x) => x.usrn.toString() === mapContext.currentLayers.zoomStreet.usrn.toString()
          );

          let mapStreets = [];

          currentStreets.forEach((street) => {
            mapStreets = street.esus.map((esu) => {
              return {
                usrn: street.usrn,
                description: street.streetDescriptor,
                language: street.language,
                locality: street.locality,
                town: street.town,
                type: street.type,
                state: street.state,
                esuId: esu.esuId,
                geometry: esu.geometry,
              };
            });
          });

          if (mapStreets && mapStreets.length > 0) zoomStreetDataRef = mapStreets;
          else zoomStreetDataRef = null;
        } else if (
          mapContext.currentMapSearchData.llpgStreets &&
          mapContext.currentMapSearchData.llpgStreets.length > 0
        ) {
          const currentLlpgStreets = mapContext.currentMapSearchData.llpgStreets.filter(
            (x) => x.usrn.toString() === mapContext.currentLayers.zoomStreet.usrn.toString()
          );

          let mapLlpgStreets = [];

          currentLlpgStreets.forEach((street) => {
            mapLlpgStreets = street.esus.map((esu) => {
              return {
                usrn: street.usrn,
                description: street.streetDescriptor,
                language: street.language,
                locality: street.locality,
                town: street.town,
                type: street.type,
                state: street.state,
                esuId: esu.esuId,
                geometry: esu.geometry,
              };
            });
          });

          if (mapLlpgStreets && mapLlpgStreets.length > 0) zoomStreetDataRef = mapLlpgStreets;
          else zoomStreetDataRef = null;
        } else zoomStreetDataRef = null;
      } else if (
        mapContext.currentLayers.zoomGroup &&
        mapContext.currentLayers.zoomGroup.streets &&
        mapContext.currentLayers.zoomGroup.streets.length > 0
      ) {
        if (
          mapContext.currentMapSearchData.streets &&
          mapContext.currentMapSearchData.streets.length > 0 &&
          userContext.current.currentUser.hasStreet
        ) {
          const currentStreets = mapContext.currentMapSearchData.streets.filter((x) =>
            mapContext.currentLayers.zoomGroup.streets.some((usrn) => usrn.toString() === x.usrn.toString())
          );

          let mapStreets = [];

          currentStreets.forEach((street) => {
            mapStreets = street.esus.map((esu) => {
              return {
                usrn: street.usrn,
                description: street.streetDescriptor,
                language: street.language,
                locality: street.locality,
                town: street.town,
                type: street.type,
                state: street.state,
                esuId: esu.esuId,
                geometry: esu.geometry,
              };
            });
          });

          if (mapStreets && mapStreets.length > 0) zoomStreetDataRef = mapStreets;
          else zoomStreetDataRef = null;
        } else if (
          mapContext.currentMapSearchData.llpgStreets &&
          mapContext.currentMapSearchData.llpgStreets.length > 0
        ) {
          const currentLlpgStreets = mapContext.currentMapSearchData.llpgStreets.filter((x) =>
            mapContext.currentLayers.zoomGroup.streets.some((usrn) => usrn.toString() === x.usrn.toString())
          );

          let mapLlpgStreets = [];

          currentLlpgStreets.forEach((street) => {
            mapLlpgStreets = street.esus.map((esu) => {
              return {
                usrn: street.usrn,
                description: street.streetDescriptor,
                language: street.language,
                locality: street.locality,
                town: street.town,
                type: street.type,
                state: street.state,
                esuId: esu.esuId,
                geometry: esu.geometry,
              };
            });
          });

          if (mapLlpgStreets && mapLlpgStreets.length > 0) zoomStreetDataRef = mapLlpgStreets;
          else zoomStreetDataRef = null;
        } else zoomStreetDataRef = null;
      } else zoomStreetDataRef = null;
      setZoomStreetData(zoomStreetDataRef);

      let mapStreets = [];
      let mapLlpgStreets = [];
      let mapAsd51 = [];
      let mapAsd52 = [];
      let mapAsd53 = [];
      let mapAsd61 = [];
      let mapAsd62 = [];
      let mapAsd63 = [];
      let mapAsd64 = [];
      let mapAsd66 = [];

      if (
        userContext.current.currentUser.hasStreet &&
        mapContext.currentMapSearchData.streets &&
        !mapContext.currentMapSearchData.editProperty
      ) {
        const currentStreets = mapContext.currentMapSearchData.editStreet
          ? mapContext.currentMapSearchData.streets.filter(
              (x) => x.usrn.toString() === mapContext.currentMapSearchData.editStreet.toString()
            )
          : mapContext.currentMapSearchData.streets;

        currentStreets.forEach((street) => {
          if (street.esus && street.esus.length > 0) {
            mapStreets = mapStreets.concat(
              street.esus.map((esu) => {
                return {
                  usrn: street.usrn,
                  description: street.description,
                  language: street.language,
                  locality: street.locality,
                  town: street.town,
                  type: street.type,
                  state: isScottish.current ? esu.state : street.state,
                  esuId: esu.esuId,
                  geometry: esu.geometry,
                };
              })
            );
          }

          if (
            (mapContext.currentMapSearchData.editStreet ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord)) &&
            street.asdType51
          ) {
            mapAsd51 = mapAsd51.concat(
              street.asdType51.map((asd) => {
                return {
                  pkId: asd.pkId,
                  usrn: asd.usrn,
                  streetStatus: asd.streetStatus,
                  custodianCode: asd.custodianCode,
                  maintainingAuthorityCode: asd.maintainingAuthorityCode,
                  wholeRoad: asd.wholeRoad,
                  geometry: asd.geometry,
                };
              })
            );
          }

          if (
            (mapContext.currentMapSearchData.editStreet ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord)) &&
            street.asdType52
          ) {
            mapAsd52 = mapAsd52.concat(
              street.asdType52.map((asd) => {
                return {
                  pkId: asd.pkId,
                  usrn: asd.usrn,
                  reinstatementCategoryCode: asd.reinstatementCategoryCode,
                  custodianCode: asd.custodianCode,
                  reinstatementAuthorityCode: asd.reinstatementAuthorityCode,
                  wholeRoad: asd.wholeRoad,
                  geometry: asd.geometry,
                };
              })
            );
          }

          if (
            (mapContext.currentMapSearchData.editStreet ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord)) &&
            street.asdType53
          ) {
            mapAsd53 = mapAsd53.concat(
              street.asdType53.map((asd) => {
                return {
                  pkId: asd.pkId,
                  usrn: asd.usrn,
                  specialDesignationCode: asd.specialDesignationCode,
                  custodianCode: asd.custodianCode,
                  authorityCode: asd.authorityCode,
                  wholeRoad: asd.wholeRoad,
                  geometry: asd.geometry,
                };
              })
            );
          }

          if (
            (mapContext.currentMapSearchData.editStreet ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord)) &&
            street.asdType61
          ) {
            mapAsd61 = mapAsd61.concat(
              street.asdType61.map((asd) => {
                return {
                  pkId: asd.pkId,
                  usrn: asd.usrn,
                  streetStatus: asd.streetStatus,
                  interestType: asd.interestType,
                  swaOrgRefAuthority: asd.swaOrgRefAuthority,
                  districtRefAuthority: asd.districtRefAuthority,
                  wholeRoad: asd.wholeRoad,
                  geometry: asd.geometry,
                };
              })
            );
          }

          if (
            (mapContext.currentMapSearchData.editStreet ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord)) &&
            street.asdType62
          ) {
            mapAsd62 = mapAsd62.concat(
              street.asdType62.map((asd) => {
                return {
                  pkId: asd.pkId,
                  usrn: asd.usrn,
                  constructionType: asd.constructionType,
                  reinstatementTypeCode: asd.reinstatementTypeCode,
                  swaOrgRefConsultant: asd.swaOrgRefConsultant,
                  districtRefConsultant: asd.districtRefConsultant,
                  wholeRoad: asd.wholeRoad,
                  geometry: asd.geometry,
                };
              })
            );
          }

          if (
            (mapContext.currentMapSearchData.editStreet ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord)) &&
            street.asdType63
          ) {
            mapAsd63 = mapAsd63.concat(
              street.asdType63.map((asd) => {
                return {
                  pkId: asd.pkId,
                  usrn: asd.usrn,
                  streetSpecialDesigCode: asd.streetSpecialDesigCode,
                  swaOrgRefConsultant: asd.swaOrgRefConsultant,
                  districtRefConsultant: asd.districtRefConsultant,
                  wholeRoad: asd.wholeRoad,
                  geometry: asd.geometry,
                };
              })
            );
          }

          if (
            (mapContext.currentMapSearchData.editStreet ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord)) &&
            street.asdType64
          ) {
            mapAsd64 = mapAsd64.concat(
              street.asdType64.map((asd) => {
                return {
                  pkId: asd.pkId,
                  usrn: asd.usrn,
                  hwwRestrictionCode: asd.hwwRestrictionCode,
                  swaOrgRefConsultant: asd.swaOrgRefConsultant,
                  districtRefConsultant: asd.districtRefConsultant,
                  wholeRoad: asd.wholeRoad,
                  geometry: asd.geometry,
                };
              })
            );
          }

          if (
            (mapContext.currentMapSearchData.editStreet ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord)) &&
            street.asdType66
          ) {
            mapAsd66 = mapAsd66.concat(
              street.asdType66.map((asd) => {
                return {
                  pkId: asd.pkId,
                  prowUsrn: asd.prowUsrn,
                  prowStatus: asd.prowStatus,
                  prowRights: asd.prowRights,
                  prowOrgRefConsultant: asd.prowOrgRefConsultant,
                  prowDistrictRefConsultant: asd.prowDistrictRefConsultant,
                  defMapGeometryType: asd.defMapGeometryType,
                  geometry: asd.geometry,
                };
              })
            );
          }
        });

        if (mapStreets && mapStreets.length > 0) streetDataRef = mapStreets;
        else streetDataRef = null;

        llpgStreetDataRef = null;

        if (mapAsd51 && mapAsd51.length > 0) asdType51DataRef = mapAsd51;
        else asdType51DataRef = null;

        if (mapAsd52 && mapAsd52.length > 0) asdType52DataRef = mapAsd52;
        else asdType52DataRef = null;

        if (mapAsd53 && mapAsd53.length > 0) asdType53DataRef = mapAsd53;
        else asdType53DataRef = null;

        if (mapAsd61 && mapAsd61.length > 0) asdType61DataRef = mapAsd61;
        else asdType61DataRef = null;

        if (mapAsd62 && mapAsd62.length > 0) asdType62DataRef = mapAsd62;
        else asdType62DataRef = null;

        if (mapAsd63 && mapAsd63.length > 0) asdType63DataRef = mapAsd63;
        else asdType63DataRef = null;

        if (mapAsd64 && mapAsd64.length > 0) asdType64DataRef = mapAsd64;
        else asdType64DataRef = null;

        if (mapAsd66 && mapAsd66.length > 0) asdType66DataRef = mapAsd66;
        else asdType66DataRef = null;
      } else if (
        !userContext.current.currentUser.hasStreet &&
        mapContext.currentMapSearchData.llpgStreets &&
        !mapContext.currentMapSearchData.editProperty
      ) {
        const currentLlpgStreets = mapContext.currentMapSearchData.editStreet
          ? mapContext.currentMapSearchData.llpgStreets.filter(
              (x) => x.usrn.toString() === mapContext.currentMapSearchData.editStreet.toString()
            )
          : mapContext.currentMapSearchData.llpgStreets;

        currentLlpgStreets.forEach((street) => {
          if (street.esus) {
            mapLlpgStreets = mapLlpgStreets.concat(
              street.esus.map((esu) => {
                return {
                  usrn: street.usrn,
                  description: street.description,
                  language: street.language,
                  locality: street.locality,
                  town: street.town,
                  type: street.type,
                  state: isScottish.current ? esu.state : street.state,
                  esuId: esu.esuId,
                  geometry: esu.geometry,
                };
              })
            );
          }
        });

        streetDataRef = null;

        if (mapLlpgStreets && mapLlpgStreets.length > 0) llpgStreetDataRef = mapLlpgStreets;
        else llpgStreetDataRef = null;

        asdType51DataRef = null;
        asdType52DataRef = null;
        asdType53DataRef = null;
        asdType61DataRef = null;
        asdType62DataRef = null;
        asdType63DataRef = null;
        asdType64DataRef = null;
        asdType66DataRef = null;
      } else {
        streetDataRef = null;
        llpgStreetDataRef = null;
        asdType51DataRef = null;
        asdType52DataRef = null;
        asdType53DataRef = null;
        asdType61DataRef = null;
        asdType62DataRef = null;
        asdType63DataRef = null;
        asdType64DataRef = null;
        asdType66DataRef = null;
      }

      setStreetData(streetDataRef);
      setLlpgStreetData(llpgStreetDataRef);
      refStreetData.current = streetDataRef;
      refLlpgStreetData.current = llpgStreetDataRef;
      refAsd51Data.current = asdType51DataRef;
      refAsd52Data.current = asdType52DataRef;
      refAsd53Data.current = asdType53DataRef;
      refAsd61Data.current = asdType61DataRef;
      refAsd62Data.current = asdType62DataRef;
      refAsd63Data.current = asdType63DataRef;
      refAsd64Data.current = asdType64DataRef;
      refAsd66Data.current = asdType66DataRef;
    }

    if (streetLayer.current) {
      const streetFeatures =
        streetDataRef &&
        streetDataRef.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths:
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.geometry
                : rec.geometry,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            USRN: rec.usrn.toString(),
            EsuId: rec.esuId.toString(),
            Description: streetToTitleCase(
              mapContext.currentStreet &&
                mapContext.currentStreet.usrn &&
                mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? `${rec.description ? rec.description : "Unknown"}${
                    mapContext.currentStreet.locality && mapContext.currentStreet.locality !== "Unassigned"
                      ? " " + mapContext.currentStreet.locality
                      : ""
                  }${
                    mapContext.currentStreet.town && mapContext.currentStreet.town !== "Unassigned"
                      ? " " + mapContext.currentStreet.town
                      : ""
                  }`
                : `${rec.description ? rec.description : "Unknown"}${
                    rec.locality && rec.locality !== "Unassigned" ? " " + rec.locality : ""
                  }${rec.town && rec.town !== "Unassigned" ? " " + rec.town : ""}`
            ),
            State:
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.state
                : rec.state,
            Type:
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.type
                : rec.type
                  ? rec.type
                  : 1,
            StateLabel: GetStreetStateLabel(
              mapContext.currentStreet &&
                mapContext.currentStreet.usrn &&
                mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.state
                : rec.state,
              false
            ),
            TypeLabel: GetStreetTypeLabel(
              mapContext.currentStreet &&
                mapContext.currentStreet.usrn &&
                mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.type
                : rec.type
                  ? rec.type
                  : 1,
              isScottish.current,
              false
            ),
            SymbolCode: `${
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.type
                : rec.type
                  ? rec.type
                  : "1"
            }, ${
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.state
                : rec.state
                  ? rec.state
                  : "2"
            }`,
            // layerTitle: streetLayerTitle,
          },
        }));

      streetLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteStreets = { deleteFeatures: result.features };
          applyEditsToLayer(streetLayer.current, deleteStreets);
        }

        if (streetFeatures) {
          const addStreets = { addFeatures: streetFeatures };
          applyEditsToLayer(streetLayer.current, addStreets);
          if (editingGraphic) streetLayer.current.popupEnabled = false;
        }
      });
    }

    if (llpgStreetLayer.current) {
      const llpgStreetFeatures =
        llpgStreetDataRef &&
        llpgStreetDataRef.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths:
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.geometry
                : rec.geometry,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            USRN: rec.usrn.toString(),
            EsuId: rec.esuId.toString(),
            Description: streetToTitleCase(
              mapContext.currentStreet &&
                mapContext.currentStreet.usrn &&
                mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? `${rec.description ? rec.description : "Unknown"}${
                    mapContext.currentStreet.locality && mapContext.currentStreet.locality !== "Unassigned"
                      ? " " + mapContext.currentStreet.locality
                      : ""
                  }${
                    mapContext.currentStreet.town && mapContext.currentStreet.town !== "Unassigned"
                      ? " " + mapContext.currentStreet.town
                      : ""
                  }`
                : `${rec.description ? rec.description : "Unknown"}${
                    rec.locality && rec.locality !== "Unassigned" ? " " + rec.locality : ""
                  }${rec.town && rec.town !== "Unassigned" ? " " + rec.town : ""}`
            ),
            State:
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.state
                : rec.state,
            Type:
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.type
                : rec.type
                  ? rec.type
                  : 1,
            StateLabel: GetStreetStateLabel(
              mapContext.currentStreet &&
                mapContext.currentStreet.usrn &&
                mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.state
                : rec.state,
              false
            ),
            TypeLabel: GetStreetTypeLabel(
              mapContext.currentStreet &&
                mapContext.currentStreet.usrn &&
                mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.type
                : rec.type
                  ? rec.type
                  : 1,
              isScottish.current,
              false
            ),
            SymbolCode: `-1, ${
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.type
                : rec.type
                  ? rec.type
                  : "1"
            }, ${
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.state
                : rec.state
                  ? rec.state
                  : "2"
            }`,
            // layerTitle: llpgStreetLayerTitle,
          },
        }));

      llpgStreetLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteLlpgStreets = { deleteFeatures: result.features };
          applyEditsToLayer(llpgStreetLayer.current, deleteLlpgStreets);
        }

        if (llpgStreetFeatures?.length > 0) {
          const addLlpgStreets = { addFeatures: llpgStreetFeatures };
          applyEditsToLayer(llpgStreetLayer.current, addLlpgStreets);
          if (editingGraphic) llpgStreetLayer.current.popupEnabled = false;
        }
      });
    }

    if (asd51Layer.current) {
      const asd51Features =
        asdType51DataRef &&
        asdType51DataRef.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths: rec.geometry,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            PkId: rec.pkId.toString(),
            USRN: rec.usrn.toString(),
            Description: `Maintenance responsibility - ${GetAsdPrimaryCodeText(
              "51",
              rec.streetStatus,
              isScottish.current
            )}`,
            Authority: GetAuthorityLabel(rec.maintainingAuthorityCode, isScottish.current),
            Custodian: GetAuthorityLabel(rec.custodianCode, isScottish.current),
            WholeRoad: GetWholeRoadLabel(rec.wholeRoad),
            // layerTitle: asd51LayerTitle,
          },
        }));

      asd51Layer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteASD51s = { deleteFeatures: result.features };
          applyEditsToLayer(asd51Layer.current, deleteASD51s);
        }

        if (asd51Features?.length > 0) {
          const addASD51s = { addFeatures: asd51Features };
          applyEditsToLayer(asd51Layer.current, addASD51s);
          if (editingGraphic) asd51Layer.current.popupEnabled = false;
        }
      });
    }

    if (asd52Layer.current) {
      const asd52Features =
        asdType52DataRef &&
        asdType52DataRef.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths: rec.geometry,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            PkId: rec.pkId.toString(),
            USRN: rec.usrn.toString(),
            Description: `Reinstatement category - ${GetAsdPrimaryCodeText(
              "52",
              rec.reinstatementCategoryCode,
              isScottish.current
            )}`,
            Authority: GetAuthorityLabel(rec.reinstatementAuthorityCode, isScottish.current),
            Custodian: GetAuthorityLabel(rec.custodianCode, isScottish.current),
            WholeRoad: GetWholeRoadLabel(rec.wholeRoad),
            // layerTitle: asd52LayerTitle,
          },
        }));

      asd52Layer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteASD52s = { deleteFeatures: result.features };
          applyEditsToLayer(asd52Layer.current, deleteASD52s);
        }
      });

      if (asd52Features?.length > 0) {
        const addASD52s = { addFeatures: asd52Features };
        applyEditsToLayer(asd52Layer.current, addASD52s);
        if (editingGraphic) asd52Layer.current.popupEnabled = false;
      }
    }

    if (asd53Layer.current) {
      const asd53Features =
        asdType53DataRef &&
        asdType53DataRef.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths: rec.geometry,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            PkId: rec.pkId.toString(),
            USRN: rec.usrn.toString(),
            Description: `Special designation - ${GetAsdPrimaryCodeText(
              "53",
              rec.specialDesignationCode,
              isScottish.current
            )}`,
            Authority: GetAuthorityLabel(rec.authorityCode, isScottish.current),
            Custodian: GetAuthorityLabel(rec.custodianCode, isScottish.current),
            WholeRoad: GetWholeRoadLabel(rec.wholeRoad),
            // layerTitle: asd53LayerTitle,
          },
        }));

      asd53Layer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteASD53s = { deleteFeatures: result.features };
          applyEditsToLayer(asd53Layer.current, deleteASD53s);
        }

        if (asd53Features?.length > 0) {
          const addASD53s = { addFeatures: asd53Features };
          applyEditsToLayer(asd53Layer.current, addASD53s);
          if (editingGraphic) asd53Layer.current.popupEnabled = false;
        }
      });
    }

    if (asd61Layer.current) {
      const asd61Features =
        asdType61DataRef &&
        asdType61DataRef.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths: rec.geometry,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            PkId: rec.pkId.toString(),
            USRN: rec.usrn.toString(),
            Description: `Interest - ${GetAsdPrimaryCodeText("61", rec.streetStatus, isScottish.current)}`,
            Interest: GetInterestLabel(rec.interestType),
            Organisation: GetAuthorityLabel(rec.swaOrgRefAuthority, isScottish.current),
            District: GetDistrictLabel(rec.districtRefAuthority, lookupContext.current),
            WholeRoad: GetWholeRoadLabel(rec.wholeRoad),
            // layerTitle: asd61LayerTitle,
          },
        }));

      asd61Layer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteASD61s = { deleteFeatures: result.features };
          applyEditsToLayer(asd61Layer.current, deleteASD61s);
        }

        if (asd61Features?.length > 0) {
          const addASD61s = { addFeatures: asd61Features };
          applyEditsToLayer(asd61Layer.current, addASD61s);
          if (editingGraphic) asd61Layer.current.popupEnabled = false;
        }
      });
    }

    if (asd62Layer.current) {
      const asd62Features =
        asdType62DataRef &&
        asdType62DataRef.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths: rec.geometry,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            PkId: rec.pkId.toString(),
            USRN: rec.usrn.toString(),
            Description: `Construction - ${GetAsdPrimaryCodeText("62", rec.constructionType, isScottish.current)}`,
            Reinstatement: GetReinstatementLabel(rec.reinstatementTypeCode, isScottish.current),
            Organisation: GetAuthorityLabel(rec.swaOrgRefConsultant, isScottish.current),
            District: GetDistrictLabel(rec.districtRefConsultant, lookupContext.current),
            WholeRoad: GetWholeRoadLabel(rec.wholeRoad),
            // layerTitle: asd62LayerTitle,
          },
        }));

      asd62Layer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteASD62s = { deleteFeatures: result.features };
          applyEditsToLayer(asd62Layer.current, deleteASD62s);
        }

        if (asd62Features?.length > 0) {
          const addASD62s = { addFeatures: asd62Features };
          applyEditsToLayer(asd62Layer.current, addASD62s);
          if (editingGraphic) asd62Layer.current.popupEnabled = false;
        }
      });
    }

    if (asd63Layer.current) {
      const asd63Features =
        asdType63DataRef &&
        asdType63DataRef.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths: rec.geometry,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            PkId: rec.pkId.toString(),
            USRN: rec.usrn.toString(),
            Description: `Special designation - ${GetAsdPrimaryCodeText(
              "63",
              rec.streetSpecialDesigCode,
              isScottish.current
            )}`,
            Organisation: GetAuthorityLabel(rec.swaOrgRefConsultant, isScottish.current),
            District: GetDistrictLabel(rec.districtRefConsultant, lookupContext.current),
            WholeRoad: GetWholeRoadLabel(rec.wholeRoad),
            // layerTitle: asd63LayerTitle,
          },
        }));

      asd63Layer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteASD63s = { deleteFeatures: result.features };
          applyEditsToLayer(asd63Layer.current, deleteASD63s);
        }

        if (asd63Features?.length > 0) {
          const addASD63s = { addFeatures: asd63Features };
          applyEditsToLayer(asd63Layer.current, addASD63s);
          if (editingGraphic) asd63Layer.current.popupEnabled = false;
        }
      });
    }

    if (asd64Layer.current) {
      const asd64Features =
        asdType64DataRef &&
        asdType64DataRef.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths: rec.geometry,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            PkId: rec.pkId.toString(),
            USRN: rec.usrn.toString(),
            Description: `Height, width and weight designation - ${GetAsdPrimaryCodeText(
              "64",
              rec.hwwRestrictionCode,
              isScottish.current
            )}`,
            Organisation: GetAuthorityLabel(rec.swaOrgRefConsultant, isScottish.current),
            District: GetDistrictLabel(rec.districtRefConsultant, lookupContext.current),
            WholeRoad: GetWholeRoadLabel(rec.wholeRoad),
            // layerTitle: asd64LayerTitle,
          },
        }));

      asd64Layer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteASD64s = { deleteFeatures: result.features };
          applyEditsToLayer(asd64Layer.current, deleteASD64s);
        }

        if (asd64Features?.length > 0) {
          const addASD64s = { addFeatures: asd64Features };
          applyEditsToLayer(asd64Layer.current, addASD64s);
          if (editingGraphic) asd64Layer.current.popupEnabled = false;
        }
      });
    }

    if (asd66Layer.current) {
      const asd66Features =
        asdType66DataRef &&
        asdType66DataRef.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths: rec.geometry,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            PkId: rec.pkId.toString(),
            USRN: rec.prowUsrn.toString(),
            Description: `Public right of way - ${GetAsdPrimaryCodeText("66", rec.prowRights, false)}`,
            ProwStatus: GetProwStatusLabel(rec.prowStatus),
            Organisation: GetAuthorityLabel(rec.prowOrgRefConsultant, false),
            District: GetDistrictLabel(rec.prowDistrictRefConsultant, lookupContext.current),
            DefMapGeometryType: GetWholeRoadLabel(rec.defMapGeometryType, true),
            // layerTitle: asd66LayerTitle,
          },
        }));

      asd66Layer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteASD66s = { deleteFeatures: result.features };
          applyEditsToLayer(asd66Layer.current, deleteASD66s);
        }

        if (asd66Features?.length > 0) {
          const addASD66s = { addFeatures: asd66Features };
          applyEditsToLayer(asd66Layer.current, addASD66s);
          if (editingGraphic) asd66Layer.current.popupEnabled = false;
        }
      });
    }

    asd51Layer.current && setASDLayerVisibility(51, asd51Layer.current, streetContext.currentRecord);
    asd52Layer.current && setASDLayerVisibility(52, asd52Layer.current, streetContext.currentRecord);
    asd53Layer.current && setASDLayerVisibility(53, asd53Layer.current, streetContext.currentRecord);
    asd61Layer.current && setASDLayerVisibility(61, asd61Layer.current, streetContext.currentRecord);
    asd62Layer.current && setASDLayerVisibility(62, asd62Layer.current, streetContext.currentRecord);
    asd63Layer.current && setASDLayerVisibility(63, asd63Layer.current, streetContext.currentRecord);
    asd64Layer.current && setASDLayerVisibility(64, asd64Layer.current, streetContext.currentRecord);
    asd66Layer.current && setASDLayerVisibility(66, asd66Layer.current, streetContext.currentRecord);

    zoomGraphicsLayer.current.graphics.removeAll();
    if (sketchLayer.current) {
      sketchLayer.current.graphics.removeAll();
    }

    if (zoomStreetDataRef) {
      let addedEsuList = [];
      zoomStreetDataRef.forEach((street) => {
        if (!addedEsuList.includes(street.esuId)) {
          zoomGraphicsLayer.current.graphics.add(createPolylineGraphic(street.geometry));
          if (sketchLayer.current) {
            sketchLayer.current.graphics.add(createPolylineGraphic(street.geometry));
          }
          addedEsuList.push(street.esuId);
        }
      });
    }

    let minX = 9999999.999;
    let minY = 9999999.999;
    let maxX = 0.0;
    let maxY = 0.0;
    let newExtent = new Extent();

    if ((mapContext.currentLayers.zoomStreet || mapContext.currentLayers.zoomGroup) && zoomStreetDataRef?.length > 0) {
      if (zoomStreetDataRef?.length > 0) {
        zoomStreetDataRef.forEach((street) => {
          street.geometry.forEach((coord) => {
            if (Array.isArray(coord[0])) {
              coord.forEach((points) => {
                if (points[0] < minX) minX = points[0];
                if (points[1] < minY) minY = points[1];
                if (points[0] > maxX) maxX = points[0];
                if (points[1] > maxY) maxY = points[1];
              });
            } else {
              if (coord[0] < minX) minX = coord[0];
              if (coord[1] < minY) minY = coord[1];
              if (coord[0] > maxX) maxX = coord[0];
              if (coord[1] > maxY) maxY = coord[1];
            }
          });
        });
      }

      if (minX !== 9999999.999 && minY !== 9999999.999 && maxX !== 0.0 && maxY !== 0.0) {
        const extentBuffer = getExtentBuffer({ xmin: minX, ymin: minY, xmax: maxX, ymax: maxY });
        backgroundExtent.current = {
          xmin: minX - extentBuffer,
          ymin: minY - extentBuffer,
          xmax: maxX + extentBuffer,
          ymax: maxY + extentBuffer,
          spatialReference: { wkid: spatialReferenceWkid.current },
          zoom: arcgisMap.zoom,
        };
      }
    } else {
      const streetUsrns = [];
      if (streetDataRef?.length > 0) {
        streetDataRef.forEach((street) => {
          if (!streetUsrns.includes(street.usrn)) {
            streetUsrns.push(street.usrn);
          }
          const streetExtent = getStreetExtent(street, spatialReferenceWkid.current);
          if (streetExtent) {
            if (streetExtent.xmin < minX) minX = streetExtent.xmin;
            if (streetExtent.ymin < minY) minY = streetExtent.ymin;
            if (streetExtent.xmax > maxX) maxX = streetExtent.xmax;
            if (streetExtent.ymax > maxY) maxY = streetExtent.ymax;
          }
        });
      }

      if (llpgStreetDataRef?.length > 0) {
        llpgStreetDataRef.forEach((street) => {
          if (!streetUsrns.includes(street.usrn)) {
            streetUsrns.push(street.usrn);
          }
          const llpgStreetExtent = getStreetExtent(street, spatialReferenceWkid.current);
          if (llpgStreetExtent) {
            if (llpgStreetExtent.xmin < minX) minX = llpgStreetExtent.xmin;
            if (llpgStreetExtent.ymin < minY) minY = llpgStreetExtent.ymin;
            if (llpgStreetExtent.xmax > maxX) maxX = llpgStreetExtent.xmax;
            if (llpgStreetExtent.ymax > maxY) maxY = llpgStreetExtent.ymax;
          }
        });
      }

      if (
        startExtent &&
        isStreetMap.current &&
        streetUsrns.length === 1 &&
        mapContext.currentMapSearchData.properties.length === 0
      ) {
        if (startExtent.xmin < minX) {
          minX = startExtent.xmin;
        }
        if (startExtent.ymin < minY) {
          minY = startExtent.ymin;
        }
        if (startExtent.xmax > maxX) {
          maxX = startExtent.xmax;
        }
        if (startExtent.ymax > maxY) {
          maxY = startExtent.ymax;
        }
      }

      if (minX !== 9999999.999 && minY !== 9999999.999 && maxX !== 0.0 && maxY !== 0.0) {
        const extentBuffer = getExtentBuffer({ xmin: minX, ymin: minY, xmax: maxX, ymax: maxY });
        backgroundExtent.current = {
          xmin: minX - extentBuffer,
          ymin: minY - extentBuffer,
          xmax: maxX + extentBuffer,
          ymax: maxY + extentBuffer,
          spatialReference: { wkid: spatialReferenceWkid.current },
          zoom: arcgisMap.zoom,
        };
      }
    }

    if (!backgroundExtent.current) {
      backgroundExtent.current = getAuthorityExtent(
        authorityCode.current,
        isScottish.current,
        isWelsh.current,
        spatialReferenceWkid.current
      );
    }

    if (backgroundExtent.current) {
      streetExtent.current = {
        xmin: backgroundExtent.current.xmin,
        ymin: backgroundExtent.current.ymin,
        xmax: backgroundExtent.current.xmax,
        ymax: backgroundExtent.current.ymax,
      };

      newExtent.xmin =
        isStreetMap.current || !propertyExtent.current
          ? backgroundExtent.current.xmin
          : propertyExtent.current.xmin < backgroundExtent.current.xmin
            ? propertyExtent.current.xmin
            : backgroundExtent.current.xmin;
      newExtent.ymin =
        isStreetMap.current || !propertyExtent.current
          ? backgroundExtent.current.ymin
          : propertyExtent.current.ymin < backgroundExtent.current.ymin
            ? propertyExtent.current.ymin
            : backgroundExtent.current.ymin;
      newExtent.xmax =
        isStreetMap.current || !propertyExtent.current
          ? backgroundExtent.current.xmax
          : propertyExtent.current.xmax > backgroundExtent.current.xmax
            ? propertyExtent.current.xmax
            : backgroundExtent.current.xmax;
      newExtent.ymax =
        isStreetMap.current || !propertyExtent.current
          ? backgroundExtent.current.ymax
          : propertyExtent.current.ymax > backgroundExtent.current.ymax
            ? propertyExtent.current.ymax
            : backgroundExtent.current.ymax;
      newExtent.spatialReference = backgroundExtent.current.spatialReference;
      arcgisMap.view.extent = newExtent;
      if (debugSetExtents.current)
        console.log("[DEV] Setting view extent [3]:", {
          xLength: newExtent.xmax - newExtent.xmin,
          yLength: newExtent.ymax - newExtent.ymin,
          mapType: mapTypeRef.current,
        });
    }
  }, [arcgisMap, mapContext, streetContext.currentStreet, streetContext.currentRecord, startExtent]);

  // Load the property layers when the map is ready
  useEffect(() => {
    if (!arcgisMap || !propertyLayer.current) return;

    const haveProperties =
      mapContext.currentMapSearchData.properties && mapContext.currentMapSearchData.properties.length > 0;
    if (debugPropertyLayer.current) console.log("[DEV] Load property layer [A]", haveProperties);

    let oldAndNewSame = haveProperties
      ? ArraysEqual(
          oldProperties.current.sort((a, b) => a.uprn - b.uprn),
          mapContext.currentMapSearchData.properties.sort((a, b) => a.uprn - b.uprn)
        )
      : false;
    if (debugPropertyLayer.current) console.log("[DEV] Load property layer [B]", oldAndNewSame);

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentMapSearchData.editStreet === null && oldEditStreet.current === null) ||
        (!!mapContext.currentMapSearchData.editStreet &&
          !!oldEditStreet.current &&
          mapContext.currentMapSearchData.editStreet === oldEditStreet.current);
      if (debugPropertyLayer.current) console.log("[DEV] Load property layer [C]", oldAndNewSame);
    }

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentMapSearchData.editProperty === null && oldEditProperty.current === null) ||
        (!!mapContext.currentMapSearchData.editProperty &&
          !!oldEditProperty.current &&
          mapContext.currentMapSearchData.editProperty === oldEditProperty.current);
      if (debugPropertyLayer.current) console.log("[DEV] Load property layer [D]", oldAndNewSame);
    }

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentLayers.zoomProperty === null && oldZoomProperty.current === null) ||
        (!!mapContext.currentLayers.zoomProperty &&
          !!oldZoomProperty.current &&
          mapContext.currentLayers.zoomProperty === oldZoomProperty.current);
      if (debugPropertyLayer.current) console.log("[DEV] Load property layer [E]", oldAndNewSame);
    }

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentLayers.zoomGroup === null && oldZoomGroup.current === null) ||
        (!!mapContext.currentLayers.zoomGroup &&
          !!oldZoomGroup.current &&
          ArraysEqual(mapContext.currentLayers.zoomGroup.properties, oldZoomGroup.current.properties) &&
          ArraysEqual(mapContext.currentLayers.zoomGroup.streets, oldZoomGroup.current.streets));
      if (debugPropertyLayer.current) console.log("[DEV] Load property layer [F]", oldAndNewSame);
    }

    if (oldAndNewSame) {
      oldAndNewSame = oldShowHistoricalExtentData.current === showHistoricalExtentData;
      if (debugPropertyLayer.current) console.log("[DEV] Load property layer [G]", oldAndNewSame);
    }

    if (oldAndNewSame) {
      oldAndNewSame =
        (mapContext.currentLayers.extents === null && oldExtents.current === null) ||
        (!!mapContext.currentLayers.extents &&
          !!oldExtents.current &&
          ArraysEqual(mapContext.currentLayers.extents, oldExtents.current));
      if (debugPropertyLayer.current) console.log("[DEV] Load property layer [H]", oldAndNewSame);
    }

    if (debugPropertyLayer.current)
      console.log("[DEV] Load property layer [I]", !haveProperties, oldAndNewSame, mapContext.updatingProvenance);
    if (!haveProperties || oldAndNewSame || mapContext.updatingProvenance) return;
    if (debugUseEffects.current || debugPropertyLayer.current)
      console.log("[DEV] useEffect [10]: Load property layers");

    oldProperties.current = mapContext.currentMapSearchData.properties;
    oldEditProperty.current = mapContext.currentMapSearchData.editProperty;
    const showHistoricalExtentChanged = oldShowHistoricalExtentData.current !== showHistoricalExtentData;
    oldZoomProperty.current = mapContext.currentLayers.zoomProperty;
    oldZoomGroup.current = mapContext.currentLayers.zoomGroup;
    oldShowHistoricalExtentData.current = showHistoricalExtentData;
    oldExtents.current = mapContext.currentLayers.extents;
    propertyExtent.current = null;

    let propertyDataRef = null;
    let zoomPropertyDataRef = null;

    const editingGraphic = !!editingObject.current && !!editingObject.current.objectType;

    if (
      mapContext.currentMapSearchData.properties &&
      mapContext.currentMapSearchData.properties.length > 0 &&
      mapContext.currentLayers.zoomProperty
    ) {
      zoomPropertyDataRef = mapContext.currentMapSearchData.properties.filter(
        (x) => Number(x.uprn) === Number(mapContext.currentLayers.zoomProperty)
      );
    } else if (
      mapContext.currentMapSearchData.properties &&
      mapContext.currentMapSearchData.properties.length > 0 &&
      mapContext.currentLayers.zoomGroup &&
      mapContext.currentLayers.zoomGroup.properties &&
      mapContext.currentLayers.zoomGroup.properties.length > 0
    ) {
      zoomPropertyDataRef = mapContext.currentMapSearchData.properties.filter((x) =>
        mapContext.currentLayers.zoomGroup.properties.some((uprn) => Number(uprn) === Number(x.uprn))
      );
    } else if (
      mapContext.currentMapSearchData.properties &&
      mapContext.currentMapSearchData.properties.length > 0 &&
      editingGraphic
    ) {
      zoomPropertyDataRef = mapContext.currentMapSearchData.properties.filter(
        (x) => Number(x.uprn) === Number(editingObject.current.objectId)
      );
    } else zoomPropertyDataRef = null;
    setZoomPropertyData(zoomPropertyDataRef);

    if (
      mapContext.currentMapSearchData.properties &&
      mapContext.currentMapSearchData.properties.length > 0 &&
      !mapContext.currentMapSearchData.editStreet
    ) {
      if (mapContext.currentMapSearchData.editProperty) {
        propertyDataRef = mapContext.currentMapSearchData.properties.filter(
          (x) => Number(x.uprn) === Number(mapContext.currentMapSearchData.editProperty)
        );
      } else propertyDataRef = mapContext.currentMapSearchData.properties;
    } else propertyDataRef = null;
    setPropertyData(propertyDataRef);
    refPropertyData.current = propertyDataRef;

    if (
      mapContext.currentLayers.extents &&
      mapContext.currentLayers.extents.length > 0 &&
      mapContext.currentMapSearchData.editProperty
    ) {
      extentData.current = showHistoricalExtentData
        ? mapContext.currentLayers.extents.filter(
            (x) => x.geometry && Number(x.uprn) === Number(mapContext.currentMapSearchData.editProperty)
          )
        : mapContext.currentLayers.extents.filter(
            (x) =>
              x.geometry && Number(x.uprn) === Number(mapContext.currentMapSearchData.editProperty) && !x.historical
          );
    } else extentData.current = null;

    if (propertyLayer.current) {
      const propertyFeatures =
        propertyDataRef &&
        propertyDataRef
          .sort((a, b) => b.logicalStatus - a.logicalStatus)
          .map((rec, index) => ({
            geometry: {
              type: "point",
              x:
                mapContext.currentProperty &&
                mapContext.currentProperty.uprn &&
                Number(mapContext.currentProperty.uprn) === Number(rec.uprn)
                  ? mapContext.currentProperty.xcoordinate
                  : rec.easting,
              y:
                mapContext.currentProperty &&
                mapContext.currentProperty.uprn &&
                Number(mapContext.currentProperty.uprn) === Number(rec.uprn)
                  ? mapContext.currentProperty.ycoordinate
                  : rec.northing,
              spatialReference: { wkid: spatialReferenceWkid.current },
            },
            attributes: {
              ObjectID: index,
              UPRN: rec.uprn.toString(),
              Address: rec.address ? addressToTitleCase(rec.address, rec.postcode) : "New Property",
              FormattedAddress: rec.formattedAddress
                ? addressToTitleCase(rec.formattedAddress, rec.postcode)
                : rec.address
                  ? addressToTitleCase(rec.address, rec.postcode)
                  : "New Property",
              Postcode: rec.postcode,
              Easting:
                mapContext.currentProperty &&
                mapContext.currentProperty.uprn &&
                Number(mapContext.currentProperty.uprn) === Number(rec.uprn)
                  ? mapContext.currentProperty.xcoordinate
                  : rec.easting,
              Northing:
                mapContext.currentProperty &&
                mapContext.currentProperty.uprn &&
                Number(mapContext.currentProperty.uprn) === Number(rec.uprn)
                  ? mapContext.currentProperty.ycoordinate
                  : rec.northing,
              DisplayLogicalStatus: propertyDataRef
                .filter((x) => Number(x.uprn) === Number(rec.uprn))
                .reduce((prev, curr) => (prev.logicalStatus < curr.logicalStatus ? prev : curr)).logicalStatus,
              LogicalStatus:
                mapContext.currentProperty &&
                mapContext.currentProperty.uprn &&
                Number(mapContext.currentProperty.uprn) === Number(rec.uprn)
                  ? mapContext.currentProperty.logicalStatus
                  : rec.logicalStatus,
              LogicalStatusLabel: GetLPILogicalStatusLabel(
                mapContext.currentProperty &&
                  mapContext.currentProperty.uprn &&
                  Number(mapContext.currentProperty.uprn) === Number(rec.uprn)
                  ? mapContext.currentProperty.logicalStatus
                  : rec.logicalStatus,
                isScottish.current
              ),
              Classification:
                mapContext.currentProperty &&
                mapContext.currentProperty.uprn &&
                Number(mapContext.currentProperty.uprn) === Number(rec.uprn)
                  ? mapContext.currentProperty.blpuClass
                  : rec.classificationCode,
              ClassificationLabel: GetClassificationLabel(
                mapContext.currentProperty &&
                  mapContext.currentProperty.uprn &&
                  Number(mapContext.currentProperty.uprn) === Number(rec.uprn)
                  ? mapContext.currentProperty.blpuClass
                  : rec.classificationCode,
                isScottish.current
              ),
              SymbolCode: `${
                mapContext.currentProperty &&
                mapContext.currentProperty.uprn &&
                Number(mapContext.currentProperty.uprn) === Number(rec.uprn)
                  ? mapContext.currentProperty
                      .filter((x) => Number(x.uprn) === Number(rec.uprn))
                      .reduce((prev, curr) => (prev.logicalStatus < curr.logicalStatus ? prev : curr)).logicalStatus
                  : propertyDataRef
                        .filter((x) => Number(x.uprn) === Number(rec.uprn))
                        .reduce((prev, curr) => (prev.logicalStatus < curr.logicalStatus ? prev : curr)).logicalStatus
                    ? propertyDataRef
                        .filter((x) => Number(x.uprn) === Number(rec.uprn))
                        .reduce((prev, curr) => (prev.logicalStatus < curr.logicalStatus ? prev : curr)).logicalStatus
                    : 5
              }, ${
                mapContext.currentProperty &&
                mapContext.currentProperty.uprn &&
                Number(mapContext.currentProperty.uprn) === Number(rec.uprn)
                  ? mapContext.currentProperty.blpuClass === "PS"
                    ? "B"
                    : mapContext.currentProperty.blpuClass?.substring(0, 1)
                  : rec.classificationCode === "PS"
                    ? "B"
                    : rec.classificationCode?.substring(0, 1)
              }`,
              // layerTitle: propertyLayerTitle,
            },
          }));

      propertyLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteProperties = { deleteFeatures: result.features };
          applyEditsToLayer(propertyLayer.current, deleteProperties);
        }

        if (propertyFeatures?.length > 0) {
          const addProperties = { addFeatures: propertyFeatures };
          applyEditsToLayer(propertyLayer.current, addProperties);
          if (editingGraphic) propertyLayer.current.popupEnabled = false;
        }
      });
    }

    if (extentLayer.current && extentData.current && extentData.current.length > 0) {
      const extentFeatures = [];
      let index = 0;

      for (let i = 0; i < extentData.current.length; i++) {
        const rec = extentData.current[i];
        if (rec.geometry[0][0].length > 2) {
          for (let j = 0; j < rec.geometry.length; j++) {
            const element = rec.geometry[j];
            extentFeatures.push({
              geometry: {
                type: "polygon",
                rings: element,
                spatialReference: { wkid: spatialReferenceWkid.current },
              },
              attributes: {
                ObjectID: index,
                PkId: rec.pkId,
                UPRN: rec.uprn,
                Code: rec.code,
                SymbolCode: `${rec.historical ? "1" : "0"}, ${rec.code}`,
                CodeLabel: GetProvenanceLabel(rec.code, isScottish.current),
                // layerTitle: provenanceLayerTitle,
              },
            });
            index++;
          }
        } else {
          extentFeatures.push({
            geometry: {
              type: "polygon",
              rings: rec.geometry,
              spatialReference: { wkid: spatialReferenceWkid.current },
            },
            attributes: {
              ObjectID: i,
              PkId: rec.pkId,
              UPRN: rec.uprn,
              Code: rec.code,
              SymbolCode: `${rec.historical ? "1" : "0"}, ${rec.code}`,
              CodeLabel: GetProvenanceLabel(rec.code, isScottish.current),
            },
          });
        }
      }

      if (extentLayer.current || showHistoricalExtentChanged) {
        extentLayer.current.queryFeatures().then((result) => {
          if (result.features.length > 0) {
            const deleteExtents = { deleteFeatures: result.features };
            applyEditsToLayer(extentLayer.current, deleteExtents);
          }

          if (extentFeatures?.length > 0) {
            const addExtents = { addFeatures: extentFeatures };
            applyEditsToLayer(extentLayer.current, addExtents);
            if (editingGraphic) {
              extentLayer.current.popupEnabled = false;
              if (highlightExtent.current && mapContext.currentHighlight.extent?.length > 0) {
                // Force the highlight to redraw as the layer has been refreshed and the highlight graphic may have been removed
                const existingHighlight = mapContext.currentHighlight.extent;
                mapContext.onHighlightClear();
                mapContext.onHighlightListItem("extent", existingHighlight);
              }
            }
          }
        });
      }
    } else if (extentLayer.current || showHistoricalExtentChanged) {
      extentLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteExtents = { deleteFeatures: result.features };
          applyEditsToLayer(extentLayer.current, deleteExtents);
        }
      });
    }

    zoomGraphicsLayer.current.graphics.removeAll();
    if (sketchLayer.current) {
      sketchLayer.current.graphics.removeAll();
    }

    if (zoomPropertyDataRef) {
      let addedUprnList = [];
      zoomPropertyDataRef.forEach((property) => {
        if (!addedUprnList.includes(property.uprn)) {
          zoomGraphicsLayer.current.graphics.add(
            createPointGraphic(
              property.easting,
              property.northing,
              property.logicalStatus,
              property.classificationCode.substring(0, 1),
              true
            )
          );
          if (sketchLayer.current) {
            sketchLayer.current.graphics.add(
              createPointGraphic(
                property.easting,
                property.northing,
                property.logicalStatus,
                property.classificationCode.substring(0, 1),
                true
              )
            );
          }
          addedUprnList.push(property.uprn);
        }
      });
    }

    let minX = 9999999.999;
    let minY = 9999999.999;
    let maxX = 0.0;
    let maxY = 0.0;
    let newExtent = new Extent();

    if (
      (mapContext.currentLayers.zoomProperty || mapContext.currentLayers.zoomGroup) &&
      zoomPropertyDataRef?.length > 0
    ) {
      if (zoomPropertyDataRef?.length > 0) {
        zoomPropertyDataRef.forEach((property) => {
          const propertyExtent = getPropertyExtent(property, spatialReferenceWkid.current);
          if (propertyExtent) {
            if (propertyExtent.xmin < minX) minX = propertyExtent.xmin;
            if (propertyExtent.ymin < minY) minY = propertyExtent.ymin;
            if (propertyExtent.xmax > maxX) maxX = propertyExtent.xmax;
            if (propertyExtent.ymax > maxY) maxY = propertyExtent.ymax;
          }
        });
      }

      if (minX !== 9999999.999 && minY !== 9999999.999 && maxX !== 0.0 && maxY !== 0.0) {
        const extentBuffer = getExtentBuffer({ xmin: minX, ymin: minY, xmax: maxX, ymax: maxY });
        backgroundExtent.current = {
          xmin: minX - extentBuffer,
          ymin: minY - extentBuffer,
          xmax: maxX + extentBuffer,
          ymax: maxY + extentBuffer,
          spatialReference: { wkid: spatialReferenceWkid.current },
          zoom: arcgisMap.zoom,
        };
      }
    } else {
      const propertyUprns = [];
      if (propertyDataRef?.length > 0) {
        propertyDataRef.forEach((property) => {
          if (!propertyUprns.includes(property.uprn)) {
            propertyUprns.push(property.uprn);
          }
          const propertyExtent = getPropertyExtent(property, spatialReferenceWkid.current);
          if (propertyExtent) {
            if (propertyExtent.xmin < minX) minX = propertyExtent.xmin;
            if (propertyExtent.ymin < minY) minY = propertyExtent.ymin;
            if (propertyExtent.xmax > maxX) maxX = propertyExtent.xmax;
            if (propertyExtent.ymax > maxY) maxY = propertyExtent.ymax;
          }
        });
      }

      if (
        startExtent &&
        isPropertyMap.current &&
        propertyUprns.length === 1 &&
        (!mapContext.currentMapSearchData.streets || mapContext.currentMapSearchData.streets?.length === 0) &&
        (!mapContext.currentMapSearchData.llpStreets || mapContext.currentMapSearchData.llpStreets?.length === 0)
      ) {
        if (startExtent.xmin < minX) {
          minX = startExtent.xmin;
        }
        if (startExtent.ymin < minY) {
          minY = startExtent.ymin;
        }
        if (startExtent.xmax > maxX) {
          maxX = startExtent.xmax;
        }
        if (startExtent.ymax > maxY) {
          maxY = startExtent.ymax;
        }
      }

      if (minX !== 9999999.999 && minY !== 9999999.999 && maxX !== 0.0 && maxY !== 0.0) {
        const extentBuffer = getExtentBuffer({ xmin: minX, ymin: minY, xmax: maxX, ymax: maxY });
        backgroundExtent.current = {
          xmin: minX - extentBuffer,
          ymin: minY - extentBuffer,
          xmax: maxX + extentBuffer,
          ymax: maxY + extentBuffer,
          zoom: arcgisMap.zoom,
          spatialReference: { wkid: spatialReferenceWkid.current },
        };
      }
    }

    if (!backgroundExtent.current) {
      backgroundExtent.current = getAuthorityExtent(
        authorityCode.current,
        isScottish.current,
        isWelsh.current,
        spatialReferenceWkid.current
      );
    }

    if (backgroundExtent.current) {
      propertyExtent.current = {
        xmin: backgroundExtent.current.xmin,
        ymin: backgroundExtent.current.ymin,
        xmax: backgroundExtent.current.xmax,
        ymax: backgroundExtent.current.ymax,
      };

      newExtent.xmin =
        isPropertyMap.current || !streetExtent.current
          ? backgroundExtent.current.xmin
          : streetExtent.current.xmin < backgroundExtent.current.xmin
            ? streetExtent.current.xmin
            : backgroundExtent.current.xmin;
      newExtent.ymin =
        isPropertyMap.current || !streetExtent.current
          ? backgroundExtent.current.ymin
          : streetExtent.current.ymin < backgroundExtent.current.ymin
            ? streetExtent.current.ymin
            : backgroundExtent.current.ymin;
      newExtent.xmax =
        isPropertyMap.current || !streetExtent.current
          ? backgroundExtent.current.xmax
          : streetExtent.current.xmax > backgroundExtent.current.xmax
            ? streetExtent.current.xmax
            : backgroundExtent.current.xmax;
      newExtent.ymax =
        isPropertyMap.current || !streetExtent.current
          ? backgroundExtent.current.ymax
          : streetExtent.current.ymax > backgroundExtent.current.ymax
            ? streetExtent.current.ymax
            : backgroundExtent.current.ymax;
      newExtent.spatialReference = backgroundExtent.current.spatialReference;
      arcgisMap.view.extent = newExtent;
      if (debugSetExtents.current)
        console.log("[DEV] Setting view extent [4]:", {
          xLength: newExtent.xmax - newExtent.xmin,
          yLength: newExtent.ymax - newExtent.ymin,
          mapType: mapTypeRef.current,
        });
    }
  }, [arcgisMap, mapContext, startExtent, showHistoricalExtentData]);

  // Update extent layer
  useEffect(() => {
    if (
      updatingPolygons.current &&
      mapContext.currentLayers.extents &&
      mapContext.currentLayers.extents.length > 0 &&
      mapContext.currentMapSearchData.editProperty
    ) {
      extentData.current = showHistoricalExtentData
        ? mapContext.currentLayers.extents.filter(
            (x) => x.geometry && Number(x.uprn) === Number(mapContext.currentMapSearchData.editProperty)
          )
        : mapContext.currentLayers.extents.filter(
            (x) =>
              x.geometry && Number(x.uprn) === Number(mapContext.currentMapSearchData.editProperty) && !x.historical
          );

      let oldAndNewSame = ArraysEqual(
        oldExtents.current.sort((a, b) => Number(a.uprn) - Number(b.uprn)),
        extentData.current.sort((a, b) => Number(a.uprn) - Number(b.uprn))
      );

      if (
        oldAndNewSame &&
        (oldShowHistoricalExtentData.current === showHistoricalExtentData ||
          !mapContext.currentLayers.extents.find((x) => x.historical))
      ) {
        return;
      }
      if (debugUseEffects.current) console.log("[DEV] useEffect [11]: Update extent layer");

      oldExtents.current = mapContext.currentLayers.extents.filter(
        (x) => x.geometry && Number(x.uprn) === Number(mapContext.currentMapSearchData.editProperty)
      );
      oldShowHistoricalExtentData.current = showHistoricalExtentData;

      const editingGraphic = !!editingObject.current && !!editingObject.current.objectType;

      if (extentLayer.current && extentData.current && extentData.current.length > 0) {
        const extentFeatures = [];
        let index = 0;

        for (let i = 0; i < extentData.current.length; i++) {
          const rec = extentData.current[i];
          if (rec.geometry[0][0].length > 2) {
            for (let j = 0; j < rec.geometry.length; j++) {
              const element = rec.geometry[j];
              extentFeatures.push({
                geometry: {
                  type: "polygon",
                  rings: element,
                  spatialReference: { wkid: spatialReferenceWkid.current },
                },
                attributes: {
                  ObjectID: index,
                  PkId: rec.pkId,
                  UPRN: rec.uprn,
                  Code: rec.code,
                  SymbolCode: `${rec.historical ? "1" : "0"}, ${rec.code}`,
                  CodeLabel: GetProvenanceLabel(rec.code, isScottish.current),
                  // layerTitle: provenanceLayerTitle,
                },
              });
              index++;
            }
          } else {
            extentFeatures.push({
              geometry: {
                type: "polygon",
                rings: rec.geometry,
                spatialReference: { wkid: spatialReferenceWkid.current },
              },
              attributes: {
                ObjectID: i,
                PkId: rec.pkId,
                UPRN: rec.uprn,
                Code: rec.code,
                SymbolCode: `${rec.historical ? "1" : "0"}, ${rec.code}`,
                CodeLabel: GetProvenanceLabel(rec.code, isScottish.current),
              },
            });
          }
        }

        if (extentLayer.current) {
          extentLayer.current.queryFeatures().then((result) => {
            if (result.features.length > 0) {
              const deleteExtents = { deleteFeatures: result.features };
              applyEditsToLayer(extentLayer.current, deleteExtents);
            }

            if (extentFeatures?.length > 0) {
              const addExtents = { addFeatures: extentFeatures };
              applyEditsToLayer(extentLayer.current, addExtents);
              if (editingGraphic) extentLayer.current.popupEnabled = false;
            }
          });
        }

        mapContext.onSetPolygonGeometry(null);
        updatingPolygons.current = false;
      } else if (extentLayer.current) {
        extentLayer.current.queryFeatures().then((result) => {
          if (result.features.length > 0) {
            const deleteExtents = { deleteFeatures: result.features };
            applyEditsToLayer(extentLayer.current, deleteExtents);
          }
        });
      }
    }
  }, [mapContext, showHistoricalExtentData]);

  // Display loaded SHP files
  useEffect(() => {
    if (!arcgisMap || !mapContext.addingShpFile) return;
    if (debugUseEffects.current) console.log("[DEV] useEffect [12]: Display loaded SHP files");

    mapContext.onAddingShpFile(false);

    // First unload all the shpFiles previously loaded
    if (loadedShpFileIds.current && loadedShpFileIds.current.length) {
      loadedShpFileIds.current.forEach((shpId) => {
        arcgisMap.map.remove(arcgisMap.map.findLayerById(shpId));
      });
    }

    if (mapContext.loadedShpFiles && mapContext.loadedShpFiles.length) {
      mapContext.loadedShpFiles.forEach((shp) => {
        const blob = new Blob([shp.geojson], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const shpLayer = new GeoJSONLayer({
          url: url,
          id: shp.id,
          copyright:
            shp.shpFile.copyright && shp.shpFile.copyright.includes("<<year>>")
              ? shp.shpFile.copyright.replace("<<year>>", new Date().getFullYear().toString())
              : shp.shpFile.copyright,
          title: `${shp.shpFile.title}`,
          fields: shp.fields,
          objectIdField: shp.fields.find((field) => field.type === "oid")?.name,
          outFields: ["*"],
          geometryType: shp.geometryType,
          listMode: shp.shpFile.displayInList ? "show" : "hide",
          maxScale: shp.shpFile.maxScale,
          minScale: shp.shpFile.minScale,
          opacity: shp.shpFile.opacity,
          spatialReference: { wkid: spatialReferenceWkid.current },
          popupTemplate: {
            title: `{${shp.popupTitleField || shp.fields[0].name}}`,
            lastEditInfoEnabled: false,
            content: [
              {
                type: "fields",
                fieldInfos: shp.fields
                  .filter((f) => f.type !== "oid")
                  .map((f) => ({
                    fieldName: f.name,
                    label: f.alias,
                  })),
              },
            ],
          },
          renderer: shp.renderer,
          visible: true,
        });

        if (shp.shpFile.esuSnap) baseLayersSnapEsu.current = baseLayersSnapEsu.current.concat([shp.id]);
        if (shp.shpFile.blpuSnap) baseLayersSnapBlpu.current = baseLayersSnapBlpu.current.concat([shp.id]);
        if (shp.shpFile.extentSnap) baseLayersSnapExtent.current = baseLayersSnapExtent.current.concat([shp.id]);

        arcgisMap.addLayer(shpLayer, shpGeoLayerIndex.current);
        shpGeoLayerIndex.current += 1;
        loadedShpFileLayers.current.push(shpLayer);
      });

      loadedShpFileTitles.current = mapContext.loadedShpFiles.map((x) => x.shpFile.title);
      loadedShpFileIds.current = mapContext.loadedShpFiles.map((x) => x.id);
      editingObject.current = null;
    } else {
      loadedShpFileTitles.current = [];
      loadedShpFileIds.current = [];
    }
  }, [mapContext, arcgisMap]);

  // Display loaded Geo-referenced files
  useEffect(() => {
    const getImageMetadata = (imageUrl) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = imageUrl;
      });

    const getImageCornerPoint = (a, b, c, d, e, f, x, y) => {
      const easting = a * x + b * y + c;
      const northing = d * x + e * y + f;
      return new Point({
        x: easting,
        y: northing,
        spatialReference: { wkid: spatialReferenceWkid.current },
      });
    };

    if (!arcgisMap || !mapContext.addingGeoFile) return;
    if (debugUseEffects.current) console.log("[DEV] useEffect [13]: Display loaded geo-referenced files");

    mapContext.onAddingGeoFile(false);

    // First unload all the geoFiles previously loaded
    if (loadedGeoFileIds.current && loadedGeoFileIds.current.length) {
      loadedGeoFileIds.current.forEach((geoId) => {
        arcgisMap.map.remove(arcgisMap.map.findLayerById(geoId));
      });
    }

    if (mapContext.loadedGeoFiles && mapContext.loadedGeoFiles.length) {
      mapContext.loadedGeoFiles.forEach((geo) => {
        let geoReference = null;

        if (!geo.worldData) {
          geoReference = new ExtentAndRotationGeoreference({
            extent: new Extent({
              spatialReference: { wkid: spatialReferenceWkid.current },
              xmin: geo.geoFile.xMin,
              ymin: geo.geoFile.yMin,
              xmax: geo.geoFile.xMax,
              ymax: geo.geoFile.yMax,
            }),
          });
          const imageElement = new ImageElement({
            type: "image",
            image: geo.imageUrl,
            georeference: geoReference,
          });

          const geoLayer = new MediaLayer({
            source: imageElement,
            id: geo.id,
            copyright:
              geo.geoFile.copyright && geo.geoFile.copyright.includes("<<year>>")
                ? geo.geoFile.copyright.replace("<<year>>", new Date().getFullYear().toString())
                : geo.geoFile.copyright,
            title: `${geo.geoFile.title}`,
            listMode: geo.geoFile.displayInList ? "show" : "hide",
            opacity: geo.geoFile.opacity,
            spatialReference: { wkid: spatialReferenceWkid.current },
            visible: true,
          });

          arcgisMap.addLayer(geoLayer, shpGeoLayerIndex.current);
          shpGeoLayerIndex.current += 1;
        } else {
          getImageMetadata(geo.imageUrl).then((img) => {
            geoReference = new CornersGeoreference({
              bottomRight: getImageCornerPoint(
                geo.worldData[0],
                geo.worldData[2],
                geo.worldData[4],
                geo.worldData[1],
                geo.worldData[3],
                geo.worldData[5],
                img.naturalWidth,
                img.naturalHeight
              ),
              bottomLeft: getImageCornerPoint(
                geo.worldData[0],
                geo.worldData[2],
                geo.worldData[4],
                geo.worldData[1],
                geo.worldData[3],
                geo.worldData[5],
                0,
                img.naturalHeight
              ),
              topRight: getImageCornerPoint(
                geo.worldData[0],
                geo.worldData[2],
                geo.worldData[4],
                geo.worldData[1],
                geo.worldData[3],
                geo.worldData[5],
                img.naturalWidth,
                0
              ),
              topLeft: new Point({
                x: geo.worldData[4],
                y: geo.worldData[5],
                spatialReference: { wkid: spatialReferenceWkid.current },
              }),
            });
            const imageElement = new ImageElement({
              type: "image",
              image: geo.imageUrl,
              georeference: geoReference,
            });

            const geoLayer = new MediaLayer({
              source: imageElement,
              id: geo.id,
              copyright:
                geo.geoFile.copyright && geo.geoFile.copyright.includes("<<year>>")
                  ? geo.geoFile.copyright.replace("<<year>>", new Date().getFullYear().toString())
                  : geo.geoFile.copyright,
              title: `${geo.geoFile.title}`,
              listMode: geo.geoFile.displayInList ? "show" : "hide",
              opacity: geo.geoFile.opacity,
              spatialReference: { wkid: spatialReferenceWkid.current },
              visible: true,
            });

            arcgisMap.addLayer(geoLayer);
            loadedGeoFileLayers.current.push(geoLayer);
          });
        }
      });

      loadedGeoFileTitles.current = mapContext.loadedGeoFiles.map((x) => x.geoFile.title);
      loadedGeoFileIds.current = mapContext.loadedGeoFiles.map((x) => x.id);
      editingObject.current = null;
    } else {
      loadedGeoFileTitles.current = [];
      loadedGeoFileIds.current = [];
    }
  }, [mapContext, arcgisMap]);

  // ASD Layer visibility
  useEffect(() => {
    if (!arcgisMap) return;
    if (debugUseEffects.current) console.log("[DEV] useEffect [14]: Set ASD layer visibility");

    if (streetContext.currentRecord) {
      setASDLayerVisibility(51, asd51Layer.current, streetContext.currentRecord);
      setASDLayerVisibility(52, asd52Layer.current, streetContext.currentRecord);
      setASDLayerVisibility(53, asd53Layer.current, streetContext.currentRecord);
      setASDLayerVisibility(61, asd61Layer.current, streetContext.currentRecord);
      setASDLayerVisibility(62, asd62Layer.current, streetContext.currentRecord);
      setASDLayerVisibility(63, asd63Layer.current, streetContext.currentRecord);
      setASDLayerVisibility(64, asd64Layer.current, streetContext.currentRecord);
      setASDLayerVisibility(66, asd66Layer.current, streetContext.currentRecord);
    }
  }, [arcgisMap, streetContext.currentRecord]);

  // Map actions and events
  useEffect(() => {
    if (arcgisMap) {
      if (debugUseEffects.current) console.log("[DEV] useEffect [15]: Set map events");
      reactiveUtils.on(
        () => arcgisMap.view.popup,
        "trigger-action",
        (event) => {
          if (
            !arcgisMap.view.popup.viewModel ||
            recordAttributes.current === arcgisMap.view.popup.viewModel.selectedFeature.attributes
          )
            return;

          recordAttributes.current = arcgisMap.view.popup.viewModel.selectedFeature.attributes;

          switch (event.action.id) {
            case "open-street-record":
              checkForUnsavedChanges(handleOpenStreet);
              break;

            case "open-asd-51-record":
              checkForUnsavedChanges(handleOpenASD51);
              break;

            case "open-asd-52-record":
              checkForUnsavedChanges(handleOpenASD52);
              break;

            case "open-asd-53-record":
              checkForUnsavedChanges(handleOpenASD53);
              break;

            case "open-asd-61-record":
              checkForUnsavedChanges(handleOpenASD61);
              break;

            case "open-asd-62-record":
              checkForUnsavedChanges(handleOpenASD62);
              break;

            case "open-asd-63-record":
              checkForUnsavedChanges(handleOpenASD63);
              break;

            case "open-asd-64-record":
              checkForUnsavedChanges(handleOpenASD64);
              break;

            case "open-asd-66-record":
              checkForUnsavedChanges(handleOpenASD66);
              break;

            case "add-property":
              checkForUnsavedChanges(handleAddProperty);
              break;

            case "add-range-properties":
              checkForUnsavedChanges(handleAddRange);
              break;

            case "divide-esu":
              handleDivideEsu();
              break;

            case "assign-esu":
              handleAssignEsu();
              break;

            case "street-street-view":
              handleStreetStreetView();
              break;

            case "street-menu":
              break;

            case "open-property-record":
              checkForUnsavedChanges(handleOpenProperty);
              break;

            case "add-child":
              checkForUnsavedChanges(handleAddChild);
              break;

            case "add-range-children":
              checkForUnsavedChanges(handleAddChildren);
              break;

            case "property-radial-search":
              // Perform a property radial search
              break;

            case "property-activity-list":
              // Add property to the activity list
              break;

            case "property-street-view":
              // Open street view at property
              handlePropertyStreetView();
              break;

            case "property-menu":
              // Open property actions menu
              break;

            default:
              break;
          }

          //Close the popup
          arcgisMap.view.popup.visible = false;
        }
      );
    }
  }, [
    arcgisMap,
    checkForUnsavedChanges,
    handleAddChild,
    handleAddChildren,
    handleAddProperty,
    handleAddRange,
    handleAssignEsu,
    handleDivideEsu,
    handleOpenASD51,
    handleOpenASD52,
    handleOpenASD53,
    handleOpenASD61,
    handleOpenASD62,
    handleOpenASD63,
    handleOpenASD64,
    handleOpenASD66,
    handleOpenProperty,
    handleOpenStreet,
    handleStreetStreetView,
    handlePropertyStreetView,
  ]);

  // Highlight selected features
  useEffect(() => {
    if (
      !arcgisMap ||
      !arcgisMap.layerViews ||
      arcgisMap.layerViews.length === 0 ||
      !mapContext.currentHighlight ||
      (!mapContext.currentHighlight.street &&
        !mapContext.currentHighlight.esu &&
        !mapContext.currentHighlight.asd51 &&
        !mapContext.currentHighlight.asd52 &&
        !mapContext.currentHighlight.asd53 &&
        !mapContext.currentHighlight.asd61 &&
        !mapContext.currentHighlight.asd62 &&
        !mapContext.currentHighlight.asd63 &&
        !mapContext.currentHighlight.asd64 &&
        !mapContext.currentHighlight.asd66 &&
        !mapContext.currentHighlight.property &&
        !mapContext.currentHighlight.selectProperties &&
        !mapContext.currentHighlight.extent)
    )
      return;
    if (debugUseEffects.current) console.log("[DEV] useEffect [16]: Highlight selected features");

    if (mapContext.currentHighlight?.street?.length > 0 && streetLayer.current) {
      arcgisMap.view
        .whenLayerView(streetLayer.current)
        .then(function (layerView) {
          let streetQuery = streetLayer.current.createQuery();
          streetQuery.where = `usrn IN ('${mapContext.currentHighlight.street.join("', '")}')`;
          streetLayer.current.queryFeatures(streetQuery).then(function (result) {
            if (highlightStreet.current) {
              highlightStreet.current.remove();
              highlightStreet.current = null;
            }
            highlightStreet.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting street", error);
          }
        });
    } else if (mapContext.currentHighlight?.street?.length > 0 && llpgStreetLayer.current) {
      arcgisMap.view
        .whenLayerView(llpgStreetLayer.current)
        .then(function (layerView) {
          let streetQuery = llpgStreetLayer.current.createQuery();
          streetQuery.where = `usrn IN ('${mapContext.currentHighlight.street.join("', '")}')`;
          llpgStreetLayer.current.queryFeatures(streetQuery).then(function (result) {
            if (highlightStreet.current) {
              highlightStreet.current.remove();
              highlightStreet.current = null;
            }
            highlightStreet.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting street", error);
          }
        });
    } else if (highlightStreet.current) {
      highlightStreet.current.remove();
      highlightStreet.current = null;
    }

    if (mapContext.currentHighlight?.esu?.length > 0 && streetLayer.current) {
      arcgisMap.view
        .whenLayerView(streetLayer.current)
        .then(function (layerView) {
          let esuQuery = streetLayer.current.createQuery();
          esuQuery.where = `esuId IN ('${mapContext.currentHighlight.esu.join("', '")}')`;
          streetLayer.current.queryFeatures(esuQuery).then(function (result) {
            existingEsuHighlight.current = mapContext.currentHighlight.esu;
            if (highlightESU.current) {
              highlightESU.current.remove();
              highlightESU.current = null;
            }
            highlightESU.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting ESU", error);
          }
        });
    } else if (highlightESU.current) {
      highlightESU.current.remove();
      highlightESU.current = null;
    }

    if (mapContext.currentHighlight?.esu?.length > 0 && backgroundStreetLayer.current) {
      arcgisMap.view
        .whenLayerView(backgroundStreetLayer.current)
        .then(function (layerView) {
          let backgroundEsuQuery = backgroundStreetLayer.current.createQuery();
          backgroundEsuQuery.where = `esuId IN ('${mapContext.currentHighlight.esu.join("', '")}')`;
          backgroundStreetLayer.current.queryFeatures(backgroundEsuQuery).then(function (result) {
            if (highlightBackgroundESU.current) {
              highlightBackgroundESU.current.remove();
              highlightBackgroundESU.current = null;
            }
            highlightBackgroundESU.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting background ESU", error);
          }
        });
    } else if (highlightBackgroundESU.current) {
      highlightBackgroundESU.current.remove();
      highlightBackgroundESU.current = null;
    }

    if (mapContext.currentHighlight?.esu?.length > 0 && unassignedEsusLayer.current) {
      arcgisMap.view
        .whenLayerView(unassignedEsusLayer.current)
        .then(function (layerView) {
          let unassignedEsuQuery = unassignedEsusLayer.current.createQuery();
          unassignedEsuQuery.where = `esuId IN ('${mapContext.currentHighlight.esu.join("', '")}')`;
          unassignedEsusLayer.current.queryFeatures(unassignedEsuQuery).then(function (result) {
            if (highlightUnassignedESU.current) {
              highlightUnassignedESU.current.remove();
              highlightUnassignedESU.current = null;
            }
            highlightUnassignedESU.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting unassigned ESU", error);
          }
        });
    } else if (highlightUnassignedESU.current) {
      highlightUnassignedESU.current.remove();
      highlightUnassignedESU.current = null;
    }

    if (mapContext.currentHighlight?.asd51?.length > 0 && asd51Layer.current) {
      arcgisMap.view
        .whenLayerView(asd51Layer.current)
        .then(function (layerView) {
          let asd51Query = asd51Layer.current.createQuery();
          asd51Query.where = `PkId IN ('${mapContext.currentHighlight.asd51.join("', '")}')`;
          asd51Layer.current.queryFeatures(asd51Query).then(function (result) {
            if (highlightASD51.current) {
              highlightASD51.current.remove();
              highlightASD51.current = null;
            }
            highlightASD51.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting ASD51", error);
          }
        });
    } else if (highlightASD51.current) {
      highlightASD51.current.remove();
      highlightASD51.current = null;
    }

    if (mapContext.currentHighlight?.asd52?.length > 0 && asd52Layer.current) {
      arcgisMap.view
        .whenLayerView(asd52Layer.current)
        .then(function (layerView) {
          let asd52Query = asd52Layer.current.createQuery();
          asd52Query.where = `PkId IN ('${mapContext.currentHighlight.asd52.join("', '")}')`;
          asd52Layer.current.queryFeatures(asd52Query).then(function (result) {
            if (highlightASD52.current) {
              highlightASD52.current.remove();
              highlightASD52.current = null;
            }
            highlightASD52.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting ASD52", error);
          }
        });
    } else if (highlightASD52.current) {
      highlightASD52.current.remove();
      highlightASD52.current = null;
    }

    if (mapContext.currentHighlight?.asd53?.length > 0 && asd53Layer.current) {
      arcgisMap.view
        .whenLayerView(asd53Layer.current)
        .then(function (layerView) {
          let asd53Query = asd53Layer.current.createQuery();
          asd53Query.where = `PkId IN ('${mapContext.currentHighlight.asd53.join("', '")}')`;
          asd53Layer.current.queryFeatures(asd53Query).then(function (result) {
            if (highlightASD53.current) {
              highlightASD53.current.remove();
              highlightASD53.current = null;
            }
            highlightASD53.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting ASD53", error);
          }
        });
    } else if (highlightASD53.current) {
      highlightASD53.current.remove();
      highlightASD53.current = null;
    }

    if (mapContext.currentHighlight?.asd61?.length > 0 && asd61Layer.current) {
      arcgisMap.view
        .whenLayerView(asd61Layer.current)
        .then(function (layerView) {
          let asd61Query = asd61Layer.current.createQuery();
          asd61Query.where = `PkId IN ('${mapContext.currentHighlight.asd61.join("', '")}')`;
          asd61Layer.current.queryFeatures(asd61Query).then(function (result) {
            if (highlightASD61.current) {
              highlightASD61.current.remove();
              highlightASD61.current = null;
            }
            highlightASD61.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting ASD61", error);
          }
        });
    } else if (highlightASD61.current) {
      highlightASD61.current.remove();
      highlightASD61.current = null;
    }

    if (mapContext.currentHighlight.asd62 && mapContext.currentHighlight.asd62.length > 0 && asd62Layer.current) {
      arcgisMap.view
        .whenLayerView(asd62Layer.current)
        .then(function (layerView) {
          let asd62Query = asd62Layer.current.createQuery();
          asd62Query.where = `PkId IN ('${mapContext.currentHighlight.asd62.join("', '")}')`;
          asd62Layer.current.queryFeatures(asd62Query).then(function (result) {
            if (highlightASD62.current) {
              highlightASD62.current.remove();
              highlightASD62.current = null;
            }
            highlightASD62.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting ASD62", error);
          }
        });
    } else if (highlightASD62.current) {
      highlightASD62.current.remove();
      highlightASD62.current = null;
    }

    if (mapContext.currentHighlight?.asd63?.length > 0 && asd63Layer.current) {
      arcgisMap.view
        .whenLayerView(asd63Layer.current)
        .then(function (layerView) {
          let asd63Query = asd63Layer.current.createQuery();
          asd63Query.where = `PkId IN ('${mapContext.currentHighlight.asd63.join("', '")}')`;
          asd63Layer.current.queryFeatures(asd63Query).then(function (result) {
            if (highlightASD63.current) {
              highlightASD63.current.remove();
              highlightASD63.current = null;
            }
            highlightASD63.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting ASD63", error);
          }
        });
    } else if (highlightASD63.current) {
      highlightASD63.current.remove();
      highlightASD63.current = null;
    }

    if (mapContext.currentHighlight?.asd64?.length > 0 && asd64Layer.current) {
      arcgisMap.view
        .whenLayerView(asd64Layer.current)
        .then(function (layerView) {
          let asd64Query = asd64Layer.current.createQuery();
          asd64Query.where = `PkId IN ('${mapContext.currentHighlight.asd64.join("', '")}')`;
          asd64Layer.current.queryFeatures(asd64Query).then(function (result) {
            if (highlightASD64.current) {
              highlightASD64.current.remove();
              highlightASD64.current = null;
            }
            highlightASD64.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting ASD64", error);
          }
        });
    } else if (highlightASD64.current) {
      highlightASD64.current.remove();
      highlightASD64.current = null;
    }

    if (mapContext.currentHighlight?.asd66?.length > 0 && asd66Layer.current) {
      arcgisMap.view
        .whenLayerView(asd66Layer.current)
        .then(function (layerView) {
          let asd66Query = asd66Layer.current.createQuery();
          asd66Query.where = `PkId IN ('${mapContext.currentHighlight.asd66.join("', '")}')`;
          asd66Layer.current.queryFeatures(asd66Query).then(function (result) {
            if (highlightASD66.current) {
              highlightASD66.current.remove();
              highlightASD66.current = null;
            }
            highlightASD66.current = layerView.highlight(result.features);
          });
        })
        .catch((error) => {
          if (error && userContext.current.currentUser.showMessages) {
            console.error("Error highlighting ASD66", error);
          }
        });
    } else if (highlightASD66.current) {
      highlightASD66.current.remove();
      highlightASD66.current = null;
    }

    if (mapContext.currentHighlight?.property?.length > 0) {
      if (propertyLayer.current) {
        arcgisMap.view
          .whenLayerView(propertyLayer.current)
          .then(function (layerView) {
            let propertyQuery = propertyLayer.current.createQuery();
            propertyQuery.where = `uprn IN ('${mapContext.currentHighlight.property.join("', '")}')`;
            propertyLayer.current.queryFeatures(propertyQuery).then(function (result) {
              if (highlightProperty.current) {
                highlightProperty.current.remove();
                highlightProperty.current = null;
              }
              highlightProperty.current = layerView.highlight(result.features);
            });
          })
          .catch((error) => {
            if (error && userContext.current.currentUser.showMessages) {
              console.error("Error highlighting properties: ", error);
            }
          });
      } else if (highlightProperty.current) {
        highlightProperty.current.remove();
        highlightProperty.current = null;
      }

      if (extentLayer.current) {
        arcgisMap.view
          .whenLayerView(extentLayer.current)
          .then(function (layerView) {
            let extentQuery = extentLayer.current.createQuery();
            extentQuery.where = `uprn IN ('${mapContext.currentHighlight.property.join("', '")}')`;
            extentLayer.current.queryFeatures(extentQuery).then(function (result) {
              if (highlightExtent.current) {
                highlightExtent.current.remove();
                highlightExtent.current = null;
              }
              highlightExtent.current = layerView.highlight(result.features);
            });
          })
          .catch((error) => {
            if (error && userContext.current.currentUser.showMessages) {
              console.error("Error highlighting extents: ", error);
            }
          });
      } else if (highlightExtent.current) {
        highlightExtent.current.remove();
        highlightExtent.current = null;
      }
    } else {
      if (highlightProperty.current) {
        highlightProperty.current.remove();
        highlightProperty.current = null;
      }
      if (highlightExtent.current) {
        highlightExtent.current.remove();
        highlightExtent.current = null;
      }
    }

    if (mapContext.currentHighlight?.selectProperties?.length > 0) {
      if (selectPropertyLayer.current) {
        arcgisMap.view
          .whenLayerView(selectPropertyLayer.current)
          .then(function (layerView) {
            let selectPropertyQuery = selectPropertyLayer.current.createQuery();
            selectPropertyQuery.where = `uprn IN ('${mapContext.currentHighlight.selectProperties.join("', '")}')`;
            selectPropertyLayer.current.queryFeatures(selectPropertyQuery).then(function (result) {
              if (highlightSelectProperty.current) {
                highlightSelectProperty.current.remove();
                highlightSelectProperty.current = null;
              }
              highlightSelectProperty.current = layerView.highlight(result.features);
            });
          })
          .catch((error) => {
            if (error && userContext.current.currentUser.showMessages) {
              console.error("Error highlighting selected properties: ", error);
            }
          });
      } else if (highlightSelectProperty.current) {
        highlightSelectProperty.current.remove();
        highlightSelectProperty.current = null;
      }
    } else if (highlightSelectProperty.current) {
      highlightSelectProperty.current.remove();
      highlightSelectProperty.current = null;
    }

    if (mapContext.currentHighlight?.extent?.length > 0) {
      if (extentLayer.current) {
        arcgisMap.view
          .whenLayerView(extentLayer.current)
          .then(function (layerView) {
            let extentQuery = extentLayer.current.createQuery();
            extentQuery.where = `pkId IN ('${mapContext.currentHighlight.extent.join("', '")}')`;
            extentLayer.current.queryFeatures(extentQuery).then(function (result) {
              if (highlightExtent.current) {
                highlightExtent.current.remove();
                highlightExtent.current = null;
              }
              highlightExtent.current = layerView.highlight(result.features);
            });
          })
          .catch((error) => {
            if (error && userContext.current.currentUser.showMessages) {
              console.error("Error highlighting extents: ", error);
            }
          });
      } else if (highlightExtent.current) {
        highlightExtent.current.remove();
        highlightExtent.current = null;
      }
    } else if (highlightExtent.current) {
      highlightExtent.current.remove();
      highlightExtent.current = null;
    }
  }, [arcgisMap, mapContext.currentHighlight]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [17]: Set editing object");
    editingObject.current = {
      objectType: mapContext.currentEditObject?.objectType,
      objectId: mapContext.currentEditObject?.objectId,
    };
  }, [mapContext.currentEditObject]);

  // Edit graphics layer & setting sketch tools
  useEffect(() => {
    const setSnappingLayers = () => {
      if (!arcgisSketch.current) return;

      const LayersUsedForSnapping = [];
      let baseLayer = null;

      if (
        sketchLayer.current &&
        [13, 22, 51, 52, 53, 61, 62, 63, 64, 66].includes(mapContext.currentEditObject.objectType)
      ) {
        LayersUsedForSnapping.push({ layer: sketchLayer.current, enabled: true });
      }

      switch (mapContext.currentEditObject.objectType) {
        case 21: // BLPU
          if (backgroundStreetLayer.current)
            LayersUsedForSnapping.push({ layer: backgroundStreetLayer.current, enabled: true });
          if (unassignedEsusLayer.current)
            LayersUsedForSnapping.push({ layer: unassignedEsusLayer.current, enabled: true });
          if (backgroundProvenanceLayer.current)
            LayersUsedForSnapping.push({ layer: backgroundProvenanceLayer.current, enabled: true });
          if (backgroundPropertyLayer.current)
            LayersUsedForSnapping.push({ layer: backgroundPropertyLayer.current, enabled: true });
          if (propertyLayer.current) LayersUsedForSnapping.push({ layer: propertyLayer.current, enabled: true });
          baseLayersSnapBlpu.current.forEach((layerId) => {
            baseLayer = arcgisMap && arcgisMap.map.findLayerById(layerId);
            if (baseLayer) LayersUsedForSnapping.push({ layer: baseLayer, enabled: true });
          });
          break;

        case 22: // Extent
          if (extentLayer.current) LayersUsedForSnapping.push({ layer: extentLayer.current, enabled: true });
          baseLayersSnapExtent.current.forEach((layerId) => {
            baseLayer = arcgisMap && arcgisMap.map.findLayerById(layerId);
            if (baseLayer) LayersUsedForSnapping.push({ layer: baseLayer, enabled: true });
          });
          break;

        default:
          break;
      }

      if ([51, 52, 53].includes(mapContext.currentEditObject.objectType)) {
        if (asd51Layer.current) LayersUsedForSnapping.push({ layer: asd51Layer.current, enabled: true });
        if (asd52Layer.current) LayersUsedForSnapping.push({ layer: asd52Layer.current, enabled: true });
        if (asd53Layer.current) LayersUsedForSnapping.push({ layer: asd53Layer.current, enabled: true });
        if (streetLayer.current) LayersUsedForSnapping.push({ layer: streetLayer.current, enabled: true });
      }

      if ([61, 62, 63, 64, 66].includes(mapContext.currentEditObject.objectType)) {
        if (asd61Layer.current) LayersUsedForSnapping.push({ layer: asd61Layer.current, enabled: true });
        if (asd62Layer.current) LayersUsedForSnapping.push({ layer: asd62Layer.current, enabled: true });
        if (asd63Layer.current) LayersUsedForSnapping.push({ layer: asd63Layer.current, enabled: true });
        if (asd64Layer.current) LayersUsedForSnapping.push({ layer: asd64Layer.current, enabled: true });
        if (asd66Layer.current) LayersUsedForSnapping.push({ layer: asd66Layer.current, enabled: true });
        if (streetLayer.current) LayersUsedForSnapping.push({ layer: streetLayer.current, enabled: true });
      }

      if ([13, 51, 52, 53, 61, 62, 63, 64, 66].includes(mapContext.currentEditObject.objectType)) {
        if (backgroundStreetLayer.current)
          LayersUsedForSnapping.push({ layer: backgroundStreetLayer.current, enabled: true });
        if (unassignedEsusLayer.current)
          LayersUsedForSnapping.push({ layer: unassignedEsusLayer.current, enabled: true });
        if (streetLayer.current) LayersUsedForSnapping.push({ layer: streetLayer.current, enabled: true });
        if (llpgStreetLayer.current) LayersUsedForSnapping.push({ layer: llpgStreetLayer.current, enabled: true });
        baseLayersSnapEsu.current.forEach((layerId) => {
          baseLayer = arcgisMap && arcgisMap.map.findLayerById(layerId);
          if (baseLayer) LayersUsedForSnapping.push({ layer: baseLayer, enabled: true });
        });
      }

      if (LayersUsedForSnapping.length > 0) arcgisSketch.current.snappingOptions.featureSources = LayersUsedForSnapping;
    };

    if (!arcgisMap || !mapContext.currentEditObject) {
      if (!mapContext.currentEditObject && sketchLayer.current?.graphics.length > 0 && !selectingProperties.current) {
        sketchLayer.current.graphics.removeAll();
        if (
          lastBackgroundExtent.current &&
          (lastBackgroundExtent.current.xmax - lastBackgroundExtent.current.xmin < 1000 ||
            lastBackgroundExtent.current.ymax - lastBackgroundExtent.current.ymin < 1000)
        ) {
          arcgisMap.view.extent = lastBackgroundExtent.current;
          if (debugSetExtents.current)
            console.log("[DEV] Setting view extent [5]:", {
              xLength: lastBackgroundExtent.current.xmax - lastBackgroundExtent.current.xmin,
              yLength: lastBackgroundExtent.current.ymax - lastBackgroundExtent.current.ymin,
              mapType: mapTypeRef.current,
            });
        }
      }
      return;
    }

    if (debugUseEffects.current) console.log("[DEV] useEffect [18]: Set editing layers and snapping");

    if (
      editingObject.current &&
      (mapContext.currentEditObject.objectType !== 22 || mapContext.updatingProvenance) &&
      mapContext.currentEditObject &&
      mapContext.currentEditObject.objectType === editingObject.current.objectType &&
      mapContext.currentEditObject.objectId === editingObject.current.objectId &&
      !mapContext.refreshSketchLayer
    ) {
      if (debugUseEffects.current) console.log("[DEV] useEffect [18A]: Set snapping layers");
      setSnappingLayers();
    } else {
      if (debugUseEffects.current) console.log("[DEV] useEffect [18B]: Add sketch layer");
      sketchLayer.current.graphics.removeAll();
      if (mapType !== "gazetteer") mapContext.onRefreshSketchLayerChange(false);

      let canContinue = false;

      if (mapContext.currentEditObject && mapContext.currentEditObject.objectType) {
        switch (mapContext.currentEditObject.objectType) {
          case 13:
            canContinue =
              !!streetLayer.current ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord);
            break;

          case 21:
            canContinue =
              !!propertyLayer.current ||
              (propertyContext.currentProperty && propertyContext.currentProperty.newProperty) ||
              (propertyContext.currentRecord && propertyContext.currentRecord.newRecord);
            break;

          case 22:
            canContinue = true;
            break;

          case 51:
            canContinue =
              !!asd51Layer.current ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord);
            break;

          case 52:
            canContinue =
              !!asd52Layer.current ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord);
            break;

          case 53:
            canContinue =
              !!asd53Layer.current ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord);
            break;

          case 61:
            canContinue =
              !!asd61Layer.current ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord);
            break;

          case 62:
            canContinue =
              !!asd62Layer.current ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord);
            break;

          case 63:
            canContinue =
              !!asd63Layer.current ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord);
            break;

          case 64:
            canContinue =
              !!asd64Layer.current ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord);
            break;

          case 66:
            canContinue =
              !!asd66Layer.current ||
              (streetContext.currentStreet && streetContext.currentStreet.newStreet) ||
              (streetContext.currentRecord && streetContext.currentRecord.newRecord);
            break;

          default:
            canContinue = false;
            break;
        }
      }

      if (!canContinue) {
        editingObject.current = null;

        if (backgroundStreetLayer.current) {
          backgroundStreetLayer.current.opacity = getLayerOpacity(backgroundStreetLayerTitle, false);
          backgroundStreetLayer.current.popupEnabled = true;
        }
        if (unassignedEsusLayer.current) {
          unassignedEsusLayer.current.opacity = getLayerOpacity(unassignedEsusLayerTitle, false);
          unassignedEsusLayer.current.popupEnabled = true;
        }
        if (streetLayer.current) {
          streetLayer.current.opacity = getLayerOpacity(streetLayerTitle, false);
          streetLayer.current.popupEnabled = true;
        }
        if (llpgStreetLayer.current) {
          llpgStreetLayer.current.opacity = getLayerOpacity(llpgStreetLayerTitle, false);
          llpgStreetLayer.current.popupEnabled = true;
        }
        if (asd51Layer.current) {
          asd51Layer.current.opacity = 1;
          asd51Layer.current.popupEnabled = true;
        }
        if (asd52Layer.current) {
          asd52Layer.current.opacity = 1;
          asd52Layer.current.popupEnabled = true;
        }
        if (asd53Layer.current) {
          asd53Layer.current.opacity = 1;
          asd53Layer.current.popupEnabled = true;
        }
        if (asd61Layer.current) {
          asd61Layer.current.opacity = 1;
          asd61Layer.current.popupEnabled = true;
        }
        if (asd62Layer.current) {
          asd62Layer.current.opacity = 1;
          asd62Layer.current.popupEnabled = true;
        }
        if (asd63Layer.current) {
          asd63Layer.current.opacity = 1;
          asd63Layer.current.popupEnabled = true;
        }
        if (asd64Layer.current) {
          asd64Layer.current.opacity = 1;
          asd64Layer.current.popupEnabled = true;
        }
        if (asd66Layer.current) {
          asd66Layer.current.opacity = 1;
          asd66Layer.current.popupEnabled = true;
        }
        if (backgroundProvenanceLayer.current) {
          backgroundProvenanceLayer.current.opacity = getLayerOpacity(backgroundProvenanceLayerTitle, false);
          backgroundProvenanceLayer.current.popupEnabled = true;
        }
        if (backgroundPropertyLayer.current) {
          backgroundPropertyLayer.current.opacity = getLayerOpacity(backgroundPropertyLayerTitle, false);
          backgroundPropertyLayer.current.popupEnabled = true;
        }
        if (propertyLayer.current) {
          propertyLayer.current.opacity = getLayerOpacity(propertyLayerTitle, false);
          propertyLayer.current.popupEnabled = true;
        }
        if (extentLayer.current) {
          extentLayer.current.opacity = getLayerOpacity(provenanceLayerTitle, false);
          extentLayer.current.popupEnabled = true;
        }
        if (loadedShpFileLayers.current.length > 0) {
          loadedShpFileLayers.current.forEach((layer) => {
            if (!layer.popupEnabled) layer.popupEnabled = true;
          });
        }
        if (loadedGeoFileLayers.current.length > 0) {
          loadedGeoFileLayers.current.forEach((layer) => {
            if (!layer.popupEnabled) layer.popupEnabled = true;
          });
        }

        if (arcgisSketch.current && arcgisMap.querySelector(`.arcgis-sketch-${mapType}`)) {
          sketchLayer.current.listMode = "hide";
          setSketchCreateTools([]);
          arcgisSketch.current.remove();
        }
      } else {
        editingObject.current = {
          objectType: mapContext.currentEditObject.objectType,
          objectId: mapContext.currentEditObject.objectId,
        };

        if (arcgisMap.view.extent) lastBackgroundExtent.current = arcgisMap.view.extent.clone();

        if (streetLayer.current) streetLayer.current.popupEnabled = false;
        if (llpgStreetLayer.current) llpgStreetLayer.current.popupEnabled = false;
        if (asd51Layer.current) asd51Layer.current.popupEnabled = false;
        if (asd52Layer.current) asd52Layer.current.popupEnabled = false;
        if (asd53Layer.current) asd53Layer.current.popupEnabled = false;
        if (asd61Layer.current) asd61Layer.current.popupEnabled = false;
        if (asd62Layer.current) asd62Layer.current.popupEnabled = false;
        if (asd63Layer.current) asd63Layer.current.popupEnabled = false;
        if (asd64Layer.current) asd64Layer.current.popupEnabled = false;
        if (asd66Layer.current) asd66Layer.current.popupEnabled = false;
        if (propertyLayer.current) propertyLayer.current.popupEnabled = false;
        if (extentLayer.current) extentLayer.current.popupEnabled = false;
        if (loadedShpFileLayers.current.length > 0) {
          loadedShpFileLayers.current.forEach((layer) => {
            layer.popupEnabled = false;
          });
        }
        if (loadedGeoFileLayers.current.length > 0) {
          loadedGeoFileLayers.current.forEach((layer) => {
            layer.popupEnabled = false;
          });
        }

        if (
          arcgisSketch.current &&
          ((mapContext.currentEditObject.objectType === 13 &&
            userContext.current &&
            userContext.current.currentUser.editStreet) ||
            ([21, 22].includes(mapContext.currentEditObject.objectType) &&
              userContext.current &&
              userContext.current.currentUser.editProperty) ||
            ([51, 52, 53, 61, 62, 63, 64, 66].includes(mapContext.currentEditObject.objectType) &&
              userContext.current &&
              userContext.current.currentUser.editASD)) &&
          !arcgisMap.querySelector(`.arcgis-sketch-${mapType}`)
        ) {
          arcgisMap.append(arcgisSketch.current);
        }

        let hasGeometry = false;
        let newExtent = new Extent();

        switch (mapContext.currentEditObject.objectType) {
          case 13: // ESU
            if (mapContext.currentMapSearchData.editStreet && !mapContext.updatingEsuGeometry) {
              if (refStreetData.current && refStreetData.current.length > 0) {
                refStreetData.current.forEach((street) => {
                  if (street.esuId === mapContext.currentEditObject.objectId) {
                    sketchLayer.current.graphics.add(createPolylineGraphic(street.geometry));
                    if (street.geometry) {
                      newExtent = bufferOperator.execute(
                        new Polyline({
                          type: "polyline",
                          paths: street.geometry,
                          spatialReference: { wkid: spatialReferenceWkid.current },
                        }),
                        getExtentBuffer(
                          new Polyline({
                            type: "polyline",
                            paths: street.geometry,
                            spatialReference: { wkid: spatialReferenceWkid.current },
                          }).extent
                        )
                      )?.extent;
                      if (newExtent) {
                        backgroundExtent.current = {
                          xmin: newExtent.xmin,
                          ymin: newExtent.ymin,
                          xmax: newExtent.xmax,
                          ymax: newExtent.ymax,
                          spatialReference: { wkid: spatialReferenceWkid.current },
                          zoom: arcgisMap.zoom,
                        };
                        arcgisMap.view.extent = newExtent;
                        if (debugSetExtents.current)
                          console.log("[DEV] Setting view extent [6]:", {
                            xLength: newExtent.xmax - newExtent.xmin,
                            yLength: newExtent.ymax - newExtent.ymin,
                            mapType: mapTypeRef.current,
                          });
                      }
                    }
                  }
                });
              }

              if (backgroundStreetLayer.current)
                backgroundStreetLayer.current.opacity = getLayerOpacity(backgroundStreetLayerTitle, true);
              if (unassignedEsusLayer.current)
                unassignedEsusLayer.current.opacity = getLayerOpacity(unassignedEsusLayerTitle, true);
              if (backgroundProvenanceLayer.current)
                backgroundProvenanceLayer.current.opacity = getLayerOpacity(backgroundProvenanceLayerTitle, true);
              if (backgroundPropertyLayer.current)
                backgroundPropertyLayer.current.opacity = getLayerOpacity(backgroundPropertyLayerTitle, true);
              if (streetLayer.current) streetLayer.current.opacity = getLayerOpacity(streetLayerTitle, true);
              if (llpgStreetLayer.current) llpgStreetLayer.current.opacity = getLayerOpacity(streetLayerTitle, true);

              sketchLayer.current.listMode = "show";
              setSnappingLayers();
            }

            if (mapContext.currentEditObject.objectId > 0) setSketchCreateTools([]);
            else {
              setSketchCreateTools(["polyline"]);
              if (!mapContext.createToolActivated && arcgisSketch.current) {
                arcgisSketch.current.create("polyline");
                mapContext.onCreateToolActivated(true);
              }
            }
            break;

          case 21: // BLPU
            if (mapContext.currentMapSearchData.editProperty) {
              if (refPropertyData.current && refPropertyData.current.length > 0) {
                refPropertyData.current.forEach((property) => {
                  if (Number(property.uprn) === Number(mapContext.currentEditObject.objectId)) {
                    sketchLayer.current.graphics.add(
                      createPointGraphic(
                        property.easting,
                        property.northing,
                        property.logicalStatus,
                        property.classificationCode?.substring(0, 1),
                        true
                      )
                    );
                  }
                });
              }

              if (backgroundStreetLayer.current)
                backgroundStreetLayer.current.opacity = getLayerOpacity(backgroundStreetLayerTitle, true);
              if (unassignedEsusLayer.current)
                unassignedEsusLayer.current.opacity = getLayerOpacity(unassignedEsusLayerTitle, true);
              if (backgroundProvenanceLayer.current)
                backgroundProvenanceLayer.current.opacity = getLayerOpacity(backgroundProvenanceLayerTitle, true);
              if (backgroundPropertyLayer.current)
                backgroundPropertyLayer.current.opacity = getLayerOpacity(backgroundPropertyLayerTitle, true);
              if (propertyLayer.current) propertyLayer.current.opacity = getLayerOpacity(propertyLayerTitle, true);

              sketchLayer.current.listMode = "show";
              setSnappingLayers();
            }

            if (Number(mapContext.currentEditObject.objectId) > 0) {
              setSketchCreateTools([]);
            } else {
              setSketchCreateTools(["point"]);
              if (!mapContext.createToolActivated && arcgisSketch.current) {
                arcgisSketch.current.create("point");
                mapContext.onCreateToolActivated(true);
              }
            }
            break;

          case 22: // Extent
            if (extentData.current && extentData.current.length > 0 && !mapContext.updatingProvenance) {
              extentData.current.forEach((extent) => {
                if (extent.pkId === mapContext.currentEditObject.objectId) {
                  hasGeometry = !!extent.geometry;
                  if (extent.geometry) {
                    sketchLayer.current.graphics.add(
                      createPolygonGraphic(extent.geometry, extent.historical, extent.code)
                    );

                    if (extent.geometry.length > 1) {
                      let extentXmin = 9999999;
                      let extentYmin = 9999999;
                      let extentXmax = 0;
                      let extentYmax = 0;

                      for (let i = 0; i < extent.geometry.length; i++) {
                        const extentPolygon = new Polygon({
                          rings: extent.geometry[i],
                          spatialReference: { wkid: spatialReferenceWkid.cu },
                        });
                        newExtent = bufferOperator.execute(
                          extentPolygon,
                          getExtentBuffer(extentPolygon.extent)
                        )?.extent;
                        if (newExtent) {
                          if (newExtent.xmin < extentXmin) extentXmin = newExtent.xmin;
                          if (newExtent.ymin < extentYmin) extentYmin = newExtent.ymin;
                          if (newExtent.xmax > extentXmax) extentXmax = newExtent.xmax;
                          if (newExtent.ymax > extentYmax) extentYmax = newExtent.ymax;
                        }
                      }
                      backgroundExtent.current = {
                        xmin: extentXmin,
                        ymin: extentYmin,
                        xmax: extentXmax,
                        ymax: extentYmax,
                        spatialReference: { wkid: spatialReferenceWkid.current },
                        zoom: arcgisMap.zoom,
                      };
                      newExtent = new Extent(backgroundExtent.current);
                      arcgisMap.view.extent = newExtent;
                      if (debugSetExtents.current)
                        console.log("[DEV] Setting view extent [7]:", {
                          xLength: newExtent.xmax - newExtent.xmin,
                          yLength: newExtent.ymax - newExtent.ymin,
                          mapType: mapTypeRef.current,
                        });
                    } else {
                      const extentPolygon = new Polygon({
                        rings: extent.geometry,
                        spatialReference: { wkid: spatialReferenceWkid.current },
                      });
                      newExtent = bufferOperator.execute(extentPolygon, getExtentBuffer(extentPolygon.extent))?.extent;
                      if (newExtent) {
                        backgroundExtent.current = {
                          xmin: newExtent.xmin,
                          ymin: newExtent.ymin,
                          xmax: newExtent.xmax,
                          ymax: newExtent.ymax,
                          spatialReference: { wkid: spatialReferenceWkid.current },
                          zoom: arcgisMap.zoom,
                        };
                        arcgisMap.view.extent = newExtent;
                        if (debugSetExtents.current)
                          console.log("[DEV] Setting view extent [8]:", {
                            xLength: newExtent.xmax - newExtent.xmin,
                            yLength: newExtent.ymax - newExtent.ymin,
                            mapType: mapTypeRef.current,
                          });
                      }
                    }
                  }
                }
              });

              if (backgroundStreetLayer.current)
                backgroundStreetLayer.current.opacity = getLayerOpacity(backgroundStreetLayerTitle, true);
              if (unassignedEsusLayer.current)
                unassignedEsusLayer.current.opacity = getLayerOpacity(unassignedEsusLayerTitle, true);
              if (backgroundProvenanceLayer.current)
                backgroundProvenanceLayer.current.opacity = getLayerOpacity(backgroundProvenanceLayerTitle, true);
              if (backgroundPropertyLayer.current)
                backgroundPropertyLayer.current.opacity = getLayerOpacity(backgroundPropertyLayerTitle, true);
              if (extentLayer.current) extentLayer.current.opacity = getLayerOpacity(provenanceLayerTitle, true);
              if (propertyLayer.current) propertyLayer.current.opacity = getLayerOpacity(propertyLayerTitle, true);

              sketchLayer.current.listMode = "show";

              setSnappingLayers();
            }

            if (mapContext.currentEditObject.objectId > 0 && hasGeometry) setSketchCreateTools([]);
            else {
              setSketchCreateTools(["polygon"]);
              if (!mapContext.createToolActivated && arcgisSketch.current) {
                arcgisSketch.current.create("polygon");
                mapContext.onCreateToolActivated(true);
              }
            }
            break;

          case 51: // Maintenance Responsibility
            if (refAsd51Data.current && refAsd51Data.current.length > 0) {
              refAsd51Data.current.forEach((asd) => {
                if (asd.pkId === mapContext.currentEditObject.objectId) {
                  hasGeometry = !!asd.geometry;
                  sketchLayer.current.graphics.add(createPolylineGraphic(asd.geometry));
                  if (hasGeometry && !asd.wholeRoad) {
                    newExtent = getStreetExtent(asd, spatialReferenceWkid.current);

                    if (newExtent) {
                      backgroundExtent.current = {
                        xmin: newExtent.xmin,
                        ymin: newExtent.ymin,
                        xmax: newExtent.xmax,
                        ymax: newExtent.ymax,
                        spatialReference: { wkid: spatialReferenceWkid.current },
                        zoom: arcgisMap.zoom,
                      };
                      arcgisMap.view.extent = newExtent;
                      if (debugSetExtents.current)
                        console.log("[DEV] Setting view extent [9]:", {
                          xLength: newExtent.xmax - newExtent.xmin,
                          yLength: newExtent.ymax - newExtent.ymin,
                          mapType: mapTypeRef.current,
                        });
                    }
                  }
                }
              });

              if (backgroundStreetLayer.current)
                backgroundStreetLayer.current.opacity = getLayerOpacity(backgroundStreetLayerTitle, true);
              if (unassignedEsusLayer.current)
                unassignedEsusLayer.current.opacity = getLayerOpacity(unassignedEsusLayerTitle, true);
              if (backgroundProvenanceLayer.current)
                backgroundProvenanceLayer.current.opacity = getLayerOpacity(backgroundProvenanceLayerTitle, true);
              if (backgroundPropertyLayer.current)
                backgroundPropertyLayer.current.opacity = getLayerOpacity(backgroundPropertyLayerTitle, true);
              if (asd51Layer.current) asd51Layer.current.opacity = 0.5;
              if (asd52Layer.current) asd52Layer.current.opacity = 0.5;
              if (asd53Layer.current) asd53Layer.current.opacity = 0.5;

              sketchLayer.current.listMode = "show";

              setSnappingLayers();
            }

            setSketchCreateTools(["polyline"]);
            if (!mapContext.createToolActivated && arcgisSketch.current) {
              arcgisSketch.current.cancel();
              mapContext.onCreateToolActivated(true);
            }
            break;

          case 52: // Reinstatement Category
            if (refAsd52Data.current && refAsd52Data.current.length > 0) {
              refAsd52Data.current.forEach((asd) => {
                if (asd.pkId === mapContext.currentEditObject.objectId) {
                  hasGeometry = !!asd.geometry;
                  sketchLayer.current.graphics.add(createPolylineGraphic(asd.geometry));
                  if (hasGeometry && !asd.wholeRoad) {
                    newExtent = getStreetExtent(asd, spatialReferenceWkid.current);

                    if (newExtent) {
                      backgroundExtent.current = {
                        xmin: newExtent.xmin,
                        ymin: newExtent.ymin,
                        xmax: newExtent.xmax,
                        ymax: newExtent.ymax,
                        spatialReference: { wkid: spatialReferenceWkid.current },
                        zoom: arcgisMap.zoom,
                      };
                      arcgisMap.view.extent = newExtent;
                      if (debugSetExtents.current)
                        console.log("[DEV] Setting view extent [10]:", {
                          xLength: newExtent.xmax - newExtent.xmin,
                          yLength: newExtent.ymax - newExtent.ymin,
                          mapType: mapTypeRef.current,
                        });
                    }
                  }
                }
              });

              if (backgroundStreetLayer.current)
                backgroundStreetLayer.current.opacity = getLayerOpacity(backgroundStreetLayerTitle, true);
              if (unassignedEsusLayer.current)
                unassignedEsusLayer.current.opacity = getLayerOpacity(unassignedEsusLayerTitle, true);
              if (backgroundProvenanceLayer.current)
                backgroundProvenanceLayer.current.opacity = getLayerOpacity(backgroundProvenanceLayerTitle, true);
              if (backgroundPropertyLayer.current)
                backgroundPropertyLayer.current.opacity = getLayerOpacity(backgroundPropertyLayerTitle, true);
              if (asd51Layer.current) asd51Layer.current.opacity = 0.5;
              if (asd52Layer.current) asd52Layer.current.opacity = 0.5;
              if (asd53Layer.current) asd53Layer.current.opacity = 0.5;

              sketchLayer.current.listMode = "show";

              setSnappingLayers();
            }

            setSketchCreateTools(["polyline"]);
            if (!mapContext.createToolActivated && arcgisSketch.current) {
              arcgisSketch.current.cancel();
              mapContext.onCreateToolActivated(true);
            }
            break;

          case 53: // Special Designation
            if (refAsd53Data.current && refAsd53Data.current.length > 0) {
              refAsd53Data.current.forEach((asd) => {
                if (asd.pkId === mapContext.currentEditObject.objectId) {
                  hasGeometry = !!asd.geometry;
                  sketchLayer.current.graphics.add(createPolylineGraphic(asd.geometry));
                  if (hasGeometry && !asd.wholeRoad) {
                    newExtent = getStreetExtent(asd, spatialReferenceWkid.current);

                    if (newExtent) {
                      backgroundExtent.current = {
                        xmin: newExtent.xmin,
                        ymin: newExtent.ymin,
                        xmax: newExtent.xmax,
                        ymax: newExtent.ymax,
                        spatialReference: { wkid: spatialReferenceWkid.current },
                        zoom: arcgisMap.zoom,
                      };
                      arcgisMap.view.extent = newExtent;
                      if (debugSetExtents.current)
                        console.log("[DEV] Setting view extent [11]:", {
                          xLength: newExtent.xmax - newExtent.xmin,
                          yLength: newExtent.ymax - newExtent.ymin,
                          mapType: mapTypeRef.current,
                        });
                    }
                  }
                }
              });

              if (backgroundStreetLayer.current)
                backgroundStreetLayer.current.opacity = getLayerOpacity(backgroundStreetLayerTitle, true);
              if (unassignedEsusLayer.current)
                unassignedEsusLayer.current.opacity = getLayerOpacity(unassignedEsusLayerTitle, true);
              if (backgroundProvenanceLayer.current)
                backgroundProvenanceLayer.current.opacity = getLayerOpacity(backgroundProvenanceLayerTitle, true);
              if (backgroundPropertyLayer.current)
                backgroundPropertyLayer.current.opacity = getLayerOpacity(backgroundPropertyLayerTitle, true);
              if (asd51Layer.current) asd51Layer.current.opacity = 0.5;
              if (asd52Layer.current) asd52Layer.current.opacity = 0.5;
              if (asd53Layer.current) asd53Layer.current.opacity = 0.5;

              sketchLayer.current.listMode = "show";

              setSnappingLayers();
            }

            setSketchCreateTools(["polyline"]);
            if (!mapContext.createToolActivated && arcgisSketch.current) {
              arcgisSketch.current.cancel();
              mapContext.onCreateToolActivated(true);
            }
            break;

          case 61: // Interest
            if (refAsd61Data.current && refAsd61Data.current.length > 0) {
              refAsd61Data.current.forEach((asd) => {
                if (asd.pkId === mapContext.currentEditObject.objectId) {
                  hasGeometry = !!asd.geometry;
                  sketchLayer.current.graphics.add(createPolylineGraphic(asd.geometry));
                  if (hasGeometry && !asd.wholeRoad) {
                    newExtent = getStreetExtent(asd, spatialReferenceWkid.current);

                    if (newExtent) {
                      backgroundExtent.current = {
                        xmin: newExtent.xmin,
                        ymin: newExtent.ymin,
                        xmax: newExtent.xmax,
                        ymax: newExtent.ymax,
                        spatialReference: { wkid: spatialReferenceWkid.current },
                        zoom: arcgisMap.zoom,
                      };
                      arcgisMap.view.extent = newExtent;
                      if (debugSetExtents.current)
                        console.log("[DEV] Setting view extent [12]:", {
                          xLength: newExtent.xmax - newExtent.xmin,
                          yLength: newExtent.ymax - newExtent.ymin,
                          mapType: mapTypeRef.current,
                        });
                    }
                  }
                }
              });

              if (backgroundStreetLayer.current)
                backgroundStreetLayer.current.opacity = getLayerOpacity(backgroundStreetLayerTitle, true);
              if (unassignedEsusLayer.current)
                unassignedEsusLayer.current.opacity = getLayerOpacity(unassignedEsusLayerTitle, true);
              if (backgroundProvenanceLayer.current)
                backgroundProvenanceLayer.current.opacity = getLayerOpacity(backgroundProvenanceLayerTitle, true);
              if (backgroundPropertyLayer.current)
                backgroundPropertyLayer.current.opacity = getLayerOpacity(backgroundPropertyLayerTitle, true);
              if (asd61Layer.current) asd61Layer.current.opacity = 0.5;
              if (asd62Layer.current) asd62Layer.current.opacity = 0.5;
              if (asd63Layer.current) asd63Layer.current.opacity = 0.5;
              if (asd64Layer.current) asd64Layer.current.opacity = 0.5;
              if (asd66Layer.current) asd66Layer.current.opacity = 0.5;

              sketchLayer.current.listMode = "show";

              setSnappingLayers();
            }

            setSketchCreateTools(["polyline"]);
            if (!mapContext.createToolActivated && arcgisSketch.current) {
              arcgisSketch.current.cancel();
              mapContext.onCreateToolActivated(true);
            }
            break;

          case 62: // Construction
            if (refAsd62Data.current && refAsd62Data.current.length > 0) {
              refAsd62Data.current.forEach((asd) => {
                if (asd.pkId === mapContext.currentEditObject.objectId) {
                  hasGeometry = !!asd.geometry;
                  sketchLayer.current.graphics.add(createPolylineGraphic(asd.geometry));
                  if (hasGeometry && !asd.wholeRoad) {
                    newExtent = getStreetExtent(asd, spatialReferenceWkid.current);

                    if (newExtent) {
                      backgroundExtent.current = {
                        xmin: newExtent.xmin,
                        ymin: newExtent.ymin,
                        xmax: newExtent.xmax,
                        ymax: newExtent.ymax,
                        spatialReference: { wkid: spatialReferenceWkid.current },
                        zoom: arcgisMap.zoom,
                      };
                      arcgisMap.view.extent = newExtent;
                      if (debugSetExtents.current)
                        console.log("[DEV] Setting view extent [13]:", {
                          xLength: newExtent.xmax - newExtent.xmin,
                          yLength: newExtent.ymax - newExtent.ymin,
                          mapType: mapTypeRef.current,
                        });
                    }
                  }
                }
              });

              if (backgroundStreetLayer.current)
                backgroundStreetLayer.current.opacity = getLayerOpacity(backgroundStreetLayerTitle, true);
              if (unassignedEsusLayer.current)
                unassignedEsusLayer.current.opacity = getLayerOpacity(unassignedEsusLayerTitle, true);
              if (backgroundProvenanceLayer.current)
                backgroundProvenanceLayer.current.opacity = getLayerOpacity(backgroundProvenanceLayerTitle, true);
              if (backgroundPropertyLayer.current)
                backgroundPropertyLayer.current.opacity = getLayerOpacity(backgroundPropertyLayerTitle, true);
              if (asd61Layer.current) asd61Layer.current.opacity = 0.5;
              if (asd62Layer.current) asd62Layer.current.opacity = 0.5;
              if (asd63Layer.current) asd63Layer.current.opacity = 0.5;
              if (asd64Layer.current) asd64Layer.current.opacity = 0.5;
              if (asd66Layer.current) asd66Layer.current.opacity = 0.5;

              sketchLayer.current.listMode = "show";

              setSnappingLayers();
            }

            setSketchCreateTools(["polyline"]);
            if (!mapContext.createToolActivated && arcgisSketch.current) {
              arcgisSketch.current.cancel();
              mapContext.onCreateToolActivated(true);
            }
            break;

          case 63: // Special Designation
            if (refAsd63Data.current && refAsd63Data.current.length > 0) {
              refAsd63Data.current.forEach((asd) => {
                if (asd.pkId === mapContext.currentEditObject.objectId) {
                  hasGeometry = !!asd.geometry;
                  sketchLayer.current.graphics.add(createPolylineGraphic(asd.geometry));
                  if (hasGeometry && !asd.wholeRoad) {
                    newExtent = getStreetExtent(asd, spatialReferenceWkid.current);

                    if (newExtent) {
                      backgroundExtent.current = {
                        xmin: newExtent.xmin,
                        ymin: newExtent.ymin,
                        xmax: newExtent.xmax,
                        ymax: newExtent.ymax,
                        spatialReference: { wkid: spatialReferenceWkid.current },
                        zoom: arcgisMap.zoom,
                      };
                      arcgisMap.view.extent = newExtent;
                      if (debugSetExtents.current)
                        console.log("[DEV] Setting view extent [14]:", {
                          xLength: newExtent.xmax - newExtent.xmin,
                          yLength: newExtent.ymax - newExtent.ymin,
                          mapType: mapTypeRef.current,
                        });
                    }
                  }
                }
              });

              if (backgroundStreetLayer.current)
                backgroundStreetLayer.current.opacity = getLayerOpacity(backgroundStreetLayerTitle, true);
              if (unassignedEsusLayer.current)
                unassignedEsusLayer.current.opacity = getLayerOpacity(unassignedEsusLayerTitle, true);
              if (backgroundProvenanceLayer.current)
                backgroundProvenanceLayer.current.opacity = getLayerOpacity(backgroundProvenanceLayerTitle, true);
              if (backgroundPropertyLayer.current)
                backgroundPropertyLayer.current.opacity = getLayerOpacity(backgroundPropertyLayerTitle, true);
              if (asd61Layer.current) asd61Layer.current.opacity = 0.5;
              if (asd62Layer.current) asd62Layer.current.opacity = 0.5;
              if (asd63Layer.current) asd63Layer.current.opacity = 0.5;
              if (asd64Layer.current) asd64Layer.current.opacity = 0.5;
              if (asd66Layer.current) asd66Layer.current.opacity = 0.5;

              sketchLayer.current.listMode = "show";

              setSnappingLayers();
            }

            setSketchCreateTools(["polyline"]);
            if (!mapContext.createToolActivated && arcgisSketch.current) {
              arcgisSketch.current.cancel();
              mapContext.onCreateToolActivated(true);
            }
            break;

          case 64: // Height, Width & Weight Restriction
            if (refAsd64Data.current && refAsd64Data.current.length > 0) {
              refAsd64Data.current.forEach((asd) => {
                if (asd.pkId === mapContext.currentEditObject.objectId) {
                  hasGeometry = !!asd.geometry;
                  sketchLayer.current.graphics.add(createPolylineGraphic(asd.geometry));
                  if (hasGeometry && !asd.wholeRoad) {
                    newExtent = getStreetExtent(asd, spatialReferenceWkid.current);

                    if (newExtent) {
                      backgroundExtent.current = {
                        xmin: newExtent.xmin,
                        ymin: newExtent.ymin,
                        xmax: newExtent.xmax,
                        ymax: newExtent.ymax,
                        spatialReference: { wkid: spatialReferenceWkid.current },
                        zoom: arcgisMap.zoom,
                      };
                      arcgisMap.view.extent = newExtent;
                      if (debugSetExtents.current)
                        console.log("[DEV] Setting view extent [15]:", {
                          xLength: newExtent.xmax - newExtent.xmin,
                          yLength: newExtent.ymax - newExtent.ymin,
                          mapType: mapTypeRef.current,
                        });
                    }
                  }
                }
              });

              if (backgroundStreetLayer.current)
                backgroundStreetLayer.current.opacity = getLayerOpacity(backgroundStreetLayerTitle, true);
              if (unassignedEsusLayer.current)
                unassignedEsusLayer.current.opacity = getLayerOpacity(unassignedEsusLayerTitle, true);
              if (backgroundProvenanceLayer.current)
                backgroundProvenanceLayer.current.opacity = getLayerOpacity(backgroundProvenanceLayerTitle, true);
              if (backgroundPropertyLayer.current)
                backgroundPropertyLayer.current.opacity = getLayerOpacity(backgroundPropertyLayerTitle, true);
              if (asd61Layer.current) asd61Layer.current.opacity = 0.5;
              if (asd62Layer.current) asd62Layer.current.opacity = 0.5;
              if (asd63Layer.current) asd63Layer.current.opacity = 0.5;
              if (asd64Layer.current) asd64Layer.current.opacity = 0.5;
              if (asd66Layer.current) asd66Layer.current.opacity = 0.5;

              sketchLayer.current.listMode = "show";

              setSnappingLayers();
            }

            setSketchCreateTools(["polyline"]);
            if (!mapContext.createToolActivated && arcgisSketch.current) {
              arcgisSketch.current.cancel();
              mapContext.onCreateToolActivated(true);
            }
            break;

          case 66: // Public right of way
            if (refAsd66Data.current && refAsd66Data.current.length > 0) {
              refAsd66Data.current.forEach((asd) => {
                if (asd.pkId === mapContext.currentEditObject.objectId) {
                  hasGeometry = !!asd.geometry;
                  sketchLayer.current.graphics.add(createPolylineGraphic(asd.geometry));
                  if (hasGeometry && !asd.defMapGeometryType) {
                    newExtent = getStreetExtent(asd, spatialReferenceWkid.current);

                    if (newExtent) {
                      backgroundExtent.current = {
                        xmin: newExtent.xmin,
                        ymin: newExtent.ymin,
                        xmax: newExtent.xmax,
                        ymax: newExtent.ymax,
                        spatialReference: { wkid: spatialReferenceWkid.current },
                        zoom: arcgisMap.zoom,
                      };
                      arcgisMap.view.extent = newExtent;
                      if (debugSetExtents.current)
                        console.log("[DEV] Setting view extent [16]:", {
                          xLength: newExtent.xmax - newExtent.xmin,
                          yLength: newExtent.ymax - newExtent.ymin,
                          mapType: mapTypeRef.current,
                        });
                    }
                  }
                }
              });

              if (backgroundStreetLayer.current)
                backgroundStreetLayer.current.opacity = getLayerOpacity(backgroundStreetLayerTitle, true);
              if (unassignedEsusLayer.current)
                unassignedEsusLayer.current.opacity = getLayerOpacity(unassignedEsusLayerTitle, true);
              if (backgroundProvenanceLayer.current)
                backgroundProvenanceLayer.current.opacity = getLayerOpacity(backgroundProvenanceLayerTitle, true);
              if (backgroundPropertyLayer.current)
                backgroundPropertyLayer.current.opacity = getLayerOpacity(backgroundPropertyLayerTitle, true);
              if (asd61Layer.current) asd61Layer.current.opacity = 0.5;
              if (asd62Layer.current) asd62Layer.current.opacity = 0.5;
              if (asd63Layer.current) asd63Layer.current.opacity = 0.5;
              if (asd64Layer.current) asd64Layer.current.opacity = 0.5;
              if (asd66Layer.current) asd66Layer.current.opacity = 0.5;

              sketchLayer.current.listMode = "show";

              setSnappingLayers();
            }

            setSketchCreateTools(["polyline"]);
            if (!mapContext.createToolActivated && arcgisSketch.current) {
              arcgisSketch.current.cancel();
              mapContext.onCreateToolActivated(true);
            }
            break;

          default:
            const editingGraphic = editingObject.current && editingObject.current.objectType;
            if (backgroundStreetLayer.current) {
              backgroundStreetLayer.current.opacity = getLayerOpacity(backgroundStreetLayerTitle, false);
              if (!editingGraphic) backgroundStreetLayer.current.popupEnabled = true;
            }
            if (unassignedEsusLayer.current) {
              unassignedEsusLayer.current.opacity = getLayerOpacity(unassignedEsusLayerTitle, false);
              if (!editingGraphic) unassignedEsusLayer.current.popupEnabled = true;
            }
            if (streetLayer.current) {
              streetLayer.current.opacity = getLayerOpacity(streetLayerTitle, false);
              if (!editingGraphic) streetLayer.current.popupEnabled = true;
            }
            if (llpgStreetLayer.current) {
              llpgStreetLayer.current.opacity = getLayerOpacity(streetLayerTitle, false);
              if (!editingGraphic) llpgStreetLayer.current.popupEnabled = true;
            }
            if (asd51Layer.current) {
              asd51Layer.current.opacity = 1;
              if (!editingGraphic) asd51Layer.current.popupEnabled = true;
            }
            if (asd52Layer.current) {
              asd52Layer.current.opacity = 1;
              if (!editingGraphic) asd52Layer.current.popupEnabled = true;
            }
            if (asd53Layer.current) {
              asd53Layer.current.opacity = 1;
              if (!editingGraphic) asd53Layer.current.popupEnabled = true;
            }
            if (asd61Layer.current) {
              asd61Layer.current.opacity = 1;
              if (!editingGraphic) asd61Layer.current.popupEnabled = true;
            }
            if (asd62Layer.current) {
              asd62Layer.current.opacity = 1;
              if (!editingGraphic) asd62Layer.current.popupEnabled = true;
            }
            if (asd63Layer.current) {
              asd63Layer.current.opacity = 1;
              if (!editingGraphic) asd63Layer.current.popupEnabled = true;
            }
            if (asd64Layer.current) {
              asd64Layer.current.opacity = 1;
              if (!editingGraphic) asd64Layer.current.popupEnabled = true;
            }
            if (asd66Layer.current) {
              asd66Layer.current.opacity = 1;
              if (!editingGraphic) asd66Layer.current.popupEnabled = true;
            }
            if (backgroundProvenanceLayer.current) {
              backgroundProvenanceLayer.current.opacity = getLayerOpacity(backgroundProvenanceLayerTitle, false);
              if (!editingGraphic) backgroundProvenanceLayer.current.popupEnabled = true;
            }
            if (backgroundPropertyLayer.current) {
              backgroundPropertyLayer.current.opacity = getLayerOpacity(backgroundPropertyLayerTitle, false);
              if (!editingGraphic) backgroundPropertyLayer.current.popupEnabled = true;
            }
            if (propertyLayer.current) {
              propertyLayer.current.opacity = getLayerOpacity(propertyLayerTitle, false);
              if (!editingGraphic) propertyLayer.current.popupEnabled = true;
            }
            if (extentLayer.current) {
              extentLayer.current.opacity = getLayerOpacity(provenanceLayerTitle, false);
              if (!editingGraphic) extentLayer.current.popupEnabled = true;
            }
            if (loadedShpFileLayers.current.length > 0 && !editingGraphic) {
              loadedShpFileLayers.current.forEach((layer) => {
                layer.popupEnabled = true;
              });
            }
            if (loadedGeoFileLayers.current.length > 0 && !editingGraphic) {
              loadedGeoFileLayers.current.forEach((layer) => {
                layer.popupEnabled = true;
              });
            }

            if (arcgisSketch.current && arcgisMap.querySelector(`.arcgis-sketch-${mapType}`)) {
              sketchLayer.current.listMode = "hide";
              setSketchCreateTools([]);
              arcgisSketch.current.remove();
            }
            break;
        }
      }
    }
  }, [
    arcgisMap,
    mapContext,
    streetContext.currentStreet,
    streetContext.currentRecord,
    propertyContext.currentProperty,
    propertyContext.currentRecord,
    mapType,
    showHistoricalExtentData,
  ]);

  // Refresh ESU geometry
  useEffect(() => {
    if (mapContext.refreshEsuGeometry && streetLayer.current && !streetLayer.current?.visible) {
      if (debugUseEffects.current) console.log("[DEV] useEffect [19]: Refresh ESU geometry");

      let mapStreets = [];
      const currentStreets = mapContext.currentMapSearchData.editStreet
        ? mapContext.currentMapSearchData.streets.filter(
            (x) => x.usrn.toString() === mapContext.currentMapSearchData.editStreet.toString()
          )
        : mapContext.currentMapSearchData.streets;

      currentStreets.forEach((street) => {
        if (street.esus && street.esus.length > 0) {
          mapStreets = mapStreets.concat(
            street.esus.map((esu) => {
              return {
                usrn: street.usrn,
                description: street.description,
                language: street.language,
                locality: street.locality,
                town: street.town,
                type: street.type,
                state: isScottish.current ? esu.state : street.state,
                esuId: esu.esuId,
                geometry: esu.geometry,
              };
            })
          );
        }
      });

      const streetFeatures =
        mapStreets &&
        mapStreets.map((rec, index) => ({
          geometry: {
            type: "polyline",
            paths:
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.geometry
                : rec.geometry,
            spatialReference: { wkid: spatialReferenceWkid.current },
          },
          attributes: {
            ObjectID: index,
            USRN: rec.usrn.toString(),
            EsuId: rec.esuId.toString(),
            Description: streetToTitleCase(
              mapContext.currentStreet &&
                mapContext.currentStreet.usrn &&
                mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? `${rec.description ? rec.description : "Unknown"}${
                    mapContext.currentStreet.locality && mapContext.currentStreet.locality !== "Unassigned"
                      ? " " + mapContext.currentStreet.locality
                      : ""
                  }${
                    mapContext.currentStreet.town && mapContext.currentStreet.town !== "Unassigned"
                      ? " " + mapContext.currentStreet.town
                      : ""
                  }`
                : `${rec.description ? rec.description : "Unknown"}${
                    rec.locality && rec.locality !== "Unassigned" ? " " + rec.locality : ""
                  }${rec.town && rec.town !== "Unassigned" ? " " + rec.town : ""}`
            ),
            State:
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.state
                : rec.state,
            Type:
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.type
                : rec.type
                  ? rec.type
                  : 1,
            StateLabel: GetStreetStateLabel(
              mapContext.currentStreet &&
                mapContext.currentStreet.usrn &&
                mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.state
                : rec.state,
              false
            ),
            TypeLabel: GetStreetTypeLabel(
              mapContext.currentStreet &&
                mapContext.currentStreet.usrn &&
                mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.type
                : rec.type
                  ? rec.type
                  : 1,
              isScottish.current,
              false
            ),
            SymbolCode: `${
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.type
                : rec.type
                  ? rec.type
                  : "1"
            }, ${
              mapContext.currentStreet &&
              mapContext.currentStreet.usrn &&
              mapContext.currentStreet.usrn.toString() === rec.usrn.toString()
                ? mapContext.currentStreet.state
                : rec.state
                  ? rec.state
                  : "2"
            }`,
            // layerTitle: streetLayerTitle,
          },
        }));

      streetLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteStreets = { deleteFeatures: result.features };
          applyEditsToLayer(streetLayer.current, deleteStreets);
        }

        if (mapStreets?.length > 0) {
          const addStreets = { addFeatures: streetFeatures };
          applyEditsToLayer(streetLayer.current, addStreets);
          streetLayer.current.visible = true;
          streetLayer.current.popupEnabled = false;
          if (highlightESU.current && existingEsuHighlight.current?.length > 0) {
            // Force the highlight to redraw as the layer has been refreshed and the highlight graphic may have been removed
            mapContext.onHighlightClear();
            mapContext.onHighlightListItem("esu", existingEsuHighlight.current);
          }
        }
      });

      mapContext.onUpdatingEsuGeometry(false);
    }
  }, [mapContext, mapContext.refreshEsuGeometry]);

  // Refresh polygon geometry
  useEffect(() => {
    if (mapContext.refreshProvenance && extentData.current?.length > 0 && extentLayer.current) {
      if (debugUseEffects.current) console.log("[DEV] useEffect [20]: Refresh polygon geometry");
      const editingGraphic = !!editingObject.current && !!editingObject.current.objectType;
      const extentFeatures = [];
      let index = 0;

      for (let i = 0; i < extentData.current.length; i++) {
        const rec = extentData.current[i];
        if (rec.geometry[0][0].length > 2) {
          for (let j = 0; j < rec.geometry.length; j++) {
            const element = rec.geometry[j];
            extentFeatures.push({
              geometry: {
                type: "polygon",
                rings: element,
                spatialReference: { wkid: spatialReferenceWkid.current },
              },
              attributes: {
                ObjectID: index,
                PkId: rec.pkId,
                UPRN: rec.uprn,
                Code: rec.code,
                SymbolCode: `${rec.historical ? "1" : "0"}, ${rec.code}`,
                CodeLabel: GetProvenanceLabel(rec.code, isScottish.current),
                // layerTitle: provenanceLayerTitle,
              },
            });
            index++;
          }
        } else {
          extentFeatures.push({
            geometry: {
              type: "polygon",
              rings: rec.geometry,
              spatialReference: { wkid: spatialReferenceWkid.current },
            },
            attributes: {
              ObjectID: i,
              PkId: rec.pkId,
              UPRN: rec.uprn,
              Code: rec.code,
              SymbolCode: `${rec.historical ? "1" : "0"}, ${rec.code}`,
              CodeLabel: GetProvenanceLabel(rec.code, isScottish.current),
            },
          });
        }
      }

      extentLayer.current.queryFeatures().then((result) => {
        if (result.features.length > 0) {
          const deleteExtents = { deleteFeatures: result.features };
          applyEditsToLayer(extentLayer.current, deleteExtents);
        }

        if (extentData.current?.length > 0) {
          const addExtents = { addFeatures: extentFeatures };
          applyEditsToLayer(extentLayer.current, addExtents);
          if (editingGraphic) {
            extentLayer.current.popupEnabled = false;
            if (highlightExtent.current && mapContext.currentHighlight.extent?.length > 0) {
              // Force the highlight to redraw as the layer has been refreshed and the highlight graphic may have been removed
              const existingHighlight = mapContext.currentHighlight.extent;
              mapContext.onHighlightClear();
              mapContext.onHighlightListItem("extent", existingHighlight);
            }
          }
        }
      });

      mapContext.onUpdatingProvenance(false);
    }
  }, [mapContext, mapContext.refreshPolygonGeometry]);

  // Capture coordinates
  useEffect(() => {
    if (!arcgisMap || !arcgisCoordinateConversion.current) return;
    if (debugUseEffects.current) console.log("[DEV] useEffect [21]: Capture coordinates mode change");

    if (
      arcgisCoordinateConversion.current &&
      ["property", "streetStart", "streetEnd", "divideEsu"].includes(mapContext.currentPointCaptureMode)
    ) {
      arcgisCoordinateConversion.current.mode = "capture";
      currentPointCaptureModeRef.current = mapContext.currentPointCaptureMode;
    } else if (mapContext.currentPointCaptureMode === "assignEsu") {
      currentPointCaptureModeRef.current = mapContext.currentPointCaptureMode;
      if (backgroundStreetLayer.current) backgroundStreetLayer.current.popupEnabled = false;
      if (unassignedEsusLayer.current) unassignedEsusLayer.current.popupEnabled = false;
      if (streetLayer.current) streetLayer.current.popupEnabled = false;
      if (llpgStreetLayer.current) llpgStreetLayer.current.popupEnabled = false;
      if (asd51Layer.current) asd51Layer.current.popupEnabled = false;
      if (asd52Layer.current) asd52Layer.current.popupEnabled = false;
      if (asd53Layer.current) asd53Layer.current.popupEnabled = false;
      if (asd61Layer.current) asd61Layer.current.popupEnabled = false;
      if (asd62Layer.current) asd62Layer.current.popupEnabled = false;
      if (asd63Layer.current) asd63Layer.current.popupEnabled = false;
      if (asd64Layer.current) asd64Layer.current.popupEnabled = false;
      if (asd66Layer.current) asd66Layer.current.popupEnabled = false;
      if (loadedShpFileLayers.current.length > 0) {
        loadedShpFileLayers.current.forEach((layer) => {
          layer.popupEnabled = false;
        });
      }
      if (loadedGeoFileLayers.current.length > 0) {
        loadedGeoFileLayers.current.forEach((layer) => {
          layer.popupEnabled = false;
        });
      }
    } else if (mapContext.currentPointCaptureMode === "selectProperties") {
      currentPointCaptureModeRef.current = mapContext.currentPointCaptureMode;
    } else {
      arcgisCoordinateConversion.current.mode = "live";
      currentPointCaptureModeRef.current = null;
      const editingGraphic = editingObject.current && editingObject.current.objectType;
      if (backgroundStreetLayer.current && !editingGraphic && !backgroundStreetLayer.current.popupEnabled)
        backgroundStreetLayer.current.popupEnabled = true;
      if (unassignedEsusLayer.current && !editingGraphic && !unassignedEsusLayer.current.popupEnabled)
        unassignedEsusLayer.current.popupEnabled = true;
      if (streetLayer.current && !editingGraphic && !streetLayer.current.popupEnabled)
        streetLayer.current.popupEnabled = true;
      if (llpgStreetLayer.current && !editingGraphic && !llpgStreetLayer.current.popupEnabled)
        llpgStreetLayer.current.popupEnabled = true;
      if (asd51Layer.current && !editingGraphic && !asd51Layer.current.popupEnabled)
        asd51Layer.current.popupEnabled = true;
      if (asd52Layer.current && !editingGraphic && !asd52Layer.current.popupEnabled)
        asd52Layer.current.popupEnabled = true;
      if (asd53Layer.current && !editingGraphic && !asd53Layer.current.popupEnabled)
        asd53Layer.current.popupEnabled = true;
      if (asd61Layer.current && !editingGraphic && !asd61Layer.current.popupEnabled)
        asd61Layer.current.popupEnabled = true;
      if (asd62Layer.current && !editingGraphic && !asd62Layer.current.popupEnabled)
        asd62Layer.current.popupEnabled = true;
      if (asd63Layer.current && !editingGraphic && !asd63Layer.current.popupEnabled)
        asd63Layer.current.popupEnabled = true;
      if (asd64Layer.current && !editingGraphic && !asd64Layer.current.popupEnabled)
        asd64Layer.current.popupEnabled = true;
      if (asd66Layer.current && !editingGraphic && !asd66Layer.current.popupEnabled)
        asd66Layer.current.popupEnabled = true;
      if (loadedShpFileLayers.current.length > 0 && !editingGraphic) {
        loadedShpFileLayers.current.forEach((layer) => {
          if (!layer.popupEnabled) {
            layer.popupEnabled = true;
          }
        });
      }
      if (loadedGeoFileLayers.current.length > 0 && !editingGraphic) {
        loadedGeoFileLayers.current.forEach((layer) => {
          if (!layer.popupEnabled) {
            layer.popupEnabled = true;
          }
        });
      }
    }
  }, [arcgisMap, mapContext.currentPointCaptureMode]);

  // Property wizard finalise
  useEffect(() => {
    if (
      propertyContext.wizardData &&
      propertyContext.wizardData.source &&
      propertyContext.wizardData.source === "map"
    ) {
      if (debugUseEffects.current) console.log("[DEV] useEffect [22]: Property wizard finalise");
      if (!propertyContext.wizardData.savedProperty || propertyContext.wizardData.savedProperty.length === 0) {
        if (propertyContext.wizardData.type === "close") {
          setOpenPropertyWizard(false);
          displayingWizard.current = false;
        }
      } else {
        const savedUprn = Array.isArray(propertyContext.wizardData.savedProperty)
          ? propertyContext.wizardData.savedProperty[0].uprn
          : propertyContext.wizardData.savedProperty.uprn;

        if (Number(savedUprn) !== Number(wizardUprn.current)) {
          wizardUprn.current = savedUprn;

          switch (propertyContext.wizardData.type) {
            case "view":
              const rangeEngLpis = Array.isArray(propertyContext.wizardData.savedProperty)
                ? propertyContext.wizardData.savedProperty[0].lpis.filter((x) => x.language === "ENG")
                : propertyContext.wizardData.savedProperty.lpis.filter((x) => x.language === "ENG");
              switch (propertyContext.wizardData.variant) {
                case "range":
                  doOpenRecord(
                    {
                      type: 15,
                      usrn: rangeEngLpis[0].usrn,
                    },
                    rangeEngLpis[0].uprn,
                    searchContext.currentSearchData.results,
                    mapContext,
                    streetContext,
                    propertyContext,
                    userContext.current,
                    isScottish.current
                  );
                  break;

                case "rangeChildren":
                  const parentRec = {
                    type: 24,
                    uprn: propertyContext.wizardData.parent.uprn,
                    address: propertyContext.wizardData.parent.address,
                    formattedAddress: propertyContext.wizardData.parent.address,
                    postcode: propertyContext.wizardData.parent.postcode,
                    easting: propertyContext.wizardData.parent.xcoordinate,
                    northing: propertyContext.wizardData.parent.ycoordinate,
                    logical_status: propertyContext.wizardData.parent.logicalStatus,
                    classification_code: propertyContext.wizardData.parent.blpuClass,
                  };
                  const relatedObj = {
                    parent: propertyContext.wizardData.parent.uprn,
                    property: rangeEngLpis[0].uprn,
                    currentUser: userContext.current.currentUser,
                  };

                  // TODO: uncommented below code when want to reintroduce asking to edit historic.
                  // if (parentRec.logical_status === 8) {
                  //   historicRec.current = { property: parentRec, related: relatedObj };
                  //   setOpenHistoricProperty(true);
                  // } else
                  doOpenRecord(
                    parentRec,
                    relatedObj,
                    searchContext.currentSearchData.results,
                    mapContext,
                    streetContext,
                    propertyContext,
                    userContext.current,
                    isScottish.current
                  );
                  break;

                default:
                  const engLpis = propertyContext.wizardData.savedProperty.lpis.filter((x) => x.language === "ENG");
                  const savedRec = {
                    type: 24,
                    uprn: propertyContext.wizardData.savedProperty.uprn,
                    address: engLpis[0].address,
                    formattedAddress: engLpis[0].address,
                    postcode: engLpis[0].postcode,
                    easting: propertyContext.wizardData.savedProperty.xcoordinate,
                    northing: propertyContext.wizardData.savedProperty.ycoordinate,
                    logical_status: propertyContext.wizardData.savedProperty.logicalStatus,
                    classification_code: propertyContext.wizardData.savedProperty.blpuClass,
                  };

                  // TODO: uncommented below code when want to reintroduce asking to edit historic.
                  // if (savedRec.logical_status === 8) {
                  //   historicRec.current = { property: savedRec, related: null };
                  //   setOpenHistoricProperty(true);
                  // } else
                  doOpenRecord(
                    savedRec,
                    null,
                    searchContext.currentSearchData.results,
                    mapContext,
                    streetContext,
                    propertyContext,
                    userContext.current,
                    isScottish.current
                  );
                  break;
              }
              setOpenPropertyWizard(false);
              break;

            case "addChild":
              GetParentHierarchy(propertyContext.wizardData.savedProperty.uprn, userContext.current).then(
                (parentProperties) => {
                  if (!parentProperties || parentProperties.parent.currentParentChildLevel < 4) {
                    propertyContext.resetPropertyErrors(false, false);
                    propertyContext.onClearWizardData();
                    mapContext.onWizardSetCoordinate(null);
                    propertyWizardType.current = "child";
                    propertyWizardParent.current = propertyContext.wizardData.parent;
                    setOpenPropertyWizard(true);
                  } else {
                    saveResult.current = false;
                    saveType.current = "maxParentLevel";
                    saveOpenRef.current = true;
                    setSaveOpen(true);
                  }
                }
              );
              break;

            case "addChildren":
              GetParentHierarchy(propertyContext.wizardData.savedProperty.uprn, userContext.current).then(
                (parentProperties) => {
                  if (!parentProperties || parentProperties.parent.currentParentChildLevel < 4) {
                    propertyContext.resetPropertyErrors(true, true);
                    propertyContext.onClearWizardData();
                    mapContext.onWizardSetCoordinate(null);
                    propertyWizardType.current = "rangeChildren";
                    propertyWizardParent.current = propertyContext.wizardData.parent;
                    setOpenPropertyWizard(true);
                  } else {
                    saveResult.current = false;
                    saveType.current = "maxParentLevel";
                    saveOpenRef.current = true;
                    setSaveOpen(true);
                  }
                }
              );
              break;

            default:
              setOpenPropertyWizard(false);
              displayingWizard.current = false;
              break;
          }
        }
      }
    }
  }, [
    propertyContext.wizardData,
    mapContext,
    propertyContext,
    streetContext,
    userContext.current.currentUser.token,
    searchContext.currentSearchData.results,
  ]);

  // Selecting properties
  useEffect(() => {
    if (!arcgisMap) return;

    if (mapContext.currentBackgroundData.properties) {
      if (debugUseEffects.current) console.log("[DEV] useEffect [23]: Selecting properties");
      if (mapContext.currentBackgroundData.properties.length > 0 && mapContext.selectingProperties) {
        if (!selectingProperties.current) {
          selectedProperties.current = [];
          selectionContext.onSelectionVariantChange(
            selectedEsus.current && selectedEsus.current.length > 0
              ? "mapEsu"
              : selectedExtents && selectedExtents.length > 0
                ? "mapExtent"
                : selectedProperties.current && selectedProperties.current.length > 0
                  ? "mapProperty"
                  : ""
          );
          selectionContext.onSelectionCountChange(
            displayExtentMergeTool
              ? selectedExtents && selectedExtents.length > 0
                ? selectedExtents.length
                : 0
              : selectedEsus.current && selectedEsus.current.length > 0
                ? selectedEsus.current.length
                : selectedProperties.current && selectedProperties.current.length > 0
                  ? selectedProperties.current.length
                  : 0
          );
          selectionContext.onPropertyUprnsChange([]);

          selectingProperties.current = true;

          sketchLayer.current.graphics.removeAll();
          sketchLayer.current.listMode = "hide";

          if (backgroundStreetLayer.current)
            backgroundStreetLayer.current.visible = getLayerVisibility(backgroundStreetLayerTitle);
          if (unassignedEsusLayer.current) unassignedEsusLayer.current.visible = false;
          if (streetLayer.current) streetLayer.current.opacity = getLayerOpacity(streetLayerTitle, true);
          if (llpgStreetLayer.current) llpgStreetLayer.current.visible = false;
          if (backgroundProvenanceLayer.current) backgroundProvenanceLayer.current.visible = false;
          if (backgroundPropertyLayer.current) {
            backgroundPropertyLayer.current.visible = false;
          }
          if (propertyLayer.current) propertyLayer.current.visible = false;
          if (selectPropertyLayer.current) selectPropertyLayer.current.visible = true;

          arcgisMap.view
            .whenLayerView(sketchLayer.current)
            .then((editGraphicsLayerView) => {
              editGraphicsLayerView.highlightOptions = {
                haloOpacity: 0,
                fillOpacity: 0,
              };
            })
            .catch((error) => {
              if (error && userContext.current.currentUser.showMessages) {
                console.error("Error setting highlight options", error);
              }
            });

          mapContext.currentBackgroundData.properties.forEach((property) => {
            sketchLayer.current.add(
              createSelectablePointGraphic(property.easting, property.northing, property.uprn, property.logicalStatus)
            );
          });

          arcgisSketch.current.availableCreateTools = [];
          arcgisSketch.current.visibleElements = {
            deleteButton: false,
            duplicateButton: false,
            settingsMenu: false,
            snappingControls: false,
            undoRedoMenu: false,
          };

          if (userContext.current && userContext.current.currentUser.editProperty && arcgisSketch.current) {
            arcgisMap.append(arcgisSketch.current);
          } else if (arcgisMap.querySelector(`.arcgis-sketch-${mapType}`)) {
            arcgisSketch.current.remove();
          }
        }
      } else if (selectingProperties.current) {
        selectingProperties.current = false;

        if (backgroundStreetLayer.current)
          backgroundStreetLayer.current.visible = getLayerVisibility(backgroundStreetLayerTitle);
        if (unassignedEsusLayer.current)
          unassignedEsusLayer.current.visible = getLayerVisibility(unassignedEsusLayerTitle);
        if (streetLayer.current) streetLayer.current.opacity = getLayerOpacity(streetLayerTitle, false);
        if (llpgStreetLayer.current) llpgStreetLayer.current.visible = true;
        if (backgroundProvenanceLayer.current)
          backgroundProvenanceLayer.current.visible = getLayerVisibility(backgroundProvenanceLayerTitle);
        if (backgroundPropertyLayer.current)
          backgroundPropertyLayer.current.visible = getLayerVisibility(backgroundPropertyLayerTitle);
        if (propertyLayer.current) propertyLayer.current.visible = true;
        if (selectPropertyLayer.current) selectPropertyLayer.current.visible = false;

        if (mapType === "gazetteer") {
          arcgisSketch.current.availableCreateTools = [];
          arcgisSketch.current.visibleElements = {
            deleteButton: true,
            duplicateButton: true,
            settingsMenu: true,
            snappingControls: true,
            undoRedoMenu: true,
          };

          if (arcgisMap.querySelector(`.arcgis-sketch-${mapType}`)) arcgisSketch.current.remove();

          sketchLayer.current.graphics.removeAll();
          sketchLayer.current.listMode = "hide";
        } else {
          // Force a refresh of the editing layer to ensure the selected object is visible
          mapContext.onRefreshSketchLayerChange(true);
        }
      }
    }
  }, [
    arcgisMap,
    mapContext.selectingProperties,
    mapContext.currentBackgroundData.properties,
    mapContext.currentEditObject,
    mapContext,
    mapType,
    selectionContext,
    selectedExtents,
    displayExtentMergeTool,
  ]);

  // Set view to authority extent on load
  useEffect(() => {
    if (mapContext.viewAuthorityExtent && arcgisMap?.view) {
      if (debugUseEffects.current) console.log("[DEV] useEffect [24]: Set view to authority extent on load");
      mapContext.onViewAuthorityExtent(false);

      // Ensure the authority code and spatial reference are set before attempting to get the authority extent
      if (authorityCode.current === 0 && settingsContext.authorityCode > 0) {
        authorityCode.current = settingsContext.authorityCode;
        isScottish.current = settingsContext.isScottish;
        isWelsh.current = settingsContext.isWelsh;
        spatialReferenceWkid.current = mapContext.spatialReference || 27700;
      }

      backgroundExtent.current = getAuthorityExtent(
        authorityCode.current,
        isScottish.current,
        isWelsh.current,
        spatialReferenceWkid.current
      );

      let newExtent = new Extent();
      newExtent.xmin = backgroundExtent.current.xmin;
      newExtent.ymin = backgroundExtent.current.ymin;
      newExtent.xmax = backgroundExtent.current.xmax;
      newExtent.ymax = backgroundExtent.current.ymax;
      newExtent.spatialReference = backgroundExtent.current.spatialReference;

      arcgisMap.view.extent = newExtent;
      if (debugSetExtents.current)
        console.log("[DEV] Setting view extent [17]:", {
          xLength: newExtent.xmax - newExtent.xmin,
          yLength: newExtent.ymax - newExtent.ymin,
          mapType: mapTypeRef.current,
        });
      if (arcgisMap.view.extent) lastBackgroundExtent.current = arcgisMap.view.extent.clone();
    }
  }, [mapContext, arcgisMap, settingsContext]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [25]: Update source street reference");
    sourceStreetRef.current = sourceStreet;
  }, [sourceStreet]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [26]: Update current street reference");
    currentStreetRef.current = currentStreet;
  }, [currentStreet]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [27]: Update current descriptor record reference");
    currentDescriptorRecordRef.current = currentDescriptorRecord;
  }, [currentDescriptorRecord]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [28]: Update current ESU record reference");
    currentEsuRecordRef.current = currentEsuRecord;
  }, [currentEsuRecord]);

  useEffect(() => {
    if (debugUseEffects.current)
      console.log("[DEV] useEffect [29]: Update current highway dedication record reference");
    currentHighwayDedicationRecordRef.current = currentHighwayDedicationRecord;
  }, [currentHighwayDedicationRecord]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [30]: Update current one-way exemption record reference");
    currentOneWayExemptionRecordRef.current = currentOneWayExemptionRecord;
  }, [currentOneWayExemptionRecord]);

  useEffect(() => {
    if (debugUseEffects.current)
      console.log("[DEV] useEffect [31]: Update current maintenance responsibility record reference");
    currentMaintenanceResponsibilityRecordRef.current = currentMaintenanceResponsibilityRecord;
  }, [currentMaintenanceResponsibilityRecord]);

  useEffect(() => {
    if (debugUseEffects.current)
      console.log("[DEV] useEffect [32]: Update current reinstatement category record reference");
    currentReinstatementCategoryRecordRef.current = currentReinstatementCategoryRecord;
  }, [currentReinstatementCategoryRecord]);

  useEffect(() => {
    if (debugUseEffects.current)
      console.log("[DEV] useEffect [33]: Update current OS special designation record reference");
    currentOSSpecialDesignationRecordRef.current = currentOSSpecialDesignationRecord;
  }, [currentOSSpecialDesignationRecord]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [34]: Update current interest record reference");
    currentInterestRecordRef.current = currentInterestRecord;
  }, [currentInterestRecord]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [35]: Update current construction record reference");
    currentConstructionRecordRef.current = currentConstructionRecord;
  }, [currentConstructionRecord]);

  useEffect(() => {
    if (debugUseEffects.current)
      console.log("[DEV] useEffect [36]: Update current special designation record reference");
    currentSpecialDesignationRecordRef.current = currentSpecialDesignationRecord;
  }, [currentSpecialDesignationRecord]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [37]: Update current HWW record reference");
    currentHWWRecordRef.current = currentHWWRecord;
  }, [currentHWWRecord]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [38]: Update current PRoW record reference");
    currentPRoWRecordRef.current = currentPRoWRecord;
  }, [currentPRoWRecord]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [39]: Update current street note record reference");
    currentStreetNoteRecordRef.current = currentStreetNoteRecord;
  }, [currentStreetNoteRecord]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [40]: Update current source property reference");
    sourcePropertyRef.current = sourceProperty;
  }, [sourceProperty]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [41]: Update current property reference");
    currentPropertyRef.current = currentProperty;
  }, [currentProperty]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [42]: Update current LPI record reference");
    currentLpiRecordRef.current = currentLpiRecord;
  }, [currentLpiRecord]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [43]: Update current provenance record reference");
    currentProvenanceRecordRef.current = currentProvenanceRecord;
  }, [currentProvenanceRecord]);

  useEffect(() => {
    if (debugUseEffects.current)
      console.log("[DEV] useEffect [44]: Update current app cross reference record reference");
    currentAppCrossRefRecordRef.current = currentAppCrossRefRecord;
  }, [currentAppCrossRefRecord]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [45]: Update current classification record reference");
    currentClassificationRecordRef.current = currentClassificationRecord;
  }, [currentClassificationRecord]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [46]: Update current organisation record reference");
    currentOrganisationRecordRef.current = currentOrganisationRecord;
  }, [currentOrganisationRecord]);

  useEffect(() => {
    if (debugUseEffects.current)
      console.log("[DEV] useEffect [47]: Update current successor cross reference record reference");
    currentSuccessorCrossRefRecordRef.current = currentSuccessorCrossRefRecord;
  }, [currentSuccessorCrossRefRecord]);

  useEffect(() => {
    if (debugUseEffects.current) console.log("[DEV] useEffect [48]: Update current property note record reference");
    currentPropertyNoteRecordRef.current = currentPropertyNoteRecord;
  }, [currentPropertyNoteRecord]);

  useEffect(() => {
    if (mapContext.sketchToolVisibility && arcgisSketch.current) {
      if (debugUseEffects.current) console.log("[DEV] useEffect [49]: Toggle sketch tool visibility");
      if (mapContext.sketchToolVisibility.hideSketch && arcgisMap.querySelector(`.arcgis-sketch-${mapType}`)) {
        arcgisSketch.current.remove();
      }

      if (!mapContext.sketchToolVisibility.hideSketch && !arcgisMap.querySelector(`.arcgis-sketch-${mapType}`)) {
        arcgisMap.append(arcgisSketch.current);
      }
    }
  }, [arcgisMap, mapContext.sketchToolVisibility, mapType]);

  useEffect(() => {
    if (selectionContext.closeControl) {
      if (debugUseEffects.current) console.log("[DEV] useEffect [50]: Close selection control");
      selectionContext.onControlClosed();
    }
  }, [selectionContext, selectionContext.closeControl, selectionContext.onControlClosed]);

  return (
    <>
      <Box sx={dataFormStyle("ADSMap")} id="ads-map-box">
        <arcgis-map
          id={`arcgis-map-${mapType}`}
          class={`arcgis-map-${mapType}`}
          constraints={{
            minZoom: 2,
            maxZoom: 23,
            rotationEnabled: false,
            lods: TileInfo.create({ spatialReference: { wkid: spatialReferenceWkid.current } }).lods,
          }}
        >
          <arcgis-placement position="bottom-left">
            <Box sx={{ display: "flex", gap: "0.7rem" }}>
              <Box sx={{ display: "flex", gap: "0" }}>
                <arcgis-zoom layout="horizontal"></arcgis-zoom>
                <arcgis-expand
                  expandTooltip="Display basemap layer list"
                  collapseIcon="chevrons-down"
                  collapseTooltip="Close basemap layer list"
                  group="main-toolbar"
                  label="Basemap layers"
                  placement="top-leading"
                  closeOnEsc
                >
                  <arcgis-basemap-gallery
                    id={`arcgis-basemap-gallery-${mapType}`}
                    class={`arcgis-basemap-gallery-${mapType}`}
                    icon="basemap"
                    label="Current basemap layers"
                    source={baseMapGallerySource}
                  ></arcgis-basemap-gallery>
                </arcgis-expand>
                <arcgis-expand
                  expandTooltip="Display layer list"
                  collapseIcon="chevrons-down"
                  collapseTooltip="Close layer list"
                  group="main-toolbar"
                  label="Layers"
                  placement="top-leading"
                  closeOnEsc
                >
                  <arcgis-layer-list
                    id={`arcgis-layer-list-${mapType}`}
                    class={`arcgis-layer-list-${mapType}`}
                    label="Current layers"
                    show-heading
                    listItemCreatedFunction={defineActions}
                  ></arcgis-layer-list>
                </arcgis-expand>
                <CalciteButton
                  icon-start="upload"
                  onClick={handleUploadFile}
                  size="small"
                  title="Upload file"
                  kind="neutral"
                  appearance="outline-fill"
                />
                {displayESUMergeTool && (
                  <CalciteButton
                    icon-start="merge-2"
                    onClick={handleMergeFeatures}
                    size="small"
                    title="Use selected lines for ESU"
                    kind="neutral"
                    appearance="outline-fill"
                  />
                )}
                {displayExtentMergeTool && (
                  <CalciteButton
                    icon-start="merge-2"
                    onClick={handleMergeFeatures}
                    size="small"
                    title="Use selected features for extent"
                    kind="neutral"
                    appearance="outline-fill"
                  />
                )}
                <arcgis-expand
                  expandIcon="measure"
                  expandTooltip="Measure distance/area"
                  collapseIcon="chevrons-down"
                  collapseTooltip="Close measure distance/area"
                  group="main-toolbar"
                  label="Measure distance/area"
                  placement="top-leading"
                  closeOnEsc
                >
                  <arcgis-expand
                    id={`arcgis-distance-measurement-expand-${mapType}`}
                    expandTooltip="Measure distance"
                    collapseIcon="chevrons-down"
                    collapseTooltip="Close measure distance"
                    label="Measure distance"
                    placement="top-leading"
                    group="measure"
                    closeOnEsc
                  >
                    <arcgis-distance-measurement-2d
                      id={`arcgis-distance-measurement-2d-${mapType}`}
                      unit="meters"
                    ></arcgis-distance-measurement-2d>
                  </arcgis-expand>
                  <arcgis-expand
                    id={`arcgis-area-measurement-expand-${mapType}`}
                    expandTooltip="Measure area"
                    collapseIcon="chevrons-down"
                    collapseTooltip="Close measure area"
                    label="Measure area"
                    placement="top-leading"
                    group="measure"
                    closeOnEsc
                  >
                    <arcgis-area-measurement-2d
                      id={`arcgis-area-measurement-2d-${mapType}`}
                      unit="metric"
                    ></arcgis-area-measurement-2d>
                  </arcgis-expand>
                  {isDistanceMeasurementActive && (
                    <CalciteButton
                      id={`clear-distance-measurement-${mapType}`}
                      icon-start="trash"
                      onClick={handleClearDistanceMeasurement}
                      size="small"
                      title="Clear measurements"
                      kind="neutral"
                      appearance="outline-fill"
                    />
                  )}
                  {isAreaMeasurementActive && (
                    <CalciteButton
                      id={`clear-area-measurement-${mapType}`}
                      icon-start="trash"
                      onClick={handleClearAreaMeasurement}
                      size="small"
                      title="Clear measurements"
                      kind="neutral"
                      appearance="outline-fill"
                    />
                  )}
                </arcgis-expand>
              </Box>
              <arcgis-scale-bar bar-style="ruler" unit="metric"></arcgis-scale-bar>
            </Box>
          </arcgis-placement>
          <arcgis-coordinate-conversion
            id={`arcgis-coordinate-conversion-${mapType}`}
            class={`arcgis-coordinate-conversion-${mapType}`}
            position="bottom-right"
            mode="live"
            hideSettingsButton
            label="Coordinates"
            storageType="local"
          ></arcgis-coordinate-conversion>
          <arcgis-sketch
            auto-destroy-disabled
            id={`arcgis-sketch-${mapType}`}
            class={`arcgis-sketch-${mapType}`}
            position="top-right"
            creationMode="update"
            availableCreateTools={sketchCreateTools}
            defaultCreateOptions={{ hasZ: false }}
            defaultUpdateOptions={{
              tool: "reshape",
              enableRotation: false,
              enableZ: false,
              multipleSelectionEnabled: false,
              toggleToolOnClick: false,
              highlightOptions: {
                enabled: false,
              },
            }}
            snappingOptions={{ enabled: true, featureSources: [] }}
            hide-create-tools-circle
            hide-create-tools-rectangle
            hide-delete-button={
              mapContext && mapContext.sketchToolVisibility
                ? mapContext.sketchToolVisibility.hideSketchDeleteButton
                : true
            }
            hide-duplicate-button
            hide-selection-count-label={hideSelectionCountLabel}
            hide-selection-tools-lasso-selection={
              mapContext && mapContext.sketchToolVisibility
                ? mapContext.sketchToolVisibility.hideSketchLassoSelection
                : true
            }
            hide-selection-tools-rectangle-selection={
              mapContext && mapContext.sketchToolVisibility
                ? mapContext.sketchToolVisibility.hideSketchRectangleSelection
                : true
            }
            hide-settings-menu
            hide-snapping-controls
            hide-tooltips-toggle
            hide-undo-redo-menu={
              mapContext && mapContext.sketchToolVisibility
                ? mapContext.sketchToolVisibility.hideSketchUndoRedoMenu
                : true
            }
            label="Sketch"
            scale="s"
          ></arcgis-sketch>
          <arcgis-placement position="top-left">
            <Stack direction="column" spacing={1}>
              {selectionContext.selectionCount > 0 && (
                <ADSSelectionControl
                  variant={selectionContext.selectionVariant}
                  selectionCount={selectionContext.selectionCount}
                  currentStreet={selectionContext.currentStreetSelection}
                  currentEsu={selectionContext.currentEsuSelection}
                  currentProperty={selectionContext.currentPropertySelection}
                  currentAddress={selectionContext.currentAddressSelection}
                  currentClassification={selectionContext.currentClassificationSelection}
                  currentOrganisation={selectionContext.currentOrganisationSelection}
                  currentSuccessorCrossRef={selectionContext.currentSuccessorCrossRefSelection}
                  currentProvenance={selectionContext.currentProvenanceSelection}
                  currentExtent={selectionContext.currentExtentSelection}
                  currentCrossReference={selectionContext.currentCrossReferenceSelection}
                  streetUsrns={selectionContext.streetUsrns}
                  propertyUprns={selectionContext.propertyUprns}
                  onSetCopyOpen={handleSetCopyOpen}
                  onError={handleSelectionControlError}
                  onDeleteClassification={handleDeleteClassification}
                  onDeleteOrganisation={handleDeleteOrganisation}
                  onDeleteSuccessorCrossRef={handleDeleteSuccessorCrossRef}
                  onDeleteProvenance={handleDeleteProvenance}
                  onMergeExtent={handleMergeFeatures}
                  onDeleteCrossReference={handleDeleteCrossReference}
                  onPropertyMoved={handlePropertyMoved}
                  onClose={handleCloseSelection}
                />
              )}
              {informationContext.informationType && (
                <ADSInformationControl
                  variant={informationContext.informationType}
                  onCancel={handleInformationCancel}
                />
              )}
            </Stack>
          </arcgis-placement>
        </arcgis-map>
      </Box>
      {(mapContext.updatingData > 0 || updatingMap) && (
        <div id="progress-indicator">
          <ADSCircularProgress
            display={mapContext.updatingData > 0 || updatingMap}
            mapProgress={searchContext.currentSearchData?.results?.length > 0}
          />
        </div>
      )}
      <Snackbar
        open={saveOpen}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClose={handleSaveClose}
      >
        <Alert
          sx={GetAlertStyle(saveResult.current)}
          icon={GetAlertIcon(saveResult.current)}
          onClose={handleSaveClose}
          severity={GetAlertSeverity(saveResult.current)}
          elevation={6}
          variant="filled"
        >
          {getAlertText()}
        </Alert>
      </Snackbar>
      <AddPropertyWizardDialog
        variant={propertyWizardType.current}
        parent={propertyWizardParent.current}
        isOpen={openPropertyWizard}
        onDone={handlePropertyWizardDone}
        onClose={handlePropertyWizardClose}
      />
      {/* // TODO: uncommented below code when want to reintroduce asking to edit historic. */}
      {/* <HistoricPropertyDialog open={openHistoricProperty} onClose={handleHistoricPropertyClose} /> */}
      <UploadShpFileDialog
        isOpen={openUploadFileDialog}
        currentIds={baseMappingLayerIds.current.concat(defaultMapLayerIds)}
        onClose={handleUploadFileDialogClose}
      />
    </>
  );
}

export default ADSMap;
